// Artifact side panels: Sources, Versions, Export, Publish, Share, Send to, Library,
// Agent runs and the template interview (T1-UI2, PR 1).
//
// Namespace `artifactPanels`; owner T1-UI2. Placeholders are `{name}`, filled with
// `formatCopy`. All 24 app languages are translated. See ./index.ts.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  editingDraftOf: 'Editing a draft of Version {number}',
  noChangesToApply: 'No changes to apply.',
  appliedDone: 'Applied.',
  mentionInChat: 'Mention in chat',

  // Shared
  retry: 'Try again',
  close: 'Close',
  // Panel states from the canonical display envelope
  panelLoading: 'Loading…',
  panelEmpty: 'Nothing here yet.',
  panelDenied: 'You do not have access to this.',
  panelBlocked: 'This is waiting on something before it can continue.',
  panelBlockedViewerContext:
    'This run has no person to read as, so it cannot continue until someone chooses who it runs as.',
  panelError: 'This could not be loaded.',
  panelStale: 'This may be out of date.',
  // Sources
  sourcesTitle: 'Sources',
  sourcesIntro: 'Where each number comes from, and how fresh it is.',
  sourcesLoading: 'Loading sources…',
  sourcesEmptyTitle: 'No sources',
  sourcesEmptyBody: 'This Artifact does not use company data.',
  sourcesErrorBody: 'Sources could not be loaded.',
  sourcesDeniedTitle: 'Some sources are hidden',
  sourcesDeniedBody:
    'You do not have access to {count} sources used here. Ask an owner to share them.',
  sourceDeniedName: 'Source you cannot see',
  sourceAsOf: 'As of {time}',
  sourceAgeUnknown: 'Data age unknown',
  sourceStatusCurrent: 'Current',
  sourceStatusStale: 'Out of date',
  sourceStatusGap: 'Missing in the latest refresh',
  sourceStatusDenied: 'No access',
  sourceLastGood: 'Last good: {time}',
  sourceUsedBy: 'Used by {labels}',
  // Versions
  openDocument: 'Open document',
  versionsShow: 'Show versions',
  versionsHide: 'Hide versions',
  versionsTitle: 'Versions',
  versionsLoading: 'Loading versions…',
  versionsEmptyTitle: 'No versions yet',
  versionsEmptyBody: 'The first version appears when this Artifact is built.',
  versionsErrorBody: 'Versions could not be loaded.',
  versionLabel: 'Version {number}',
  versionOpen: 'Open version {number}',
  versionCurrent: 'Current',
  versionPublished: 'Published',
  versionBy: 'By {actor}',
  versionOriginNative: 'In Poneva',
  versionOriginMcp: 'via your own agent',
  versionOriginTerminal: 'via the terminal',
  versionOriginJob: 'Scheduled run',
  versionCausePerson: 'Edited',
  versionCauseAgent: 'Agent update',
  versionCauseRefresh: 'Refreshed',
  versionCauseRestore: 'Restored',
  versionCauseImport: 'Imported',
  versionCheckedYes: 'Checked',
  versionCheckedNo: 'Not checked yet',
  versionCheckedInvalidated: 'Edited since the last check',
  compareStart: 'Compare',
  compareWith: 'Compare with version {number}',
  compareTitle: 'What changed from v{from} to v{to}',
  compareFromMark: 'From',
  compareToMark: 'To',
  comparePick: 'Pick a second version to compare.',
  compareNone: 'No differences.',
  compareLoading: 'Comparing…',
  compareError: 'The comparison could not be loaded.',
  compareBefore: 'Before',
  compareAfter: 'After',
  compareAdded: 'Added',
  compareRemoved: 'Removed',
  compareChanged: 'Changed',
  restore: 'Restore this version',
  restoreConfirmTitle: 'Restore version {number}?',
  restoreConfirmBody: 'This makes a new version from version {number}. Nothing is deleted.',
  restoreConfirm: 'Restore',
  restoreCancel: 'Cancel',
  restoring: 'Restoring…',
  restoreFailed: 'The version could not be restored.',
  restoreStale: 'This Artifact changed while you were looking. Reload the versions and try again.',
  restoreDenied: 'You do not have permission to restore versions.',
  restored: 'Version {number} restored as a new version.',
  // Publish
  publishTitle: 'Publish',
  publishIntro: 'Version {number} will be visible to {audience}.',
  publishOwnerLine: 'Viewers see what {owner} could see when this version was published.',
  publishChecksTitle: 'Checks',
  checkPassed: 'Checked',
  checkRunning: 'Checking…',
  checkFailed: 'A check failed',
  checkStale: 'Edited since the last check',
  publishFixInChat: 'Fix in chat',
  publishAction: 'Publish version {number}',
  publishing: 'Publishing…',
  publishedDone: 'Version {number} is live for {audience}.',
  publishConfirmGrant:
    'This version needs new access to a company system. Review the access before publishing.',
  publishConfirmWrite:
    'This version writes to a company system. Review the access before publishing.',
  publishConfirmDestructive: 'This version removes data. Confirm it in Poneva first.',
  publishConflict: 'A different version was published first. Reload to see it.',
  publishBlockedNotAuthorized: 'You do not have permission to publish.',
  publishBlockedCheckFailed: 'A check failed, so this version cannot be published.',
  publishBlockedCheckStale: 'The Artifact changed after its last check. Check it again.',
  publishBlockedObjects: 'Some files could not be verified. Try again in a moment.',
  publishBlockedPolicy: 'Company settings changed. Review and try again.',
  publishBlockedApproval: 'The confirmation is no longer valid. Confirm again.',
  publishBlockedDependency: 'A needed service is unavailable. Try again later.',
  publishAsk: 'Ask {publishers} to publish',
  publishAskFooter: 'People who can publish: {publishers}.',
  publishAskNone: 'Ask someone who can publish.',
  publishWarnings: 'Worth a look before you publish',
  checkWarnStale: '{subject} is out of date ({time}).',
  checkWarnGap: '{subject} was missing in the latest refresh.',
  checkWarnUnknownSource: 'A source you cannot see needs a look.',
  checkRefreshFirst: 'Refresh first',
  checkFailedRow: '{subject} failed its check.',
  checkFailedGeneric: 'A check failed.',
  publishReviewAccess: 'Review access',
  publishRemovalBody: 'v{number} removes {what} ({count}). A restore point is saved first.',
  publishReviewRemoval: "Review what's removed",
  publishAndRemove: 'Publish and remove',
  publishReviewFirst: 'Review what is removed before you publish.',
  publishDoneTitle: 'Version {number} is live for {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'They will find it in their Library and pinned for the team.',
  publishUndo: 'Undo',
  publishCopyLink: 'Copy link',
  publishDone: 'Done',
  shareRoleViewMeaning: 'Can open and read it',
  shareRoleEditMeaning: 'Can change it in chat and with the inspector',
  shareRolePublishMeaning: 'Can also publish and unpublish',
  shareRoleMenu: 'Role for {name}',
  shareUnpublishConfirm: 'People lose access. The Artifact and its versions are kept.',
  shareUnpublishCancel: 'Keep it live',
  sendDestinationLine: '{provider} · {count} people',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    "Everyone in {destination} sees the summary. {outside} of the {total} aren't in this Artifact's audience, so the link shows them “Ask for access”.",
  sendAudienceGeneric: 'People outside the audience see “Ask for access”.',
  sendPreview: 'What they will see',
  // Share
  shareTitle: 'Share',
  shareLoading: 'Loading sharing…',
  shareErrorBody: 'Sharing could not be loaded.',
  sharePrivate: 'Only you can see this Artifact.',
  shareShared: 'Visible to {audience}.',
  shareRevoked: 'Sharing is turned off. Nobody else can open this Artifact.',
  shareOwnerSnapshot: 'Viewers see the numbers as published. They cannot refresh or edit.',
  shareRoleView: 'Can view',
  shareRoleEdit: 'Can edit',
  shareRolePublish: 'Can publish',
  shareLivePublished: 'Live: version {number}',
  shareUnpublish: 'Unpublish',
  shareManageDenied: 'Only an editor can change who sees this.',
  // Send to
  sendTitle: 'Send to',
  sendIntro: 'Send a link and summary through a connected app. You approve every send.',
  sendLoading: 'Loading destinations…',
  sendErrorBody: 'Destinations could not be loaded.',
  sendNone: 'No connected app can receive this yet. Connect Slack, Teams or email first.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'Email',
  sendAudience: 'Goes to {audience}',
  sendSummary: 'Message',
  sendLeaves: 'A link is sent.',
  sendLeavesAttachment: 'A link and the reviewed file are sent.',
  sendNoAttachments: 'This destination cannot take files, so only a link is sent.',
  sendScheduled: 'Scheduled sends need your approval each time.',
  sendAction: 'Review and send',
  sendFileUnsupported:
    'This destination cannot take the file. Send a link only, or choose another destination.',
  sendLinkOnly: 'Send a link only',
  sendWaiting: 'Waiting for your approval. Nothing has been sent.',
  sendDelivered: 'Sent to {destination}.',
  sendFailed: 'It was not sent. Nothing left Poneva.',
  sendUnknown: 'We could not confirm delivery to {destination}. Check there before sending again.',
  sendBlockedNotAuthorized: 'You do not have permission to send this.',
  sendBlockedUnsupported: 'This destination is not supported.',
  sendBlockedApproval: 'The approval is no longer valid. Review and send again.',
  sendBlockedChanged: 'The message or recipient changed. Review it again.',
  sendBlockedDependency: 'The connected app is unavailable. Try again later.',
  // Library
  libraryTitle: 'Library',
  librarySegmentAll: 'All',
  librarySegmentMine: 'Mine',
  librarySegmentShared: 'Shared with me',
  librarySearch: 'Search Artifacts',
  libraryKindAll: 'All kinds',
  libraryKindReports: 'Reports and pages',
  libraryKindDecks: 'Decks',
  libraryKindDocuments: 'Documents',
  libraryKindSpreadsheets: 'Spreadsheets',
  libraryKindApps: 'Apps',
  libraryAppsMatchNoun: 'one:App matches in Apps →|other:Apps match in Apps →',
  libraryMake: 'Start in chat',
  libraryEmptySharedHint: 'When someone shares an Artifact with you, it appears here.',
  libraryRefreshFailedAt: 'Couldn’t refresh on {time}.',
  libraryRefreshTryAgain: 'Try again',
  libraryVia: 'via {owner}',
  savedPrivate: 'Private',
  savedVersion: 'Version {n}',
  libraryLoading: 'Loading your Library…',
  libraryErrorBody: 'Your Library couldn’t load right now.',
  startersLoadFailed: 'Templates couldn’t load right now.',
  libraryCannotOpen: '{title} can’t be opened here yet.',
  libraryEmptyTitle: 'Nothing here yet',
  libraryEmptyAllTitle: 'Make something worth sharing.',
  libraryEmptyAllBody:
    'Documents, decks and apps you make in chat, or that others share with you, appear here.',
  libraryEmptyMineBody: 'Artifacts you make appear here.',
  libraryEmptySharedBody: 'Nothing has been shared with you yet.',
  libraryNoMatchTitle: 'No matches',
  libraryNoMatchBody: 'Try another word or a different kind.',
  libraryMore: 'Show more',
  libraryOwner: 'By {owner}',
  libraryUpdated: 'Edited {time}',
  libraryPublishedAs: 'Published · v{number}',
  libraryDraft: 'Draft',
  libraryUnpublished: 'Unpublished',
  libraryRefreshFailed: 'Refresh failed',
  libraryRefreshRunning: 'Refreshing',
  libraryAudience: 'For {audience}',
  kindReport: 'Report',
  kindPage: 'Page',
  kindSite: 'Site',
  kindDeck: 'Deck',
  kindDocument: 'Document',
  kindApp: 'App',
  kindSpreadsheet: 'Spreadsheet',
  kindDashboard: 'Dashboard',
  kindTable: 'Table',
  kindChart: 'Chart',
  kindMarkdown: 'Note',
  kindKpi: 'Metric',
  libraryExportsList: 'Exports to {formats}',
  libraryOpensToRead: 'Opens to read',
  libraryReadOnly: 'Read-only',
  libraryLiveView: 'Live view',
  libraryOneNumber: 'One number · as of {time}',
  kindExportsReport: 'Exports to PDF',
  kindExportsPage: 'Read-only page',
  kindExportsSite: 'Read-only site',
  kindExportsDeck: 'Present, or export to PowerPoint and PDF',
  kindExportsDocument: 'Exports to Word and PDF',
  kindExportsSpreadsheet: 'Exports to Excel and CSV',
  kindExportsDashboard: 'Live view',
  kindExportsTable: 'Exports to Excel and CSV',
  kindExportsChart: 'Chart view',
  // Agent runs (Inbox rows) and the Agent page
  agentPublished: '{agent} published {title} v{number}.',
  agentCheckedOn: 'Checked at {views}, {themes}.',
  agentUndo: 'Undo',
  agentRestorePrevious: 'Restore v{number}',
  agentHeld: '{title} v{number} is waiting as a draft.',
  agentHeldMismatch: '{count} numbers did not match their sources.',
  agentHeldCheck: 'A check did not pass.',
  agentFixInChat: 'Fix in chat',
  agentConfirmDrop:
    '{agent} wants to remove {what} from {title}. A restore point is saved first. Nothing changes until you decide.',
  agentConfirmDropCount:
    '{agent} wants to remove {what} ({count} values) from {title}. A restore point is saved first. Nothing changes until you decide.',
  agentViewDesktop: 'desktop',
  agentViewTablet: 'tablet',
  agentViewPhone: 'phone',
  agentThemeLight: 'light',
  agentThemeDark: 'dark',
  agentLookAt: 'Look at v{number}',
  agentConfirmWrite: '{agent} wants to write {what}. Nothing is written until you decide.',
  agentConfirmGrant: '{agent} wants access to {what}. Nothing is shared until you decide.',
  agentReview: 'Review',
  agentSkipped: '{agent} skipped {part}.',
  agentSkippedWhy: 'It reads each person’s own {system}, and this run has no person to read as.',
  agentChooseWho: 'Choose who it runs as',
  agentChangeInChat: 'Change in chat',
  agentFailed: '{agent} could not refresh {title}.',
  agentFailedBody: 'The last published version is still live.',
  agentRetry: 'Try again',
  agentRunning: '{agent} is updating {title}.',
  agentMakesTitle: 'Makes and updates',
  agentMakesLoading: 'Loading…',
  agentMakesErrorBody: 'This could not be loaded.',
  agentMakesNone: 'This Agent does not make Artifacts yet.',
  agentMakesNew: 'A new Artifact each run',
  agentMakesUpdate: 'Updates one Artifact each run',
  agentNextRun: 'Next run {when}',
  agentNoSchedule: 'Not scheduled',
  agentCreditsPerRun: 'About {credits} credits per run',
  agentCreditsUnknown: 'Credits per run are not known yet',
  agentLastResult: 'Last result',
  // Template interview
  interviewTitle: 'Set it up',
  interviewIntro: 'A few questions shape it.',
  interviewOptional: '(optional)',
  interviewAnswerNeeded: 'Answer this to continue.',
  interviewReadOnly: 'Read only',
  interviewChange: 'Change',
  interviewConnectionYours: 'your {system} connection',
  interviewConnectionOther: "{name}'s {system} connection",
  interviewDateAny: 'Any period',
  interviewDatePrevious: 'Previous month',
  interviewDateNext: 'Next month',
  interviewDatePresets: 'Quick ranges',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Clear',
  interviewDateDone: 'Done',
  interviewChooseSource: 'Choose a source',
  interviewSourceChosen: 'Source chosen',
  interviewSourceUnavailable: 'Choosing a source isn’t available here yet, so this can’t continue.',
  interviewSourceUnavailableOptional:
    'Choosing a source isn’t available here yet. You can continue without one.',
  interviewStarterWithdrawn:
    'This template isn’t available any more. Drafts you already started from it still open.',
  interviewDateFrom: 'From',
  interviewDateTo: 'To',
  interviewContinue: 'Continue',
  interviewGrantTitle: 'Allow reading {source}?',
  interviewGrantBody:
    'It uses {connection}, read only, for {scope}. Nothing is read until you allow it, and no sample rows are shown.',
  interviewAllow: 'Allow',
  interviewNotNow: 'Not now',
  interviewConfiguring: 'Setting it up from your answers…',
  interviewChecking: 'Checking it before it opens…',
  interviewComplete: '{title} is ready.',
  interviewOpen: 'Open {title}',
  interviewBlocked: 'This could not continue.',
  interviewFailed: 'Setting it up failed. Your answers are kept.',
  interviewCancelled: 'This was cancelled.',
  interviewSaving: 'Saving…',
  interviewSaved: 'Saved',
  interviewConflict:
    'This draft changed somewhere else. Your answers are kept here until you load the current draft, which replaces them.',
  interviewReloadCurrent: 'Load the current draft',
  interviewUnsettled: "We couldn't confirm your last save. Your answers are kept here.",
  interviewCheckAgain: 'Check again',
  finishAction: 'Save privately',
  finishSaving: 'Saving…',
  finishSaved: '{title} is saved, visible only to you.',
  finishNotSaved: 'It wasn’t saved. Your draft is still here.',
  finishUnconfirmed: 'We couldn’t confirm whether it was saved.',
  candidateInvalid: 'The draft couldn’t be made into a document.',
  finishOpenInLibrary: 'Open in Library',
  savedOpenFailed: 'Couldn’t open it right now.',
  savedInLibrary: 'It’s in your Library.',
  openSavedTitle: 'Open {title} →',
  interviewReasonNotAuthorized: 'You do not have permission to change this draft.',
  interviewReasonDraftNotOpen: 'This draft is no longer open.',
  interviewReasonInvalid: 'One of the answers is not valid.',
  interviewReasonObjects: 'Some files could not be verified. Your answers are kept.',
  interviewReasonDependency: 'A needed service is unavailable. Your answers are kept.',
  interviewReasonReconcile: 'The last save is still being confirmed.',
  // Export
  exportTitle: 'Export',
  exportIntro: 'Download this Artifact. The file says where its data comes from.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Editable slides with speaker notes',
  exportHintPdf: 'Exactly as shown',
  exportHintDocx: 'Editable, keeps headings and tables',
  exportHintXlsx: 'All sheets, keeps formulas',
  exportHintCsv: 'Values only, UTF-8',
  exportUnavailable: 'Not available right now',
  exportRunning: 'Preparing {format}…',
  exportValidating: 'Checking the {format} file…',
  exportReady: '{format} file is ready',
  exportDownload: 'Download {name}',
  exportVersion: 'Exported version {number}',
  exportFailed: 'The {format} file could not be made.',
  exportFailedChecks: 'The {format} file did not pass its checks, so it was not offered.',
  exportDenied: 'You do not have access to export this Artifact.',
  exportRevoked: 'Sharing was turned off, so this Artifact can no longer be exported.',
  exportStale: 'This Artifact changed while exporting. Export again to get the newest version.',
  exportProvenanceTitle: 'Every file includes',
  exportProvenanceLine: 'Made with Poneva · {title} · v{number} · {data} · made {madeAt}',
  exportProvenanceAsOf: 'data as of {time}',
  exportPlacementNotesAbout: 'On the Notes sheet, and an about file beside the CSV',
  exportProvenanceRange: 'data from {earliest} to {latest}',
  exportProvenanceUnknown: 'data age unknown',
  exportAboutFile: 'About this file ({format})',
  exportPlacementFooter: "In every page's footer",
  exportPlacementSlide: "In every slide's footer",
  exportPlacementNotes: 'On the Notes sheet',
  exportPlacementAbout: 'In an about file beside the CSV',
  exportSourcesPage: 'Add a sources page',
  exportSourcesPageHint: 'A last page listing each number, its source and its time.',
  exportSourcesPageDeck: "The deck's own Sources slide goes with it.",
  exportSourcesPageSaved: 'Remembered for this Artifact, including scheduled sends.',
  libraryKindSheetTitle: 'Kind',
  shareAccessSheetTitle: 'Access',

  requestShowAll: 'Show all',
  requestShowLess: 'Show less',
  approveGettingReady: 'Getting this ready…',
  approveNotReady: 'This can’t be approved right now. Reload the page, then try again.',
  tryWithModel: 'Try with {model}',
  tryWithRequesting: 'Starting…',
  tryWithUnavailable: 'This can’t be started right now.',
};
export type ArtifactPanelsCopyKey = keyof typeof en;

const bg: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Редактирате чернова на версия {number}',
  noChangesToApply: 'Няма промени за прилагане.',
  appliedDone: 'Приложено.',
  mentionInChat: 'Спомени в чата',

  kindMarkdown: 'Бележка',
  kindKpi: 'Показател',
  libraryExportsList: 'Експортира се като {formats}',
  libraryOpensToRead: 'Отваря се за четене',
  libraryReadOnly: 'Само за четене',
  libraryLiveView: 'Изглед на живо',
  libraryOneNumber: 'Едно число · към {time}',
  retry: 'Опитайте отново',
  close: 'Затвори',
  panelLoading: 'Зареждане…',
  panelEmpty: 'Още няма нищо.',
  panelDenied: 'Нямате достъп до това.',
  panelBlocked: 'Това изчаква нещо, за да продължи.',
  panelBlockedViewerContext:
    'Това изпълнение няма човек, от чието име да чете, и не може да продължи, докато някой не избере от чие име да работи.',
  panelError: 'Това не можа да се зареди.',
  panelStale: 'Това може да е остаряло.',
  sourcesTitle: 'Източници',
  sourcesIntro: 'Откъде идва всяко число и колко е актуално.',
  sourcesLoading: 'Зареждане на източниците…',
  sourcesEmptyTitle: 'Няма източници',
  sourcesEmptyBody: 'Този артефакт не използва данни на компанията.',
  sourcesErrorBody: 'Източниците не можаха да се заредят.',
  sourcesDeniedTitle: 'Някои източници са скрити',
  sourcesDeniedBody:
    'Нямате достъп до {count} източника, използвани тук. Помолете собственик да ги сподели.',
  sourceDeniedName: 'Източник, който не виждате',
  sourceAsOf: 'Към {time}',
  sourceAgeUnknown: 'Възрастта на данните е неизвестна',
  sourceStatusCurrent: 'Актуален',
  sourceStatusStale: 'Остарял',
  sourceStatusGap: 'Липсва в последното обновяване',
  sourceStatusDenied: 'Няма достъп',
  sourceLastGood: 'Последно успешно: {time}',
  sourceUsedBy: 'Използва се от {labels}',
  openDocument: 'Отвори документа',
  versionsShow: 'Покажи версиите',
  versionsHide: 'Скрий версиите',
  versionsTitle: 'Версии',
  versionsLoading: 'Зареждане на версиите…',
  versionsEmptyTitle: 'Още няма версии',
  versionsEmptyBody: 'Първата версия се появява, когато този артефакт бъде създаден.',
  versionsErrorBody: 'Версиите не можаха да се заредят.',
  versionLabel: 'Версия {number}',
  versionOpen: 'Отвори версия {number}',
  versionCurrent: 'Текуща',
  versionPublished: 'Публикувана',
  versionBy: 'От {actor}',
  versionOriginNative: 'В Poneva',
  versionOriginMcp: 'чрез вашия собствен агент',
  versionOriginTerminal: 'чрез терминала',
  versionOriginJob: 'Планирано изпълнение',
  versionCausePerson: 'Редактирана',
  versionCauseAgent: 'Обновяване от агент',
  versionCauseRefresh: 'Обновена',
  versionCauseRestore: 'Възстановена',
  versionCauseImport: 'Импортирана',
  versionCheckedYes: 'Проверена',
  versionCheckedNo: 'Още не е проверена',
  versionCheckedInvalidated: 'Редактирана след последната проверка',
  compareStart: 'Сравни',
  compareWith: 'Сравни с версия {number}',
  compareTitle: 'Какво се е променило от v{from} до v{to}',
  compareFromMark: 'От',
  compareToMark: 'До',
  comparePick: 'Изберете втора версия за сравнение.',
  compareNone: 'Няма разлики.',
  compareLoading: 'Сравняване…',
  compareError: 'Сравнението не можа да се зареди.',
  compareBefore: 'Преди',
  compareAfter: 'След',
  compareAdded: 'Добавено',
  compareRemoved: 'Премахнато',
  compareChanged: 'Променено',
  restore: 'Възстанови тази версия',
  restoreConfirmTitle: 'Да се възстанови ли версия {number}?',
  restoreConfirmBody: 'Това създава нова версия от версия {number}. Нищо не се изтрива.',
  restoreConfirm: 'Възстанови',
  restoreCancel: 'Отказ',
  restoring: 'Възстановяване…',
  restoreFailed: 'Версията не можа да се възстанови.',
  restoreStale:
    'Този артефакт се промени, докато го разглеждахте. Заредете версиите отново и опитайте пак.',
  restoreDenied: 'Нямате право да възстановявате версии.',
  restored: 'Версия {number} е възстановена като нова версия.',
  publishTitle: 'Публикуване',
  publishIntro: 'Версия {number} ще бъде видима за {audience}.',
  publishOwnerLine:
    'Зрителите виждат това, което {owner} е можел да вижда, когато тази версия е била публикувана.',
  publishChecksTitle: 'Проверки',
  checkPassed: 'Проверена',
  checkRunning: 'Проверява се…',
  checkFailed: 'Проверка не е преминала',
  checkStale: 'Редактирана след последната проверка',
  publishFixInChat: 'Поправи в чата',
  publishAction: 'Публикувай версия {number}',
  publishing: 'Публикуване…',
  publishedDone: 'Версия {number} е активна за {audience}.',
  publishConfirmGrant:
    'Тази версия изисква нов достъп до фирмена система. Прегледайте достъпа, преди да публикувате.',
  publishConfirmWrite:
    'Тази версия записва във фирмена система. Прегледайте достъпа, преди да публикувате.',
  publishConfirmDestructive: 'Тази версия премахва данни. Първо го потвърдете в Poneva.',
  publishConflict: 'Първо е публикувана друга версия. Заредете отново, за да я видите.',
  publishBlockedNotAuthorized: 'Нямате право да публикувате.',
  publishBlockedCheckFailed: 'Проверка не е преминала, затова тази версия не може да се публикува.',
  publishBlockedCheckStale:
    'Артефактът се е променил след последната си проверка. Проверете го отново.',
  publishBlockedObjects: 'Някои файлове не можаха да бъдат потвърдени. Опитайте отново след малко.',
  publishBlockedPolicy: 'Настройките на компанията са се променили. Прегледайте и опитайте отново.',
  publishBlockedApproval: 'Потвърждението вече не е валидно. Потвърдете отново.',
  publishBlockedDependency: 'Необходима услуга не е достъпна. Опитайте отново по-късно.',
  publishAsk: 'Помолете {publishers} да публикуват',
  publishAskFooter: 'Хора, които могат да публикуват: {publishers}.',
  publishAskNone: 'Помолете някого, който може да публикува.',
  publishWarnings: 'Струва си да погледнете, преди да публикувате',
  checkWarnStale: '{subject} е остарял ({time}).',
  checkWarnGap: '{subject} липсваше в последното обновяване.',
  checkWarnUnknownSource: 'Източник, който не виждате, се нуждае от преглед.',
  checkRefreshFirst: 'Първо обновете',
  checkFailedRow: '{subject} не премина проверката си.',
  checkFailedGeneric: 'Проверка не е преминала.',
  publishReviewAccess: 'Прегледай достъпа',
  publishRemovalBody:
    'v{number} премахва {what} ({count}). Първо се запазва точка за възстановяване.',
  publishReviewRemoval: 'Прегледай какво се премахва',
  publishAndRemove: 'Публикувай и премахни',
  publishReviewFirst: 'Прегледайте какво се премахва, преди да публикувате.',
  publishDoneTitle: 'Версия {number} е активна за {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Те ще я намерят в своята библиотека и закачена за екипа.',
  publishUndo: 'Отмени',
  publishCopyLink: 'Копирай връзката',
  publishDone: 'Готово',
  shareRoleViewMeaning: 'Може да го отваря и чете',
  shareRoleEditMeaning: 'Може да го променя в чата и с инспектора',
  shareRolePublishMeaning: 'Може също да публикува и да премахва публикуването',
  shareRoleMenu: 'Роля за {name}',
  shareUnpublishConfirm: 'Хората губят достъп. Артефактът и неговите версии се запазват.',
  shareUnpublishCancel: 'Остави активен',
  sendDestinationLine: '{provider} · {count} души',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Всички в {destination} виждат обобщението. {outside} от {total} не са в аудиторията на този артефакт, затова връзката им показва „Поискай достъп“.',
  sendAudienceGeneric: 'Хората извън аудиторията виждат „Поискай достъп“.',
  sendPreview: 'Какво ще видят',
  shareTitle: 'Споделяне',
  shareLoading: 'Зареждане на споделянето…',
  shareErrorBody: 'Споделянето не можа да се зареди.',
  sharePrivate: 'Само вие виждате този артефакт.',
  shareShared: 'Видим за {audience}.',
  shareRevoked: 'Споделянето е изключено. Никой друг не може да отвори този артефакт.',
  shareOwnerSnapshot:
    'Зрителите виждат числата така, както са публикувани. Не могат да ги обновяват или редактират.',
  shareRoleView: 'Може да вижда',
  shareRoleEdit: 'Може да редактира',
  shareRolePublish: 'Може да публикува',
  shareLivePublished: 'Активна: версия {number}',
  shareUnpublish: 'Премахни публикуването',
  shareManageDenied: 'Само редактор може да променя кой вижда това.',
  sendTitle: 'Изпрати до',
  sendIntro:
    'Изпратете връзка и обобщение през свързано приложение. Вие одобрявате всяко изпращане.',
  sendLoading: 'Зареждане на местоназначенията…',
  sendErrorBody: 'Местоназначенията не можаха да се заредят.',
  sendNone:
    'Все още никое свързано приложение не може да получи това. Първо свържете Slack, Teams или имейл.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'Имейл',
  sendAudience: 'Отива при {audience}',
  sendSummary: 'Съобщение',
  sendLeaves: 'Изпраща се връзка.',
  sendLeavesAttachment: 'Изпращат се връзка и прегледаният файл.',
  sendNoAttachments: 'Това местоназначение не приема файлове, затова се изпраща само връзка.',
  sendScheduled: 'Планираните изпращания се нуждаят от вашето одобрение всеки път.',
  sendAction: 'Прегледай и изпрати',
  sendFileUnsupported:
    'Това местоназначение не приема файла. Изпратете само връзка или изберете друго местоназначение.',
  sendLinkOnly: 'Изпрати само връзка',
  sendWaiting: 'Чака вашето одобрение. Нищо не е изпратено.',
  sendDelivered: 'Изпратено до {destination}.',
  sendFailed: 'Не е изпратено. Нищо не е напуснало Poneva.',
  sendUnknown:
    'Не можахме да потвърдим доставката до {destination}. Проверете там, преди да изпратите отново.',
  sendBlockedNotAuthorized: 'Нямате право да изпратите това.',
  sendBlockedUnsupported: 'Това местоназначение не се поддържа.',
  sendBlockedApproval: 'Одобрението вече не е валидно. Прегледайте и изпратете отново.',
  sendBlockedChanged: 'Съобщението или получателят са се променили. Прегледайте отново.',
  sendBlockedDependency: 'Свързаното приложение не е достъпно. Опитайте отново по-късно.',
  libraryTitle: 'Библиотека',
  librarySegmentAll: 'Всички',
  librarySegmentMine: 'Мои',
  librarySegmentShared: 'Споделени с мен',
  librarySearch: 'Търсене на артефакти',
  libraryKindAll: 'Всички видове',
  libraryKindReports: 'Отчети и страници',
  libraryKindDecks: 'Презентации',
  libraryKindDocuments: 'Документи',
  libraryKindSpreadsheets: 'Електронни таблици',
  libraryKindApps: 'Приложения',
  libraryAppsMatchNoun:
    'one:приложение съвпада в Приложения →|other:приложения съвпадат в Приложения →',
  libraryMake: 'Започнете в чата',
  libraryEmptySharedHint: 'Когато някой сподели артефакт с вас, той се появява тук.',
  libraryRefreshFailedAt: 'Неуспешно обновяване на {time}.',
  libraryRefreshTryAgain: 'Опитайте отново',
  libraryVia: 'чрез {owner}',
  savedPrivate: 'Личен',
  savedVersion: 'Версия {n}',
  libraryLoading: 'Зареждане на вашата библиотека…',
  libraryErrorBody: 'Библиотеката ви не можа да се зареди в момента.',
  startersLoadFailed: 'Шаблоните не можаха да се заредят в момента.',
  libraryCannotOpen: '„{title}“ все още не може да се отвори тук.',
  libraryEmptyTitle: 'Още няма нищо',
  libraryEmptyAllTitle: 'Създайте нещо, което си заслужава да споделите.',
  libraryEmptyAllBody:
    'Документи, презентации и приложения, които създавате в чата или които други споделят с вас, се появяват тук.',
  libraryEmptyMineBody: 'Артефактите, които създавате, се появяват тук.',
  libraryEmptySharedBody: 'Още нищо не е споделено с вас.',
  libraryNoMatchTitle: 'Няма съвпадения',
  libraryNoMatchBody: 'Опитайте с друга дума или друг вид.',
  libraryMore: 'Покажи още',
  libraryOwner: 'От {owner}',
  libraryUpdated: 'Редактиран {time}',
  libraryPublishedAs: 'Публикуван · v{number}',
  libraryDraft: 'Чернова',
  libraryUnpublished: 'Непубликуван',
  libraryRefreshFailed: 'Обновяването не успя',
  libraryRefreshRunning: 'Обновява се',
  libraryAudience: 'За {audience}',
  kindReport: 'Отчет',
  kindPage: 'Страница',
  kindSite: 'Сайт',
  kindDeck: 'Презентация',
  kindDocument: 'Документ',
  kindApp: 'Приложение',
  kindSpreadsheet: 'Електронна таблица',
  kindDashboard: 'Табло',
  kindTable: 'Таблица',
  kindChart: 'Диаграма',
  kindExportsReport: 'Експортира се в PDF',
  kindExportsPage: 'Страница само за четене',
  kindExportsSite: 'Сайт само за четене',
  kindExportsDeck: 'Представяне или експорт в PowerPoint и PDF',
  kindExportsDocument: 'Експортира се в Word и PDF',
  kindExportsSpreadsheet: 'Експортира се в Excel и CSV',
  kindExportsDashboard: 'Изглед на живо',
  kindExportsTable: 'Експортира се в Excel и CSV',
  kindExportsChart: 'Изглед на диаграма',
  agentPublished: '{agent} публикува {title} v{number}.',
  agentCheckedOn: 'Проверено на {views}, {themes}.',
  agentUndo: 'Отмени',
  agentRestorePrevious: 'Възстанови v{number}',
  agentHeld: '{title} v{number} чака като чернова.',
  agentHeldMismatch: '{count} числа не съвпаднаха с източниците си.',
  agentHeldCheck: 'Една проверка не премина.',
  agentFixInChat: 'Поправи в чата',
  agentConfirmDrop:
    '{agent} иска да премахне {what} от {title}. Първо се запазва точка за възстановяване. Нищо не се променя, докато не решите.',
  agentConfirmDropCount:
    '{agent} иска да премахне {what} ({count} стойности) от {title}. Първо се запазва точка за възстановяване. Нищо не се променя, докато не решите.',
  agentViewDesktop: 'компютър',
  agentViewTablet: 'таблет',
  agentViewPhone: 'телефон',
  agentThemeLight: 'светла',
  agentThemeDark: 'тъмна',
  agentLookAt: 'Прегледай v{number}',
  agentConfirmWrite: '{agent} иска да запише {what}. Нищо не се записва, докато не решите.',
  agentConfirmGrant: '{agent} иска достъп до {what}. Нищо не се споделя, докато не решите.',
  agentReview: 'Прегледай',
  agentSkipped: '{agent} пропусна {part}.',
  agentSkippedWhy:
    'Чете личния {system} на всеки човек, а това изпълнение няма човек, от чието име да чете.',
  agentChooseWho: 'Изберете от чие име да работи',
  agentChangeInChat: 'Промени в чата',
  agentFailed: '{agent} не успя да обнови {title}.',
  agentFailedBody: 'Последната публикувана версия остава активна.',
  agentRetry: 'Опитайте отново',
  agentRunning: '{agent} обновява {title}.',
  agentMakesTitle: 'Създава и обновява',
  agentMakesLoading: 'Зареждане…',
  agentMakesErrorBody: 'Това не можа да се зареди.',
  agentMakesNone: 'Този агент още не създава артефакти.',
  agentMakesNew: 'Нов артефакт при всяко изпълнение',
  agentMakesUpdate: 'Обновява един артефакт при всяко изпълнение',
  agentNextRun: 'Следващо изпълнение {when}',
  agentNoSchedule: 'Няма график',
  agentCreditsPerRun: 'Около {credits} кредита на изпълнение',
  agentCreditsUnknown: 'Кредитите на изпълнение още не са известни',
  agentLastResult: 'Последен резултат',
  interviewTitle: 'Настройте го',
  interviewIntro: 'Няколко въпроса го оформят.',
  interviewOptional: '(по избор)',
  interviewAnswerNeeded: 'Отговорете, за да продължите.',
  interviewReadOnly: 'Само за четене',
  interviewChange: 'Промени',
  interviewConnectionYours: 'вашата връзка с {system}',
  interviewConnectionOther: 'връзката на {name} с {system}',
  interviewDateAny: 'Всеки период',
  interviewDatePrevious: 'Предишен месец',
  interviewDateNext: 'Следващ месец',
  interviewDatePresets: 'Бързи периоди',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Изчисти',
  interviewDateDone: 'Готово',
  interviewChooseSource: 'Изберете източник',
  interviewSourceChosen: 'Източникът е избран',
  interviewSourceUnavailable:
    'Изборът на източник още не е наличен тук, затова не може да продължи.',
  interviewSourceUnavailableOptional:
    'Изборът на източник все още не е наличен тук. Можете да продължите и без него.',
  interviewStarterWithdrawn:
    'Този шаблон вече не е наличен. Черновите, които вече сте започнали от него, продължават да се отварят.',
  interviewDateFrom: 'От',
  interviewDateTo: 'До',
  interviewContinue: 'Продължи',
  interviewGrantTitle: 'Да се разреши ли четенето на {source}?',
  interviewGrantBody:
    'Използва {connection}, само за четене, за {scope}. Нищо не се чете, докато не разрешите, и не се показват примерни редове.',
  interviewAllow: 'Разреши',
  interviewNotNow: 'Не сега',
  interviewConfiguring: 'Настройване според вашите отговори…',
  interviewChecking: 'Проверка, преди да се отвори…',
  interviewComplete: '{title} е готов.',
  interviewOpen: 'Отвори {title}',
  interviewBlocked: 'Това не можа да продължи.',
  interviewFailed: 'Настройването не успя. Отговорите ви са запазени.',
  interviewCancelled: 'Това беше отменено.',
  interviewSaving: 'Запазване…',
  interviewSaved: 'Запазено',
  interviewConflict:
    'Тази чернова е променена другаде. Отговорите ви остават тук, докато не заредите текущата чернова, която ги замества.',
  interviewReloadCurrent: 'Зареди текущата чернова',
  interviewUnsettled: 'Не можахме да потвърдим последното ви запазване. Отговорите ви остават тук.',
  interviewCheckAgain: 'Провери отново',
  finishAction: 'Запази лично',
  finishSaving: 'Запазване…',
  finishSaved: '„{title}“ е запазен и се вижда само от вас.',
  finishNotSaved: 'Не беше запазен. Черновата ви още е тук.',
  finishUnconfirmed: 'Не успяхме да потвърдим дали е запазен.',
  candidateInvalid: 'Черновата не можа да бъде превърната в документ.',
  finishOpenInLibrary: 'Отваряне в Библиотеката',
  savedOpenFailed: 'Не можа да се отвори в момента.',
  savedInLibrary: 'Намира се във вашата Библиотека.',
  openSavedTitle: 'Отвори {title} →',
  interviewReasonNotAuthorized: 'Нямате право да променяте тази чернова.',
  interviewReasonDraftNotOpen: 'Тази чернова вече не е отворена.',
  interviewReasonInvalid: 'Един от отговорите не е валиден.',
  interviewReasonObjects: 'Някои файлове не можаха да бъдат потвърдени. Отговорите ви са запазени.',
  interviewReasonDependency: 'Необходима услуга не е достъпна. Отговорите ви са запазени.',
  interviewReasonReconcile: 'Последното запазване все още се потвърждава.',
  exportTitle: 'Експорт',
  exportIntro: 'Изтеглете този артефакт. Файлът посочва откъде идват данните му.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Редактируеми слайдове с бележки на лектора',
  exportHintPdf: 'Точно както е показано',
  exportHintDocx: 'Редактируем, запазва заглавията и таблиците',
  exportHintXlsx: 'Всички листове, запазва формулите',
  exportHintCsv: 'Само стойности, UTF-8',
  exportUnavailable: 'Не е налично в момента',
  exportRunning: 'Подготовка на {format}…',
  exportValidating: 'Проверка на файла {format}…',
  exportReady: 'Файлът {format} е готов',
  exportDownload: 'Изтегли {name}',
  exportVersion: 'Експортирана версия {number}',
  exportFailed: 'Файлът {format} не можа да бъде създаден.',
  exportFailedChecks: 'Файлът {format} не премина проверките си, затова не е предложен.',
  exportDenied: 'Нямате достъп да експортирате този артефакт.',
  exportRevoked: 'Споделянето е изключено, затова този артефакт вече не може да се експортира.',
  exportStale:
    'Този артефакт се промени по време на експорта. Експортирайте отново, за да получите най-новата версия.',
  exportProvenanceTitle: 'Всеки файл включва',
  exportProvenanceLine: 'Създадено с Poneva · {title} · v{number} · {data} · създадено {madeAt}',
  exportProvenanceAsOf: 'данни към {time}',
  exportPlacementNotesAbout: 'В листа Бележки и в информационен файл до CSV',
  exportProvenanceRange: 'данни от {earliest} до {latest}',
  exportProvenanceUnknown: 'възрастта на данните е неизвестна',
  exportAboutFile: 'За този файл ({format})',
  exportPlacementFooter: 'В долния колонтитул на всяка страница',
  exportPlacementSlide: 'В долния колонтитул на всеки слайд',
  exportPlacementNotes: 'В листа Бележки',
  exportPlacementAbout: 'В информационен файл до CSV',
  exportSourcesPage: 'Добави страница с източници',
  exportSourcesPageHint: 'Последна страница, която изброява всяко число, неговия източник и време.',
  exportSourcesPageDeck: 'Слайдът с източниците на презентацията се включва автоматично.',
  exportSourcesPageSaved: 'Запомнено за този артефакт, включително за планираните изпращания.',
  libraryKindSheetTitle: 'Вид',
  shareAccessSheetTitle: 'Достъп',

  requestShowAll: 'Покажи всичко',
  requestShowLess: 'Покажи по-малко',
  approveGettingReady: 'Подготвяме това…',
  approveNotReady:
    'Това не може да бъде одобрено в момента. Презаредете страницата и опитайте отново.',
  tryWithModel: 'Опитай с {model}',
  tryWithRequesting: 'Стартиране…',
  tryWithUnavailable: 'Това не може да бъде стартирано в момента.',
};

const cs: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Upravujete koncept verze {number}',
  noChangesToApply: 'Žádné změny k použití.',
  appliedDone: 'Použito.',
  mentionInChat: 'Zmínit v chatu',

  kindMarkdown: 'Poznámka',
  kindKpi: 'Metrika',
  libraryExportsList: 'Export do {formats}',
  libraryOpensToRead: 'Otevře se ke čtení',
  libraryReadOnly: 'Jen pro čtení',
  libraryLiveView: 'Živý pohled',
  libraryOneNumber: 'Jedno číslo · k {time}',
  retry: 'Zkusit znovu',
  close: 'Zavřít',
  panelLoading: 'Načítání…',
  panelEmpty: 'Zatím tu nic není.',
  panelDenied: 'K tomuto nemáte přístup.',
  panelBlocked: 'Než bude možné pokračovat, čeká se na něco jiného.',
  panelBlockedViewerContext:
    'Toto spuštění nemá osobu, jejímž jménem by data četlo, takže nemůže pokračovat, dokud někdo nevybere, jejímž jménem poběží.',
  panelError: 'Tohle se nepodařilo načíst.',
  panelStale: 'Může být neaktuální.',
  sourcesTitle: 'Zdroje',
  sourcesIntro: 'Odkud každé číslo pochází a jak je aktuální.',
  sourcesLoading: 'Načítání zdrojů…',
  sourcesEmptyTitle: 'Žádné zdroje',
  sourcesEmptyBody: 'Tento artefakt nepoužívá firemní data.',
  sourcesErrorBody: 'Zdroje se nepodařilo načíst.',
  sourcesDeniedTitle: 'Některé zdroje jsou skryté',
  sourcesDeniedBody:
    'Počet zde použitých zdrojů, ke kterým nemáte přístup: {count}. Požádejte vlastníka, aby je sdílel.',
  sourceDeniedName: 'Zdroj, který nevidíte',
  sourceAsOf: 'K {time}',
  sourceAgeUnknown: 'Stáří dat neznámé',
  sourceStatusCurrent: 'Aktuální',
  sourceStatusStale: 'Neaktuální',
  sourceStatusGap: 'Chybí v poslední aktualizaci',
  sourceStatusDenied: 'Bez přístupu',
  sourceLastGood: 'Naposledy v pořádku: {time}',
  sourceUsedBy: 'Používá: {labels}',
  openDocument: 'Otevřít dokument',
  versionsShow: 'Zobrazit verze',
  versionsHide: 'Skrýt verze',
  versionsTitle: 'Verze',
  versionsLoading: 'Načítání verzí…',
  versionsEmptyTitle: 'Zatím žádné verze',
  versionsEmptyBody: 'První verze se objeví, až bude tento artefakt vytvořen.',
  versionsErrorBody: 'Verze se nepodařilo načíst.',
  versionLabel: 'Verze {number}',
  versionOpen: 'Otevřít verzi {number}',
  versionCurrent: 'Aktuální',
  versionPublished: 'Zveřejněná',
  versionBy: 'Autor: {actor}',
  versionOriginNative: 'V Poneva',
  versionOriginMcp: 'přes vašeho vlastního agenta',
  versionOriginTerminal: 'přes terminál',
  versionOriginJob: 'Naplánované spuštění',
  versionCausePerson: 'Upraveno',
  versionCauseAgent: 'Aktualizace agentem',
  versionCauseRefresh: 'Aktualizováno',
  versionCauseRestore: 'Obnoveno',
  versionCauseImport: 'Importováno',
  versionCheckedYes: 'Zkontrolováno',
  versionCheckedNo: 'Zatím nezkontrolováno',
  versionCheckedInvalidated: 'Upraveno od poslední kontroly',
  compareStart: 'Porovnat',
  compareWith: 'Porovnat s verzí {number}',
  compareTitle: 'Co se změnilo od v{from} do v{to}',
  compareFromMark: 'Z',
  compareToMark: 'Na',
  comparePick: 'Vyberte druhou verzi k porovnání.',
  compareNone: 'Žádné rozdíly.',
  compareLoading: 'Porovnávání…',
  compareError: 'Porovnání se nepodařilo načíst.',
  compareBefore: 'Před',
  compareAfter: 'Po',
  compareAdded: 'Přidáno',
  compareRemoved: 'Odebráno',
  compareChanged: 'Změněno',
  restore: 'Obnovit tuto verzi',
  restoreConfirmTitle: 'Obnovit verzi {number}?',
  restoreConfirmBody: 'Z verze {number} vznikne nová verze. Nic se nesmaže.',
  restoreConfirm: 'Obnovit',
  restoreCancel: 'Zrušit',
  restoring: 'Obnovování…',
  restoreFailed: 'Verzi se nepodařilo obnovit.',
  restoreStale:
    'Tento artefakt se změnil, zatímco jste ho prohlíželi. Načtěte verze znovu a zkuste to znovu.',
  restoreDenied: 'K obnovování verzí nemáte oprávnění.',
  restored: 'Verze {number} byla obnovena jako nová verze.',
  publishTitle: 'Zveřejnit',
  publishIntro: 'Verzi {number} uvidí: {audience}.',
  publishOwnerLine: 'Čtenáři uvidí to, co mohl vidět {owner}, když byla tato verze zveřejněna.',
  publishChecksTitle: 'Kontroly',
  checkPassed: 'Zkontrolováno',
  checkRunning: 'Kontrola…',
  checkFailed: 'Kontrola selhala',
  checkStale: 'Upraveno od poslední kontroly',
  publishFixInChat: 'Opravit v chatu',
  publishAction: 'Zveřejnit verzi {number}',
  publishing: 'Zveřejňování…',
  publishedDone: 'Verze {number} je živě pro: {audience}.',
  publishConfirmGrant:
    'Tato verze potřebuje nový přístup k firemnímu systému. Před zveřejněním přístup zkontrolujte.',
  publishConfirmWrite:
    'Tato verze zapisuje do firemního systému. Před zveřejněním přístup zkontrolujte.',
  publishConfirmDestructive: 'Tato verze odstraňuje data. Nejprve to potvrďte v Poneva.',
  publishConflict: 'Nejdřív byla zveřejněna jiná verze. Načtěte znovu, abyste ji viděli.',
  publishBlockedNotAuthorized: 'Ke zveřejňování nemáte oprávnění.',
  publishBlockedCheckFailed: 'Kontrola selhala, proto tuto verzi nelze zveřejnit.',
  publishBlockedCheckStale: 'Artefakt se po poslední kontrole změnil. Zkontrolujte ho znovu.',
  publishBlockedObjects: 'Některé soubory se nepodařilo ověřit. Zkuste to za chvíli.',
  publishBlockedPolicy: 'Firemní nastavení se změnilo. Zkontrolujte ho a zkuste to znovu.',
  publishBlockedApproval: 'Potvrzení už neplatí. Potvrďte znovu.',
  publishBlockedDependency: 'Potřebná služba není dostupná. Zkuste to později.',
  publishAsk: 'Požádat o zveřejnění: {publishers}',
  publishAskFooter: 'Lidé, kteří mohou zveřejňovat: {publishers}.',
  publishAskNone: 'Požádejte někoho, kdo může zveřejňovat.',
  publishWarnings: 'Stojí za kontrolu před zveřejněním',
  checkWarnStale: '{subject} je neaktuální ({time}).',
  checkWarnGap: '{subject} – chybí v poslední aktualizaci.',
  checkWarnUnknownSource: 'Zdroj, který nevidíte, vyžaduje pozornost.',
  checkRefreshFirst: 'Nejdřív aktualizovat',
  checkFailedRow: '{subject} – kontrola selhala.',
  checkFailedGeneric: 'Kontrola selhala.',
  publishReviewAccess: 'Zkontrolovat přístup',
  publishRemovalBody: 'v{number} odstraňuje: {what} ({count}). Nejprve se uloží bod obnovy.',
  publishReviewRemoval: 'Zkontrolovat, co se odstraní',
  publishAndRemove: 'Zveřejnit a odstranit',
  publishReviewFirst: 'Před zveřejněním zkontrolujte, co se odstraní.',
  publishDoneTitle: 'Verze {number} je živě pro: {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Najdou ji ve své Knihovně a připnutou pro tým.',
  publishUndo: 'Zpět',
  publishCopyLink: 'Kopírovat odkaz',
  publishDone: 'Hotovo',
  shareRoleViewMeaning: 'Může ji otevřít a číst',
  shareRoleEditMeaning: 'Může ji měnit v chatu a v inspektoru',
  shareRolePublishMeaning: 'Může také zveřejňovat a rušit zveřejnění',
  shareRoleMenu: 'Role pro: {name}',
  shareUnpublishConfirm: 'Lidé ztratí přístup. Artefakt a jeho verze zůstanou zachovány.',
  shareUnpublishCancel: 'Ponechat živé',
  sendDestinationLine: '{provider} · lidí: {count}',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Shrnutí uvidí všichni v {destination}. Lidí mimo okruh tohoto artefaktu je {outside} z {total}, takže jim odkaz ukáže „Požádat o přístup“.',
  sendAudienceGeneric: 'Lidé mimo okruh uvidí „Požádat o přístup“.',
  sendPreview: 'Co uvidí',
  shareTitle: 'Sdílet',
  shareLoading: 'Načítání sdílení…',
  shareErrorBody: 'Sdílení se nepodařilo načíst.',
  sharePrivate: 'Tento artefakt vidíte jen vy.',
  shareShared: 'Viditelné pro: {audience}.',
  shareRevoked: 'Sdílení je vypnuté. Nikdo jiný tento artefakt neotevře.',
  shareOwnerSnapshot:
    'Čtenáři vidí čísla tak, jak byla zveřejněna. Nemohou je aktualizovat ani upravovat.',
  shareRoleView: 'Může zobrazit',
  shareRoleEdit: 'Může upravovat',
  shareRolePublish: 'Může zveřejňovat',
  shareLivePublished: 'Živě: verze {number}',
  shareUnpublish: 'Zrušit zveřejnění',
  shareManageDenied: 'Kdo to vidí, může změnit jen editor.',
  sendTitle: 'Odeslat do',
  sendIntro: 'Odešlete odkaz a shrnutí přes připojenou aplikaci. Každé odeslání schvalujete vy.',
  sendLoading: 'Načítání cílů…',
  sendErrorBody: 'Cíle se nepodařilo načíst.',
  sendNone:
    'Zatím to nemůže přijmout žádná připojená aplikace. Nejprve připojte Slack, Teams nebo e-mail.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-mail',
  sendAudience: 'Půjde pro: {audience}',
  sendSummary: 'Zpráva',
  sendLeaves: 'Odešle se odkaz.',
  sendLeavesAttachment: 'Odešle se odkaz a zkontrolovaný soubor.',
  sendNoAttachments: 'Tento cíl nepřijímá soubory, takže se odešle jen odkaz.',
  sendScheduled: 'Naplánovaná odeslání vyžadují vaše schválení pokaždé.',
  sendAction: 'Zkontrolovat a odeslat',
  sendFileUnsupported: 'Tento cíl soubor nepřijme. Odešlete jen odkaz, nebo vyberte jiný cíl.',
  sendLinkOnly: 'Odeslat jen odkaz',
  sendWaiting: 'Čeká na vaše schválení. Nic nebylo odesláno.',
  sendDelivered: 'Odesláno do: {destination}.',
  sendFailed: 'Neodesláno. Nic Poneva neopustilo.',
  sendUnknown:
    'Doručení do {destination} se nepodařilo potvrdit. Než odešlete znovu, zkontrolujte to tam.',
  sendBlockedNotAuthorized: 'K odeslání nemáte oprávnění.',
  sendBlockedUnsupported: 'Tento cíl není podporován.',
  sendBlockedApproval: 'Schválení už neplatí. Zkontrolujte a odešlete znovu.',
  sendBlockedChanged: 'Zpráva nebo příjemce se změnili. Zkontrolujte to znovu.',
  sendBlockedDependency: 'Připojená aplikace není dostupná. Zkuste to později.',
  libraryTitle: 'Knihovna',
  librarySegmentAll: 'Vše',
  librarySegmentMine: 'Moje',
  librarySegmentShared: 'Sdílené se mnou',
  librarySearch: 'Hledat artefakty',
  libraryKindAll: 'Všechny druhy',
  libraryKindReports: 'Reporty a stránky',
  libraryKindDecks: 'Prezentace',
  libraryKindDocuments: 'Dokumenty',
  libraryKindSpreadsheets: 'Tabulky',
  libraryKindApps: 'Aplikace',
  libraryAppsMatchNoun:
    'one:aplikace odpovídá v sekci Aplikace →|few:aplikace odpovídají v sekci Aplikace →|many:aplikace odpovídá v sekci Aplikace →|other:aplikací odpovídá v sekci Aplikace →',
  libraryMake: 'Začít v chatu',
  libraryEmptySharedHint: 'Když s vámi někdo sdílí artefakt, objeví se tady.',
  libraryRefreshFailedAt: 'Aktualizace se nezdařila {time}.',
  libraryRefreshTryAgain: 'Zkusit znovu',
  libraryVia: 'přes: {owner}',
  savedPrivate: 'Soukromé',
  savedVersion: 'Verze {n}',
  libraryLoading: 'Načítání Knihovny…',
  libraryErrorBody: 'Knihovnu se právě nepodařilo načíst.',
  startersLoadFailed: 'Šablony se teď nepodařilo načíst.',
  libraryCannotOpen: '„{title}“ se zde zatím nedá otevřít.',
  libraryEmptyTitle: 'Zatím tu nic není',
  libraryEmptyAllTitle: 'Vytvořte něco, co stojí za sdílení.',
  libraryEmptyAllBody:
    'Dokumenty, prezentace a aplikace, které vytvoříte v chatu nebo které s vámi ostatní sdílejí, se zobrazí tady.',
  libraryEmptyMineBody: 'Artefakty, které vytvoříte, se objeví tady.',
  libraryEmptySharedBody: 'Zatím s vámi nikdo nic nesdílel.',
  libraryNoMatchTitle: 'Žádné shody',
  libraryNoMatchBody: 'Zkuste jiné slovo nebo jiný druh.',
  libraryMore: 'Zobrazit další',
  libraryOwner: 'Autor: {owner}',
  libraryUpdated: 'Upraveno {time}',
  libraryPublishedAs: 'Zveřejněno · v{number}',
  libraryDraft: 'Koncept',
  libraryUnpublished: 'Nezveřejněno',
  libraryRefreshFailed: 'Aktualizace selhala',
  libraryRefreshRunning: 'Aktualizace probíhá',
  libraryAudience: 'Pro: {audience}',
  kindReport: 'Report',
  kindPage: 'Stránka',
  kindSite: 'Web',
  kindDeck: 'Prezentace',
  kindDocument: 'Dokument',
  kindApp: 'Aplikace',
  kindSpreadsheet: 'Tabulka',
  kindDashboard: 'Přehled',
  kindTable: 'Tabulka',
  kindChart: 'Graf',
  kindExportsReport: 'Export do PDF',
  kindExportsPage: 'Stránka jen pro čtení',
  kindExportsSite: 'Web jen pro čtení',
  kindExportsDeck: 'Prezentovat, nebo exportovat do PowerPointu a PDF',
  kindExportsDocument: 'Export do Wordu a PDF',
  kindExportsSpreadsheet: 'Export do Excelu a CSV',
  kindExportsDashboard: 'Živý náhled',
  kindExportsTable: 'Export do Excelu a CSV',
  kindExportsChart: 'Zobrazení grafu',
  agentPublished: '{agent}: zveřejněno {title} v{number}.',
  agentCheckedOn: 'Zkontrolováno na: {views}, {themes}.',
  agentUndo: 'Zpět',
  agentRestorePrevious: 'Obnovit v{number}',
  agentHeld: '{title} v{number} čeká jako koncept.',
  agentHeldMismatch: 'Čísla, která se neshodují se zdroji: {count}.',
  agentHeldCheck: 'Kontrola neprošla.',
  agentFixInChat: 'Opravit v chatu',
  agentConfirmDrop:
    '{agent} chce odstranit {what} z: {title}. Nejprve se uloží bod obnovy. Nic se nezmění, dokud se nerozhodnete.',
  agentConfirmDropCount:
    '{agent} chce odstranit {what} (hodnot: {count}) z: {title}. Nejprve se uloží bod obnovy. Nic se nezmění, dokud se nerozhodnete.',
  agentViewDesktop: 'počítač',
  agentViewTablet: 'tablet',
  agentViewPhone: 'telefon',
  agentThemeLight: 'světlý',
  agentThemeDark: 'tmavý',
  agentLookAt: 'Zobrazit v{number}',
  agentConfirmWrite: '{agent} chce zapsat: {what}. Nic se nezapíše, dokud se nerozhodnete.',
  agentConfirmGrant: '{agent} chce přístup k: {what}. Nic se nesdílí, dokud se nerozhodnete.',
  agentReview: 'Zkontrolovat',
  agentSkipped: '{agent}: vynecháno {part}.',
  agentSkippedWhy:
    'Čte vlastní {system} každého člověka a toto spuštění nemá osobu, jejímž jménem by četlo.',
  agentChooseWho: 'Vyberte, jejímž jménem poběží',
  agentChangeInChat: 'Změnit v chatu',
  agentFailed: '{agent}: nepodařilo se aktualizovat {title}.',
  agentFailedBody: 'Poslední zveřejněná verze je stále živě.',
  agentRetry: 'Zkusit znovu',
  agentRunning: '{agent} aktualizuje {title}.',
  agentMakesTitle: 'Vytváří a aktualizuje',
  agentMakesLoading: 'Načítání…',
  agentMakesErrorBody: 'Tohle se nepodařilo načíst.',
  agentMakesNone: 'Tento agent zatím nevytváří žádné artefakty.',
  agentMakesNew: 'Nový artefakt při každém spuštění',
  agentMakesUpdate: 'Při každém spuštění aktualizuje jeden artefakt',
  agentNextRun: 'Další spuštění: {when}',
  agentNoSchedule: 'Nenaplánováno',
  agentCreditsPerRun: 'Kredity za spuštění: zhruba {credits}',
  agentCreditsUnknown: 'Kredity za spuštění zatím nejsou známé',
  agentLastResult: 'Poslední výsledek',
  interviewTitle: 'Nastavení',
  interviewIntro: 'Pár otázek ho utvoří.',
  interviewOptional: '(volitelné)',
  interviewAnswerNeeded: 'Odpovězte, abyste mohli pokračovat.',
  interviewReadOnly: 'Jen pro čtení',
  interviewChange: 'Změnit',
  interviewConnectionYours: 'vaše připojení k {system}',
  interviewConnectionOther: 'připojení k {system} uživatele {name}',
  interviewDateAny: 'Jakékoli období',
  interviewDatePrevious: 'Předchozí měsíc',
  interviewDateNext: 'Další měsíc',
  interviewDatePresets: 'Rychlé rozsahy',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Vymazat',
  interviewDateDone: 'Hotovo',
  interviewChooseSource: 'Vyberte zdroj',
  interviewSourceChosen: 'Zdroj je vybrán',
  interviewSourceUnavailable: 'Výběr zdroje zde zatím není k dispozici, proto nelze pokračovat.',
  interviewSourceUnavailableOptional:
    'Výběr zdroje tu zatím není k dispozici. Můžete pokračovat i bez něj.',
  interviewStarterWithdrawn:
    'Tato šablona už není k dispozici. Koncepty, které jste z ní už začali, lze dál otevřít.',
  interviewDateFrom: 'Od',
  interviewDateTo: 'Do',
  interviewContinue: 'Pokračovat',
  interviewGrantTitle: 'Povolit čtení: {source}?',
  interviewGrantBody:
    'Používá {connection}, jen pro čtení, pro: {scope}. Nic se nepřečte, dokud to nepovolíte, a nezobrazují se žádné ukázkové řádky.',
  interviewAllow: 'Povolit',
  interviewNotNow: 'Teď ne',
  interviewConfiguring: 'Nastavování podle vašich odpovědí…',
  interviewChecking: 'Kontrola před otevřením…',
  interviewComplete: '{title} je připraveno.',
  interviewOpen: 'Otevřít: {title}',
  interviewBlocked: 'Nešlo pokračovat.',
  interviewFailed: 'Nastavení se nezdařilo. Vaše odpovědi jsou zachovány.',
  interviewCancelled: 'Bylo zrušeno.',
  interviewSaving: 'Ukládání…',
  interviewSaved: 'Uloženo',
  interviewConflict:
    'Tento koncept se změnil jinde. Vaše odpovědi tu zůstanou, dokud nenačtete aktuální koncept, který je nahradí.',
  interviewReloadCurrent: 'Načíst aktuální koncept',
  interviewUnsettled: 'Poslední uložení se nepodařilo potvrdit. Vaše odpovědi tu zůstávají.',
  interviewCheckAgain: 'Zkontrolovat znovu',
  finishAction: 'Uložit soukromě',
  finishSaving: 'Ukládání…',
  finishSaved: '„{title}“ je uložen a vidíte ho jen vy.',
  finishNotSaved: 'Neuložilo se. Váš koncept tu stále je.',
  finishUnconfirmed: 'Nepodařilo se ověřit, zda se uložilo.',
  candidateInvalid: 'Z konceptu se nepodařilo vytvořit dokument.',
  finishOpenInLibrary: 'Otevřít v Knihovně',
  savedOpenFailed: 'Teď to nešlo otevřít.',
  savedInLibrary: 'Najdete to ve své Knihovně.',
  openSavedTitle: 'Otevřít: {title} →',
  interviewReasonNotAuthorized: 'Ke změně tohoto konceptu nemáte oprávnění.',
  interviewReasonDraftNotOpen: 'Tento koncept už není otevřený.',
  interviewReasonInvalid: 'Jedna z odpovědí není platná.',
  interviewReasonObjects: 'Některé soubory se nepodařilo ověřit. Vaše odpovědi jsou zachovány.',
  interviewReasonDependency: 'Potřebná služba není dostupná. Vaše odpovědi jsou zachovány.',
  interviewReasonReconcile: 'Poslední uložení se stále potvrzuje.',
  exportTitle: 'Exportovat',
  exportIntro: 'Stáhněte si tento artefakt. Soubor uvádí, odkud jeho data pocházejí.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Upravitelné snímky s poznámkami řečníka',
  exportHintPdf: 'Přesně tak, jak se zobrazuje',
  exportHintDocx: 'Upravitelný, zachová nadpisy a tabulky',
  exportHintXlsx: 'Všechny listy, zachová vzorce',
  exportHintCsv: 'Jen hodnoty, UTF-8',
  exportUnavailable: 'Právě není k dispozici',
  exportRunning: 'Příprava: {format}…',
  exportValidating: 'Kontrola souboru: {format}…',
  exportReady: 'Soubor {format} je připraven',
  exportDownload: 'Stáhnout {name}',
  exportVersion: 'Exportovaná verze {number}',
  exportFailed: 'Soubor {format} se nepodařilo vytvořit.',
  exportFailedChecks: 'Soubor {format} neprošel kontrolami, proto nebyl nabídnut.',
  exportDenied: 'K exportu tohoto artefaktu nemáte přístup.',
  exportRevoked: 'Sdílení bylo vypnuto, proto už tento artefakt nelze exportovat.',
  exportStale:
    'Tento artefakt se během exportu změnil. Exportujte znovu, abyste získali nejnovější verzi.',
  exportProvenanceTitle: 'Každý soubor obsahuje',
  exportProvenanceLine: 'Vytvořeno v Poneva · {title} · v{number} · {data} · vytvořeno {madeAt}',
  exportProvenanceAsOf: 'data k {time}',
  exportPlacementNotesAbout: 'Na listu Poznámky a v informačním souboru vedle CSV',
  exportProvenanceRange: 'data od {earliest} do {latest}',
  exportProvenanceUnknown: 'stáří dat neznámé',
  exportAboutFile: 'O tomto souboru ({format})',
  exportPlacementFooter: 'V zápatí každé stránky',
  exportPlacementSlide: 'V zápatí každého snímku',
  exportPlacementNotes: 'Na listu Poznámky',
  exportPlacementAbout: 'V informačním souboru vedle CSV',
  exportSourcesPage: 'Přidat stránku se zdroji',
  exportSourcesPageHint: 'Poslední stránka s výpisem každého čísla, jeho zdroje a času.',
  exportSourcesPageDeck: 'Součástí je i snímek Zdroje z prezentace.',
  exportSourcesPageSaved: 'Zapamatováno pro tento artefakt, včetně naplánovaných odeslání.',
  libraryKindSheetTitle: 'Druh',
  shareAccessSheetTitle: 'Přístup',

  requestShowAll: 'Zobrazit vše',
  requestShowLess: 'Zobrazit méně',
  approveGettingReady: 'Připravujeme to…',
  approveNotReady: 'Teď to nelze schválit. Znovu načtěte stránku a zkuste to znovu.',
  tryWithModel: 'Zkusit s modelem {model}',
  tryWithRequesting: 'Spouští se…',
  tryWithUnavailable: 'Teď to nelze spustit.',
};

const da: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Redigerer et udkast af version {number}',
  noChangesToApply: 'Ingen ændringer at anvende.',
  appliedDone: 'Anvendt.',
  mentionInChat: 'Nævn i chatten',

  kindMarkdown: 'Note',
  kindKpi: 'Nøgletal',
  libraryExportsList: 'Eksporteres til {formats}',
  libraryOpensToRead: 'Åbnes til læsning',
  libraryReadOnly: 'Skrivebeskyttet',
  libraryLiveView: 'Live-visning',
  libraryOneNumber: 'Ét tal · pr. {time}',
  retry: 'Prøv igen',
  close: 'Luk',
  panelLoading: 'Indlæser…',
  panelEmpty: 'Intet her endnu.',
  panelDenied: 'Du har ikke adgang til dette.',
  panelBlocked: 'Dette venter på noget, før det kan fortsætte.',
  panelBlockedViewerContext:
    'Denne kørsel har ingen person at læse som, så den kan ikke fortsætte, før nogen vælger, hvem den kører som.',
  panelError: 'Dette kunne ikke indlæses.',
  panelStale: 'Dette er muligvis forældet.',
  sourcesTitle: 'Kilder',
  sourcesIntro: 'Hvor hvert tal kommer fra, og hvor friskt det er.',
  sourcesLoading: 'Indlæser kilder…',
  sourcesEmptyTitle: 'Ingen kilder',
  sourcesEmptyBody: 'Denne artefakt bruger ikke virksomhedsdata.',
  sourcesErrorBody: 'Kilderne kunne ikke indlæses.',
  sourcesDeniedTitle: 'Nogle kilder er skjult',
  sourcesDeniedBody:
    'Du har ikke adgang til {count} kilder, der bruges her. Bed en ejer om at dele dem.',
  sourceDeniedName: 'Kilde, du ikke kan se',
  sourceAsOf: 'Pr. {time}',
  sourceAgeUnknown: 'Dataenes alder er ukendt',
  sourceStatusCurrent: 'Aktuel',
  sourceStatusStale: 'Forældet',
  sourceStatusGap: 'Manglede ved seneste opdatering',
  sourceStatusDenied: 'Ingen adgang',
  sourceLastGood: 'Senest fungerende: {time}',
  sourceUsedBy: 'Bruges af {labels}',
  openDocument: 'Åbn dokument',
  versionsShow: 'Vis versioner',
  versionsHide: 'Skjul versioner',
  versionsTitle: 'Versioner',
  versionsLoading: 'Indlæser versioner…',
  versionsEmptyTitle: 'Ingen versioner endnu',
  versionsEmptyBody: 'Den første version vises, når denne artefakt er bygget.',
  versionsErrorBody: 'Versionerne kunne ikke indlæses.',
  versionLabel: 'Version {number}',
  versionOpen: 'Åbn version {number}',
  versionCurrent: 'Aktuel',
  versionPublished: 'Udgivet',
  versionBy: 'Af {actor}',
  versionOriginNative: 'I Poneva',
  versionOriginMcp: 'via din egen agent',
  versionOriginTerminal: 'via terminalen',
  versionOriginJob: 'Planlagt kørsel',
  versionCausePerson: 'Redigeret',
  versionCauseAgent: 'Agentopdatering',
  versionCauseRefresh: 'Opdateret',
  versionCauseRestore: 'Gendannet',
  versionCauseImport: 'Importeret',
  versionCheckedYes: 'Kontrolleret',
  versionCheckedNo: 'Ikke kontrolleret endnu',
  versionCheckedInvalidated: 'Redigeret siden sidste kontrol',
  compareStart: 'Sammenlign',
  compareWith: 'Sammenlign med version {number}',
  compareTitle: 'Hvad der ændrede sig fra v{from} til v{to}',
  compareFromMark: 'Fra',
  compareToMark: 'Til',
  comparePick: 'Vælg en anden version at sammenligne med.',
  compareNone: 'Ingen forskelle.',
  compareLoading: 'Sammenligner…',
  compareError: 'Sammenligningen kunne ikke indlæses.',
  compareBefore: 'Før',
  compareAfter: 'Efter',
  compareAdded: 'Tilføjet',
  compareRemoved: 'Fjernet',
  compareChanged: 'Ændret',
  restore: 'Gendan denne version',
  restoreConfirmTitle: 'Gendan version {number}?',
  restoreConfirmBody: 'Dette opretter en ny version ud fra version {number}. Intet slettes.',
  restoreConfirm: 'Gendan',
  restoreCancel: 'Annuller',
  restoring: 'Gendanner…',
  restoreFailed: 'Versionen kunne ikke gendannes.',
  restoreStale:
    'Denne artefakt ændrede sig, mens du kiggede. Indlæs versionerne igen, og prøv igen.',
  restoreDenied: 'Du har ikke tilladelse til at gendanne versioner.',
  restored: 'Version {number} er gendannet som en ny version.',
  publishTitle: 'Udgiv',
  publishIntro: 'Version {number} bliver synlig for {audience}.',
  publishOwnerLine: 'Læsere ser det, som {owner} kunne se, da denne version blev udgivet.',
  publishChecksTitle: 'Kontroller',
  checkPassed: 'Kontrolleret',
  checkRunning: 'Kontrollerer…',
  checkFailed: 'En kontrol mislykkedes',
  checkStale: 'Redigeret siden sidste kontrol',
  publishFixInChat: 'Ret i chatten',
  publishAction: 'Udgiv version {number}',
  publishing: 'Udgiver…',
  publishedDone: 'Version {number} er live for {audience}.',
  publishConfirmGrant:
    'Denne version har brug for ny adgang til et virksomhedssystem. Gennemgå adgangen, før du udgiver.',
  publishConfirmWrite:
    'Denne version skriver til et virksomhedssystem. Gennemgå adgangen, før du udgiver.',
  publishConfirmDestructive: 'Denne version fjerner data. Bekræft det først i Poneva.',
  publishConflict: 'En anden version blev udgivet først. Indlæs igen for at se den.',
  publishBlockedNotAuthorized: 'Du har ikke tilladelse til at udgive.',
  publishBlockedCheckFailed: 'En kontrol mislykkedes, så denne version kan ikke udgives.',
  publishBlockedCheckStale: 'Artefakten ændrede sig efter sin sidste kontrol. Kontrollér den igen.',
  publishBlockedObjects: 'Nogle filer kunne ikke verificeres. Prøv igen om lidt.',
  publishBlockedPolicy: 'Virksomhedens indstillinger er ændret. Gennemgå og prøv igen.',
  publishBlockedApproval: 'Bekræftelsen er ikke længere gyldig. Bekræft igen.',
  publishBlockedDependency: 'En nødvendig tjeneste er ikke tilgængelig. Prøv igen senere.',
  publishAsk: 'Bed {publishers} om at udgive',
  publishAskFooter: 'Personer, der kan udgive: {publishers}.',
  publishAskNone: 'Bed en person, der kan udgive.',
  publishWarnings: 'Værd at se på, før du udgiver',
  checkWarnStale: '{subject} er forældet ({time}).',
  checkWarnGap: '{subject} manglede ved seneste opdatering.',
  checkWarnUnknownSource: 'En kilde, du ikke kan se, skal gennemses.',
  checkRefreshFirst: 'Opdater først',
  checkFailedRow: '{subject} bestod ikke sin kontrol.',
  checkFailedGeneric: 'En kontrol mislykkedes.',
  publishReviewAccess: 'Gennemgå adgang',
  publishRemovalBody: 'v{number} fjerner {what} ({count}). Et gendannelsespunkt gemmes først.',
  publishReviewRemoval: 'Gennemgå, hvad der fjernes',
  publishAndRemove: 'Udgiv og fjern',
  publishReviewFirst: 'Gennemgå, hvad der fjernes, før du udgiver.',
  publishDoneTitle: 'Version {number} er live for {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'De finder den i deres bibliotek og fastgjort for teamet.',
  publishUndo: 'Fortryd',
  publishCopyLink: 'Kopiér link',
  publishDone: 'Færdig',
  shareRoleViewMeaning: 'Kan åbne og læse den',
  shareRoleEditMeaning: 'Kan ændre den i chatten og med inspektøren',
  shareRolePublishMeaning: 'Kan også udgive og fjerne udgivelsen',
  shareRoleMenu: 'Rolle for {name}',
  shareUnpublishConfirm: 'Folk mister adgangen. Artefakten og dens versioner bevares.',
  shareUnpublishCancel: 'Behold live',
  sendDestinationLine: '{provider} · {count} personer',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Alle i {destination} ser resuméet. {outside} af de {total} er ikke i denne artefakts målgruppe, så linket viser dem „Bed om adgang“.',
  sendAudienceGeneric: 'Personer uden for målgruppen ser „Bed om adgang“.',
  sendPreview: 'Det vil de se',
  shareTitle: 'Del',
  shareLoading: 'Indlæser deling…',
  shareErrorBody: 'Delingen kunne ikke indlæses.',
  sharePrivate: 'Kun du kan se denne artefakt.',
  shareShared: 'Synlig for {audience}.',
  shareRevoked: 'Deling er slået fra. Ingen andre kan åbne denne artefakt.',
  shareOwnerSnapshot:
    'Læsere ser tallene, som de blev udgivet. De kan ikke opdatere eller redigere.',
  shareRoleView: 'Kan se',
  shareRoleEdit: 'Kan redigere',
  shareRolePublish: 'Kan udgive',
  shareLivePublished: 'Live: version {number}',
  shareUnpublish: 'Fjern udgivelse',
  shareManageDenied: 'Kun en redaktør kan ændre, hvem der ser dette.',
  sendTitle: 'Send til',
  sendIntro: 'Send et link og et resumé via en tilsluttet app. Du godkender hver afsendelse.',
  sendLoading: 'Indlæser modtagere…',
  sendErrorBody: 'Modtagerne kunne ikke indlæses.',
  sendNone:
    'Ingen tilsluttet app kan modtage dette endnu. Tilslut først Slack, Teams eller e-mail.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-mail',
  sendAudience: 'Går til {audience}',
  sendSummary: 'Besked',
  sendLeaves: 'Et link sendes.',
  sendLeavesAttachment: 'Et link og den gennemgåede fil sendes.',
  sendNoAttachments: 'Denne modtager kan ikke tage imod filer, så kun et link sendes.',
  sendScheduled: 'Planlagte afsendelser kræver din godkendelse hver gang.',
  sendAction: 'Gennemgå og send',
  sendFileUnsupported:
    'Denne modtager kan ikke tage imod filen. Send kun et link, eller vælg en anden modtager.',
  sendLinkOnly: 'Send kun et link',
  sendWaiting: 'Venter på din godkendelse. Intet er sendt.',
  sendDelivered: 'Sendt til {destination}.',
  sendFailed: 'Det blev ikke sendt. Intet forlod Poneva.',
  sendUnknown: 'Vi kunne ikke bekræfte leveringen til {destination}. Tjek der, før du sender igen.',
  sendBlockedNotAuthorized: 'Du har ikke tilladelse til at sende dette.',
  sendBlockedUnsupported: 'Denne modtager understøttes ikke.',
  sendBlockedApproval: 'Godkendelsen er ikke længere gyldig. Gennemgå og send igen.',
  sendBlockedChanged: 'Beskeden eller modtageren er ændret. Gennemgå den igen.',
  sendBlockedDependency: 'Den tilsluttede app er ikke tilgængelig. Prøv igen senere.',
  libraryTitle: 'Bibliotek',
  librarySegmentAll: 'Alle',
  librarySegmentMine: 'Mine',
  librarySegmentShared: 'Delt med mig',
  librarySearch: 'Søg i artefakter',
  libraryKindAll: 'Alle typer',
  libraryKindReports: 'Rapporter og sider',
  libraryKindDecks: 'Præsentationer',
  libraryKindDocuments: 'Dokumenter',
  libraryKindSpreadsheets: 'Regneark',
  libraryKindApps: 'Apps',
  libraryAppsMatchNoun: 'one:app matcher i Apps →|other:apps matcher i Apps →',
  libraryMake: 'Start i chatten',
  libraryEmptySharedHint: 'Når nogen deler en artefakt med dig, vises den her.',
  libraryRefreshFailedAt: 'Kunne ikke opdatere {time}.',
  libraryRefreshTryAgain: 'Prøv igen',
  libraryVia: 'via {owner}',
  savedPrivate: 'Privat',
  savedVersion: 'Version {n}',
  libraryLoading: 'Indlæser dit bibliotek…',
  libraryErrorBody: 'Dit bibliotek kunne ikke indlæses lige nu.',
  startersLoadFailed: 'Skabelonerne kunne ikke indlæses lige nu.',
  libraryCannotOpen: '»{title}« kan endnu ikke åbnes her.',
  libraryEmptyTitle: 'Intet her endnu',
  libraryEmptyAllTitle: 'Lav noget, der er værd at dele.',
  libraryEmptyAllBody:
    'Dokumenter, præsentationer og apps, du laver i chatten, eller som andre deler med dig, vises her.',
  libraryEmptyMineBody: 'Artefakter, du laver, vises her.',
  libraryEmptySharedBody: 'Der er ikke delt noget med dig endnu.',
  libraryNoMatchTitle: 'Ingen match',
  libraryNoMatchBody: 'Prøv et andet ord eller en anden type.',
  libraryMore: 'Vis flere',
  libraryOwner: 'Af {owner}',
  libraryUpdated: 'Redigeret {time}',
  libraryPublishedAs: 'Udgivet · v{number}',
  libraryDraft: 'Kladde',
  libraryUnpublished: 'Ikke udgivet',
  libraryRefreshFailed: 'Opdatering mislykkedes',
  libraryRefreshRunning: 'Opdaterer',
  libraryAudience: 'Til {audience}',
  kindReport: 'Rapport',
  kindPage: 'Side',
  kindSite: 'Websted',
  kindDeck: 'Præsentation',
  kindDocument: 'Dokument',
  kindApp: 'App',
  kindSpreadsheet: 'Regneark',
  kindDashboard: 'Dashboard',
  kindTable: 'Tabel',
  kindChart: 'Diagram',
  kindExportsReport: 'Eksporteres til PDF',
  kindExportsPage: 'Skrivebeskyttet side',
  kindExportsSite: 'Skrivebeskyttet websted',
  kindExportsDeck: 'Præsentér, eller eksportér til PowerPoint og PDF',
  kindExportsDocument: 'Eksporteres til Word og PDF',
  kindExportsSpreadsheet: 'Eksporteres til Excel og CSV',
  kindExportsDashboard: 'Liveview',
  kindExportsTable: 'Eksporteres til Excel og CSV',
  kindExportsChart: 'Diagramvisning',
  agentPublished: '{agent} udgav {title} v{number}.',
  agentCheckedOn: 'Kontrolleret på {views}, {themes}.',
  agentUndo: 'Fortryd',
  agentRestorePrevious: 'Gendan v{number}',
  agentHeld: '{title} v{number} venter som kladde.',
  agentHeldMismatch: '{count} tal stemte ikke med deres kilder.',
  agentHeldCheck: 'En kontrol blev ikke bestået.',
  agentFixInChat: 'Ret i chatten',
  agentConfirmDrop:
    '{agent} vil fjerne {what} fra {title}. Et gendannelsespunkt gemmes først. Intet ændres, før du beslutter.',
  agentConfirmDropCount:
    '{agent} vil fjerne {what} ({count} værdier) fra {title}. Et gendannelsespunkt gemmes først. Intet ændres, før du beslutter.',
  agentViewDesktop: 'computer',
  agentViewTablet: 'tablet',
  agentViewPhone: 'telefon',
  agentThemeLight: 'lyst tema',
  agentThemeDark: 'mørkt tema',
  agentLookAt: 'Se på v{number}',
  agentConfirmWrite: '{agent} vil skrive {what}. Intet skrives, før du beslutter.',
  agentConfirmGrant: '{agent} vil have adgang til {what}. Intet deles, før du beslutter.',
  agentReview: 'Gennemgå',
  agentSkipped: '{agent} sprang {part} over.',
  agentSkippedWhy:
    'Den læser hver persons egen {system}, og denne kørsel har ingen person at læse som.',
  agentChooseWho: 'Vælg, hvem den kører som',
  agentChangeInChat: 'Ændr i chatten',
  agentFailed: '{agent} kunne ikke opdatere {title}.',
  agentFailedBody: 'Den senest udgivne version er stadig live.',
  agentRetry: 'Prøv igen',
  agentRunning: '{agent} opdaterer {title}.',
  agentMakesTitle: 'Laver og opdaterer',
  agentMakesLoading: 'Indlæser…',
  agentMakesErrorBody: 'Dette kunne ikke indlæses.',
  agentMakesNone: 'Denne agent laver ikke artefakter endnu.',
  agentMakesNew: 'En ny artefakt ved hver kørsel',
  agentMakesUpdate: 'Opdaterer én artefakt ved hver kørsel',
  agentNextRun: 'Næste kørsel {when}',
  agentNoSchedule: 'Ikke planlagt',
  agentCreditsPerRun: 'Cirka {credits} kreditter pr. kørsel',
  agentCreditsUnknown: 'Kreditter pr. kørsel kendes ikke endnu',
  agentLastResult: 'Seneste resultat',
  interviewTitle: 'Sæt det op',
  interviewIntro: 'Et par spørgsmål former det.',
  interviewOptional: '(valgfrit)',
  interviewAnswerNeeded: 'Besvar dette for at fortsætte.',
  interviewReadOnly: 'Skrivebeskyttet',
  interviewChange: 'Ændr',
  interviewConnectionYours: 'din {system}-forbindelse',
  interviewConnectionOther: '{name}s {system}-forbindelse',
  interviewDateAny: 'Alle perioder',
  interviewDatePrevious: 'Forrige måned',
  interviewDateNext: 'Næste måned',
  interviewDatePresets: 'Hurtigvalg',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Ryd',
  interviewDateDone: 'Færdig',
  interviewChooseSource: 'Vælg en kilde',
  interviewSourceChosen: 'Kilde valgt',
  interviewSourceUnavailable:
    'Valg af kilde er endnu ikke tilgængeligt her, så det kan ikke fortsætte.',
  interviewSourceUnavailableOptional:
    'Valg af kilde er ikke tilgængeligt her endnu. Du kan fortsætte uden.',
  interviewStarterWithdrawn:
    'Denne skabelon er ikke længere tilgængelig. Kladder, du allerede har startet ud fra den, kan stadig åbnes.',
  interviewDateFrom: 'Fra',
  interviewDateTo: 'Til',
  interviewContinue: 'Fortsæt',
  interviewGrantTitle: 'Tillad læsning af {source}?',
  interviewGrantBody:
    'Den bruger {connection}, skrivebeskyttet, til {scope}. Intet læses, før du tillader det, og der vises ingen eksempelrækker.',
  interviewAllow: 'Tillad',
  interviewNotNow: 'Ikke nu',
  interviewConfiguring: 'Sætter det op ud fra dine svar…',
  interviewChecking: 'Kontrollerer det, før det åbnes…',
  interviewComplete: '{title} er klar.',
  interviewOpen: 'Åbn {title}',
  interviewBlocked: 'Dette kunne ikke fortsætte.',
  interviewFailed: 'Opsætningen mislykkedes. Dine svar er gemt.',
  interviewCancelled: 'Dette blev annulleret.',
  interviewSaving: 'Gemmer…',
  interviewSaved: 'Gemt',
  interviewConflict:
    'Denne kladde blev ændret et andet sted. Dine svar bevares her, indtil du indlæser den aktuelle kladde, som erstatter dem.',
  interviewReloadCurrent: 'Indlæs den aktuelle kladde',
  interviewUnsettled: 'Vi kunne ikke bekræfte din seneste gemning. Dine svar bevares her.',
  interviewCheckAgain: 'Kontrollér igen',
  finishAction: 'Gem privat',
  finishSaving: 'Gemmer…',
  finishSaved: '{title} er gemt og kun synlig for dig.',
  finishNotSaved: 'Det blev ikke gemt. Din kladde er her stadig.',
  finishUnconfirmed: 'Vi kunne ikke bekræfte, om det blev gemt.',
  candidateInvalid: 'Kladden kunne ikke laves til et dokument.',
  finishOpenInLibrary: 'Åbn i Bibliotek',
  savedOpenFailed: 'Det kunne ikke åbnes lige nu.',
  savedInLibrary: 'Den ligger i dit Bibliotek.',
  openSavedTitle: 'Åbn {title} →',
  interviewReasonNotAuthorized: 'Du har ikke tilladelse til at ændre denne kladde.',
  interviewReasonDraftNotOpen: 'Denne kladde er ikke længere åben.',
  interviewReasonInvalid: 'Et af svarene er ikke gyldigt.',
  interviewReasonObjects: 'Nogle filer kunne ikke verificeres. Dine svar er gemt.',
  interviewReasonDependency: 'En nødvendig tjeneste er ikke tilgængelig. Dine svar er gemt.',
  interviewReasonReconcile: 'Den seneste gemning bliver stadig bekræftet.',
  exportTitle: 'Eksportér',
  exportIntro: 'Download denne artefakt. Filen angiver, hvor dens data kommer fra.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Redigerbare slides med talenoter',
  exportHintPdf: 'Præcis som vist',
  exportHintDocx: 'Redigerbar, beholder overskrifter og tabeller',
  exportHintXlsx: 'Alle ark, beholder formler',
  exportHintCsv: 'Kun værdier, UTF-8',
  exportUnavailable: 'Ikke tilgængelig lige nu',
  exportRunning: 'Forbereder {format}…',
  exportValidating: 'Kontrollerer {format}-filen…',
  exportReady: '{format}-filen er klar',
  exportDownload: 'Download {name}',
  exportVersion: 'Eksporteret version {number}',
  exportFailed: '{format}-filen kunne ikke laves.',
  exportFailedChecks: '{format}-filen bestod ikke sine kontroller, så den blev ikke tilbudt.',
  exportDenied: 'Du har ikke adgang til at eksportere denne artefakt.',
  exportRevoked: 'Delingen blev slået fra, så denne artefakt kan ikke længere eksporteres.',
  exportStale:
    'Denne artefakt ændrede sig under eksporten. Eksportér igen for at få den nyeste version.',
  exportProvenanceTitle: 'Hver fil indeholder',
  exportProvenanceLine: 'Lavet med Poneva · {title} · v{number} · {data} · lavet {madeAt}',
  exportProvenanceAsOf: 'data pr. {time}',
  exportPlacementNotesAbout: 'På arket Noter og i en om-fil ved siden af CSV-filen',
  exportProvenanceRange: 'data fra {earliest} til {latest}',
  exportProvenanceUnknown: 'dataenes alder ukendt',
  exportAboutFile: 'Om denne fil ({format})',
  exportPlacementFooter: 'I sidefoden på hver side',
  exportPlacementSlide: 'I fodlinjen på hvert slide',
  exportPlacementNotes: 'På arket Noter',
  exportPlacementAbout: 'I en om-fil ved siden af CSV-filen',
  exportSourcesPage: 'Tilføj en kildeside',
  exportSourcesPageHint: 'En sidste side, der viser hvert tal, dets kilde og dets tidspunkt.',
  exportSourcesPageDeck: 'Præsentationens egen kildeside følger med.',
  exportSourcesPageSaved: 'Huskes for denne artefakt, også ved planlagte afsendelser.',
  libraryKindSheetTitle: 'Type',
  shareAccessSheetTitle: 'Adgang',

  requestShowAll: 'Vis alt',
  requestShowLess: 'Vis mindre',
  approveGettingReady: 'Gør det klar…',
  approveNotReady: 'Det kan ikke godkendes lige nu. Genindlæs siden, og prøv igen.',
  tryWithModel: 'Prøv med {model}',
  tryWithRequesting: 'Starter…',
  tryWithUnavailable: 'Det kan ikke startes lige nu.',
};

const de: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Sie bearbeiten einen Entwurf von Version {number}',
  noChangesToApply: 'Keine Änderungen zum Übernehmen.',
  appliedDone: 'Übernommen.',
  mentionInChat: 'Im Chat erwähnen',

  kindMarkdown: 'Notiz',
  kindKpi: 'Kennzahl',
  libraryExportsList: 'Export als {formats}',
  libraryOpensToRead: 'Öffnet zum Lesen',
  libraryReadOnly: 'Nur lesen',
  libraryLiveView: 'Live-Ansicht',
  libraryOneNumber: 'Eine Zahl · Stand {time}',
  retry: 'Erneut versuchen',
  close: 'Schließen',
  panelLoading: 'Wird geladen…',
  panelEmpty: 'Hier ist noch nichts.',
  panelDenied: 'Sie haben keinen Zugriff darauf.',
  panelBlocked: 'Hier wird auf etwas gewartet, bevor es weitergehen kann.',
  panelBlockedViewerContext:
    'Dieser Lauf hat keine Person, als die er lesen kann. Er kann erst weitermachen, wenn jemand festlegt, als wer er läuft.',
  panelError: 'Das konnte nicht geladen werden.',
  panelStale: 'Das ist möglicherweise nicht mehr aktuell.',
  sourcesTitle: 'Quellen',
  sourcesIntro: 'Woher jede Zahl stammt und wie aktuell sie ist.',
  sourcesLoading: 'Quellen werden geladen…',
  sourcesEmptyTitle: 'Keine Quellen',
  sourcesEmptyBody: 'Dieses Artefakt verwendet keine Unternehmensdaten.',
  sourcesErrorBody: 'Die Quellen konnten nicht geladen werden.',
  sourcesDeniedTitle: 'Einige Quellen sind ausgeblendet',
  sourcesDeniedBody:
    'Sie haben keinen Zugriff auf {count} hier verwendete Quellen. Bitten Sie eine Eigentümerin oder einen Eigentümer, sie freizugeben.',
  sourceDeniedName: 'Quelle, die Sie nicht sehen können',
  sourceAsOf: 'Stand: {time}',
  sourceAgeUnknown: 'Alter der Daten unbekannt',
  sourceStatusCurrent: 'Aktuell',
  sourceStatusStale: 'Veraltet',
  sourceStatusGap: 'Bei der letzten Aktualisierung nicht vorhanden',
  sourceStatusDenied: 'Kein Zugriff',
  sourceLastGood: 'Zuletzt in Ordnung: {time}',
  sourceUsedBy: 'Verwendet von {labels}',
  openDocument: 'Dokument öffnen',
  versionsShow: 'Versionen anzeigen',
  versionsHide: 'Versionen ausblenden',
  versionsTitle: 'Versionen',
  versionsLoading: 'Versionen werden geladen…',
  versionsEmptyTitle: 'Noch keine Versionen',
  versionsEmptyBody: 'Die erste Version erscheint, sobald dieses Artefakt erstellt ist.',
  versionsErrorBody: 'Die Versionen konnten nicht geladen werden.',
  versionLabel: 'Version {number}',
  versionOpen: 'Version {number} öffnen',
  versionCurrent: 'Aktuell',
  versionPublished: 'Veröffentlicht',
  versionBy: 'Von {actor}',
  versionOriginNative: 'In Poneva',
  versionOriginMcp: 'über Ihren eigenen Agenten',
  versionOriginTerminal: 'über das Terminal',
  versionOriginJob: 'Geplanter Lauf',
  versionCausePerson: 'Bearbeitet',
  versionCauseAgent: 'Agent-Update',
  versionCauseRefresh: 'Aktualisiert',
  versionCauseRestore: 'Wiederhergestellt',
  versionCauseImport: 'Importiert',
  versionCheckedYes: 'Geprüft',
  versionCheckedNo: 'Noch nicht geprüft',
  versionCheckedInvalidated: 'Seit der letzten Prüfung bearbeitet',
  compareStart: 'Vergleichen',
  compareWith: 'Mit Version {number} vergleichen',
  compareTitle: 'Was sich von v{from} zu v{to} geändert hat',
  compareFromMark: 'Von',
  compareToMark: 'Bis',
  comparePick: 'Wählen Sie eine zweite Version zum Vergleichen.',
  compareNone: 'Keine Unterschiede.',
  compareLoading: 'Wird verglichen…',
  compareError: 'Der Vergleich konnte nicht geladen werden.',
  compareBefore: 'Vorher',
  compareAfter: 'Nachher',
  compareAdded: 'Hinzugefügt',
  compareRemoved: 'Entfernt',
  compareChanged: 'Geändert',
  restore: 'Diese Version wiederherstellen',
  restoreConfirmTitle: 'Version {number} wiederherstellen?',
  restoreConfirmBody:
    'Daraus wird eine neue Version von Version {number}. Es wird nichts gelöscht.',
  restoreConfirm: 'Wiederherstellen',
  restoreCancel: 'Abbrechen',
  restoring: 'Wird wiederhergestellt…',
  restoreFailed: 'Die Version konnte nicht wiederhergestellt werden.',
  restoreStale:
    'Dieses Artefakt hat sich geändert, während Sie es angesehen haben. Laden Sie die Versionen neu und versuchen Sie es erneut.',
  restoreDenied: 'Sie haben keine Berechtigung, Versionen wiederherzustellen.',
  restored: 'Version {number} wurde als neue Version wiederhergestellt.',
  publishTitle: 'Veröffentlichen',
  publishIntro: 'Version {number} wird für {audience} sichtbar sein.',
  publishOwnerLine:
    'Betrachter sehen, was {owner} bei der Veröffentlichung dieser Version sehen konnte.',
  publishChecksTitle: 'Prüfungen',
  checkPassed: 'Geprüft',
  checkRunning: 'Wird geprüft…',
  checkFailed: 'Eine Prüfung ist fehlgeschlagen',
  checkStale: 'Seit der letzten Prüfung bearbeitet',
  publishFixInChat: 'Im Chat beheben',
  publishAction: 'Version {number} veröffentlichen',
  publishing: 'Wird veröffentlicht…',
  publishedDone: 'Version {number} ist für {audience} live.',
  publishConfirmGrant:
    'Diese Version braucht neuen Zugriff auf ein Unternehmenssystem. Prüfen Sie den Zugriff vor der Veröffentlichung.',
  publishConfirmWrite:
    'Diese Version schreibt in ein Unternehmenssystem. Prüfen Sie den Zugriff vor der Veröffentlichung.',
  publishConfirmDestructive: 'Diese Version entfernt Daten. Bestätigen Sie das zuerst in Poneva.',
  publishConflict:
    'Zuvor wurde eine andere Version veröffentlicht. Laden Sie neu, um sie zu sehen.',
  publishBlockedNotAuthorized: 'Sie haben keine Berechtigung zum Veröffentlichen.',
  publishBlockedCheckFailed:
    'Eine Prüfung ist fehlgeschlagen, daher kann diese Version nicht veröffentlicht werden.',
  publishBlockedCheckStale:
    'Das Artefakt hat sich nach der letzten Prüfung geändert. Prüfen Sie es erneut.',
  publishBlockedObjects:
    'Einige Dateien konnten nicht überprüft werden. Versuchen Sie es gleich noch einmal.',
  publishBlockedPolicy:
    'Die Unternehmenseinstellungen haben sich geändert. Prüfen Sie sie und versuchen Sie es erneut.',
  publishBlockedApproval: 'Die Bestätigung ist nicht mehr gültig. Bestätigen Sie erneut.',
  publishBlockedDependency:
    'Ein benötigter Dienst ist nicht verfügbar. Versuchen Sie es später erneut.',
  publishAsk: '{publishers} um Veröffentlichung bitten',
  publishAskFooter: 'Personen, die veröffentlichen können: {publishers}.',
  publishAskNone: 'Fragen Sie jemanden, der veröffentlichen kann.',
  publishWarnings: 'Vor dem Veröffentlichen einen Blick wert',
  checkWarnStale: '{subject} ist veraltet ({time}).',
  checkWarnGap: '{subject} fehlte bei der letzten Aktualisierung.',
  checkWarnUnknownSource: 'Eine Quelle, die Sie nicht sehen können, muss geprüft werden.',
  checkRefreshFirst: 'Zuerst aktualisieren',
  checkFailedRow: '{subject} hat die Prüfung nicht bestanden.',
  checkFailedGeneric: 'Eine Prüfung ist fehlgeschlagen.',
  publishReviewAccess: 'Zugriff prüfen',
  publishRemovalBody:
    'v{number} entfernt {what} ({count}). Zuvor wird ein Wiederherstellungspunkt gespeichert.',
  publishReviewRemoval: 'Entfernte Inhalte prüfen',
  publishAndRemove: 'Veröffentlichen und entfernen',
  publishReviewFirst: 'Prüfen Sie vor der Veröffentlichung, was entfernt wird.',
  publishDoneTitle: 'Version {number} ist für {audience} live.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Sie finden es in ihrer Bibliothek und für das Team angeheftet.',
  publishUndo: 'Rückgängig',
  publishCopyLink: 'Link kopieren',
  publishDone: 'Fertig',
  shareRoleViewMeaning: 'Kann es öffnen und lesen',
  shareRoleEditMeaning: 'Kann es im Chat und mit dem Inspektor ändern',
  shareRolePublishMeaning: 'Kann außerdem veröffentlichen und die Veröffentlichung zurücknehmen',
  shareRoleMenu: 'Rolle für {name}',
  shareUnpublishConfirm:
    'Die Personen verlieren den Zugriff. Das Artefakt und seine Versionen bleiben erhalten.',
  shareUnpublishCancel: 'Live lassen',
  sendDestinationLine: '{provider} · {count} Personen',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Alle in {destination} sehen die Zusammenfassung. {outside} von {total} gehören nicht zum Publikum dieses Artefakts, daher zeigt ihnen der Link „Zugriff anfragen“.',
  sendAudienceGeneric: 'Personen außerhalb des Publikums sehen „Zugriff anfragen“.',
  sendPreview: 'Das sehen sie',
  shareTitle: 'Teilen',
  shareLoading: 'Freigabe wird geladen…',
  shareErrorBody: 'Die Freigabe konnte nicht geladen werden.',
  sharePrivate: 'Nur Sie können dieses Artefakt sehen.',
  shareShared: 'Sichtbar für {audience}.',
  shareRevoked: 'Die Freigabe ist ausgeschaltet. Niemand sonst kann dieses Artefakt öffnen.',
  shareOwnerSnapshot:
    'Betrachter sehen die Zahlen wie veröffentlicht. Sie können nicht aktualisieren oder bearbeiten.',
  shareRoleView: 'Kann ansehen',
  shareRoleEdit: 'Kann bearbeiten',
  shareRolePublish: 'Kann veröffentlichen',
  shareLivePublished: 'Live: Version {number}',
  shareUnpublish: 'Veröffentlichung zurücknehmen',
  shareManageDenied: 'Nur Bearbeitende können ändern, wer das sieht.',
  sendTitle: 'Senden an',
  sendIntro:
    'Senden Sie einen Link und eine Zusammenfassung über eine verbundene App. Sie genehmigen jeden Versand.',
  sendLoading: 'Ziele werden geladen…',
  sendErrorBody: 'Die Ziele konnten nicht geladen werden.',
  sendNone:
    'Noch keine verbundene App kann das empfangen. Verbinden Sie zuerst Slack, Teams oder E-Mail.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-Mail',
  sendAudience: 'Geht an {audience}',
  sendSummary: 'Nachricht',
  sendLeaves: 'Ein Link wird gesendet.',
  sendLeavesAttachment: 'Ein Link und die geprüfte Datei werden gesendet.',
  sendNoAttachments: 'Dieses Ziel kann keine Dateien annehmen, daher wird nur ein Link gesendet.',
  sendScheduled: 'Geplante Sendungen brauchen jedes Mal Ihre Genehmigung.',
  sendAction: 'Prüfen und senden',
  sendFileUnsupported:
    'Dieses Ziel kann die Datei nicht annehmen. Senden Sie nur einen Link oder wählen Sie ein anderes Ziel.',
  sendLinkOnly: 'Nur einen Link senden',
  sendWaiting: 'Wartet auf Ihre Genehmigung. Es wurde nichts gesendet.',
  sendDelivered: 'An {destination} gesendet.',
  sendFailed: 'Es wurde nicht gesendet. Nichts hat Poneva verlassen.',
  sendUnknown:
    'Wir konnten die Zustellung an {destination} nicht bestätigen. Prüfen Sie dort nach, bevor Sie erneut senden.',
  sendBlockedNotAuthorized: 'Sie haben keine Berechtigung, dies zu senden.',
  sendBlockedUnsupported: 'Dieses Ziel wird nicht unterstützt.',
  sendBlockedApproval: 'Die Genehmigung ist nicht mehr gültig. Prüfen Sie und senden Sie erneut.',
  sendBlockedChanged: 'Die Nachricht oder der Empfänger hat sich geändert. Prüfen Sie sie erneut.',
  sendBlockedDependency: 'Die verbundene App ist nicht verfügbar. Versuchen Sie es später erneut.',
  libraryTitle: 'Bibliothek',
  librarySegmentAll: 'Alle',
  librarySegmentMine: 'Meine',
  librarySegmentShared: 'Mit mir geteilt',
  librarySearch: 'Artefakte durchsuchen',
  libraryKindAll: 'Alle Arten',
  libraryKindReports: 'Berichte und Seiten',
  libraryKindDecks: 'Präsentationen',
  libraryKindDocuments: 'Dokumente',
  libraryKindSpreadsheets: 'Tabellenkalkulationen',
  libraryKindApps: 'Apps',
  libraryAppsMatchNoun: 'one:App passt in Apps →|other:Apps passen in Apps →',
  libraryMake: 'Im Chat beginnen',
  libraryEmptySharedHint: 'Wenn jemand ein Artefakt mit Ihnen teilt, erscheint es hier.',
  libraryRefreshFailedAt: 'Aktualisierung am {time} fehlgeschlagen.',
  libraryRefreshTryAgain: 'Erneut versuchen',
  libraryVia: 'über {owner}',
  savedPrivate: 'Privat',
  savedVersion: 'Version {n}',
  libraryLoading: 'Ihre Bibliothek wird geladen…',
  libraryErrorBody: 'Ihre Bibliothek konnte gerade nicht geladen werden.',
  startersLoadFailed: 'Vorlagen konnten gerade nicht geladen werden.',
  libraryCannotOpen: '„{title}“ kann hier noch nicht geöffnet werden.',
  libraryEmptyTitle: 'Hier ist noch nichts',
  libraryEmptyAllTitle: 'Erstellen Sie etwas, das sich zu teilen lohnt.',
  libraryEmptyAllBody:
    'Dokumente, Präsentationen und Apps, die Sie im Chat erstellen oder die andere mit Ihnen teilen, erscheinen hier.',
  libraryEmptyMineBody: 'Artefakte, die Sie erstellen, erscheinen hier.',
  libraryEmptySharedBody: 'Mit Ihnen wurde noch nichts geteilt.',
  libraryNoMatchTitle: 'Keine Treffer',
  libraryNoMatchBody: 'Versuchen Sie ein anderes Wort oder eine andere Art.',
  libraryMore: 'Mehr anzeigen',
  libraryOwner: 'Von {owner}',
  libraryUpdated: 'Bearbeitet {time}',
  libraryPublishedAs: 'Veröffentlicht · v{number}',
  libraryDraft: 'Entwurf',
  libraryUnpublished: 'Nicht veröffentlicht',
  libraryRefreshFailed: 'Aktualisierung fehlgeschlagen',
  libraryRefreshRunning: 'Wird aktualisiert',
  libraryAudience: 'Für {audience}',
  kindReport: 'Bericht',
  kindPage: 'Seite',
  kindSite: 'Website',
  kindDeck: 'Präsentation',
  kindDocument: 'Dokument',
  kindApp: 'App',
  kindSpreadsheet: 'Tabellenkalkulation',
  kindDashboard: 'Dashboard',
  kindTable: 'Tabelle',
  kindChart: 'Diagramm',
  kindExportsReport: 'Export als PDF',
  kindExportsPage: 'Schreibgeschützte Seite',
  kindExportsSite: 'Schreibgeschützte Website',
  kindExportsDeck: 'Präsentieren oder als PowerPoint und PDF exportieren',
  kindExportsDocument: 'Export als Word und PDF',
  kindExportsSpreadsheet: 'Export als Excel und CSV',
  kindExportsDashboard: 'Live-Ansicht',
  kindExportsTable: 'Export als Excel und CSV',
  kindExportsChart: 'Diagrammansicht',
  agentPublished: '{agent} hat {title} v{number} veröffentlicht.',
  agentCheckedOn: 'Geprüft auf {views}, {themes}.',
  agentUndo: 'Rückgängig',
  agentRestorePrevious: 'v{number} wiederherstellen',
  agentHeld: '{title} v{number} wartet als Entwurf.',
  agentHeldMismatch: '{count} Zahlen stimmten nicht mit ihren Quellen überein.',
  agentHeldCheck: 'Eine Prüfung wurde nicht bestanden.',
  agentFixInChat: 'Im Chat beheben',
  agentConfirmDrop:
    '{agent} möchte {what} aus {title} entfernen. Zuvor wird ein Wiederherstellungspunkt gespeichert. Nichts ändert sich, bevor Sie entscheiden.',
  agentConfirmDropCount:
    '{agent} möchte {what} ({count} Werte) aus {title} entfernen. Zuvor wird ein Wiederherstellungspunkt gespeichert. Nichts ändert sich, bevor Sie entscheiden.',
  agentViewDesktop: 'Desktop',
  agentViewTablet: 'Tablet',
  agentViewPhone: 'Smartphone',
  agentThemeLight: 'hell',
  agentThemeDark: 'dunkel',
  agentLookAt: 'v{number} ansehen',
  agentConfirmWrite:
    '{agent} möchte {what} schreiben. Nichts wird geschrieben, bevor Sie entscheiden.',
  agentConfirmGrant:
    '{agent} möchte Zugriff auf {what}. Nichts wird freigegeben, bevor Sie entscheiden.',
  agentReview: 'Prüfen',
  agentSkipped: '{agent} hat {part} übersprungen.',
  agentSkippedWhy:
    'Es liest das jeweils eigene {system} jeder Person, und dieser Lauf hat keine Person, als die er lesen kann.',
  agentChooseWho: 'Festlegen, als wer er läuft',
  agentChangeInChat: 'Im Chat ändern',
  agentFailed: '{agent} konnte {title} nicht aktualisieren.',
  agentFailedBody: 'Die zuletzt veröffentlichte Version ist weiterhin live.',
  agentRetry: 'Erneut versuchen',
  agentRunning: '{agent} aktualisiert {title}.',
  agentMakesTitle: 'Erstellt und aktualisiert',
  agentMakesLoading: 'Wird geladen…',
  agentMakesErrorBody: 'Das konnte nicht geladen werden.',
  agentMakesNone: 'Dieser Agent erstellt noch keine Artefakte.',
  agentMakesNew: 'Bei jedem Lauf ein neues Artefakt',
  agentMakesUpdate: 'Aktualisiert bei jedem Lauf ein Artefakt',
  agentNextRun: 'Nächster Lauf {when}',
  agentNoSchedule: 'Nicht geplant',
  agentCreditsPerRun: 'Etwa {credits} Credits pro Lauf',
  agentCreditsUnknown: 'Die Credits pro Lauf sind noch nicht bekannt',
  agentLastResult: 'Letztes Ergebnis',
  interviewTitle: 'Einrichten',
  interviewIntro: 'Ein paar Fragen legen es fest.',
  interviewOptional: '(optional)',
  interviewAnswerNeeded: 'Beantworten Sie dies, um fortzufahren.',
  interviewReadOnly: 'Nur lesen',
  interviewChange: 'Ändern',
  interviewConnectionYours: 'Ihre {system}-Verbindung',
  interviewConnectionOther: 'die {system}-Verbindung von {name}',
  interviewDateAny: 'Beliebiger Zeitraum',
  interviewDatePrevious: 'Vorheriger Monat',
  interviewDateNext: 'Nächster Monat',
  interviewDatePresets: 'Schnellauswahl',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Löschen',
  interviewDateDone: 'Fertig',
  interviewChooseSource: 'Quelle wählen',
  interviewSourceChosen: 'Quelle gewählt',
  interviewSourceUnavailable:
    'Die Auswahl einer Quelle ist hier noch nicht verfügbar, daher kann es nicht weitergehen.',
  interviewSourceUnavailableOptional:
    'Die Auswahl einer Quelle ist hier noch nicht verfügbar. Sie können auch ohne fortfahren.',
  interviewStarterWithdrawn:
    'Diese Vorlage ist nicht mehr verfügbar. Entwürfe, die Sie bereits damit begonnen haben, lassen sich weiterhin öffnen.',
  interviewDateFrom: 'Von',
  interviewDateTo: 'Bis',
  interviewContinue: 'Weiter',
  interviewGrantTitle: 'Lesen von {source} erlauben?',
  interviewGrantBody:
    'Es nutzt {connection}, nur lesend, für {scope}. Nichts wird gelesen, bevor Sie es erlauben, und es werden keine Beispielzeilen angezeigt.',
  interviewAllow: 'Erlauben',
  interviewNotNow: 'Nicht jetzt',
  interviewConfiguring: 'Wird anhand Ihrer Antworten eingerichtet…',
  interviewChecking: 'Wird geprüft, bevor es sich öffnet…',
  interviewComplete: '{title} ist fertig.',
  interviewOpen: '{title} öffnen',
  interviewBlocked: 'Es konnte nicht fortgesetzt werden.',
  interviewFailed: 'Die Einrichtung ist fehlgeschlagen. Ihre Antworten bleiben erhalten.',
  interviewCancelled: 'Das wurde abgebrochen.',
  interviewSaving: 'Wird gespeichert…',
  interviewSaved: 'Gespeichert',
  interviewConflict:
    'Dieser Entwurf wurde an anderer Stelle geändert. Ihre Antworten bleiben hier erhalten, bis Sie den aktuellen Entwurf laden, der sie ersetzt.',
  interviewReloadCurrent: 'Aktuellen Entwurf laden',
  interviewUnsettled:
    'Wir konnten Ihr letztes Speichern nicht bestätigen. Ihre Antworten bleiben hier erhalten.',
  interviewCheckAgain: 'Erneut prüfen',
  finishAction: 'Privat speichern',
  finishSaving: 'Wird gespeichert…',
  finishSaved: '{title} ist gespeichert und nur für Sie sichtbar.',
  finishNotSaved: 'Es wurde nicht gespeichert. Ihr Entwurf ist noch da.',
  finishUnconfirmed: 'Wir konnten nicht bestätigen, ob es gespeichert wurde.',
  candidateInvalid: 'Der Entwurf konnte nicht zu einem Dokument werden.',
  finishOpenInLibrary: 'In der Bibliothek öffnen',
  savedOpenFailed: 'Das konnte gerade nicht geöffnet werden.',
  savedInLibrary: 'Es ist in Ihrer Bibliothek.',
  openSavedTitle: '{title} öffnen →',
  interviewReasonNotAuthorized: 'Sie haben keine Berechtigung, diesen Entwurf zu ändern.',
  interviewReasonDraftNotOpen: 'Dieser Entwurf ist nicht mehr geöffnet.',
  interviewReasonInvalid: 'Eine der Antworten ist ungültig.',
  interviewReasonObjects:
    'Einige Dateien konnten nicht überprüft werden. Ihre Antworten bleiben erhalten.',
  interviewReasonDependency:
    'Ein benötigter Dienst ist nicht verfügbar. Ihre Antworten bleiben erhalten.',
  interviewReasonReconcile: 'Das letzte Speichern wird noch bestätigt.',
  exportTitle: 'Exportieren',
  exportIntro: 'Laden Sie dieses Artefakt herunter. Die Datei gibt an, woher ihre Daten stammen.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Bearbeitbare Folien mit Sprechernotizen',
  exportHintPdf: 'Genau wie angezeigt',
  exportHintDocx: 'Bearbeitbar, behält Überschriften und Tabellen',
  exportHintXlsx: 'Alle Blätter, behält Formeln',
  exportHintCsv: 'Nur Werte, UTF-8',
  exportUnavailable: 'Im Moment nicht verfügbar',
  exportRunning: '{format} wird vorbereitet…',
  exportValidating: '{format}-Datei wird geprüft…',
  exportReady: '{format}-Datei ist bereit',
  exportDownload: '{name} herunterladen',
  exportVersion: 'Exportierte Version {number}',
  exportFailed: 'Die {format}-Datei konnte nicht erstellt werden.',
  exportFailedChecks:
    'Die {format}-Datei hat ihre Prüfungen nicht bestanden und wurde deshalb nicht angeboten.',
  exportDenied: 'Sie haben keinen Zugriff, dieses Artefakt zu exportieren.',
  exportRevoked:
    'Die Freigabe wurde ausgeschaltet, daher kann dieses Artefakt nicht mehr exportiert werden.',
  exportStale:
    'Dieses Artefakt hat sich während des Exports geändert. Exportieren Sie erneut, um die neueste Version zu erhalten.',
  exportProvenanceTitle: 'Jede Datei enthält',
  exportProvenanceLine: 'Erstellt mit Poneva · {title} · v{number} · {data} · erstellt {madeAt}',
  exportProvenanceAsOf: 'Daten vom {time}',
  exportPlacementNotesAbout: 'Auf dem Blatt „Notizen“ und in einer Infodatei neben der CSV',
  exportProvenanceRange: 'Daten von {earliest} bis {latest}',
  exportProvenanceUnknown: 'Alter der Daten unbekannt',
  exportAboutFile: 'Über diese Datei ({format})',
  exportPlacementFooter: 'In der Fußzeile jeder Seite',
  exportPlacementSlide: 'In der Fußzeile jeder Folie',
  exportPlacementNotes: 'Auf dem Blatt „Notizen“',
  exportPlacementAbout: 'In einer Infodatei neben der CSV',
  exportSourcesPage: 'Quellenseite hinzufügen',
  exportSourcesPageHint: 'Eine letzte Seite mit jeder Zahl, ihrer Quelle und ihrem Zeitpunkt.',
  exportSourcesPageDeck: 'Die Quellenfolie der Präsentation wird mitgeliefert.',
  exportSourcesPageSaved: 'Für dieses Artefakt gemerkt, auch für geplante Sendungen.',
  libraryKindSheetTitle: 'Art',
  shareAccessSheetTitle: 'Zugriff',

  requestShowAll: 'Alles anzeigen',
  requestShowLess: 'Weniger anzeigen',
  approveGettingReady: 'Wird vorbereitet…',
  approveNotReady:
    'Das kann gerade nicht genehmigt werden. Laden Sie die Seite neu und versuchen Sie es dann erneut.',
  tryWithModel: 'Mit {model} versuchen',
  tryWithRequesting: 'Wird gestartet…',
  tryWithUnavailable: 'Das kann gerade nicht gestartet werden.',
};

const el: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Επεξεργάζεστε ένα προσχέδιο της έκδοσης {number}',
  noChangesToApply: 'Δεν υπάρχουν αλλαγές για εφαρμογή.',
  appliedDone: 'Εφαρμόστηκε.',
  mentionInChat: 'Αναφορά στη συνομιλία',

  kindMarkdown: 'Σημείωση',
  kindKpi: 'Μέτρηση',
  libraryExportsList: 'Εξαγωγή σε {formats}',
  libraryOpensToRead: 'Ανοίγει για ανάγνωση',
  libraryReadOnly: 'Μόνο ανάγνωση',
  libraryLiveView: 'Ζωντανή προβολή',
  libraryOneNumber: 'Ένας αριθμός · στις {time}',
  retry: 'Δοκιμάστε ξανά',
  close: 'Κλείσιμο',
  panelLoading: 'Φόρτωση…',
  panelEmpty: 'Δεν υπάρχει τίποτα ακόμη.',
  panelDenied: 'Δεν έχετε πρόσβαση σε αυτό.',
  panelBlocked: 'Αυτό περιμένει κάτι για να συνεχίσει.',
  panelBlockedViewerContext:
    'Αυτή η εκτέλεση δεν έχει άτομο για λογαριασμό του οποίου να διαβάζει, οπότε δεν μπορεί να συνεχίσει μέχρι κάποιος να επιλέξει για λογαριασμό ποιου εκτελείται.',
  panelError: 'Δεν ήταν δυνατή η φόρτωση.',
  panelStale: 'Αυτό μπορεί να είναι παλιό.',
  sourcesTitle: 'Πηγές',
  sourcesIntro: 'Από πού προέρχεται κάθε αριθμός και πόσο πρόσφατος είναι.',
  sourcesLoading: 'Φόρτωση πηγών…',
  sourcesEmptyTitle: 'Καμία πηγή',
  sourcesEmptyBody: 'Αυτό το δημιούργημα δεν χρησιμοποιεί δεδομένα της εταιρείας.',
  sourcesErrorBody: 'Δεν ήταν δυνατή η φόρτωση των πηγών.',
  sourcesDeniedTitle: 'Ορισμένες πηγές είναι κρυμμένες',
  sourcesDeniedBody:
    'Δεν έχετε πρόσβαση σε {count} πηγές που χρησιμοποιούνται εδώ. Ζητήστε από έναν ιδιοκτήτη να τις κοινοποιήσει.',
  sourceDeniedName: 'Πηγή που δεν μπορείτε να δείτε',
  sourceAsOf: 'Έως {time}',
  sourceAgeUnknown: 'Άγνωστη ηλικία δεδομένων',
  sourceStatusCurrent: 'Τρέχουσα',
  sourceStatusStale: 'Παλιά',
  sourceStatusGap: 'Λείπει από την τελευταία ανανέωση',
  sourceStatusDenied: 'Χωρίς πρόσβαση',
  sourceLastGood: 'Τελευταία επιτυχής: {time}',
  sourceUsedBy: 'Χρησιμοποιείται από: {labels}',
  openDocument: 'Άνοιγμα εγγράφου',
  versionsShow: 'Εμφάνιση εκδόσεων',
  versionsHide: 'Απόκρυψη εκδόσεων',
  versionsTitle: 'Εκδόσεις',
  versionsLoading: 'Φόρτωση εκδόσεων…',
  versionsEmptyTitle: 'Καμία έκδοση ακόμη',
  versionsEmptyBody: 'Η πρώτη έκδοση εμφανίζεται όταν δημιουργηθεί αυτό το δημιούργημα.',
  versionsErrorBody: 'Δεν ήταν δυνατή η φόρτωση των εκδόσεων.',
  versionLabel: 'Έκδοση {number}',
  versionOpen: 'Άνοιγμα της έκδοσης {number}',
  versionCurrent: 'Τρέχουσα',
  versionPublished: 'Δημοσιευμένη',
  versionBy: 'Από {actor}',
  versionOriginNative: 'Στο Poneva',
  versionOriginMcp: 'μέσω του δικού σας πράκτορα',
  versionOriginTerminal: 'μέσω τερματικού',
  versionOriginJob: 'Προγραμματισμένη εκτέλεση',
  versionCausePerson: 'Επεξεργασία',
  versionCauseAgent: 'Ενημέρωση από πράκτορα',
  versionCauseRefresh: 'Ανανέωση',
  versionCauseRestore: 'Επαναφορά',
  versionCauseImport: 'Εισαγωγή',
  versionCheckedYes: 'Ελέγχθηκε',
  versionCheckedNo: 'Δεν έχει ελεγχθεί ακόμη',
  versionCheckedInvalidated: 'Επεξεργάστηκε μετά τον τελευταίο έλεγχο',
  compareStart: 'Σύγκριση',
  compareWith: 'Σύγκριση με την έκδοση {number}',
  compareTitle: 'Τι άλλαξε από v{from} έως v{to}',
  compareFromMark: 'Από',
  compareToMark: 'Έως',
  comparePick: 'Επιλέξτε μια δεύτερη έκδοση για σύγκριση.',
  compareNone: 'Καμία διαφορά.',
  compareLoading: 'Σύγκριση…',
  compareError: 'Δεν ήταν δυνατή η φόρτωση της σύγκρισης.',
  compareBefore: 'Πριν',
  compareAfter: 'Μετά',
  compareAdded: 'Προστέθηκε',
  compareRemoved: 'Αφαιρέθηκε',
  compareChanged: 'Άλλαξε',
  restore: 'Επαναφορά αυτής της έκδοσης',
  restoreConfirmTitle: 'Επαναφορά της έκδοσης {number};',
  restoreConfirmBody:
    'Δημιουργείται μια νέα έκδοση από την έκδοση {number}. Δεν διαγράφεται τίποτα.',
  restoreConfirm: 'Επαναφορά',
  restoreCancel: 'Ακύρωση',
  restoring: 'Επαναφορά…',
  restoreFailed: 'Δεν ήταν δυνατή η επαναφορά της έκδοσης.',
  restoreStale:
    'Αυτό το δημιούργημα άλλαξε ενώ το βλέπατε. Φορτώστε ξανά τις εκδόσεις και δοκιμάστε πάλι.',
  restoreDenied: 'Δεν έχετε άδεια να επαναφέρετε εκδόσεις.',
  restored: 'Η έκδοση {number} επαναφέρθηκε ως νέα έκδοση.',
  publishTitle: 'Δημοσίευση',
  publishIntro: 'Η έκδοση {number} θα είναι ορατή σε {audience}.',
  publishOwnerLine:
    'Οι θεατές βλέπουν ό,τι μπορούσε να δει ο/η {owner} όταν δημοσιεύτηκε αυτή η έκδοση.',
  publishChecksTitle: 'Έλεγχοι',
  checkPassed: 'Ελέγχθηκε',
  checkRunning: 'Έλεγχος…',
  checkFailed: 'Ένας έλεγχος απέτυχε',
  checkStale: 'Επεξεργάστηκε μετά τον τελευταίο έλεγχο',
  publishFixInChat: 'Διόρθωση στη συνομιλία',
  publishAction: 'Δημοσίευση έκδοσης {number}',
  publishing: 'Δημοσίευση…',
  publishedDone: 'Η έκδοση {number} είναι ενεργή για {audience}.',
  publishConfirmGrant:
    'Αυτή η έκδοση χρειάζεται νέα πρόσβαση σε σύστημα της εταιρείας. Ελέγξτε την πρόσβαση πριν από τη δημοσίευση.',
  publishConfirmWrite:
    'Αυτή η έκδοση γράφει σε σύστημα της εταιρείας. Ελέγξτε την πρόσβαση πριν από τη δημοσίευση.',
  publishConfirmDestructive: 'Αυτή η έκδοση αφαιρεί δεδομένα. Επιβεβαιώστε το πρώτα στο Poneva.',
  publishConflict: 'Δημοσιεύτηκε πρώτα μια άλλη έκδοση. Φορτώστε ξανά για να τη δείτε.',
  publishBlockedNotAuthorized: 'Δεν έχετε άδεια δημοσίευσης.',
  publishBlockedCheckFailed: 'Ένας έλεγχος απέτυχε, οπότε αυτή η έκδοση δεν μπορεί να δημοσιευτεί.',
  publishBlockedCheckStale: 'Το δημιούργημα άλλαξε μετά τον τελευταίο έλεγχό του. Ελέγξτε το ξανά.',
  publishBlockedObjects: 'Ορισμένα αρχεία δεν ήταν δυνατό να επαληθευτούν. Δοκιμάστε ξανά σε λίγο.',
  publishBlockedPolicy: 'Οι ρυθμίσεις της εταιρείας άλλαξαν. Ελέγξτε και δοκιμάστε ξανά.',
  publishBlockedApproval: 'Η επιβεβαίωση δεν ισχύει πια. Επιβεβαιώστε ξανά.',
  publishBlockedDependency: 'Μια απαραίτητη υπηρεσία δεν είναι διαθέσιμη. Δοκιμάστε ξανά αργότερα.',
  publishAsk: 'Ζητήστε από {publishers} να δημοσιεύσουν',
  publishAskFooter: 'Άτομα που μπορούν να δημοσιεύσουν: {publishers}.',
  publishAskNone: 'Ζητήστε το από κάποιον που μπορεί να δημοσιεύσει.',
  publishWarnings: 'Αξίζει να δείτε πριν δημοσιεύσετε',
  checkWarnStale: 'Το στοιχείο {subject} είναι παλιό ({time}).',
  checkWarnGap: 'Το στοιχείο {subject} έλειπε από την τελευταία ανανέωση.',
  checkWarnUnknownSource: 'Μια πηγή που δεν μπορείτε να δείτε χρειάζεται έλεγχο.',
  checkRefreshFirst: 'Ανανεώστε πρώτα',
  checkFailedRow: 'Το στοιχείο {subject} απέτυχε στον έλεγχό του.',
  checkFailedGeneric: 'Ένας έλεγχος απέτυχε.',
  publishReviewAccess: 'Έλεγχος πρόσβασης',
  publishRemovalBody:
    'Η v{number} αφαιρεί {what} ({count}). Πρώτα αποθηκεύεται ένα σημείο επαναφοράς.',
  publishReviewRemoval: 'Έλεγχος των αφαιρέσεων',
  publishAndRemove: 'Δημοσίευση και αφαίρεση',
  publishReviewFirst: 'Ελέγξτε τι αφαιρείται πριν δημοσιεύσετε.',
  publishDoneTitle: 'Η έκδοση {number} είναι ενεργή για {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Θα τη βρουν στη Βιβλιοθήκη τους και καρφιτσωμένη για την ομάδα.',
  publishUndo: 'Αναίρεση',
  publishCopyLink: 'Αντιγραφή συνδέσμου',
  publishDone: 'Τέλος',
  shareRoleViewMeaning: 'Μπορεί να το ανοίξει και να το διαβάσει',
  shareRoleEditMeaning: 'Μπορεί να το αλλάξει στη συνομιλία και με τον επιθεωρητή',
  shareRolePublishMeaning: 'Μπορεί επίσης να δημοσιεύσει και να αποσύρει τη δημοσίευση',
  shareRoleMenu: 'Ρόλος για {name}',
  shareUnpublishConfirm:
    'Οι χρήστες χάνουν την πρόσβαση. Το δημιούργημα και οι εκδόσεις του διατηρούνται.',
  shareUnpublishCancel: 'Να μείνει ενεργό',
  sendDestinationLine: '{provider} · {count} άτομα',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Όλοι στο {destination} βλέπουν τη σύνοψη. Οι {outside} από τους {total} δεν ανήκουν στο κοινό αυτού του δημιουργήματος, οπότε ο σύνδεσμος τους δείχνει «Αίτημα πρόσβασης».',
  sendAudienceGeneric: 'Όσοι είναι εκτός κοινού βλέπουν «Αίτημα πρόσβασης».',
  sendPreview: 'Τι θα δουν',
  shareTitle: 'Κοινοποίηση',
  shareLoading: 'Φόρτωση κοινοποίησης…',
  shareErrorBody: 'Δεν ήταν δυνατή η φόρτωση της κοινοποίησης.',
  sharePrivate: 'Μόνο εσείς βλέπετε αυτό το δημιούργημα.',
  shareShared: 'Ορατό σε {audience}.',
  shareRevoked:
    'Η κοινοποίηση είναι απενεργοποιημένη. Κανείς άλλος δεν μπορεί να ανοίξει αυτό το δημιούργημα.',
  shareOwnerSnapshot:
    'Οι θεατές βλέπουν τους αριθμούς όπως δημοσιεύτηκαν. Δεν μπορούν να ανανεώσουν ή να επεξεργαστούν.',
  shareRoleView: 'Μπορεί να δει',
  shareRoleEdit: 'Μπορεί να επεξεργαστεί',
  shareRolePublish: 'Μπορεί να δημοσιεύσει',
  shareLivePublished: 'Ενεργή: έκδοση {number}',
  shareUnpublish: 'Απόσυρση δημοσίευσης',
  shareManageDenied: 'Μόνο ένας επεξεργαστής μπορεί να αλλάξει ποιος το βλέπει.',
  sendTitle: 'Αποστολή σε',
  sendIntro:
    'Στείλτε έναν σύνδεσμο και μια σύνοψη μέσω μιας συνδεδεμένης εφαρμογής. Εγκρίνετε κάθε αποστολή.',
  sendLoading: 'Φόρτωση προορισμών…',
  sendErrorBody: 'Δεν ήταν δυνατή η φόρτωση των προορισμών.',
  sendNone:
    'Καμία συνδεδεμένη εφαρμογή δεν μπορεί ακόμη να το λάβει. Συνδέστε πρώτα το Slack, το Teams ή το email.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'Email',
  sendAudience: 'Πηγαίνει σε {audience}',
  sendSummary: 'Μήνυμα',
  sendLeaves: 'Αποστέλλεται ένας σύνδεσμος.',
  sendLeavesAttachment: 'Αποστέλλονται ένας σύνδεσμος και το αρχείο που ελέγξατε.',
  sendNoAttachments: 'Αυτός ο προορισμός δεν δέχεται αρχεία, οπότε αποστέλλεται μόνο σύνδεσμος.',
  sendScheduled: 'Οι προγραμματισμένες αποστολές χρειάζονται την έγκρισή σας κάθε φορά.',
  sendAction: 'Έλεγχος και αποστολή',
  sendFileUnsupported:
    'Αυτός ο προορισμός δεν δέχεται το αρχείο. Στείλτε μόνο σύνδεσμο ή επιλέξτε άλλον προορισμό.',
  sendLinkOnly: 'Αποστολή μόνο συνδέσμου',
  sendWaiting: 'Αναμονή για την έγκρισή σας. Δεν έχει σταλεί τίποτα.',
  sendDelivered: 'Στάλθηκε στον προορισμό {destination}.',
  sendFailed: 'Δεν στάλθηκε. Τίποτα δεν βγήκε από το Poneva.',
  sendUnknown:
    'Δεν ήταν δυνατό να επιβεβαιωθεί η παράδοση στον προορισμό {destination}. Ελέγξτε εκεί πριν στείλετε ξανά.',
  sendBlockedNotAuthorized: 'Δεν έχετε άδεια να το στείλετε.',
  sendBlockedUnsupported: 'Αυτός ο προορισμός δεν υποστηρίζεται.',
  sendBlockedApproval: 'Η έγκριση δεν ισχύει πια. Ελέγξτε και στείλτε ξανά.',
  sendBlockedChanged: 'Το μήνυμα ή ο παραλήπτης άλλαξε. Ελέγξτε ξανά.',
  sendBlockedDependency: 'Η συνδεδεμένη εφαρμογή δεν είναι διαθέσιμη. Δοκιμάστε ξανά αργότερα.',
  libraryTitle: 'Βιβλιοθήκη',
  librarySegmentAll: 'Όλα',
  librarySegmentMine: 'Δικά μου',
  librarySegmentShared: 'Κοινοποιημένα σε μένα',
  librarySearch: 'Αναζήτηση δημιουργημάτων',
  libraryKindAll: 'Όλα τα είδη',
  libraryKindReports: 'Αναφορές και σελίδες',
  libraryKindDecks: 'Παρουσιάσεις',
  libraryKindDocuments: 'Έγγραφα',
  libraryKindSpreadsheets: 'Υπολογιστικά φύλλα',
  libraryKindApps: 'Εφαρμογές',
  libraryAppsMatchNoun:
    'one:εφαρμογή ταιριάζει σε: Εφαρμογές →|other:εφαρμογές ταιριάζουν σε: Εφαρμογές →',
  libraryMake: 'Ξεκινήστε στη συνομιλία',
  libraryEmptySharedHint: 'Όταν κάποιος κοινοποιήσει ένα δημιούργημα σε εσάς, εμφανίζεται εδώ.',
  libraryRefreshFailedAt: 'Η ανανέωση απέτυχε στις {time}.',
  libraryRefreshTryAgain: 'Δοκιμάστε ξανά',
  libraryVia: 'μέσω {owner}',
  savedPrivate: 'Ιδιωτικό',
  savedVersion: 'Έκδοση {n}',
  libraryLoading: 'Φόρτωση της Βιβλιοθήκης σας…',
  libraryErrorBody: 'Η Βιβλιοθήκη σας δεν μπόρεσε να φορτωθεί αυτή τη στιγμή.',
  startersLoadFailed: 'Δεν ήταν δυνατή η φόρτωση των προτύπων αυτή τη στιγμή.',
  libraryCannotOpen: '«{title}»: δεν μπορεί να ανοίξει εδώ ακόμη.',
  libraryEmptyTitle: 'Δεν υπάρχει τίποτα ακόμη',
  libraryEmptyAllTitle: 'Δημιουργήστε κάτι που αξίζει να μοιραστείτε.',
  libraryEmptyAllBody:
    'Τα έγγραφα, οι παρουσιάσεις και οι εφαρμογές που δημιουργείτε στη συνομιλία ή που μοιράζονται άλλοι μαζί σας εμφανίζονται εδώ.',
  libraryEmptyMineBody: 'Τα δημιουργήματα που φτιάχνετε εμφανίζονται εδώ.',
  libraryEmptySharedBody: 'Δεν έχει κοινοποιηθεί τίποτα σε εσάς ακόμη.',
  libraryNoMatchTitle: 'Κανένα αποτέλεσμα',
  libraryNoMatchBody: 'Δοκιμάστε άλλη λέξη ή διαφορετικό είδος.',
  libraryMore: 'Εμφάνιση περισσότερων',
  libraryOwner: 'Από {owner}',
  libraryUpdated: 'Επεξεργάστηκε {time}',
  libraryPublishedAs: 'Δημοσιευμένο · v{number}',
  libraryDraft: 'Πρόχειρο',
  libraryUnpublished: 'Μη δημοσιευμένο',
  libraryRefreshFailed: 'Η ανανέωση απέτυχε',
  libraryRefreshRunning: 'Ανανεώνεται',
  libraryAudience: 'Για {audience}',
  kindReport: 'Αναφορά',
  kindPage: 'Σελίδα',
  kindSite: 'Ιστότοπος',
  kindDeck: 'Παρουσίαση',
  kindDocument: 'Έγγραφο',
  kindApp: 'Εφαρμογή',
  kindSpreadsheet: 'Υπολογιστικό φύλλο',
  kindDashboard: 'Πίνακας ελέγχου',
  kindTable: 'Πίνακας',
  kindChart: 'Γράφημα',
  kindExportsReport: 'Εξάγεται σε PDF',
  kindExportsPage: 'Σελίδα μόνο για ανάγνωση',
  kindExportsSite: 'Ιστότοπος μόνο για ανάγνωση',
  kindExportsDeck: 'Παρουσιάστε ή εξαγάγετε σε PowerPoint και PDF',
  kindExportsDocument: 'Εξάγεται σε Word και PDF',
  kindExportsSpreadsheet: 'Εξάγεται σε Excel και CSV',
  kindExportsDashboard: 'Προβολή σε πραγματικό χρόνο',
  kindExportsTable: 'Εξάγεται σε Excel και CSV',
  kindExportsChart: 'Προβολή γραφήματος',
  agentPublished: 'Ο πράκτορας {agent} δημοσίευσε το {title} v{number}.',
  agentCheckedOn: 'Ελέγχθηκε σε {views}, {themes}.',
  agentUndo: 'Αναίρεση',
  agentRestorePrevious: 'Επαναφορά v{number}',
  agentHeld: 'Το {title} v{number} περιμένει ως πρόχειρο.',
  agentHeldMismatch: '{count} αριθμοί δεν ταίριαζαν με τις πηγές τους.',
  agentHeldCheck: 'Ένας έλεγχος δεν πέρασε.',
  agentFixInChat: 'Διόρθωση στη συνομιλία',
  agentConfirmDrop:
    'Ο πράκτορας {agent} θέλει να αφαιρέσει {what} από το {title}. Πρώτα αποθηκεύεται ένα σημείο επαναφοράς. Τίποτα δεν αλλάζει μέχρι να αποφασίσετε.',
  agentConfirmDropCount:
    'Ο πράκτορας {agent} θέλει να αφαιρέσει {what} ({count} τιμές) από το {title}. Πρώτα αποθηκεύεται ένα σημείο επαναφοράς. Τίποτα δεν αλλάζει μέχρι να αποφασίσετε.',
  agentViewDesktop: 'υπολογιστή',
  agentViewTablet: 'tablet',
  agentViewPhone: 'τηλέφωνο',
  agentThemeLight: 'φωτεινό',
  agentThemeDark: 'σκούρο',
  agentLookAt: 'Δείτε την v{number}',
  agentConfirmWrite:
    'Ο πράκτορας {agent} θέλει να γράψει {what}. Τίποτα δεν γράφεται μέχρι να αποφασίσετε.',
  agentConfirmGrant:
    'Ο πράκτορας {agent} θέλει πρόσβαση σε {what}. Τίποτα δεν κοινοποιείται μέχρι να αποφασίσετε.',
  agentReview: 'Έλεγχος',
  agentSkipped: 'Ο πράκτορας {agent} παρέλειψε: {part}.',
  agentSkippedWhy:
    'Διαβάζει το προσωπικό {system} κάθε ατόμου, και αυτή η εκτέλεση δεν έχει άτομο για λογαριασμό του οποίου να διαβάζει.',
  agentChooseWho: 'Επιλέξτε για λογαριασμό ποιου εκτελείται',
  agentChangeInChat: 'Αλλαγή στη συνομιλία',
  agentFailed: 'Ο πράκτορας {agent} δεν μπόρεσε να ανανεώσει το {title}.',
  agentFailedBody: 'Η τελευταία δημοσιευμένη έκδοση παραμένει ενεργή.',
  agentRetry: 'Δοκιμάστε ξανά',
  agentRunning: 'Ο πράκτορας {agent} ενημερώνει το {title}.',
  agentMakesTitle: 'Δημιουργεί και ενημερώνει',
  agentMakesLoading: 'Φόρτωση…',
  agentMakesErrorBody: 'Δεν ήταν δυνατή η φόρτωση.',
  agentMakesNone: 'Αυτός ο πράκτορας δεν δημιουργεί ακόμη δημιουργήματα.',
  agentMakesNew: 'Νέο δημιούργημα σε κάθε εκτέλεση',
  agentMakesUpdate: 'Ενημερώνει ένα δημιούργημα σε κάθε εκτέλεση',
  agentNextRun: 'Επόμενη εκτέλεση {when}',
  agentNoSchedule: 'Χωρίς προγραμματισμό',
  agentCreditsPerRun: 'Περίπου {credits} πιστώσεις ανά εκτέλεση',
  agentCreditsUnknown: 'Οι πιστώσεις ανά εκτέλεση δεν είναι ακόμη γνωστές',
  agentLastResult: 'Τελευταίο αποτέλεσμα',
  interviewTitle: 'Ρύθμιση',
  interviewIntro: 'Μερικές ερωτήσεις το διαμορφώνουν.',
  interviewOptional: '(προαιρετικό)',
  interviewAnswerNeeded: 'Απαντήστε για να συνεχίσετε.',
  interviewReadOnly: 'Μόνο ανάγνωση',
  interviewChange: 'Αλλαγή',
  interviewConnectionYours: 'η σύνδεσή σας με το {system}',
  interviewConnectionOther: 'η σύνδεση του/της {name} με το {system}',
  interviewDateAny: 'Οποιαδήποτε περίοδος',
  interviewDatePrevious: 'Προηγούμενος μήνας',
  interviewDateNext: 'Επόμενος μήνας',
  interviewDatePresets: 'Γρήγορα διαστήματα',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Εκκαθάριση',
  interviewDateDone: 'Τέλος',
  interviewChooseSource: 'Επιλέξτε πηγή',
  interviewSourceChosen: 'Η πηγή επιλέχθηκε',
  interviewSourceUnavailable:
    'Η επιλογή πηγής δεν είναι ακόμη διαθέσιμη εδώ, επομένως δεν μπορεί να συνεχιστεί.',
  interviewSourceUnavailableOptional:
    'Η επιλογή πηγής δεν είναι ακόμη διαθέσιμη εδώ. Μπορείτε να συνεχίσετε χωρίς πηγή.',
  interviewStarterWithdrawn:
    'Αυτό το πρότυπο δεν είναι πλέον διαθέσιμο. Τα πρόχειρα που έχετε ήδη ξεκινήσει από αυτό εξακολουθούν να ανοίγουν.',
  interviewDateFrom: 'Από',
  interviewDateTo: 'Έως',
  interviewContinue: 'Συνέχεια',
  interviewGrantTitle: 'Να επιτραπεί η ανάγνωση του {source};',
  interviewGrantBody:
    'Χρησιμοποιεί {connection}, μόνο για ανάγνωση, για {scope}. Τίποτα δεν διαβάζεται μέχρι να το επιτρέψετε και δεν εμφανίζονται δείγματα γραμμών.',
  interviewAllow: 'Να επιτραπεί',
  interviewNotNow: 'Όχι τώρα',
  interviewConfiguring: 'Ρύθμιση με βάση τις απαντήσεις σας…',
  interviewChecking: 'Έλεγχος πριν ανοίξει…',
  interviewComplete: 'Το {title} είναι έτοιμο.',
  interviewOpen: 'Άνοιγμα του {title}',
  interviewBlocked: 'Δεν ήταν δυνατή η συνέχεια.',
  interviewFailed: 'Η ρύθμιση απέτυχε. Οι απαντήσεις σας διατηρούνται.',
  interviewCancelled: 'Αυτό ακυρώθηκε.',
  interviewSaving: 'Αποθήκευση…',
  interviewSaved: 'Αποθηκεύτηκε',
  interviewConflict:
    'Αυτό το πρόχειρο άλλαξε αλλού. Οι απαντήσεις σας διατηρούνται εδώ μέχρι να φορτώσετε το τρέχον πρόχειρο, το οποίο τις αντικαθιστά.',
  interviewReloadCurrent: 'Φόρτωση του τρέχοντος προχείρου',
  interviewUnsettled:
    'Δεν ήταν δυνατό να επιβεβαιωθεί η τελευταία αποθήκευσή σας. Οι απαντήσεις σας διατηρούνται εδώ.',
  interviewCheckAgain: 'Έλεγχος ξανά',
  finishAction: 'Ιδιωτική αποθήκευση',
  finishSaving: 'Αποθήκευση…',
  finishSaved: 'Το «{title}» αποθηκεύτηκε και το βλέπετε μόνο εσείς.',
  finishNotSaved: 'Δεν αποθηκεύτηκε. Το πρόχειρό σας είναι ακόμη εδώ.',
  finishUnconfirmed: 'Δεν ήταν δυνατό να επιβεβαιώσουμε αν αποθηκεύτηκε.',
  candidateInvalid: 'Το πρόχειρο δεν μπόρεσε να γίνει έγγραφο.',
  finishOpenInLibrary: 'Άνοιγμα στη Βιβλιοθήκη',
  savedOpenFailed: 'Δεν ήταν δυνατό το άνοιγμα αυτή τη στιγμή.',
  savedInLibrary: 'Βρίσκεται στη Βιβλιοθήκη σας.',
  openSavedTitle: 'Άνοιγμα του {title} →',
  interviewReasonNotAuthorized: 'Δεν έχετε άδεια να αλλάξετε αυτό το πρόχειρο.',
  interviewReasonDraftNotOpen: 'Αυτό το πρόχειρο δεν είναι πια ανοιχτό.',
  interviewReasonInvalid: 'Μία από τις απαντήσεις δεν είναι έγκυρη.',
  interviewReasonObjects:
    'Ορισμένα αρχεία δεν ήταν δυνατό να επαληθευτούν. Οι απαντήσεις σας διατηρούνται.',
  interviewReasonDependency:
    'Μια απαραίτητη υπηρεσία δεν είναι διαθέσιμη. Οι απαντήσεις σας διατηρούνται.',
  interviewReasonReconcile: 'Η τελευταία αποθήκευση επιβεβαιώνεται ακόμη.',
  exportTitle: 'Εξαγωγή',
  exportIntro:
    'Κατεβάστε αυτό το δημιούργημα. Το αρχείο αναφέρει από πού προέρχονται τα δεδομένα του.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Επεξεργάσιμες διαφάνειες με σημειώσεις ομιλητή',
  exportHintPdf: 'Ακριβώς όπως εμφανίζεται',
  exportHintDocx: 'Επεξεργάσιμο, διατηρεί επικεφαλίδες και πίνακες',
  exportHintXlsx: 'Όλα τα φύλλα, διατηρεί τους τύπους',
  exportHintCsv: 'Μόνο τιμές, UTF-8',
  exportUnavailable: 'Δεν είναι διαθέσιμο αυτή τη στιγμή',
  exportRunning: 'Προετοιμασία {format}…',
  exportValidating: 'Έλεγχος του αρχείου {format}…',
  exportReady: 'Το αρχείο {format} είναι έτοιμο',
  exportDownload: 'Λήψη {name}',
  exportVersion: 'Εξαγμένη έκδοση {number}',
  exportFailed: 'Δεν ήταν δυνατή η δημιουργία του αρχείου {format}.',
  exportFailedChecks: 'Το αρχείο {format} δεν πέρασε τους ελέγχους του, οπότε δεν προσφέρθηκε.',
  exportDenied: 'Δεν έχετε πρόσβαση για να εξαγάγετε αυτό το δημιούργημα.',
  exportRevoked:
    'Η κοινοποίηση απενεργοποιήθηκε, οπότε αυτό το δημιούργημα δεν μπορεί πλέον να εξαχθεί.',
  exportStale:
    'Αυτό το δημιούργημα άλλαξε κατά την εξαγωγή. Εξαγάγετε ξανά για να πάρετε την πιο πρόσφατη έκδοση.',
  exportProvenanceTitle: 'Κάθε αρχείο περιλαμβάνει',
  exportProvenanceLine:
    'Δημιουργήθηκε με το Poneva · {title} · v{number} · {data} · δημιουργήθηκε {madeAt}',
  exportProvenanceAsOf: 'δεδομένα έως {time}',
  exportPlacementNotesAbout: 'Στο φύλλο Σημειώσεις και σε ένα αρχείο πληροφοριών δίπλα στο CSV',
  exportProvenanceRange: 'δεδομένα από {earliest} έως {latest}',
  exportProvenanceUnknown: 'άγνωστη ηλικία δεδομένων',
  exportAboutFile: 'Σχετικά με αυτό το αρχείο ({format})',
  exportPlacementFooter: 'Στο υποσέλιδο κάθε σελίδας',
  exportPlacementSlide: 'Στο υποσέλιδο κάθε διαφάνειας',
  exportPlacementNotes: 'Στο φύλλο Σημειώσεις',
  exportPlacementAbout: 'Σε ένα αρχείο πληροφοριών δίπλα στο CSV',
  exportSourcesPage: 'Προσθήκη σελίδας πηγών',
  exportSourcesPageHint:
    'Μια τελευταία σελίδα που παραθέτει κάθε αριθμό, την πηγή και την ώρα του.',
  exportSourcesPageDeck: 'Η διαφάνεια Πηγές της παρουσίασης περιλαμβάνεται.',
  exportSourcesPageSaved:
    'Αποθηκεύεται για αυτό το δημιούργημα, συμπεριλαμβανομένων των προγραμματισμένων αποστολών.',
  libraryKindSheetTitle: 'Είδος',
  shareAccessSheetTitle: 'Πρόσβαση',

  requestShowAll: 'Εμφάνιση όλων',
  requestShowLess: 'Εμφάνιση λιγότερων',
  approveGettingReady: 'Προετοιμάζεται…',
  approveNotReady:
    'Δεν είναι δυνατή η έγκριση αυτή τη στιγμή. Φορτώστε ξανά τη σελίδα και δοκιμάστε ξανά.',
  tryWithModel: 'Δοκιμή με {model}',
  tryWithRequesting: 'Έναρξη…',
  tryWithUnavailable: 'Δεν είναι δυνατή η έναρξη αυτή τη στιγμή.',
};

const es: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Estás editando un borrador de la versión {number}',
  noChangesToApply: 'No hay cambios que aplicar.',
  appliedDone: 'Aplicado.',
  mentionInChat: 'Mencionar en el chat',

  kindMarkdown: 'Nota',
  kindKpi: 'Métrica',
  libraryExportsList: 'Se exporta a {formats}',
  libraryOpensToRead: 'Se abre para leer',
  libraryReadOnly: 'Solo lectura',
  libraryLiveView: 'Vista en vivo',
  libraryOneNumber: 'Un número · a {time}',
  retry: 'Reintentar',
  close: 'Cerrar',
  panelLoading: 'Cargando…',
  panelEmpty: 'Aún no hay nada aquí.',
  panelDenied: 'No tienes acceso a esto.',
  panelBlocked: 'Esto está esperando algo antes de poder continuar.',
  panelBlockedViewerContext:
    'Esta ejecución no tiene ninguna persona como la que leer, así que no puede continuar hasta que alguien elija como quién se ejecuta.',
  panelError: 'No se pudo cargar.',
  panelStale: 'Puede que esto esté desactualizado.',
  sourcesTitle: 'Fuentes',
  sourcesIntro: 'De dónde viene cada cifra y qué tan reciente es.',
  sourcesLoading: 'Cargando fuentes…',
  sourcesEmptyTitle: 'Sin fuentes',
  sourcesEmptyBody: 'Este artefacto no usa datos de la empresa.',
  sourcesErrorBody: 'No se pudieron cargar las fuentes.',
  sourcesDeniedTitle: 'Algunas fuentes están ocultas',
  sourcesDeniedBody:
    'No tienes acceso a {count} fuentes que se usan aquí. Pide a un propietario que las comparta.',
  sourceDeniedName: 'Fuente que no puedes ver',
  sourceAsOf: 'Al {time}',
  sourceAgeUnknown: 'Antigüedad de los datos desconocida',
  sourceStatusCurrent: 'Actual',
  sourceStatusStale: 'Desactualizada',
  sourceStatusGap: 'Falta en la última actualización',
  sourceStatusDenied: 'Sin acceso',
  sourceLastGood: 'Última correcta: {time}',
  sourceUsedBy: 'Usada por {labels}',
  openDocument: 'Abrir documento',
  versionsShow: 'Mostrar versiones',
  versionsHide: 'Ocultar versiones',
  versionsTitle: 'Versiones',
  versionsLoading: 'Cargando versiones…',
  versionsEmptyTitle: 'Aún no hay versiones',
  versionsEmptyBody: 'La primera versión aparece cuando se crea este artefacto.',
  versionsErrorBody: 'No se pudieron cargar las versiones.',
  versionLabel: 'Versión {number}',
  versionOpen: 'Abrir la versión {number}',
  versionCurrent: 'Actual',
  versionPublished: 'Publicada',
  versionBy: 'Por {actor}',
  versionOriginNative: 'En Poneva',
  versionOriginMcp: 'mediante tu propio agente',
  versionOriginTerminal: 'mediante el terminal',
  versionOriginJob: 'Ejecución programada',
  versionCausePerson: 'Editada',
  versionCauseAgent: 'Actualización del agente',
  versionCauseRefresh: 'Actualizada',
  versionCauseRestore: 'Restaurada',
  versionCauseImport: 'Importada',
  versionCheckedYes: 'Comprobada',
  versionCheckedNo: 'Aún sin comprobar',
  versionCheckedInvalidated: 'Editada desde la última comprobación',
  compareStart: 'Comparar',
  compareWith: 'Comparar con la versión {number}',
  compareTitle: 'Qué cambió de v{from} a v{to}',
  compareFromMark: 'De',
  compareToMark: 'A',
  comparePick: 'Elige una segunda versión para comparar.',
  compareNone: 'Sin diferencias.',
  compareLoading: 'Comparando…',
  compareError: 'No se pudo cargar la comparación.',
  compareBefore: 'Antes',
  compareAfter: 'Después',
  compareAdded: 'Añadido',
  compareRemoved: 'Eliminado',
  compareChanged: 'Cambiado',
  restore: 'Restaurar esta versión',
  restoreConfirmTitle: '¿Restaurar la versión {number}?',
  restoreConfirmBody:
    'Esto crea una versión nueva a partir de la versión {number}. No se elimina nada.',
  restoreConfirm: 'Restaurar',
  restoreCancel: 'Cancelar',
  restoring: 'Restaurando…',
  restoreFailed: 'No se pudo restaurar la versión.',
  restoreStale:
    'Este artefacto cambió mientras lo mirabas. Recarga las versiones e inténtalo de nuevo.',
  restoreDenied: 'No tienes permiso para restaurar versiones.',
  restored: 'La versión {number} se restauró como una versión nueva.',
  publishTitle: 'Publicar',
  publishIntro: 'La versión {number} será visible para {audience}.',
  publishOwnerLine:
    'Quienes la vean verán lo que {owner} podía ver cuando se publicó esta versión.',
  publishChecksTitle: 'Comprobaciones',
  checkPassed: 'Comprobada',
  checkRunning: 'Comprobando…',
  checkFailed: 'Una comprobación falló',
  checkStale: 'Editada desde la última comprobación',
  publishFixInChat: 'Corregir en el chat',
  publishAction: 'Publicar la versión {number}',
  publishing: 'Publicando…',
  publishedDone: 'La versión {number} está activa para {audience}.',
  publishConfirmGrant:
    'Esta versión necesita un nuevo acceso a un sistema de la empresa. Revisa el acceso antes de publicar.',
  publishConfirmWrite:
    'Esta versión escribe en un sistema de la empresa. Revisa el acceso antes de publicar.',
  publishConfirmDestructive: 'Esta versión elimina datos. Confírmalo primero en Poneva.',
  publishConflict: 'Antes se publicó una versión distinta. Recarga para verla.',
  publishBlockedNotAuthorized: 'No tienes permiso para publicar.',
  publishBlockedCheckFailed: 'Una comprobación falló, así que esta versión no se puede publicar.',
  publishBlockedCheckStale:
    'El artefacto cambió después de su última comprobación. Compruébalo de nuevo.',
  publishBlockedObjects:
    'Algunos archivos no se pudieron verificar. Inténtalo de nuevo en un momento.',
  publishBlockedPolicy: 'Cambió la configuración de la empresa. Revísala e inténtalo de nuevo.',
  publishBlockedApproval: 'La confirmación ya no es válida. Confirma de nuevo.',
  publishBlockedDependency: 'Un servicio necesario no está disponible. Inténtalo más tarde.',
  publishAsk: 'Pedir a {publishers} que publique',
  publishAskFooter: 'Personas que pueden publicar: {publishers}.',
  publishAskNone: 'Pídeselo a alguien que pueda publicar.',
  publishWarnings: 'Conviene revisarlo antes de publicar',
  checkWarnStale: '{subject} está desactualizado ({time}).',
  checkWarnGap: '{subject} faltaba en la última actualización.',
  checkWarnUnknownSource: 'Una fuente que no puedes ver necesita revisión.',
  checkRefreshFirst: 'Actualizar primero',
  checkFailedRow: '{subject} no pasó su comprobación.',
  checkFailedGeneric: 'Una comprobación falló.',
  publishReviewAccess: 'Revisar acceso',
  publishRemovalBody:
    'v{number} elimina {what} ({count}). Antes se guarda un punto de restauración.',
  publishReviewRemoval: 'Revisar lo que se elimina',
  publishAndRemove: 'Publicar y eliminar',
  publishReviewFirst: 'Revisa lo que se elimina antes de publicar.',
  publishDoneTitle: 'La versión {number} está activa para {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Lo encontrarán en su biblioteca y fijado para el equipo.',
  publishUndo: 'Deshacer',
  publishCopyLink: 'Copiar enlace',
  publishDone: 'Listo',
  shareRoleViewMeaning: 'Puede abrirlo y leerlo',
  shareRoleEditMeaning: 'Puede cambiarlo en el chat y con el inspector',
  shareRolePublishMeaning: 'También puede publicar y despublicar',
  shareRoleMenu: 'Rol de {name}',
  shareUnpublishConfirm:
    'Las personas pierden el acceso. El artefacto y sus versiones se conservan.',
  shareUnpublishCancel: 'Mantener activo',
  sendDestinationLine: '{provider} · {count} personas',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Todos en {destination} ven el resumen. {outside} de {total} no están en la audiencia de este artefacto, así que el enlace les muestra «Solicitar acceso».',
  sendAudienceGeneric: 'Las personas fuera de la audiencia ven «Solicitar acceso».',
  sendPreview: 'Lo que verán',
  shareTitle: 'Compartir',
  shareLoading: 'Cargando el uso compartido…',
  shareErrorBody: 'No se pudo cargar el uso compartido.',
  sharePrivate: 'Solo tú puedes ver este artefacto.',
  shareShared: 'Visible para {audience}.',
  shareRevoked: 'El uso compartido está desactivado. Nadie más puede abrir este artefacto.',
  shareOwnerSnapshot:
    'Quienes lo ven ven las cifras tal como se publicaron. No pueden actualizar ni editar.',
  shareRoleView: 'Puede ver',
  shareRoleEdit: 'Puede editar',
  shareRolePublish: 'Puede publicar',
  shareLivePublished: 'Activa: versión {number}',
  shareUnpublish: 'Despublicar',
  shareManageDenied: 'Solo un editor puede cambiar quién ve esto.',
  sendTitle: 'Enviar a',
  sendIntro:
    'Envía un enlace y un resumen mediante una aplicación conectada. Tú apruebas cada envío.',
  sendLoading: 'Cargando destinos…',
  sendErrorBody: 'No se pudieron cargar los destinos.',
  sendNone:
    'Todavía ninguna aplicación conectada puede recibir esto. Conecta primero Slack, Teams o el correo.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'Correo',
  sendAudience: 'Va a {audience}',
  sendSummary: 'Mensaje',
  sendLeaves: 'Se envía un enlace.',
  sendLeavesAttachment: 'Se envían un enlace y el archivo revisado.',
  sendNoAttachments: 'Este destino no admite archivos, así que solo se envía un enlace.',
  sendScheduled: 'Los envíos programados necesitan tu aprobación cada vez.',
  sendAction: 'Revisar y enviar',
  sendFileUnsupported:
    'Este destino no admite el archivo. Envía solo un enlace o elige otro destino.',
  sendLinkOnly: 'Enviar solo un enlace',
  sendWaiting: 'Esperando tu aprobación. No se ha enviado nada.',
  sendDelivered: 'Enviado a {destination}.',
  sendFailed: 'No se envió. Nada salió de Poneva.',
  sendUnknown:
    'No pudimos confirmar la entrega a {destination}. Compruébalo allí antes de volver a enviar.',
  sendBlockedNotAuthorized: 'No tienes permiso para enviar esto.',
  sendBlockedUnsupported: 'Este destino no es compatible.',
  sendBlockedApproval: 'La aprobación ya no es válida. Revisa y envía de nuevo.',
  sendBlockedChanged: 'El mensaje o el destinatario cambió. Revísalo de nuevo.',
  sendBlockedDependency: 'La aplicación conectada no está disponible. Inténtalo más tarde.',
  libraryTitle: 'Biblioteca',
  librarySegmentAll: 'Todos',
  librarySegmentMine: 'Míos',
  librarySegmentShared: 'Compartidos conmigo',
  librarySearch: 'Buscar artefactos',
  libraryKindAll: 'Todos los tipos',
  libraryKindReports: 'Informes y páginas',
  libraryKindDecks: 'Presentaciones',
  libraryKindDocuments: 'Documentos',
  libraryKindSpreadsheets: 'Hojas de cálculo',
  libraryKindApps: 'Apps',
  libraryAppsMatchNoun: 'one:app coincide en Apps →|other:apps coinciden en Apps →',
  libraryMake: 'Empezar en el chat',
  libraryEmptySharedHint: 'Cuando alguien comparta un artefacto contigo, aparecerá aquí.',
  libraryRefreshFailedAt: 'No se pudo actualizar el {time}.',
  libraryRefreshTryAgain: 'Reintentar',
  libraryVia: 'mediante {owner}',
  savedPrivate: 'Privado',
  savedVersion: 'Versión {n}',
  libraryLoading: 'Cargando tu biblioteca…',
  libraryErrorBody: 'Tu biblioteca no se pudo cargar en este momento.',
  startersLoadFailed: 'No se han podido cargar las plantillas en este momento.',
  libraryCannotOpen: '«{title}» aún no se puede abrir aquí.',
  libraryEmptyTitle: 'Aún no hay nada aquí',
  libraryEmptyAllTitle: 'Crea algo que valga la pena compartir.',
  libraryEmptyAllBody:
    'Los documentos, presentaciones y apps que crees en el chat, o que otros compartan contigo, aparecen aquí.',
  libraryEmptyMineBody: 'Los artefactos que creas aparecen aquí.',
  libraryEmptySharedBody: 'Aún no se ha compartido nada contigo.',
  libraryNoMatchTitle: 'Sin coincidencias',
  libraryNoMatchBody: 'Prueba con otra palabra o con otro tipo.',
  libraryMore: 'Mostrar más',
  libraryOwner: 'Por {owner}',
  libraryUpdated: 'Editado {time}',
  libraryPublishedAs: 'Publicado · v{number}',
  libraryDraft: 'Borrador',
  libraryUnpublished: 'Sin publicar',
  libraryRefreshFailed: 'Falló la actualización',
  libraryRefreshRunning: 'Actualizando',
  libraryAudience: 'Para {audience}',
  kindReport: 'Informe',
  kindPage: 'Página',
  kindSite: 'Sitio',
  kindDeck: 'Presentación',
  kindDocument: 'Documento',
  kindApp: 'App',
  kindSpreadsheet: 'Hoja de cálculo',
  kindDashboard: 'Panel',
  kindTable: 'Tabla',
  kindChart: 'Gráfico',
  kindExportsReport: 'Se exporta a PDF',
  kindExportsPage: 'Página de solo lectura',
  kindExportsSite: 'Sitio de solo lectura',
  kindExportsDeck: 'Presenta, o exporta a PowerPoint y PDF',
  kindExportsDocument: 'Se exporta a Word y PDF',
  kindExportsSpreadsheet: 'Se exporta a Excel y CSV',
  kindExportsDashboard: 'Vista en vivo',
  kindExportsTable: 'Se exporta a Excel y CSV',
  kindExportsChart: 'Vista de gráfico',
  agentPublished: '{agent} publicó {title} v{number}.',
  agentCheckedOn: 'Comprobado en {views}, {themes}.',
  agentUndo: 'Deshacer',
  agentRestorePrevious: 'Restaurar v{number}',
  agentHeld: '{title} v{number} está esperando como borrador.',
  agentHeldMismatch: '{count} cifras no coincidían con sus fuentes.',
  agentHeldCheck: 'Una comprobación no pasó.',
  agentFixInChat: 'Corregir en el chat',
  agentConfirmDrop:
    '{agent} quiere eliminar {what} de {title}. Antes se guarda un punto de restauración. No cambia nada hasta que tú decidas.',
  agentConfirmDropCount:
    '{agent} quiere eliminar {what} ({count} valores) de {title}. Antes se guarda un punto de restauración. No cambia nada hasta que tú decidas.',
  agentViewDesktop: 'escritorio',
  agentViewTablet: 'tableta',
  agentViewPhone: 'teléfono',
  agentThemeLight: 'claro',
  agentThemeDark: 'oscuro',
  agentLookAt: 'Ver v{number}',
  agentConfirmWrite: '{agent} quiere escribir {what}. No se escribe nada hasta que tú decidas.',
  agentConfirmGrant: '{agent} quiere acceso a {what}. No se comparte nada hasta que tú decidas.',
  agentReview: 'Revisar',
  agentSkipped: '{agent} omitió {part}.',
  agentSkippedWhy:
    'Lee el {system} propio de cada persona, y esta ejecución no tiene ninguna persona como la que leer.',
  agentChooseWho: 'Elegir como quién se ejecuta',
  agentChangeInChat: 'Cambiar en el chat',
  agentFailed: '{agent} no pudo actualizar {title}.',
  agentFailedBody: 'La última versión publicada sigue activa.',
  agentRetry: 'Reintentar',
  agentRunning: '{agent} está actualizando {title}.',
  agentMakesTitle: 'Crea y actualiza',
  agentMakesLoading: 'Cargando…',
  agentMakesErrorBody: 'No se pudo cargar.',
  agentMakesNone: 'Este agente aún no crea artefactos.',
  agentMakesNew: 'Un artefacto nuevo en cada ejecución',
  agentMakesUpdate: 'Actualiza un artefacto en cada ejecución',
  agentNextRun: 'Próxima ejecución {when}',
  agentNoSchedule: 'Sin programar',
  agentCreditsPerRun: 'Unos {credits} créditos por ejecución',
  agentCreditsUnknown: 'Los créditos por ejecución aún no se conocen',
  agentLastResult: 'Último resultado',
  interviewTitle: 'Configurarlo',
  interviewIntro: 'Unas pocas preguntas lo definen.',
  interviewOptional: '(opcional)',
  interviewAnswerNeeded: 'Responde esto para continuar.',
  interviewReadOnly: 'Solo lectura',
  interviewChange: 'Cambiar',
  interviewConnectionYours: 'tu conexión de {system}',
  interviewConnectionOther: 'la conexión de {system} de {name}',
  interviewDateAny: 'Cualquier periodo',
  interviewDatePrevious: 'Mes anterior',
  interviewDateNext: 'Mes siguiente',
  interviewDatePresets: 'Rangos rápidos',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Borrar',
  interviewDateDone: 'Listo',
  interviewChooseSource: 'Elige una fuente',
  interviewSourceChosen: 'Fuente elegida',
  interviewSourceUnavailable:
    'Elegir una fuente aún no está disponible aquí, así que no se puede continuar.',
  interviewSourceUnavailableOptional:
    'Elegir una fuente aún no está disponible aquí. Puedes continuar sin ella.',
  interviewStarterWithdrawn:
    'Esta plantilla ya no está disponible. Los borradores que ya empezaste con ella se siguen abriendo.',
  interviewDateFrom: 'Desde',
  interviewDateTo: 'Hasta',
  interviewContinue: 'Continuar',
  interviewGrantTitle: '¿Permitir la lectura de {source}?',
  interviewGrantBody:
    'Usa {connection}, solo lectura, para {scope}. No se lee nada hasta que lo permitas, y no se muestran filas de ejemplo.',
  interviewAllow: 'Permitir',
  interviewNotNow: 'Ahora no',
  interviewConfiguring: 'Configurándolo a partir de tus respuestas…',
  interviewChecking: 'Comprobándolo antes de abrirlo…',
  interviewComplete: '{title} está listo.',
  interviewOpen: 'Abrir {title}',
  interviewBlocked: 'No se pudo continuar.',
  interviewFailed: 'La configuración falló. Tus respuestas se conservan.',
  interviewCancelled: 'Esto se canceló.',
  interviewSaving: 'Guardando…',
  interviewSaved: 'Guardado',
  interviewConflict:
    'Este borrador cambió en otro lugar. Tus respuestas se conservan aquí hasta que cargues el borrador actual, que las reemplaza.',
  interviewReloadCurrent: 'Cargar el borrador actual',
  interviewUnsettled: 'No pudimos confirmar tu último guardado. Tus respuestas se conservan aquí.',
  interviewCheckAgain: 'Comprobar de nuevo',
  finishAction: 'Guardar en privado',
  finishSaving: 'Guardando…',
  finishSaved: '«{title}» se ha guardado y solo tú puedes verlo.',
  finishNotSaved: 'No se guardó. Tu borrador sigue aquí.',
  finishUnconfirmed: 'No pudimos confirmar si se guardó.',
  candidateInvalid: 'El borrador no se pudo convertir en un documento.',
  finishOpenInLibrary: 'Abrir en la Biblioteca',
  savedOpenFailed: 'No se ha podido abrir ahora mismo.',
  savedInLibrary: 'Está en tu Biblioteca.',
  openSavedTitle: 'Abrir {title} →',
  interviewReasonNotAuthorized: 'No tienes permiso para cambiar este borrador.',
  interviewReasonDraftNotOpen: 'Este borrador ya no está abierto.',
  interviewReasonInvalid: 'Una de las respuestas no es válida.',
  interviewReasonObjects: 'Algunos archivos no se pudieron verificar. Tus respuestas se conservan.',
  interviewReasonDependency:
    'Un servicio necesario no está disponible. Tus respuestas se conservan.',
  interviewReasonReconcile: 'El último guardado aún se está confirmando.',
  exportTitle: 'Exportar',
  exportIntro: 'Descarga este artefacto. El archivo indica de dónde vienen sus datos.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Diapositivas editables con notas del orador',
  exportHintPdf: 'Exactamente como se muestra',
  exportHintDocx: 'Editable, conserva títulos y tablas',
  exportHintXlsx: 'Todas las hojas, conserva las fórmulas',
  exportHintCsv: 'Solo valores, UTF-8',
  exportUnavailable: 'No disponible por ahora',
  exportRunning: 'Preparando {format}…',
  exportValidating: 'Comprobando el archivo {format}…',
  exportReady: 'El archivo {format} está listo',
  exportDownload: 'Descargar {name}',
  exportVersion: 'Versión exportada {number}',
  exportFailed: 'No se pudo crear el archivo {format}.',
  exportFailedChecks: 'El archivo {format} no pasó sus comprobaciones, así que no se ofreció.',
  exportDenied: 'No tienes acceso para exportar este artefacto.',
  exportRevoked: 'Se desactivó el uso compartido, así que este artefacto ya no se puede exportar.',
  exportStale:
    'Este artefacto cambió durante la exportación. Exporta de nuevo para obtener la versión más reciente.',
  exportProvenanceTitle: 'Cada archivo incluye',
  exportProvenanceLine: 'Creado con Poneva · {title} · v{number} · {data} · creado {madeAt}',
  exportProvenanceAsOf: 'datos al {time}',
  exportPlacementNotesAbout: 'En la hoja Notas y en un archivo de información junto al CSV',
  exportProvenanceRange: 'datos de {earliest} a {latest}',
  exportProvenanceUnknown: 'antigüedad de los datos desconocida',
  exportAboutFile: 'Acerca de este archivo ({format})',
  exportPlacementFooter: 'En el pie de cada página',
  exportPlacementSlide: 'En el pie de cada diapositiva',
  exportPlacementNotes: 'En la hoja Notas',
  exportPlacementAbout: 'En un archivo de información junto al CSV',
  exportSourcesPage: 'Añadir una página de fuentes',
  exportSourcesPageHint: 'Una última página que lista cada cifra, su fuente y su hora.',
  exportSourcesPageDeck: 'Se incluye la diapositiva de Fuentes de la presentación.',
  exportSourcesPageSaved: 'Se recuerda para este artefacto, también en los envíos programados.',
  libraryKindSheetTitle: 'Tipo',
  shareAccessSheetTitle: 'Acceso',

  requestShowAll: 'Mostrar todo',
  requestShowLess: 'Mostrar menos',
  approveGettingReady: 'Preparándolo…',
  approveNotReady: 'No se puede aprobar ahora mismo. Recarga la página y vuelve a intentarlo.',
  tryWithModel: 'Probar con {model}',
  tryWithRequesting: 'Iniciando…',
  tryWithUnavailable: 'No se puede iniciar ahora mismo.',
};

const et: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Muudad versiooni {number} mustandit',
  noChangesToApply: 'Rakendatavaid muudatusi pole.',
  appliedDone: 'Rakendatud.',
  mentionInChat: 'Maini vestluses',

  kindMarkdown: 'Märkus',
  kindKpi: 'Näitaja',
  libraryExportsList: 'Eksport: {formats}',
  libraryOpensToRead: 'Avaneb lugemiseks',
  libraryReadOnly: 'Ainult lugemiseks',
  libraryLiveView: 'Reaalajas vaade',
  libraryOneNumber: 'Üks arv · seisuga {time}',
  retry: 'Proovi uuesti',
  close: 'Sulge',
  panelLoading: 'Laadimine…',
  panelEmpty: 'Siin pole veel midagi.',
  panelDenied: 'Sul pole sellele juurdepääsu.',
  panelBlocked: 'See ootab millegi järel, enne kui saab jätkuda.',
  panelBlockedViewerContext:
    'Sellel käivitusel pole isikut, kelle õigustega lugeda, seega ei saa see jätkuda enne, kui keegi valib, kelle nimel see töötab.',
  panelError: 'Seda ei õnnestunud laadida.',
  panelStale: 'See võib olla aegunud.',
  sourcesTitle: 'Allikad',
  sourcesIntro: 'Kust iga number pärineb ja kui värske see on.',
  sourcesLoading: 'Allikate laadimine…',
  sourcesEmptyTitle: 'Allikaid pole',
  sourcesEmptyBody: 'See artefakt ei kasuta ettevõtte andmeid.',
  sourcesErrorBody: 'Allikaid ei õnnestunud laadida.',
  sourcesDeniedTitle: 'Mõned allikad on peidetud',
  sourcesDeniedBody:
    'Sul pole juurdepääsu {count} siin kasutatud allikale. Palu omanikul need jagada.',
  sourceDeniedName: 'Allikas, mida sa ei näe',
  sourceAsOf: 'Seisuga {time}',
  sourceAgeUnknown: 'Andmete vanus pole teada',
  sourceStatusCurrent: 'Ajakohane',
  sourceStatusStale: 'Aegunud',
  sourceStatusGap: 'Viimasest värskendusest puudu',
  sourceStatusDenied: 'Juurdepääs puudub',
  sourceLastGood: 'Viimati korras: {time}',
  sourceUsedBy: 'Kasutajad: {labels}',
  openDocument: 'Ava dokument',
  versionsShow: 'Kuva versioonid',
  versionsHide: 'Peida versioonid',
  versionsTitle: 'Versioonid',
  versionsLoading: 'Versioonide laadimine…',
  versionsEmptyTitle: 'Versioone veel pole',
  versionsEmptyBody: 'Esimene versioon ilmub, kui see artefakt on valmis ehitatud.',
  versionsErrorBody: 'Versioone ei õnnestunud laadida.',
  versionLabel: 'Versioon {number}',
  versionOpen: 'Ava versioon {number}',
  versionCurrent: 'Praegune',
  versionPublished: 'Avaldatud',
  versionBy: 'Autor: {actor}',
  versionOriginNative: 'Ponevas',
  versionOriginMcp: 'sinu enda agendi kaudu',
  versionOriginTerminal: 'terminali kaudu',
  versionOriginJob: 'Ajastatud käivitus',
  versionCausePerson: 'Muudetud',
  versionCauseAgent: 'Agendi uuendus',
  versionCauseRefresh: 'Värskendatud',
  versionCauseRestore: 'Taastatud',
  versionCauseImport: 'Imporditud',
  versionCheckedYes: 'Kontrollitud',
  versionCheckedNo: 'Pole veel kontrollitud',
  versionCheckedInvalidated: 'Muudetud pärast viimast kontrolli',
  compareStart: 'Võrdle',
  compareWith: 'Võrdle versiooniga {number}',
  compareTitle: 'Mis muutus versioonist v{from} versiooniks v{to}',
  compareFromMark: 'Algus',
  compareToMark: 'Lõpp',
  comparePick: 'Vali võrdlemiseks teine versioon.',
  compareNone: 'Erinevusi pole.',
  compareLoading: 'Võrdlemine…',
  compareError: 'Võrdlust ei õnnestunud laadida.',
  compareBefore: 'Enne',
  compareAfter: 'Pärast',
  compareAdded: 'Lisatud',
  compareRemoved: 'Eemaldatud',
  compareChanged: 'Muudetud',
  restore: 'Taasta see versioon',
  restoreConfirmTitle: 'Kas taastada versioon {number}?',
  restoreConfirmBody: 'See loob versioonist {number} uue versiooni. Midagi ei kustutata.',
  restoreConfirm: 'Taasta',
  restoreCancel: 'Tühista',
  restoring: 'Taastamine…',
  restoreFailed: 'Versiooni ei õnnestunud taastada.',
  restoreStale:
    'See artefakt muutus, kui sa seda vaatasid. Laadi versioonid uuesti ja proovi uuesti.',
  restoreDenied: 'Sul pole õigust versioone taastada.',
  restored: 'Versioon {number} taastati uue versioonina.',
  publishTitle: 'Avalda',
  publishIntro: 'Versioon {number} on nähtav: {audience}.',
  publishOwnerLine: 'Vaatajad näevad seda, mida {owner} nägi versiooni avaldamise ajal.',
  publishChecksTitle: 'Kontrollid',
  checkPassed: 'Kontrollitud',
  checkRunning: 'Kontrollimine…',
  checkFailed: 'Kontroll ebaõnnestus',
  checkStale: 'Muudetud pärast viimast kontrolli',
  publishFixInChat: 'Paranda vestluses',
  publishAction: 'Avalda versioon {number}',
  publishing: 'Avaldamine…',
  publishedDone: 'Versioon {number} on avaldatud: {audience}.',
  publishConfirmGrant:
    'See versioon vajab uut juurdepääsu ettevõtte süsteemile. Vaata juurdepääs enne avaldamist üle.',
  publishConfirmWrite:
    'See versioon kirjutab ettevõtte süsteemi. Vaata juurdepääs enne avaldamist üle.',
  publishConfirmDestructive: 'See versioon eemaldab andmeid. Kinnita see esmalt Ponevas.',
  publishConflict: 'Enne avaldati teine versioon. Laadi uuesti, et seda näha.',
  publishBlockedNotAuthorized: 'Sul pole õigust avaldada.',
  publishBlockedCheckFailed: 'Kontroll ebaõnnestus, seega ei saa seda versiooni avaldada.',
  publishBlockedCheckStale: 'Artefakt muutus pärast viimast kontrolli. Kontrolli see uuesti.',
  publishBlockedObjects: 'Mõnda faili ei õnnestunud kontrollida. Proovi hetke pärast uuesti.',
  publishBlockedPolicy: 'Ettevõtte seaded muutusid. Vaata need üle ja proovi uuesti.',
  publishBlockedApproval: 'Kinnitus ei kehti enam. Kinnita uuesti.',
  publishBlockedDependency: 'Vajalik teenus pole saadaval. Proovi hiljem uuesti.',
  publishAsk: 'Palu avaldada: {publishers}',
  publishAskFooter: 'Avaldada saavad: {publishers}.',
  publishAskNone: 'Palu kedagi, kes saab avaldada.',
  publishWarnings: 'Tasub enne avaldamist üle vaadata',
  checkWarnStale: '{subject} on aegunud ({time}).',
  checkWarnGap: '{subject} puudus viimasest värskendusest.',
  checkWarnUnknownSource: 'Allikas, mida sa ei näe, vajab tähelepanu.',
  checkRefreshFirst: 'Värskenda esmalt',
  checkFailedRow: '{subject} ei läbinud kontrolli.',
  checkFailedGeneric: 'Kontroll ebaõnnestus.',
  publishReviewAccess: 'Vaata juurdepääs üle',
  publishRemovalBody: 'v{number} eemaldab: {what} ({count}). Taastepunkt salvestatakse enne.',
  publishReviewRemoval: 'Vaata eemaldatav üle',
  publishAndRemove: 'Avalda ja eemalda',
  publishReviewFirst: 'Vaata eemaldatav enne avaldamist üle.',
  publishDoneTitle: 'Versioon {number} on avaldatud: {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Nad leiavad selle oma Raamatukogust ja see kinnitatakse meeskonnale.',
  publishUndo: 'Võta tagasi',
  publishCopyLink: 'Kopeeri link',
  publishDone: 'Valmis',
  shareRoleViewMeaning: 'Saab avada ja lugeda',
  shareRoleEditMeaning: 'Saab muuta vestluses ja inspektoriga',
  shareRolePublishMeaning: 'Saab ka avaldada ja avaldamist tühistada',
  shareRoleMenu: 'Roll: {name}',
  shareUnpublishConfirm: 'Inimesed kaotavad juurdepääsu. Artefakt ja selle versioonid säilivad.',
  shareUnpublishCancel: 'Jäta avaldatuks',
  sendDestinationLine: '{provider} · {count} inimest',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Kõik kohas {destination} näevad kokkuvõtet. {total} inimesest {outside} ei kuulu selle artefakti vaatajaskonda, seega näitab link neile teksti „Küsi juurdepääsu”.',
  sendAudienceGeneric: 'Vaatajaskonnast väljaspool olevad näevad teksti „Küsi juurdepääsu”.',
  sendPreview: 'Mida nad näevad',
  shareTitle: 'Jaga',
  shareLoading: 'Jagamise laadimine…',
  shareErrorBody: 'Jagamist ei õnnestunud laadida.',
  sharePrivate: 'Seda artefakti näed ainult sina.',
  shareShared: 'Nähtav: {audience}.',
  shareRevoked: 'Jagamine on välja lülitatud. Keegi teine ei saa seda artefakti avada.',
  shareOwnerSnapshot:
    'Vaatajad näevad numbreid nii, nagu need avaldati. Nad ei saa värskendada ega muuta.',
  shareRoleView: 'Saab vaadata',
  shareRoleEdit: 'Saab muuta',
  shareRolePublish: 'Saab avaldada',
  shareLivePublished: 'Avaldatud: versioon {number}',
  shareUnpublish: 'Tühista avaldamine',
  shareManageDenied: 'Ainult muutja saab muuta, kes seda näeb.',
  sendTitle: 'Saada',
  sendIntro: 'Saada link ja kokkuvõte ühendatud rakenduse kaudu. Sa kinnitad iga saatmise.',
  sendLoading: 'Sihtkohtade laadimine…',
  sendErrorBody: 'Sihtkohti ei õnnestunud laadida.',
  sendNone:
    'Ükski ühendatud rakendus ei saa seda veel vastu võtta. Ühenda esmalt Slack, Teams või e-post.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-post',
  sendAudience: 'Läheb: {audience}',
  sendSummary: 'Sõnum',
  sendLeaves: 'Saadetakse link.',
  sendLeavesAttachment: 'Saadetakse link ja üle vaadatud fail.',
  sendNoAttachments: 'See sihtkoht ei võta faile vastu, seega saadetakse ainult link.',
  sendScheduled: 'Ajastatud saatmised vajavad sinu kinnitust iga kord.',
  sendAction: 'Vaata üle ja saada',
  sendFileUnsupported:
    'See sihtkoht ei võta faili vastu. Saada ainult link või vali teine sihtkoht.',
  sendLinkOnly: 'Saada ainult link',
  sendWaiting: 'Ootab sinu kinnitust. Midagi pole saadetud.',
  sendDelivered: 'Saadetud: {destination}.',
  sendFailed: 'Seda ei saadetud. Ponevast ei lahkunud midagi.',
  sendUnknown:
    'Kohale {destination} toimetamist ei õnnestunud kinnitada. Kontrolli seal enne uuesti saatmist.',
  sendBlockedNotAuthorized: 'Sul pole õigust seda saata.',
  sendBlockedUnsupported: 'Seda sihtkohta ei toetata.',
  sendBlockedApproval: 'Kinnitus ei kehti enam. Vaata üle ja saada uuesti.',
  sendBlockedChanged: 'Sõnum või saaja muutus. Vaata see uuesti üle.',
  sendBlockedDependency: 'Ühendatud rakendus pole saadaval. Proovi hiljem uuesti.',
  libraryTitle: 'Raamatukogu',
  librarySegmentAll: 'Kõik',
  librarySegmentMine: 'Minu omad',
  librarySegmentShared: 'Minuga jagatud',
  librarySearch: 'Otsi artefakte',
  libraryKindAll: 'Kõik liigid',
  libraryKindReports: 'Aruanded ja lehed',
  libraryKindDecks: 'Esitlused',
  libraryKindDocuments: 'Dokumendid',
  libraryKindSpreadsheets: 'Tabelid',
  libraryKindApps: 'Rakendused',
  libraryAppsMatchNoun:
    'one:rakendus sobib jaotises Rakendused →|other:rakendust sobivad jaotises Rakendused →',
  libraryMake: 'Alusta vestluses',
  libraryEmptySharedHint: 'Kui keegi jagab sinuga artefakti, ilmub see siia.',
  libraryRefreshFailedAt: 'Värskendamine ebaõnnestus {time}.',
  libraryRefreshTryAgain: 'Proovi uuesti',
  libraryVia: 'kaudu: {owner}',
  savedPrivate: 'Privaatne',
  savedVersion: 'Versioon {n}',
  libraryLoading: 'Sinu Raamatukogu laadimine…',
  libraryErrorBody: 'Sinu Raamatukogu ei õnnestunud praegu laadida.',
  startersLoadFailed: 'Malle ei õnnestunud praegu laadida.',
  libraryCannotOpen: '„{title}” pole siin veel avatav.',
  libraryEmptyTitle: 'Siin pole veel midagi',
  libraryEmptyAllTitle: 'Loo midagi, mida tasub jagada.',
  libraryEmptyAllBody:
    'Siin kuvatakse dokumendid, esitlused ja rakendused, mille lood vestluses või mida teised sinuga jagavad.',
  libraryEmptyMineBody: 'Siin on artefaktid, mille sa teed.',
  libraryEmptySharedBody: 'Sinuga pole veel midagi jagatud.',
  libraryNoMatchTitle: 'Vasteid pole',
  libraryNoMatchBody: 'Proovi teist sõna või teist liiki.',
  libraryMore: 'Näita rohkem',
  libraryOwner: 'Autor: {owner}',
  libraryUpdated: 'Muudetud {time}',
  libraryPublishedAs: 'Avaldatud · v{number}',
  libraryDraft: 'Mustand',
  libraryUnpublished: 'Avaldamata',
  libraryRefreshFailed: 'Värskendamine ebaõnnestus',
  libraryRefreshRunning: 'Värskendamine',
  libraryAudience: 'Kellele: {audience}',
  kindReport: 'Aruanne',
  kindPage: 'Leht',
  kindSite: 'Sait',
  kindDeck: 'Esitlus',
  kindDocument: 'Dokument',
  kindApp: 'Rakendus',
  kindSpreadsheet: 'Tabel',
  kindDashboard: 'Töölaud',
  kindTable: 'Tabel',
  kindChart: 'Diagramm',
  kindExportsReport: 'Eksporditav PDF-ina',
  kindExportsPage: 'Ainult lugemiseks leht',
  kindExportsSite: 'Ainult lugemiseks sait',
  kindExportsDeck: 'Esita või ekspordi PowerPointi ja PDF-ina',
  kindExportsDocument: 'Eksporditav Wordi ja PDF-ina',
  kindExportsSpreadsheet: 'Eksporditav Excelisse ja CSV-na',
  kindExportsDashboard: 'Reaalajavaade',
  kindExportsTable: 'Eksporditav Excelisse ja CSV-na',
  kindExportsChart: 'Diagrammivaade',
  agentPublished: '{agent} avaldas: {title} v{number}.',
  agentCheckedOn: 'Kontrollitud vaadetes {views}, {themes}.',
  agentUndo: 'Võta tagasi',
  agentRestorePrevious: 'Taasta v{number}',
  agentHeld: '{title} v{number} ootab mustandina.',
  agentHeldMismatch: '{count} numbrit ei klappinud allikatega.',
  agentHeldCheck: 'Üks kontroll ei läbinud.',
  agentFixInChat: 'Paranda vestluses',
  agentConfirmDrop:
    '{agent} soovib eemaldada {what} objektist {title}. Taastepunkt salvestatakse enne. Midagi ei muutu enne, kui sa otsustad.',
  agentConfirmDropCount:
    '{agent} soovib eemaldada {what} ({count} väärtust) objektist {title}. Taastepunkt salvestatakse enne. Midagi ei muutu enne, kui sa otsustad.',
  agentViewDesktop: 'töölaud',
  agentViewTablet: 'tahvelarvuti',
  agentViewPhone: 'telefon',
  agentThemeLight: 'hele',
  agentThemeDark: 'tume',
  agentLookAt: 'Vaata v{number}',
  agentConfirmWrite: '{agent} soovib kirjutada: {what}. Midagi ei kirjutata enne, kui sa otsustad.',
  agentConfirmGrant: '{agent} soovib juurdepääsu: {what}. Midagi ei jagata enne, kui sa otsustad.',
  agentReview: 'Vaata üle',
  agentSkipped: '{agent} jättis vahele: {part}.',
  agentSkippedWhy:
    'See loeb iga inimese enda süsteemi {system} ja sellel käivitusel pole inimest, kelle õigustega lugeda.',
  agentChooseWho: 'Vali, kelle nimel see töötab',
  agentChangeInChat: 'Muuda vestluses',
  agentFailed: '{agent} ei saanud objekti {title} värskendada.',
  agentFailedBody: 'Viimati avaldatud versioon on endiselt avaldatud.',
  agentRetry: 'Proovi uuesti',
  agentRunning: '{agent} uuendab objekti {title}.',
  agentMakesTitle: 'Teeb ja uuendab',
  agentMakesLoading: 'Laadimine…',
  agentMakesErrorBody: 'Seda ei õnnestunud laadida.',
  agentMakesNone: 'See agent ei tee veel artefakte.',
  agentMakesNew: 'Iga käivitus teeb uue artefakti',
  agentMakesUpdate: 'Iga käivitus uuendab ühte artefakti',
  agentNextRun: 'Järgmine käivitus {when}',
  agentNoSchedule: 'Pole ajastatud',
  agentCreditsPerRun: 'Umbes {credits} krediiti käivituse kohta',
  agentCreditsUnknown: 'Krediidid käivituse kohta pole veel teada',
  agentLastResult: 'Viimane tulemus',
  interviewTitle: 'Seadista',
  interviewIntro: 'Mõni küsimus kujundab tulemuse.',
  interviewOptional: '(valikuline)',
  interviewAnswerNeeded: 'Vasta sellele, et jätkata.',
  interviewReadOnly: 'Ainult lugemiseks',
  interviewChange: 'Muuda',
  interviewConnectionYours: 'sinu {system} ühendus',
  interviewConnectionOther: 'kasutaja {name} {system} ühendus',
  interviewDateAny: 'Mis tahes periood',
  interviewDatePrevious: 'Eelmine kuu',
  interviewDateNext: 'Järgmine kuu',
  interviewDatePresets: 'Kiirvalikud',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Tühjenda',
  interviewDateDone: 'Valmis',
  interviewChooseSource: 'Vali allikas',
  interviewSourceChosen: 'Allikas valitud',
  interviewSourceUnavailable: 'Allika valimine pole siin veel saadaval, seega ei saa jätkata.',
  interviewSourceUnavailableOptional:
    'Allika valimine pole siin veel saadaval. Võid jätkata ka ilma selleta.',
  interviewStarterWithdrawn:
    'See mall pole enam saadaval. Sellest juba alustatud mustandid avanevad endiselt.',
  interviewDateFrom: 'Alates',
  interviewDateTo: 'Kuni',
  interviewContinue: 'Jätka',
  interviewGrantTitle: 'Kas lubada lugeda: {source}?',
  interviewGrantBody:
    'See kasutab ühendust {connection}, ainult lugemiseks, eesmärgil: {scope}. Midagi ei loeta enne, kui sa lubad, ja näidisridu ei kuvata.',
  interviewAllow: 'Luba',
  interviewNotNow: 'Mitte praegu',
  interviewConfiguring: 'Seadistamine sinu vastuste põhjal…',
  interviewChecking: 'Kontrollimine enne avamist…',
  interviewComplete: '{title} on valmis.',
  interviewOpen: 'Ava {title}',
  interviewBlocked: 'See ei saanud jätkuda.',
  interviewFailed: 'Seadistamine ebaõnnestus. Sinu vastused säilivad.',
  interviewCancelled: 'See tühistati.',
  interviewSaving: 'Salvestamine…',
  interviewSaved: 'Salvestatud',
  interviewConflict:
    'See mustand muutus mujal. Sinu vastused säilivad siin, kuni laadid praeguse mustandi, mis need asendab.',
  interviewReloadCurrent: 'Laadi praegune mustand',
  interviewUnsettled: 'Viimast salvestust ei õnnestunud kinnitada. Sinu vastused säilivad siin.',
  interviewCheckAgain: 'Kontrolli uuesti',
  finishAction: 'Salvesta privaatselt',
  finishSaving: 'Salvestamine…',
  finishSaved: '{title} on salvestatud ja ainult sina näed seda.',
  finishNotSaved: 'Seda ei salvestatud. Sinu mustand on endiselt alles.',
  finishUnconfirmed: 'Me ei saanud kinnitada, kas see salvestati.',
  candidateInvalid: 'Mustandist ei õnnestunud dokumenti teha.',
  finishOpenInLibrary: 'Ava Raamatukogus',
  savedOpenFailed: 'Seda ei õnnestunud praegu avada.',
  savedInLibrary: 'See on sinu Raamatukogus.',
  openSavedTitle: 'Ava {title} →',
  interviewReasonNotAuthorized: 'Sul pole õigust seda mustandit muuta.',
  interviewReasonDraftNotOpen: 'See mustand pole enam avatud.',
  interviewReasonInvalid: 'Üks vastustest ei sobi.',
  interviewReasonObjects: 'Mõnda faili ei õnnestunud kontrollida. Sinu vastused säilivad.',
  interviewReasonDependency: 'Vajalik teenus pole saadaval. Sinu vastused säilivad.',
  interviewReasonReconcile: 'Viimast salvestust kinnitatakse veel.',
  exportTitle: 'Ekspordi',
  exportIntro: 'Laadi see artefakt alla. Fail ütleb, kust selle andmed pärinevad.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Muudetavad slaidid koos kõneleja märkmetega',
  exportHintPdf: 'Täpselt nii, nagu näidatud',
  exportHintDocx: 'Muudetav, säilitab pealkirjad ja tabelid',
  exportHintXlsx: 'Kõik lehed, säilitab valemid',
  exportHintCsv: 'Ainult väärtused, UTF-8',
  exportUnavailable: 'Praegu pole saadaval',
  exportRunning: 'Ettevalmistamine: {format}…',
  exportValidating: 'Faili {format} kontrollimine…',
  exportReady: 'Fail {format} on valmis',
  exportDownload: 'Laadi alla {name}',
  exportVersion: 'Eksporditud versioon {number}',
  exportFailed: 'Faili {format} ei õnnestunud teha.',
  exportFailedChecks: 'Fail {format} ei läbinud kontrolle, seega seda ei pakutud.',
  exportDenied: 'Sul pole juurdepääsu selle artefakti eksportimiseks.',
  exportRevoked: 'Jagamine lülitati välja, seega seda artefakti ei saa enam eksportida.',
  exportStale: 'See artefakt muutus ekspordi ajal. Ekspordi uuesti, et saada uusim versioon.',
  exportProvenanceTitle: 'Iga fail sisaldab',
  exportProvenanceLine: 'Tehtud Ponevaga · {title} · v{number} · {data} · tehtud {madeAt}',
  exportProvenanceAsOf: 'andmed seisuga {time}',
  exportPlacementNotesAbout: 'Märkmete lehel ja CSV kõrval olevas teabefailis',
  exportProvenanceRange: 'andmed vahemikust {earliest} kuni {latest}',
  exportProvenanceUnknown: 'andmete vanus pole teada',
  exportAboutFile: 'Selle faili teave ({format})',
  exportPlacementFooter: 'Iga lehe jaluses',
  exportPlacementSlide: 'Iga slaidi jaluses',
  exportPlacementNotes: 'Märkmete lehel',
  exportPlacementAbout: 'CSV kõrval olevas teabefailis',
  exportSourcesPage: 'Lisa allikate leht',
  exportSourcesPageHint: 'Viimane leht, mis loetleb iga numbri, selle allika ja aja.',
  exportSourcesPageDeck: 'Esitluse enda Allikate slaid tuleb kaasa.',
  exportSourcesPageSaved: 'Jäetakse selle artefakti jaoks meelde, sh ajastatud saatmiste puhul.',
  libraryKindSheetTitle: 'Liik',
  shareAccessSheetTitle: 'Juurdepääs',

  requestShowAll: 'Kuva kõik',
  requestShowLess: 'Kuva vähem',
  approveGettingReady: 'Valmistame ette…',
  approveNotReady: 'Seda ei saa praegu kinnitada. Laadi leht uuesti ja proovi siis uuesti.',
  tryWithModel: 'Proovi mudeliga {model}',
  tryWithRequesting: 'Käivitamine…',
  tryWithUnavailable: 'Seda ei saa praegu käivitada.',
};

const fi: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Muokkaat version {number} luonnosta',
  noChangesToApply: 'Ei käyttöön otettavia muutoksia.',
  appliedDone: 'Otettu käyttöön.',
  mentionInChat: 'Mainitse keskustelussa',

  kindMarkdown: 'Muistiinpano',
  kindKpi: 'Mittari',
  libraryExportsList: 'Vienti: {formats}',
  libraryOpensToRead: 'Avautuu luettavaksi',
  libraryReadOnly: 'Vain luku',
  libraryLiveView: 'Reaaliaikainen näkymä',
  libraryOneNumber: 'Yksi luku · {time}',
  retry: 'Yritä uudelleen',
  close: 'Sulje',
  panelLoading: 'Ladataan…',
  panelEmpty: 'Täällä ei ole vielä mitään.',
  panelDenied: 'Sinulla ei ole pääsyä tähän.',
  panelBlocked: 'Tämä odottaa jotakin, ennen kuin se voi jatkua.',
  panelBlockedViewerContext:
    'Tällä ajolla ei ole henkilöä, jonka oikeuksilla lukea, joten se ei voi jatkua, ennen kuin joku valitsee, kenenä se suoritetaan.',
  panelError: 'Tätä ei voitu ladata.',
  panelStale: 'Tämä voi olla vanhentunut.',
  sourcesTitle: 'Lähteet',
  sourcesIntro: 'Mistä kukin luku tulee ja kuinka tuore se on.',
  sourcesLoading: 'Ladataan lähteitä…',
  sourcesEmptyTitle: 'Ei lähteitä',
  sourcesEmptyBody: 'Tämä artefakti ei käytä yrityksen tietoja.',
  sourcesErrorBody: 'Lähteitä ei voitu ladata.',
  sourcesDeniedTitle: 'Osa lähteistä on piilotettu',
  sourcesDeniedBody:
    'Sinulla ei ole pääsyä {count} tässä käytettyyn lähteeseen. Pyydä omistajaa jakamaan ne.',
  sourceDeniedName: 'Lähde, jota et voi nähdä',
  sourceAsOf: 'Tilanne: {time}',
  sourceAgeUnknown: 'Tietojen ikä ei ole tiedossa',
  sourceStatusCurrent: 'Ajan tasalla',
  sourceStatusStale: 'Vanhentunut',
  sourceStatusGap: 'Puuttui viimeisimmästä päivityksestä',
  sourceStatusDenied: 'Ei pääsyä',
  sourceLastGood: 'Viimeksi toimiva: {time}',
  sourceUsedBy: 'Käyttäjä: {labels}',
  openDocument: 'Avaa asiakirja',
  versionsShow: 'Näytä versiot',
  versionsHide: 'Piilota versiot',
  versionsTitle: 'Versiot',
  versionsLoading: 'Ladataan versioita…',
  versionsEmptyTitle: 'Ei vielä versioita',
  versionsEmptyBody: 'Ensimmäinen versio ilmestyy, kun tämä artefakti on rakennettu.',
  versionsErrorBody: 'Versioita ei voitu ladata.',
  versionLabel: 'Versio {number}',
  versionOpen: 'Avaa versio {number}',
  versionCurrent: 'Nykyinen',
  versionPublished: 'Julkaistu',
  versionBy: 'Tekijä: {actor}',
  versionOriginNative: 'Ponevassa',
  versionOriginMcp: 'oman agenttisi kautta',
  versionOriginTerminal: 'päätteen kautta',
  versionOriginJob: 'Ajastettu ajo',
  versionCausePerson: 'Muokattu',
  versionCauseAgent: 'Agentin päivitys',
  versionCauseRefresh: 'Päivitetty',
  versionCauseRestore: 'Palautettu',
  versionCauseImport: 'Tuotu',
  versionCheckedYes: 'Tarkistettu',
  versionCheckedNo: 'Ei vielä tarkistettu',
  versionCheckedInvalidated: 'Muokattu viimeisen tarkistuksen jälkeen',
  compareStart: 'Vertaa',
  compareWith: 'Vertaa versioon {number}',
  compareTitle: 'Mikä muuttui versiosta v{from} versioon v{to}',
  compareFromMark: 'Mistä',
  compareToMark: 'Mihin',
  comparePick: 'Valitse toinen vertailtava versio.',
  compareNone: 'Ei eroja.',
  compareLoading: 'Verrataan…',
  compareError: 'Vertailua ei voitu ladata.',
  compareBefore: 'Ennen',
  compareAfter: 'Jälkeen',
  compareAdded: 'Lisätty',
  compareRemoved: 'Poistettu',
  compareChanged: 'Muutettu',
  restore: 'Palauta tämä versio',
  restoreConfirmTitle: 'Palautetaanko versio {number}?',
  restoreConfirmBody: 'Tästä syntyy uusi versio versiosta {number}. Mitään ei poisteta.',
  restoreConfirm: 'Palauta',
  restoreCancel: 'Peruuta',
  restoring: 'Palautetaan…',
  restoreFailed: 'Versiota ei voitu palauttaa.',
  restoreStale:
    'Tämä artefakti muuttui, kun katselit sitä. Lataa versiot uudelleen ja yritä uudelleen.',
  restoreDenied: 'Sinulla ei ole oikeutta palauttaa versioita.',
  restored: 'Versio {number} palautettiin uutena versiona.',
  publishTitle: 'Julkaise',
  publishIntro: 'Versio {number} näkyy kohderyhmälle {audience}.',
  publishOwnerLine:
    'Katsojat näkevät sen, minkä {owner} olisi nähnyt, kun tämä versio julkaistiin.',
  publishChecksTitle: 'Tarkistukset',
  checkPassed: 'Tarkistettu',
  checkRunning: 'Tarkistetaan…',
  checkFailed: 'Tarkistus epäonnistui',
  checkStale: 'Muokattu viimeisen tarkistuksen jälkeen',
  publishFixInChat: 'Korjaa keskustelussa',
  publishAction: 'Julkaise versio {number}',
  publishing: 'Julkaistaan…',
  publishedDone: 'Versio {number} on käytössä: {audience}.',
  publishConfirmGrant:
    'Tämä versio tarvitsee uuden käyttöoikeuden yrityksen järjestelmään. Tarkista oikeudet ennen julkaisua.',
  publishConfirmWrite:
    'Tämä versio kirjoittaa yrityksen järjestelmään. Tarkista oikeudet ennen julkaisua.',
  publishConfirmDestructive: 'Tämä versio poistaa tietoja. Vahvista se ensin Ponevassa.',
  publishConflict: 'Toinen versio julkaistiin ensin. Lataa uudelleen nähdäksesi sen.',
  publishBlockedNotAuthorized: 'Sinulla ei ole oikeutta julkaista.',
  publishBlockedCheckFailed: 'Tarkistus epäonnistui, joten tätä versiota ei voi julkaista.',
  publishBlockedCheckStale:
    'Artefakti muuttui viimeisen tarkistuksen jälkeen. Tarkista se uudelleen.',
  publishBlockedObjects: 'Joitakin tiedostoja ei voitu vahvistaa. Yritä hetken kuluttua uudelleen.',
  publishBlockedPolicy: 'Yrityksen asetukset muuttuivat. Tarkista ne ja yritä uudelleen.',
  publishBlockedApproval: 'Vahvistus ei ole enää voimassa. Vahvista uudelleen.',
  publishBlockedDependency: 'Tarvittava palvelu ei ole käytettävissä. Yritä myöhemmin uudelleen.',
  publishAsk: 'Pyydä julkaisua: {publishers}',
  publishAskFooter: 'Julkaisuoikeus on näillä henkilöillä: {publishers}.',
  publishAskNone: 'Pyydä jotakuta, jolla on julkaisuoikeus.',
  publishWarnings: 'Kannattaa katsoa ennen julkaisua',
  checkWarnStale: '{subject} on vanhentunut ({time}).',
  checkWarnGap: '{subject} puuttui viimeisimmästä päivityksestä.',
  checkWarnUnknownSource: 'Lähde, jota et voi nähdä, vaatii huomiota.',
  checkRefreshFirst: 'Päivitä ensin',
  checkFailedRow: '{subject} ei läpäissyt tarkistusta.',
  checkFailedGeneric: 'Tarkistus epäonnistui.',
  publishReviewAccess: 'Tarkista oikeudet',
  publishRemovalBody:
    'v{number} poistaa kohteen {what} ({count}). Palautuspiste tallennetaan ensin.',
  publishReviewRemoval: 'Tarkista poistettavat',
  publishAndRemove: 'Julkaise ja poista',
  publishReviewFirst: 'Tarkista poistettavat ennen julkaisua.',
  publishDoneTitle: 'Versio {number} on käytössä: {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'He löytävät sen Kirjastostaan, ja se kiinnitetään tiimille.',
  publishUndo: 'Kumoa',
  publishCopyLink: 'Kopioi linkki',
  publishDone: 'Valmis',
  shareRoleViewMeaning: 'Voi avata ja lukea',
  shareRoleEditMeaning: 'Voi muokata keskustelussa ja tarkastimella',
  shareRolePublishMeaning: 'Voi myös julkaista ja poistaa julkaisun',
  shareRoleMenu: 'Rooli: {name}',
  shareUnpublishConfirm: 'Ihmiset menettävät pääsyn. Artefakti ja sen versiot säilyvät.',
  shareUnpublishCancel: 'Pidä käytössä',
  sendDestinationLine: '{provider} · {count} henkilöä',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Kaikki kohteessa {destination} näkevät yhteenvedon. Kaikista {total} henkilöstä {outside} ei kuulu tämän artefaktin yleisöön, joten linkki näyttää heille tekstin ”Pyydä pääsyä”.',
  sendAudienceGeneric: 'Yleisön ulkopuoliset näkevät tekstin ”Pyydä pääsyä”.',
  sendPreview: 'Mitä he näkevät',
  shareTitle: 'Jaa',
  shareLoading: 'Ladataan jakoa…',
  shareErrorBody: 'Jakoa ei voitu ladata.',
  sharePrivate: 'Vain sinä näet tämän artefaktin.',
  shareShared: 'Näkyy: {audience}.',
  shareRevoked: 'Jako on poistettu käytöstä. Kukaan muu ei voi avata tätä artefaktia.',
  shareOwnerSnapshot:
    'Katsojat näkevät luvut sellaisina kuin ne julkaistiin. He eivät voi päivittää tai muokata.',
  shareRoleView: 'Voi katsella',
  shareRoleEdit: 'Voi muokata',
  shareRolePublish: 'Voi julkaista',
  shareLivePublished: 'Käytössä: versio {number}',
  shareUnpublish: 'Poista julkaisu',
  shareManageDenied: 'Vain muokkaaja voi muuttaa, ketkä tämän näkevät.',
  sendTitle: 'Lähetä',
  sendIntro:
    'Lähetä linkki ja yhteenveto yhdistetyn sovelluksen kautta. Hyväksyt jokaisen lähetyksen.',
  sendLoading: 'Ladataan kohteita…',
  sendErrorBody: 'Kohteita ei voitu ladata.',
  sendNone:
    'Mikään yhdistetty sovellus ei voi vielä vastaanottaa tätä. Yhdistä ensin Slack, Teams tai sähköposti.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'Sähköposti',
  sendAudience: 'Vastaanottajat: {audience}',
  sendSummary: 'Viesti',
  sendLeaves: 'Lähetetään linkki.',
  sendLeavesAttachment: 'Lähetetään linkki ja tarkistettu tiedosto.',
  sendNoAttachments: 'Tämä kohde ei voi vastaanottaa tiedostoja, joten vain linkki lähetetään.',
  sendScheduled: 'Ajastetut lähetykset tarvitsevat hyväksyntäsi joka kerta.',
  sendAction: 'Tarkista ja lähetä',
  sendFileUnsupported:
    'Tämä kohde ei voi vastaanottaa tiedostoa. Lähetä vain linkki tai valitse toinen kohde.',
  sendLinkOnly: 'Lähetä vain linkki',
  sendWaiting: 'Odottaa hyväksyntääsi. Mitään ei ole lähetetty.',
  sendDelivered: 'Lähetetty kohteeseen {destination}.',
  sendFailed: 'Sitä ei lähetetty. Mikään ei poistunut Ponevasta.',
  sendUnknown:
    'Toimitusta kohteeseen {destination} ei voitu vahvistaa. Tarkista sieltä, ennen kuin lähetät uudelleen.',
  sendBlockedNotAuthorized: 'Sinulla ei ole oikeutta lähettää tätä.',
  sendBlockedUnsupported: 'Tätä kohdetta ei tueta.',
  sendBlockedApproval: 'Hyväksyntä ei ole enää voimassa. Tarkista ja lähetä uudelleen.',
  sendBlockedChanged: 'Viesti tai vastaanottaja muuttui. Tarkista se uudelleen.',
  sendBlockedDependency: 'Yhdistetty sovellus ei ole käytettävissä. Yritä myöhemmin uudelleen.',
  libraryTitle: 'Kirjasto',
  librarySegmentAll: 'Kaikki',
  librarySegmentMine: 'Omat',
  librarySegmentShared: 'Jaetut minulle',
  librarySearch: 'Hae artefakteja',
  libraryKindAll: 'Kaikki lajit',
  libraryKindReports: 'Raportit ja sivut',
  libraryKindDecks: 'Esitykset',
  libraryKindDocuments: 'Asiakirjat',
  libraryKindSpreadsheets: 'Taulukkolaskennat',
  libraryKindApps: 'Sovellukset',
  libraryAppsMatchNoun:
    'one:sovellus vastaa hakua kohdassa Sovellukset →|other:sovellusta vastaa hakua kohdassa Sovellukset →',
  libraryMake: 'Aloita chatissa',
  libraryEmptySharedHint: 'Kun joku jakaa artefaktin kanssasi, se näkyy tässä.',
  libraryRefreshFailedAt: 'Päivitys epäonnistui {time}.',
  libraryRefreshTryAgain: 'Yritä uudelleen',
  libraryVia: 'kautta: {owner}',
  savedPrivate: 'Yksityinen',
  savedVersion: 'Versio {n}',
  libraryLoading: 'Ladataan Kirjastoasi…',
  libraryErrorBody: 'Kirjastoasi ei voitu ladata juuri nyt.',
  startersLoadFailed: 'Malleja ei voitu ladata juuri nyt.',
  libraryCannotOpen: 'Kohdetta ”{title}” ei voi vielä avata täällä.',
  libraryEmptyTitle: 'Täällä ei ole vielä mitään',
  libraryEmptyAllTitle: 'Tee jotain, joka kannattaa jakaa.',
  libraryEmptyAllBody:
    'Chatissa tekemäsi tai muiden kanssasi jakamat asiakirjat, esitykset ja sovellukset näkyvät tässä.',
  libraryEmptyMineBody: 'Tekemäsi artefaktit näkyvät tässä.',
  libraryEmptySharedBody: 'Sinulle ei ole vielä jaettu mitään.',
  libraryNoMatchTitle: 'Ei osumia',
  libraryNoMatchBody: 'Kokeile toista sanaa tai eri lajia.',
  libraryMore: 'Näytä lisää',
  libraryOwner: 'Tekijä: {owner}',
  libraryUpdated: 'Muokattu {time}',
  libraryPublishedAs: 'Julkaistu · v{number}',
  libraryDraft: 'Luonnos',
  libraryUnpublished: 'Julkaisematon',
  libraryRefreshFailed: 'Päivitys epäonnistui',
  libraryRefreshRunning: 'Päivitetään',
  libraryAudience: 'Kohderyhmä: {audience}',
  kindReport: 'Raportti',
  kindPage: 'Sivu',
  kindSite: 'Sivusto',
  kindDeck: 'Esitys',
  kindDocument: 'Asiakirja',
  kindApp: 'Sovellus',
  kindSpreadsheet: 'Taulukkolaskenta',
  kindDashboard: 'Koontinäkymä',
  kindTable: 'Taulukko',
  kindChart: 'Kaavio',
  kindExportsReport: 'Vie PDF-muotoon',
  kindExportsPage: 'Vain luku -sivu',
  kindExportsSite: 'Vain luku -sivusto',
  kindExportsDeck: 'Esitä tai vie PowerPointiin ja PDF-muotoon',
  kindExportsDocument: 'Vie Wordiin ja PDF-muotoon',
  kindExportsSpreadsheet: 'Vie Exceliin ja CSV-muotoon',
  kindExportsDashboard: 'Live-näkymä',
  kindExportsTable: 'Vie Exceliin ja CSV-muotoon',
  kindExportsChart: 'Kaavionäkymä',
  agentPublished: '{agent} julkaisi kohteen {title} v{number}.',
  agentCheckedOn: 'Tarkistettu näkymissä {views}, {themes}.',
  agentUndo: 'Kumoa',
  agentRestorePrevious: 'Palauta v{number}',
  agentHeld: '{title} v{number} odottaa luonnoksena.',
  agentHeldMismatch: '{count} lukua ei vastannut lähteitään.',
  agentHeldCheck: 'Tarkistus ei mennyt läpi.',
  agentFixInChat: 'Korjaa keskustelussa',
  agentConfirmDrop:
    '{agent} haluaa poistaa kohteen {what} kohteesta {title}. Palautuspiste tallennetaan ensin. Mikään ei muutu, ennen kuin päätät.',
  agentConfirmDropCount:
    '{agent} haluaa poistaa kohteen {what} ({count} arvoa) kohteesta {title}. Palautuspiste tallennetaan ensin. Mikään ei muutu, ennen kuin päätät.',
  agentViewDesktop: 'työpöytä',
  agentViewTablet: 'tabletti',
  agentViewPhone: 'puhelin',
  agentThemeLight: 'vaalea',
  agentThemeDark: 'tumma',
  agentLookAt: 'Katso v{number}',
  agentConfirmWrite:
    '{agent} haluaa kirjoittaa kohteen {what}. Mitään ei kirjoiteta, ennen kuin päätät.',
  agentConfirmGrant:
    '{agent} haluaa käyttöoikeuden kohteeseen {what}. Mitään ei jaeta, ennen kuin päätät.',
  agentReview: 'Tarkista',
  agentSkipped: '{agent} ohitti kohdan {part}.',
  agentSkippedWhy:
    'Se lukee kunkin henkilön oman järjestelmän {system}, eikä tällä ajolla ole henkilöä, jonka oikeuksilla lukea.',
  agentChooseWho: 'Valitse, kenenä se suoritetaan',
  agentChangeInChat: 'Muuta keskustelussa',
  agentFailed: '{agent} ei voinut päivittää kohdetta {title}.',
  agentFailedBody: 'Viimeksi julkaistu versio on edelleen käytössä.',
  agentRetry: 'Yritä uudelleen',
  agentRunning: '{agent} päivittää kohdetta {title}.',
  agentMakesTitle: 'Tekee ja päivittää',
  agentMakesLoading: 'Ladataan…',
  agentMakesErrorBody: 'Tätä ei voitu ladata.',
  agentMakesNone: 'Tämä agentti ei vielä tee artefakteja.',
  agentMakesNew: 'Uusi artefakti joka ajolla',
  agentMakesUpdate: 'Päivittää yhden artefaktin joka ajolla',
  agentNextRun: 'Seuraava ajo {when}',
  agentNoSchedule: 'Ei ajastettu',
  agentCreditsPerRun: 'Ajokohtaiset krediitit: noin {credits}',
  agentCreditsUnknown: 'Ajokohtaisia krediittejä ei vielä tiedetä',
  agentLastResult: 'Viimeisin tulos',
  interviewTitle: 'Ota käyttöön',
  interviewIntro: 'Muutama kysymys muokkaa lopputulosta.',
  interviewOptional: '(valinnainen)',
  interviewAnswerNeeded: 'Vastaa tähän jatkaaksesi.',
  interviewReadOnly: 'Vain luku',
  interviewChange: 'Muuta',
  interviewConnectionYours: 'oma {system}-yhteytesi',
  interviewConnectionOther: 'käyttäjän {name} {system}-yhteys',
  interviewDateAny: 'Mikä tahansa ajanjakso',
  interviewDatePrevious: 'Edellinen kuukausi',
  interviewDateNext: 'Seuraava kuukausi',
  interviewDatePresets: 'Pikavalinnat',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Tyhjennä',
  interviewDateDone: 'Valmis',
  interviewChooseSource: 'Valitse lähde',
  interviewSourceChosen: 'Lähde valittu',
  interviewSourceUnavailable:
    'Lähteen valinta ei ole vielä käytettävissä tässä, joten jatkaminen ei onnistu.',
  interviewSourceUnavailableOptional:
    'Lähteen valitseminen ei ole vielä käytettävissä täällä. Voit jatkaa ilman lähdettä.',
  interviewStarterWithdrawn:
    'Tämä malli ei ole enää käytettävissä. Siitä jo aloittamasi luonnokset avautuvat edelleen.',
  interviewDateFrom: 'Alkaen',
  interviewDateTo: 'Asti',
  interviewContinue: 'Jatka',
  interviewGrantTitle: 'Sallitaanko lukeminen: {source}?',
  interviewGrantBody:
    'Se käyttää yhteyttä {connection}, vain luku, kohteelle {scope}. Mitään ei lueta, ennen kuin sallit sen, eikä esimerkkirivejä näytetä.',
  interviewAllow: 'Salli',
  interviewNotNow: 'Ei nyt',
  interviewConfiguring: 'Otetaan käyttöön vastaustesi perusteella…',
  interviewChecking: 'Tarkistetaan ennen avaamista…',
  interviewComplete: '{title} on valmis.',
  interviewOpen: 'Avaa {title}',
  interviewBlocked: 'Tämä ei voinut jatkua.',
  interviewFailed: 'Käyttöönotto epäonnistui. Vastauksesi säilyvät.',
  interviewCancelled: 'Tämä peruttiin.',
  interviewSaving: 'Tallennetaan…',
  interviewSaved: 'Tallennettu',
  interviewConflict:
    'Tämä luonnos muuttui jossain muualla. Vastauksesi säilyvät tässä, kunnes lataat nykyisen luonnoksen, joka korvaa ne.',
  interviewReloadCurrent: 'Lataa nykyinen luonnos',
  interviewUnsettled: 'Viimeisintä tallennusta ei voitu vahvistaa. Vastauksesi säilyvät tässä.',
  interviewCheckAgain: 'Tarkista uudelleen',
  finishAction: 'Tallenna yksityisesti',
  finishSaving: 'Tallennetaan…',
  finishSaved: '{title} on tallennettu, ja vain sinä näet sen.',
  finishNotSaved: 'Sitä ei tallennettu. Luonnoksesi on yhä tallessa.',
  finishUnconfirmed: 'Emme voineet vahvistaa, tallennettiinko se.',
  candidateInvalid: 'Luonnoksesta ei voitu tehdä dokumenttia.',
  finishOpenInLibrary: 'Avaa Kirjastossa',
  savedOpenFailed: 'Avaaminen ei onnistunut juuri nyt.',
  savedInLibrary: 'Se on Kirjastossasi.',
  openSavedTitle: 'Avaa {title} →',
  interviewReasonNotAuthorized: 'Sinulla ei ole oikeutta muuttaa tätä luonnosta.',
  interviewReasonDraftNotOpen: 'Tämä luonnos ei ole enää auki.',
  interviewReasonInvalid: 'Yksi vastauksista ei kelpaa.',
  interviewReasonObjects: 'Joitakin tiedostoja ei voitu vahvistaa. Vastauksesi säilyvät.',
  interviewReasonDependency: 'Tarvittava palvelu ei ole käytettävissä. Vastauksesi säilyvät.',
  interviewReasonReconcile: 'Viimeistä tallennusta vahvistetaan vielä.',
  exportTitle: 'Vie',
  exportIntro: 'Lataa tämä artefakti. Tiedostossa kerrotaan, mistä sen tiedot tulevat.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Muokattavat diat puhujan muistiinpanoineen',
  exportHintPdf: 'Täsmälleen sellaisena kuin se näkyy',
  exportHintDocx: 'Muokattava, säilyttää otsikot ja taulukot',
  exportHintXlsx: 'Kaikki taulukot, säilyttää kaavat',
  exportHintCsv: 'Vain arvot, UTF-8',
  exportUnavailable: 'Ei käytettävissä juuri nyt',
  exportRunning: 'Valmistellaan: {format}…',
  exportValidating: 'Tarkistetaan tiedosto: {format}…',
  exportReady: 'Tiedosto {format} on valmis',
  exportDownload: 'Lataa {name}',
  exportVersion: 'Viety versio {number}',
  exportFailed: 'Tiedostoa {format} ei voitu tehdä.',
  exportFailedChecks: 'Tiedosto {format} ei läpäissyt tarkistuksia, joten sitä ei tarjottu.',
  exportDenied: 'Sinulla ei ole pääsyä tämän artefaktin vientiin.',
  exportRevoked: 'Jako poistettiin käytöstä, joten tätä artefaktia ei voi enää viedä.',
  exportStale: 'Tämä artefakti muuttui viennin aikana. Vie uudelleen saadaksesi uusimman version.',
  exportProvenanceTitle: 'Jokaisessa tiedostossa on',
  exportProvenanceLine: 'Tehty Poneva-palvelulla · {title} · v{number} · {data} · tehty {madeAt}',
  exportProvenanceAsOf: 'tiedot tilanteesta {time}',
  exportPlacementNotesAbout:
    'Muistiinpanot-taulukossa ja CSV-tiedoston vieressä olevassa tietotiedostossa',
  exportProvenanceRange: 'tiedot väliltä {earliest}–{latest}',
  exportProvenanceUnknown: 'tietojen ikä ei ole tiedossa',
  exportAboutFile: 'Tietoja tästä tiedostosta ({format})',
  exportPlacementFooter: 'Jokaisen sivun alatunnisteessa',
  exportPlacementSlide: 'Jokaisen dian alatunnisteessa',
  exportPlacementNotes: 'Muistiinpanot-taulukossa',
  exportPlacementAbout: 'CSV-tiedoston vieressä olevassa tietotiedostossa',
  exportSourcesPage: 'Lisää lähdesivu',
  exportSourcesPageHint: 'Viimeinen sivu, jossa luetellaan jokainen luku, sen lähde ja ajankohta.',
  exportSourcesPageDeck: 'Esityksen oma Lähteet-dia tulee mukaan.',
  exportSourcesPageSaved: 'Muistetaan tälle artefaktille, myös ajastetuissa lähetyksissä.',
  libraryKindSheetTitle: 'Laji',
  shareAccessSheetTitle: 'Käyttöoikeus',

  requestShowAll: 'Näytä kaikki',
  requestShowLess: 'Näytä vähemmän',
  approveGettingReady: 'Valmistellaan…',
  approveNotReady:
    'Tätä ei voi hyväksyä juuri nyt. Lataa sivu uudelleen ja yritä sitten uudelleen.',
  tryWithModel: 'Kokeile mallilla {model}',
  tryWithRequesting: 'Käynnistetään…',
  tryWithUnavailable: 'Tätä ei voi käynnistää juuri nyt.',
};

const fr: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Vous modifiez un brouillon de la version {number}',
  noChangesToApply: 'Aucune modification à appliquer.',
  appliedDone: 'Appliqué.',
  mentionInChat: 'Mentionner dans le chat',

  kindMarkdown: 'Note',
  kindKpi: 'Indicateur',
  libraryExportsList: 'Export en {formats}',
  libraryOpensToRead: 'S’ouvre en lecture',
  libraryReadOnly: 'Lecture seule',
  libraryLiveView: 'Vue en direct',
  libraryOneNumber: 'Un chiffre · au {time}',
  retry: 'Réessayer',
  close: 'Fermer',
  panelLoading: 'Chargement…',
  panelEmpty: 'Rien ici pour le moment.',
  panelDenied: 'Vous n’avez pas accès à cet élément.',
  panelBlocked: 'Cet élément attend quelque chose avant de pouvoir continuer.',
  panelBlockedViewerContext:
    'Cette exécution n’a pas de personne au nom de qui lire. Elle ne peut pas continuer tant que quelqu’un n’a pas choisi en quel nom elle s’exécute.',
  panelError: 'Impossible de charger cet élément.',
  panelStale: 'Cet élément est peut-être obsolète.',
  sourcesTitle: 'Sources',
  sourcesIntro: 'D’où vient chaque chiffre, et son degré d’actualité.',
  sourcesLoading: 'Chargement des sources…',
  sourcesEmptyTitle: 'Aucune source',
  sourcesEmptyBody: 'Cet artefact n’utilise pas de données de l’entreprise.',
  sourcesErrorBody: 'Impossible de charger les sources.',
  sourcesDeniedTitle: 'Certaines sources sont masquées',
  sourcesDeniedBody:
    'Vous n’avez pas accès à {count} sources utilisées ici. Demandez à un propriétaire de les partager.',
  sourceDeniedName: 'Source que vous ne pouvez pas voir',
  sourceAsOf: 'Au {time}',
  sourceAgeUnknown: 'Ancienneté des données inconnue',
  sourceStatusCurrent: 'À jour',
  sourceStatusStale: 'Obsolète',
  sourceStatusGap: 'Absente de la dernière actualisation',
  sourceStatusDenied: 'Aucun accès',
  sourceLastGood: 'Dernière version valide : {time}',
  sourceUsedBy: 'Utilisée par {labels}',
  openDocument: 'Ouvrir le document',
  versionsShow: 'Afficher les versions',
  versionsHide: 'Masquer les versions',
  versionsTitle: 'Versions',
  versionsLoading: 'Chargement des versions…',
  versionsEmptyTitle: 'Aucune version pour le moment',
  versionsEmptyBody: 'La première version apparaît lorsque cet artefact est créé.',
  versionsErrorBody: 'Impossible de charger les versions.',
  versionLabel: 'Version {number}',
  versionOpen: 'Ouvrir la version {number}',
  versionCurrent: 'Actuelle',
  versionPublished: 'Publiée',
  versionBy: 'Par {actor}',
  versionOriginNative: 'Dans Poneva',
  versionOriginMcp: 'via votre propre agent',
  versionOriginTerminal: 'via le terminal',
  versionOriginJob: 'Exécution planifiée',
  versionCausePerson: 'Modifiée',
  versionCauseAgent: 'Mise à jour par l’agent',
  versionCauseRefresh: 'Actualisée',
  versionCauseRestore: 'Restaurée',
  versionCauseImport: 'Importée',
  versionCheckedYes: 'Vérifiée',
  versionCheckedNo: 'Pas encore vérifiée',
  versionCheckedInvalidated: 'Modifiée depuis la dernière vérification',
  compareStart: 'Comparer',
  compareWith: 'Comparer avec la version {number}',
  compareTitle: 'Ce qui a changé entre v{from} et v{to}',
  compareFromMark: 'De',
  compareToMark: 'À',
  comparePick: 'Choisissez une deuxième version à comparer.',
  compareNone: 'Aucune différence.',
  compareLoading: 'Comparaison en cours…',
  compareError: 'Impossible de charger la comparaison.',
  compareBefore: 'Avant',
  compareAfter: 'Après',
  compareAdded: 'Ajouté',
  compareRemoved: 'Supprimé',
  compareChanged: 'Modifié',
  restore: 'Restaurer cette version',
  restoreConfirmTitle: 'Restaurer la version {number} ?',
  restoreConfirmBody:
    'Cela crée une nouvelle version à partir de la version {number}. Rien n’est supprimé.',
  restoreConfirm: 'Restaurer',
  restoreCancel: 'Annuler',
  restoring: 'Restauration…',
  restoreFailed: 'Impossible de restaurer la version.',
  restoreStale:
    'Cet artefact a changé pendant que vous le consultiez. Rechargez les versions et réessayez.',
  restoreDenied: 'Vous n’avez pas l’autorisation de restaurer des versions.',
  restored: 'La version {number} a été restaurée comme nouvelle version.',
  publishTitle: 'Publier',
  publishIntro: 'La version {number} sera visible par {audience}.',
  publishOwnerLine:
    'Les lecteurs voient ce que {owner} pouvait voir lors de la publication de cette version.',
  publishChecksTitle: 'Vérifications',
  checkPassed: 'Vérifiée',
  checkRunning: 'Vérification en cours…',
  checkFailed: 'Une vérification a échoué',
  checkStale: 'Modifiée depuis la dernière vérification',
  publishFixInChat: 'Corriger dans le chat',
  publishAction: 'Publier la version {number}',
  publishing: 'Publication…',
  publishedDone: 'La version {number} est en ligne pour {audience}.',
  publishConfirmGrant:
    'Cette version a besoin d’un nouvel accès à un système de l’entreprise. Vérifiez l’accès avant de publier.',
  publishConfirmWrite:
    'Cette version écrit dans un système de l’entreprise. Vérifiez l’accès avant de publier.',
  publishConfirmDestructive:
    'Cette version supprime des données. Confirmez-le d’abord dans Poneva.',
  publishConflict: 'Une autre version a été publiée avant. Rechargez pour la voir.',
  publishBlockedNotAuthorized: 'Vous n’avez pas l’autorisation de publier.',
  publishBlockedCheckFailed:
    'Une vérification a échoué, cette version ne peut donc pas être publiée.',
  publishBlockedCheckStale:
    'L’artefact a changé après sa dernière vérification. Vérifiez-le à nouveau.',
  publishBlockedObjects: 'Certains fichiers n’ont pas pu être vérifiés. Réessayez dans un instant.',
  publishBlockedPolicy: 'Les paramètres de l’entreprise ont changé. Vérifiez-les et réessayez.',
  publishBlockedApproval: 'La confirmation n’est plus valide. Confirmez à nouveau.',
  publishBlockedDependency: 'Un service nécessaire est indisponible. Réessayez plus tard.',
  publishAsk: 'Demander à {publishers} de publier',
  publishAskFooter: 'Personnes pouvant publier : {publishers}.',
  publishAskNone: 'Demandez à quelqu’un qui peut publier.',
  publishWarnings: 'À regarder avant de publier',
  checkWarnStale: '{subject} est obsolète ({time}).',
  checkWarnGap: '{subject} était absent de la dernière actualisation.',
  checkWarnUnknownSource: 'Une source que vous ne pouvez pas voir demande votre attention.',
  checkRefreshFirst: 'Actualiser d’abord',
  checkFailedRow: '{subject} a échoué à sa vérification.',
  checkFailedGeneric: 'Une vérification a échoué.',
  publishReviewAccess: 'Vérifier l’accès',
  publishRemovalBody:
    'v{number} supprime {what} ({count}). Un point de restauration est d’abord enregistré.',
  publishReviewRemoval: 'Vérifier ce qui est supprimé',
  publishAndRemove: 'Publier et supprimer',
  publishReviewFirst: 'Vérifiez ce qui est supprimé avant de publier.',
  publishDoneTitle: 'La version {number} est en ligne pour {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Ces personnes la trouveront dans leur bibliothèque et épinglée pour l’équipe.',
  publishUndo: 'Annuler',
  publishCopyLink: 'Copier le lien',
  publishDone: 'Terminé',
  shareRoleViewMeaning: 'Peut l’ouvrir et le lire',
  shareRoleEditMeaning: 'Peut le modifier dans le chat et avec l’inspecteur',
  shareRolePublishMeaning: 'Peut aussi publier et dépublier',
  shareRoleMenu: 'Rôle pour {name}',
  shareUnpublishConfirm:
    'Ces personnes perdent l’accès. L’artefact et ses versions sont conservés.',
  shareUnpublishCancel: 'Laisser en ligne',
  sendDestinationLine: '{provider} · {count} personnes',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Tout le monde dans {destination} voit le résumé. {outside} personnes sur {total} ne font pas partie de l’audience de cet artefact, le lien leur affiche donc « Demander l’accès ».',
  sendAudienceGeneric: 'Les personnes hors de l’audience voient « Demander l’accès ».',
  sendPreview: 'Ce qu’elles verront',
  shareTitle: 'Partager',
  shareLoading: 'Chargement du partage…',
  shareErrorBody: 'Impossible de charger le partage.',
  sharePrivate: 'Vous seul pouvez voir cet artefact.',
  shareShared: 'Visible par {audience}.',
  shareRevoked: 'Le partage est désactivé. Personne d’autre ne peut ouvrir cet artefact.',
  shareOwnerSnapshot:
    'Les lecteurs voient les chiffres tels que publiés. Ils ne peuvent ni actualiser ni modifier.',
  shareRoleView: 'Peut consulter',
  shareRoleEdit: 'Peut modifier',
  shareRolePublish: 'Peut publier',
  shareLivePublished: 'En ligne : version {number}',
  shareUnpublish: 'Dépublier',
  shareManageDenied: 'Seul un éditeur peut changer qui voit cet élément.',
  sendTitle: 'Envoyer à',
  sendIntro:
    'Envoyez un lien et un résumé via une application connectée. Vous approuvez chaque envoi.',
  sendLoading: 'Chargement des destinations…',
  sendErrorBody: 'Impossible de charger les destinations.',
  sendNone:
    'Aucune application connectée ne peut encore recevoir cet élément. Connectez d’abord Slack, Teams ou l’e-mail.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-mail',
  sendAudience: 'Destinataires : {audience}',
  sendSummary: 'Message',
  sendLeaves: 'Un lien est envoyé.',
  sendLeavesAttachment: 'Un lien et le fichier vérifié sont envoyés.',
  sendNoAttachments:
    'Cette destination ne peut pas recevoir de fichiers, seul un lien est donc envoyé.',
  sendScheduled: 'Les envois planifiés nécessitent votre approbation à chaque fois.',
  sendAction: 'Vérifier et envoyer',
  sendFileUnsupported:
    'Cette destination ne peut pas recevoir le fichier. Envoyez seulement un lien, ou choisissez une autre destination.',
  sendLinkOnly: 'Envoyer seulement un lien',
  sendWaiting: 'En attente de votre approbation. Rien n’a été envoyé.',
  sendDelivered: 'Envoyé à {destination}.',
  sendFailed: 'Rien n’a été envoyé. Rien n’a quitté Poneva.',
  sendUnknown:
    'Nous n’avons pas pu confirmer la livraison à {destination}. Vérifiez sur place avant de renvoyer.',
  sendBlockedNotAuthorized: 'Vous n’avez pas l’autorisation d’envoyer cet élément.',
  sendBlockedUnsupported: 'Cette destination n’est pas prise en charge.',
  sendBlockedApproval: 'L’approbation n’est plus valide. Vérifiez et envoyez à nouveau.',
  sendBlockedChanged: 'Le message ou le destinataire a changé. Vérifiez-le à nouveau.',
  sendBlockedDependency: 'L’application connectée est indisponible. Réessayez plus tard.',
  libraryTitle: 'Bibliothèque',
  librarySegmentAll: 'Tous',
  librarySegmentMine: 'Les miens',
  librarySegmentShared: 'Partagés avec moi',
  librarySearch: 'Rechercher des artefacts',
  libraryKindAll: 'Tous les types',
  libraryKindReports: 'Rapports et pages',
  libraryKindDecks: 'Présentations',
  libraryKindDocuments: 'Documents',
  libraryKindSpreadsheets: 'Feuilles de calcul',
  libraryKindApps: 'Apps',
  libraryAppsMatchNoun: 'one:app correspond dans Apps →|other:apps correspondent dans Apps →',
  libraryMake: 'Commencer dans le chat',
  libraryEmptySharedHint: 'Lorsque quelqu’un partage un artefact avec vous, il apparaît ici.',
  libraryRefreshFailedAt: 'Échec de l’actualisation le {time}.',
  libraryRefreshTryAgain: 'Réessayer',
  libraryVia: 'via {owner}',
  savedPrivate: 'Privé',
  savedVersion: 'Version {n}',
  libraryLoading: 'Chargement de votre bibliothèque…',
  libraryErrorBody: 'Impossible de charger votre bibliothèque pour le moment.',
  startersLoadFailed: 'Impossible de charger les modèles pour le moment.',
  libraryCannotOpen: 'L’ouverture de « {title} » n’est pas encore possible ici.',
  libraryEmptyTitle: 'Rien ici pour le moment',
  libraryEmptyAllTitle: 'Créez quelque chose qui mérite d’être partagé.',
  libraryEmptyAllBody:
    'Les documents, présentations et apps que vous créez dans le chat, ou que d’autres partagent avec vous, apparaissent ici.',
  libraryEmptyMineBody: 'Les artefacts que vous créez apparaissent ici.',
  libraryEmptySharedBody: 'Rien n’a encore été partagé avec vous.',
  libraryNoMatchTitle: 'Aucun résultat',
  libraryNoMatchBody: 'Essayez un autre mot ou un autre type.',
  libraryMore: 'Afficher plus',
  libraryOwner: 'Par {owner}',
  libraryUpdated: 'Modifié {time}',
  libraryPublishedAs: 'Publié · v{number}',
  libraryDraft: 'Brouillon',
  libraryUnpublished: 'Non publié',
  libraryRefreshFailed: 'Échec de l’actualisation',
  libraryRefreshRunning: 'Actualisation en cours',
  libraryAudience: 'Pour {audience}',
  kindReport: 'Rapport',
  kindPage: 'Page',
  kindSite: 'Site',
  kindDeck: 'Présentation',
  kindDocument: 'Document',
  kindApp: 'App',
  kindSpreadsheet: 'Feuille de calcul',
  kindDashboard: 'Tableau de bord',
  kindTable: 'Tableau',
  kindChart: 'Graphique',
  kindExportsReport: 'Export en PDF',
  kindExportsPage: 'Page en lecture seule',
  kindExportsSite: 'Site en lecture seule',
  kindExportsDeck: 'Présenter, ou exporter en PowerPoint et PDF',
  kindExportsDocument: 'Export en Word et PDF',
  kindExportsSpreadsheet: 'Export en Excel et CSV',
  kindExportsDashboard: 'Vue en direct',
  kindExportsTable: 'Export en Excel et CSV',
  kindExportsChart: 'Vue graphique',
  agentPublished: '{agent} a publié {title} v{number}.',
  agentCheckedOn: 'Vérifié sur {views}, {themes}.',
  agentUndo: 'Annuler',
  agentRestorePrevious: 'Restaurer v{number}',
  agentHeld: '{title} v{number} est en attente comme brouillon.',
  agentHeldMismatch: '{count} chiffres ne correspondaient pas à leurs sources.',
  agentHeldCheck: 'Une vérification n’a pas réussi.',
  agentFixInChat: 'Corriger dans le chat',
  agentConfirmDrop:
    '{agent} veut supprimer {what} de {title}. Un point de restauration est d’abord enregistré. Rien ne change tant que vous n’avez pas décidé.',
  agentConfirmDropCount:
    '{agent} veut supprimer {what} ({count} valeurs) de {title}. Un point de restauration est d’abord enregistré. Rien ne change tant que vous n’avez pas décidé.',
  agentViewDesktop: 'ordinateur',
  agentViewTablet: 'tablette',
  agentViewPhone: 'téléphone',
  agentThemeLight: 'clair',
  agentThemeDark: 'sombre',
  agentLookAt: 'Voir v{number}',
  agentConfirmWrite:
    '{agent} veut écrire {what}. Rien n’est écrit tant que vous n’avez pas décidé.',
  agentConfirmGrant:
    '{agent} veut accéder à {what}. Rien n’est partagé tant que vous n’avez pas décidé.',
  agentReview: 'Vérifier',
  agentSkipped: '{agent} a ignoré {part}.',
  agentSkippedWhy:
    'Il lit le {system} propre à chaque personne, et cette exécution n’a pas de personne au nom de qui lire.',
  agentChooseWho: 'Choisir en quel nom il s’exécute',
  agentChangeInChat: 'Modifier dans le chat',
  agentFailed: '{agent} n’a pas pu actualiser {title}.',
  agentFailedBody: 'La dernière version publiée est toujours en ligne.',
  agentRetry: 'Réessayer',
  agentRunning: '{agent} met à jour {title}.',
  agentMakesTitle: 'Crée et met à jour',
  agentMakesLoading: 'Chargement…',
  agentMakesErrorBody: 'Impossible de charger cet élément.',
  agentMakesNone: 'Cet agent ne crée pas encore d’artefacts.',
  agentMakesNew: 'Un nouvel artefact à chaque exécution',
  agentMakesUpdate: 'Met à jour un artefact à chaque exécution',
  agentNextRun: 'Prochaine exécution {when}',
  agentNoSchedule: 'Non planifié',
  agentCreditsPerRun: 'Environ {credits} crédits par exécution',
  agentCreditsUnknown: 'Les crédits par exécution ne sont pas encore connus',
  agentLastResult: 'Dernier résultat',
  interviewTitle: 'Configurer',
  interviewIntro: 'Quelques questions le définissent.',
  interviewOptional: '(facultatif)',
  interviewAnswerNeeded: 'Répondez pour continuer.',
  interviewReadOnly: 'Lecture seule',
  interviewChange: 'Modifier',
  interviewConnectionYours: 'votre connexion {system}',
  interviewConnectionOther: 'la connexion {system} de {name}',
  interviewDateAny: 'Toute période',
  interviewDatePrevious: 'Mois précédent',
  interviewDateNext: 'Mois suivant',
  interviewDatePresets: 'Plages rapides',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Effacer',
  interviewDateDone: 'Terminé',
  interviewChooseSource: 'Choisir une source',
  interviewSourceChosen: 'Source choisie',
  interviewSourceUnavailable:
    'Le choix d’une source n’est pas encore disponible ici, donc la suite est impossible.',
  interviewSourceUnavailableOptional:
    'Le choix d’une source n’est pas encore disponible ici. Vous pouvez continuer sans source.',
  interviewStarterWithdrawn:
    'Ce modèle n’est plus disponible. Les brouillons déjà commencés à partir de celui-ci s’ouvrent toujours.',
  interviewDateFrom: 'Du',
  interviewDateTo: 'Au',
  interviewContinue: 'Continuer',
  interviewGrantTitle: 'Autoriser la lecture de {source} ?',
  interviewGrantBody:
    'Il utilise {connection}, en lecture seule, pour {scope}. Rien n’est lu tant que vous ne l’autorisez pas, et aucun exemple de ligne n’est affiché.',
  interviewAllow: 'Autoriser',
  interviewNotNow: 'Pas maintenant',
  interviewConfiguring: 'Configuration à partir de vos réponses…',
  interviewChecking: 'Vérification avant l’ouverture…',
  interviewComplete: '{title} est prêt.',
  interviewOpen: 'Ouvrir {title}',
  interviewBlocked: 'Impossible de continuer.',
  interviewFailed: 'La configuration a échoué. Vos réponses sont conservées.',
  interviewCancelled: 'Cette opération a été annulée.',
  interviewSaving: 'Enregistrement…',
  interviewSaved: 'Enregistré',
  interviewConflict:
    'Ce brouillon a changé ailleurs. Vos réponses sont conservées ici jusqu’à ce que vous chargiez le brouillon actuel, qui les remplace.',
  interviewReloadCurrent: 'Charger le brouillon actuel',
  interviewUnsettled:
    'Nous n’avons pas pu confirmer votre dernier enregistrement. Vos réponses sont conservées ici.',
  interviewCheckAgain: 'Vérifier à nouveau',
  finishAction: 'Enregistrer en privé',
  finishSaving: 'Enregistrement…',
  finishSaved: '« {title} » est enregistré, visible uniquement par vous.',
  finishNotSaved: 'Il n’a pas été enregistré. Votre brouillon est toujours là.',
  finishUnconfirmed: 'Nous n’avons pas pu confirmer s’il a été enregistré.',
  candidateInvalid: 'Le brouillon n’a pas pu devenir un document.',
  finishOpenInLibrary: 'Ouvrir dans la Bibliothèque',
  savedOpenFailed: 'Impossible de l’ouvrir pour le moment.',
  savedInLibrary: 'Il se trouve dans votre Bibliothèque.',
  openSavedTitle: 'Ouvrir {title} →',
  interviewReasonNotAuthorized: 'Vous n’avez pas l’autorisation de modifier ce brouillon.',
  interviewReasonDraftNotOpen: 'Ce brouillon n’est plus ouvert.',
  interviewReasonInvalid: 'L’une des réponses n’est pas valide.',
  interviewReasonObjects:
    'Certains fichiers n’ont pas pu être vérifiés. Vos réponses sont conservées.',
  interviewReasonDependency:
    'Un service nécessaire est indisponible. Vos réponses sont conservées.',
  interviewReasonReconcile: 'Le dernier enregistrement est encore en cours de confirmation.',
  exportTitle: 'Exporter',
  exportIntro: 'Téléchargez cet artefact. Le fichier indique d’où viennent ses données.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Diapositives modifiables avec notes de l’orateur',
  exportHintPdf: 'Exactement comme affiché',
  exportHintDocx: 'Modifiable, conserve titres et tableaux',
  exportHintXlsx: 'Toutes les feuilles, conserve les formules',
  exportHintCsv: 'Valeurs seulement, UTF-8',
  exportUnavailable: 'Indisponible pour le moment',
  exportRunning: 'Préparation du fichier {format}…',
  exportValidating: 'Vérification du fichier {format}…',
  exportReady: 'Le fichier {format} est prêt',
  exportDownload: 'Télécharger {name}',
  exportVersion: 'Version exportée {number}',
  exportFailed: 'Impossible de créer le fichier {format}.',
  exportFailedChecks:
    'Le fichier {format} n’a pas réussi ses vérifications, il n’a donc pas été proposé.',
  exportDenied: 'Vous n’avez pas accès à l’export de cet artefact.',
  exportRevoked: 'Le partage a été désactivé, cet artefact ne peut donc plus être exporté.',
  exportStale:
    'Cet artefact a changé pendant l’export. Exportez à nouveau pour obtenir la dernière version.',
  exportProvenanceTitle: 'Chaque fichier contient',
  exportProvenanceLine: 'Créé avec Poneva · {title} · v{number} · {data} · créé {madeAt}',
  exportProvenanceAsOf: 'données au {time}',
  exportPlacementNotesAbout: 'Dans la feuille Notes, et dans un fichier À propos à côté du CSV',
  exportProvenanceRange: 'données de {earliest} à {latest}',
  exportProvenanceUnknown: 'ancienneté des données inconnue',
  exportAboutFile: 'À propos de ce fichier ({format})',
  exportPlacementFooter: 'Dans le pied de page de chaque page',
  exportPlacementSlide: 'Dans le pied de page de chaque diapositive',
  exportPlacementNotes: 'Dans la feuille Notes',
  exportPlacementAbout: 'Dans un fichier À propos à côté du CSV',
  exportSourcesPage: 'Ajouter une page des sources',
  exportSourcesPageHint: 'Une dernière page listant chaque chiffre, sa source et son horodatage.',
  exportSourcesPageDeck: 'La diapositive Sources de la présentation est incluse.',
  exportSourcesPageSaved: 'Mémorisé pour cet artefact, y compris pour les envois planifiés.',
  libraryKindSheetTitle: 'Type',
  shareAccessSheetTitle: 'Accès',

  requestShowAll: 'Tout afficher',
  requestShowLess: 'Afficher moins',
  approveGettingReady: 'Préparation en cours…',
  approveNotReady: 'Impossible d’approuver pour le moment. Rechargez la page, puis réessayez.',
  tryWithModel: 'Essayer avec {model}',
  tryWithRequesting: 'Démarrage…',
  tryWithUnavailable: 'Impossible de lancer pour le moment.',
};

const ga: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Dréacht de Leagan {number} á chur in eagar',
  noChangesToApply: 'Níl aon athruithe le cur i bhfeidhm.',
  appliedDone: 'Curtha i bhfeidhm.',
  mentionInChat: 'Luaigh sa chomhrá',

  kindMarkdown: 'Nóta',
  kindKpi: 'Méadracht',
  libraryExportsList: 'Easpórtáil go {formats}',
  libraryOpensToRead: 'Osclaítear le léamh',
  libraryReadOnly: 'Inléite amháin',
  libraryLiveView: 'Amharc beo',
  libraryOneNumber: 'Uimhir amháin · amhail {time}',
  retry: 'Bain triail eile as',
  close: 'Dún',
  panelLoading: 'Á lódáil…',
  panelEmpty: 'Níl aon rud anseo fós.',
  panelDenied: 'Níl rochtain agat air seo.',
  panelBlocked: 'Tá seo ag fanacht le rud éigin sula bhféadfaidh sé leanúint ar aghaidh.',
  panelBlockedViewerContext:
    'Níl aon duine roghnaithe don rith seo le léamh ar a shon, mar sin ní féidir leis leanúint ar aghaidh go dtí go roghnóidh duine cé ar a shon a ritheann sé.',
  panelError: 'Níorbh fhéidir seo a lódáil.',
  panelStale: 'D’fhéadfadh seo a bheith as dáta.',
  sourcesTitle: 'Foinsí',
  sourcesIntro: 'Cá as a dtagann gach uimhir, agus cé chomh úr is atá sí.',
  sourcesLoading: 'Foinsí á lódáil…',
  sourcesEmptyTitle: 'Gan foinsí',
  sourcesEmptyBody: 'Ní úsáideann an déantán seo sonraí an chomhlachta.',
  sourcesErrorBody: 'Níorbh fhéidir foinsí a lódáil.',
  sourcesDeniedTitle: 'Tá roinnt foinsí i bhfolach',
  sourcesDeniedBody:
    'Níl rochtain agat ar roinnt foinsí a úsáidtear anseo (líon: {count}). Iarr ar úinéir iad a chomhroinnt.',
  sourceDeniedName: 'Foinse nach bhfeiceann tú',
  sourceAsOf: 'Amhail {time}',
  sourceAgeUnknown: 'Aois na sonraí anaithnid',
  sourceStatusCurrent: 'Reatha',
  sourceStatusStale: 'As dáta',
  sourceStatusGap: 'Ar iarraidh san athnuachan is déanaí',
  sourceStatusDenied: 'Gan rochtain',
  sourceLastGood: 'An ceann maith is déanaí: {time}',
  sourceUsedBy: 'Úsáidte ag {labels}',
  openDocument: 'Oscail an doiciméad',
  versionsShow: 'Taispeáin na leaganacha',
  versionsHide: 'Folaigh na leaganacha',
  versionsTitle: 'Leaganacha',
  versionsLoading: 'Leaganacha á lódáil…',
  versionsEmptyTitle: 'Níl aon leagan ann fós',
  versionsEmptyBody: 'Feictear an chéad leagan nuair a thógtar an déantán seo.',
  versionsErrorBody: 'Níorbh fhéidir leaganacha a lódáil.',
  versionLabel: 'Leagan {number}',
  versionOpen: 'Oscail leagan {number}',
  versionCurrent: 'Reatha',
  versionPublished: 'Foilsithe',
  versionBy: 'Le {actor}',
  versionOriginNative: 'I Poneva',
  versionOriginMcp: 'trí do ghníomhaire féin',
  versionOriginTerminal: 'tríd an teirminéal',
  versionOriginJob: 'Rith sceidealaithe',
  versionCausePerson: 'Curtha in eagar',
  versionCauseAgent: 'Nuashonrú gníomhaire',
  versionCauseRefresh: 'Athnuaite',
  versionCauseRestore: 'Athchóirithe',
  versionCauseImport: 'Iompórtáilte',
  versionCheckedYes: 'Seiceáilte',
  versionCheckedNo: 'Gan seiceáil fós',
  versionCheckedInvalidated: 'Curtha in eagar ó rinneadh an seiceáil is déanaí',
  compareStart: 'Cuir i gcomparáid',
  compareWith: 'Cuir i gcomparáid le leagan {number}',
  compareTitle: 'Cad a athraigh ó v{from} go v{to}',
  compareFromMark: 'Ó',
  compareToMark: 'Go',
  comparePick: 'Roghnaigh dara leagan le cur i gcomparáid.',
  compareNone: 'Gan aon difríochtaí.',
  compareLoading: 'Á chur i gcomparáid…',
  compareError: 'Níorbh fhéidir an chomparáid a lódáil.',
  compareBefore: 'Roimh',
  compareAfter: 'Tar éis',
  compareAdded: 'Curtha leis',
  compareRemoved: 'Bainte',
  compareChanged: 'Athraithe',
  restore: 'Athchóirigh an leagan seo',
  restoreConfirmTitle: 'Leagan {number} a athchóiriú?',
  restoreConfirmBody: 'Déanann sé seo leagan nua ó leagan {number}. Ní scriostar aon rud.',
  restoreConfirm: 'Athchóirigh',
  restoreCancel: 'Cealaigh',
  restoring: 'Ag athchóiriú…',
  restoreFailed: 'Níorbh fhéidir an leagan a athchóiriú.',
  restoreStale:
    'Athraíodh an déantán seo agus tú ag féachaint air. Athlódáil na leaganacha agus bain triail eile as.',
  restoreDenied: 'Níl cead agat leaganacha a athchóiriú.',
  restored: 'Athchóiríodh leagan {number} mar leagan nua.',
  publishTitle: 'Foilsigh',
  publishIntro: 'Beidh leagan {number} le feiceáil ag {audience}.',
  publishOwnerLine:
    'Feiceann lucht féachana an méid a d’fhéadfadh {owner} a fheiceáil nuair a foilsíodh an leagan seo.',
  publishChecksTitle: 'Seiceálacha',
  checkPassed: 'Seiceáilte',
  checkRunning: 'Ag seiceáil…',
  checkFailed: 'Theip ar sheiceáil',
  checkStale: 'Curtha in eagar ó rinneadh an seiceáil is déanaí',
  publishFixInChat: 'Ceartaigh sa chomhrá',
  publishAction: 'Foilsigh leagan {number}',
  publishing: 'Ag foilsiú…',
  publishedDone: 'Tá leagan {number} beo do {audience}.',
  publishConfirmGrant:
    'Teastaíonn rochtain nua ar chóras comhlachta ón leagan seo. Déan athbhreithniú ar an rochtain sula bhfoilsíonn tú é.',
  publishConfirmWrite:
    'Scríobhann an leagan seo chuig córas comhlachta. Déan athbhreithniú ar an rochtain sula bhfoilsíonn tú é.',
  publishConfirmDestructive: 'Baineann an leagan seo sonraí. Deimhnigh é i Poneva ar dtús.',
  publishConflict: 'Foilsíodh leagan eile ar dtús. Athlódáil chun é a fheiceáil.',
  publishBlockedNotAuthorized: 'Níl cead agat foilsiú.',
  publishBlockedCheckFailed: 'Theip ar sheiceáil, mar sin ní féidir an leagan seo a fhoilsiú.',
  publishBlockedCheckStale: 'Athraíodh an déantán tar éis a sheiceála deireanaí. Seiceáil arís é.',
  publishBlockedObjects:
    'Níorbh fhéidir roinnt comhad a fhíorú. Bain triail eile as i gceann nóiméid.',
  publishBlockedPolicy:
    'Athraíodh socruithe an chomhlachta. Déan athbhreithniú agus bain triail eile as.',
  publishBlockedApproval: 'Níl an deimhniú bailí a thuilleadh. Deimhnigh arís é.',
  publishBlockedDependency: 'Níl seirbhís riachtanach ar fáil. Bain triail eile as níos déanaí.',
  publishAsk: 'Iarr ar {publishers} foilsiú',
  publishAskFooter: 'Daoine atá in ann foilsiú: {publishers}.',
  publishAskNone: 'Iarr ar dhuine atá in ann foilsiú.',
  publishWarnings: 'Is fiú súil a chaitheamh orthu sula bhfoilsíonn tú',
  checkWarnStale: 'Tá {subject} as dáta ({time}).',
  checkWarnGap: 'Bhí {subject} ar iarraidh san athnuachan is déanaí.',
  checkWarnUnknownSource: 'Teastaíonn athbhreithniú ar fhoinse nach bhfeiceann tú.',
  checkRefreshFirst: 'Athnuaigh ar dtús',
  checkFailedRow: 'Theip ar an seiceáil le haghaidh {subject}.',
  checkFailedGeneric: 'Theip ar sheiceáil.',
  publishReviewAccess: 'Déan athbhreithniú ar an rochtain',
  publishRemovalBody:
    'Baineann v{number} {what} ({count}). Sábháiltear pointe athchóirithe ar dtús.',
  publishReviewRemoval: 'Déan athbhreithniú ar an méid a bhaintear',
  publishAndRemove: 'Foilsigh agus bain',
  publishReviewFirst: 'Déan athbhreithniú ar an méid a bhaintear sula bhfoilsíonn tú.',
  publishDoneTitle: 'Tá leagan {number} beo do {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Gheobhaidh siad é ina Leabharlann agus greamaithe don fhoireann.',
  publishUndo: 'Cealaigh',
  publishCopyLink: 'Cóipeáil an nasc',
  publishDone: 'Déanta',
  shareRoleViewMeaning: 'Is féidir é a oscailt agus a léamh',
  shareRoleEditMeaning: 'Is féidir é a athrú sa chomhrá agus leis an gcigire',
  shareRolePublishMeaning: 'Is féidir freisin é a fhoilsiú agus a dhífhoilsiú',
  shareRoleMenu: 'Ról do {name}',
  shareUnpublishConfirm: 'Cailleann daoine rochtain. Coinnítear an déantán agus a leaganacha.',
  shareUnpublishCancel: 'Coinnigh beo é',
  sendDestinationLine: '{provider} · daoine: {count}',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Feiceann gach duine i {destination} an achoimre. Níl {outside} as {total} i lucht féachana an déantáin seo, mar sin taispeánann an nasc “Iarr rochtain” dóibh.',
  sendAudienceGeneric: 'Feiceann daoine lasmuigh den lucht féachana “Iarr rochtain”.',
  sendPreview: 'Cad a fheicfidh siad',
  shareTitle: 'Comhroinn',
  shareLoading: 'Comhroinnt á lódáil…',
  shareErrorBody: 'Níorbh fhéidir an chomhroinnt a lódáil.',
  sharePrivate: 'Ní fheiceann ach tusa an déantán seo.',
  shareShared: 'Le feiceáil ag {audience}.',
  shareRevoked: 'Tá an chomhroinnt múchta. Ní féidir le haon duine eile an déantán seo a oscailt.',
  shareOwnerSnapshot:
    'Feiceann lucht féachana na huimhreacha mar a foilsíodh iad. Ní féidir leo iad a athnuachan ná a chur in eagar.',
  shareRoleView: 'Féadann féachaint',
  shareRoleEdit: 'Féadann cur in eagar',
  shareRolePublish: 'Féadann foilsiú',
  shareLivePublished: 'Beo: leagan {number}',
  shareUnpublish: 'Dífhoilsigh',
  shareManageDenied: 'Ní féidir ach le heagarthóir a athrú cé a fheiceann seo.',
  sendTitle: 'Seol chuig',
  sendIntro: 'Seol nasc agus achoimre trí aip cheangailte. Ceadaíonn tú gach seoladh.',
  sendLoading: 'Cinn scríbe á lódáil…',
  sendErrorBody: 'Níorbh fhéidir na cinn scríbe a lódáil.',
  sendNone:
    'Níl aip cheangailte ann fós a fhéadfadh é seo a fháil. Ceangail Slack, Teams nó ríomhphost ar dtús.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'Ríomhphost',
  sendAudience: 'Téann sé chuig {audience}',
  sendSummary: 'Teachtaireacht',
  sendLeaves: 'Seoltar nasc.',
  sendLeavesAttachment: 'Seoltar nasc agus an comhad athbhreithnithe.',
  sendNoAttachments:
    'Ní féidir comhaid a sheoladh chuig an gceann scríbe seo, mar sin ní sheoltar ach nasc.',
  sendScheduled: 'Teastaíonn do cheadú ó sheoltaí sceidealaithe gach uair.',
  sendAction: 'Déan athbhreithniú agus seol',
  sendFileUnsupported:
    'Ní féidir leis an gceann scríbe seo an comhad a ghlacadh. Seol nasc amháin, nó roghnaigh ceann scríbe eile.',
  sendLinkOnly: 'Seol nasc amháin',
  sendWaiting: 'Ag fanacht le do cheadú. Níor seoladh aon rud.',
  sendDelivered: 'Seolta chuig {destination}.',
  sendFailed: 'Níor seoladh é. Níor fhág aon rud Poneva.',
  sendUnknown:
    'Níorbh fhéidir linn seachadadh chuig {destination} a dheimhniú. Seiceáil ansin sula seolann tú arís é.',
  sendBlockedNotAuthorized: 'Níl cead agat é seo a sheoladh.',
  sendBlockedUnsupported: 'Ní thacaítear leis an gceann scríbe seo.',
  sendBlockedApproval: 'Níl an ceadú bailí a thuilleadh. Déan athbhreithniú agus seol arís.',
  sendBlockedChanged: 'Athraíodh an teachtaireacht nó an faighteoir. Déan athbhreithniú air arís.',
  sendBlockedDependency: 'Níl an aip cheangailte ar fáil. Bain triail eile as níos déanaí.',
  libraryTitle: 'Leabharlann',
  librarySegmentAll: 'Gach rud',
  librarySegmentMine: 'Mo chuid',
  librarySegmentShared: 'Comhroinnte liom',
  librarySearch: 'Cuardaigh déantáin',
  libraryKindAll: 'Gach cineál',
  libraryKindReports: 'Tuarascálacha agus leathanaigh',
  libraryKindDecks: 'Láithreoireachtaí',
  libraryKindDocuments: 'Doiciméid',
  libraryKindSpreadsheets: 'Scarbhileoga',
  libraryKindApps: 'Aipeanna',
  libraryAppsMatchNoun:
    'one:aip ag teacht leis in Aipeanna →|two:aip ag teacht leis in Aipeanna →|few:aip ag teacht leis in Aipeanna →|many:n-aip ag teacht leis in Aipeanna →|other:aip ag teacht leis in Aipeanna →',
  libraryMake: 'Tosaigh sa chomhrá',
  libraryEmptySharedHint: 'Nuair a chomhroinneann duine déantán leat, feictear anseo é.',
  libraryRefreshFailedAt: 'Níorbh fhéidir athnuachan a dhéanamh ar {time}.',
  libraryRefreshTryAgain: 'Bain triail eile as',
  libraryVia: 'trí {owner}',
  savedPrivate: 'Príobháideach',
  savedVersion: 'Leagan {n}',
  libraryLoading: 'Do Leabharlann á lódáil…',
  libraryErrorBody: 'Níorbh fhéidir do Leabharlann a lódáil faoi láthair.',
  startersLoadFailed: 'Níorbh fhéidir na teimpléid a lódáil faoi láthair.',
  libraryCannotOpen: 'Ní féidir “{title}” a oscailt anseo go fóill.',
  libraryEmptyTitle: 'Níl aon rud anseo fós',
  libraryEmptyAllTitle: 'Déan rud is fiú a roinnt.',
  libraryEmptyAllBody:
    'Taispeántar anseo doiciméid, cuir i láthair agus aipeanna a dhéanann tú sa chomhrá, nó a roinneann daoine eile leat.',
  libraryEmptyMineBody: 'Feictear anseo déantáin a chruthaíonn tú.',
  libraryEmptySharedBody: 'Níor comhroinneadh aon rud leat fós.',
  libraryNoMatchTitle: 'Níor aimsíodh aon rud',
  libraryNoMatchBody: 'Bain triail as focal eile nó as cineál eile.',
  libraryMore: 'Taispeáin tuilleadh',
  libraryOwner: 'Le {owner}',
  libraryUpdated: 'Curtha in eagar {time}',
  libraryPublishedAs: 'Foilsithe · v{number}',
  libraryDraft: 'Dréacht',
  libraryUnpublished: 'Gan foilsiú',
  libraryRefreshFailed: 'Theip ar an athnuachan',
  libraryRefreshRunning: 'Ag athnuachan',
  libraryAudience: 'Do {audience}',
  kindReport: 'Tuarascáil',
  kindPage: 'Leathanach',
  kindSite: 'Suíomh',
  kindDeck: 'Láithreoireacht',
  kindDocument: 'Doiciméad',
  kindApp: 'Aip',
  kindSpreadsheet: 'Scarbhileog',
  kindDashboard: 'Painéal ionstraimí',
  kindTable: 'Tábla',
  kindChart: 'Cairt',
  kindExportsReport: 'Easpórtáiltear go PDF',
  kindExportsPage: 'Leathanach inléite amháin',
  kindExportsSite: 'Suíomh inléite amháin',
  kindExportsDeck: 'Cuir i láthair, nó easpórtáil go PowerPoint agus PDF',
  kindExportsDocument: 'Easpórtáiltear go Word agus PDF',
  kindExportsSpreadsheet: 'Easpórtáiltear go Excel agus CSV',
  kindExportsDashboard: 'Amharc beo',
  kindExportsTable: 'Easpórtáiltear go Excel agus CSV',
  kindExportsChart: 'Amharc cairte',
  agentPublished: 'D’fhoilsigh {agent} {title} v{number}.',
  agentCheckedOn: 'Seiceáilte ar {views}, {themes}.',
  agentUndo: 'Cealaigh',
  agentRestorePrevious: 'Athchóirigh v{number}',
  agentHeld: 'Tá {title} v{number} ag fanacht mar dhréacht.',
  agentHeldMismatch: 'Níor tháinig uimhreacha lena bhfoinsí le chéile: {count}.',
  agentHeldCheck: 'Níor éirigh le seiceáil.',
  agentFixInChat: 'Ceartaigh sa chomhrá',
  agentConfirmDrop:
    'Ba mhaith le {agent} {what} a bhaint de {title}. Sábháiltear pointe athchóirithe ar dtús. Ní athraíonn aon rud go dtí go gcinneann tú.',
  agentConfirmDropCount:
    'Ba mhaith le {agent} {what} (luachanna: {count}) a bhaint de {title}. Sábháiltear pointe athchóirithe ar dtús. Ní athraíonn aon rud go dtí go gcinneann tú.',
  agentViewDesktop: 'ríomhaire deisce',
  agentViewTablet: 'táibléad',
  agentViewPhone: 'fón',
  agentThemeLight: 'geal',
  agentThemeDark: 'dorcha',
  agentLookAt: 'Féach ar v{number}',
  agentConfirmWrite:
    'Ba mhaith le {agent} {what} a scríobh. Ní scríobhtar aon rud go dtí go gcinneann tú.',
  agentConfirmGrant:
    'Ba mhaith le {agent} rochtain a fháil ar {what}. Ní chomhroinntear aon rud go dtí go gcinneann tú.',
  agentReview: 'Athbhreithniú',
  agentSkipped: 'Scipeáil {agent} {part}.',
  agentSkippedWhy:
    'Léann sé {system} gach duine ar leith, agus níl aon duine roghnaithe don rith seo.',
  agentChooseWho: 'Roghnaigh cé ar a shon a ritheann sé',
  agentChangeInChat: 'Athraigh sa chomhrá',
  agentFailed: 'Níorbh fhéidir le {agent} {title} a athnuachan.',
  agentFailedBody: 'Tá an leagan foilsithe is déanaí fós beo.',
  agentRetry: 'Bain triail eile as',
  agentRunning: 'Tá {agent} ag nuashonrú {title}.',
  agentMakesTitle: 'Cruthaíonn agus nuashonraíonn',
  agentMakesLoading: 'Á lódáil…',
  agentMakesErrorBody: 'Níorbh fhéidir seo a lódáil.',
  agentMakesNone: 'Ní chruthaíonn an gníomhaire seo déantáin fós.',
  agentMakesNew: 'Déantán nua gach rith',
  agentMakesUpdate: 'Nuashonraíonn sé déantán amháin gach rith',
  agentNextRun: 'An chéad rith eile {when}',
  agentNoSchedule: 'Gan sceideal',
  agentCreditsPerRun: 'Thart ar {credits} creidmheas an rith',
  agentCreditsUnknown: 'Níl creidmheas gach rith ar eolas fós',
  agentLastResult: 'An toradh is déanaí',
  interviewTitle: 'Cuir ar bun é',
  interviewIntro: 'Cuireann cúpla ceist cruth air.',
  interviewOptional: '(roghnach)',
  interviewAnswerNeeded: 'Freagair seo chun leanúint ar aghaidh.',
  interviewReadOnly: 'Inléite amháin',
  interviewChange: 'Athraigh',
  interviewConnectionYours: 'do cheangal {system}',
  interviewConnectionOther: 'an ceangal {system} ag {name}',
  interviewDateAny: 'Aon tréimhse',
  interviewDatePrevious: 'An mhí roimhe',
  interviewDateNext: 'An mhí seo chugainn',
  interviewDatePresets: 'Raonta tapa',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Glan',
  interviewDateDone: 'Déanta',
  interviewChooseSource: 'Roghnaigh foinse',
  interviewSourceChosen: 'Foinse roghnaithe',
  interviewSourceUnavailable:
    'Níl roghnú foinse ar fáil anseo go fóill, mar sin ní féidir leanúint ar aghaidh.',
  interviewSourceUnavailableOptional:
    'Níl foinse a roghnú ar fáil anseo fós. Is féidir leat leanúint ar aghaidh gan foinse.',
  interviewStarterWithdrawn:
    'Níl an teimpléad seo ar fáil a thuilleadh. Osclaíonn dréachtaí a thosaigh tú uaidh cheana féin fós.',
  interviewDateFrom: 'Ó',
  interviewDateTo: 'Go',
  interviewContinue: 'Lean ar aghaidh',
  interviewGrantTitle: 'Ceadaigh {source} a léamh?',
  interviewGrantBody:
    'Úsáideann sé {connection}, inléite amháin, le haghaidh {scope}. Ní léitear aon rud go gceadaíonn tú é, agus ní thaispeántar aon sraitheanna samplacha.',
  interviewAllow: 'Ceadaigh',
  interviewNotNow: 'Ní anois',
  interviewConfiguring: 'Á chur ar bun de réir do chuid freagraí…',
  interviewChecking: 'Á sheiceáil sula n-osclaítear é…',
  interviewComplete: 'Tá {title} réidh.',
  interviewOpen: 'Oscail {title}',
  interviewBlocked: 'Níorbh fhéidir leanúint ar aghaidh.',
  interviewFailed: 'Theip ar an gcur ar bun. Coinnítear do chuid freagraí.',
  interviewCancelled: 'Cealaíodh é seo.',
  interviewSaving: 'Ag sábháil…',
  interviewSaved: 'Sábháilte',
  interviewConflict:
    'Athraíodh an dréacht seo in áit eile. Coinnítear do chuid freagraí anseo go lódálfaidh tú an dréacht reatha, rud a chuirfidh iad ina n-áit.',
  interviewReloadCurrent: 'Lódáil an dréacht reatha',
  interviewUnsettled:
    'Níorbh fhéidir linn do shábháil deiridh a dheimhniú. Coinnítear do chuid freagraí anseo.',
  interviewCheckAgain: 'Seiceáil arís',
  finishAction: 'Sábháil go príobháideach',
  finishSaving: 'Á shábháil…',
  finishSaved: 'Sábháladh “{title}”, agus níl sé le feiceáil ach agatsa.',
  finishNotSaved: 'Níor sábháladh é. Tá do dhréacht anseo fós.',
  finishUnconfirmed: 'Níorbh fhéidir linn a dhearbhú ar sábháladh é.',
  candidateInvalid: 'Níorbh fhéidir doiciméad a dhéanamh den dréacht.',
  finishOpenInLibrary: 'Oscail sa Leabharlann',
  savedOpenFailed: 'Níorbh fhéidir é a oscailt faoi láthair.',
  savedInLibrary: 'Tá sé i do Leabharlann.',
  openSavedTitle: 'Oscail {title} →',
  interviewReasonNotAuthorized: 'Níl cead agat an dréacht seo a athrú.',
  interviewReasonDraftNotOpen: 'Níl an dréacht seo oscailte a thuilleadh.',
  interviewReasonInvalid: 'Níl ceann de na freagraí bailí.',
  interviewReasonObjects: 'Níorbh fhéidir roinnt comhad a fhíorú. Coinnítear do chuid freagraí.',
  interviewReasonDependency: 'Níl seirbhís riachtanach ar fáil. Coinnítear do chuid freagraí.',
  interviewReasonReconcile: 'Tá an sábháil deiridh á deimhniú fós.',
  exportTitle: 'Easpórtáil',
  exportIntro: 'Íoslódáil an déantán seo. Insíonn an comhad cá dtagann a shonraí.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Sleamhnáin in-eagarthóireachta le nótaí cainteora',
  exportHintPdf: 'Díreach mar a thaispeántar é',
  exportHintDocx: 'In-eagarthóireachta, coinníonn sé ceannteidil agus táblaí',
  exportHintXlsx: 'Gach bileog, coinníonn sé foirmlí',
  exportHintCsv: 'Luachanna amháin, UTF-8',
  exportUnavailable: 'Níl sé ar fáil faoi láthair',
  exportRunning: '{format} á ullmhú…',
  exportValidating: 'An comhad {format} á sheiceáil…',
  exportReady: 'Tá an comhad {format} réidh',
  exportDownload: 'Íoslódáil {name}',
  exportVersion: 'Leagan {number} easpórtáilte',
  exportFailed: 'Níorbh fhéidir an comhad {format} a dhéanamh.',
  exportFailedChecks:
    'Níor éirigh leis an gcomhad {format} a sheiceálacha, mar sin níor tairgeadh é.',
  exportDenied: 'Níl rochtain agat an déantán seo a easpórtáil.',
  exportRevoked:
    'Múchadh an chomhroinnt, mar sin ní féidir an déantán seo a easpórtáil a thuilleadh.',
  exportStale:
    'Athraíodh an déantán seo le linn easpórtála. Easpórtáil arís chun an leagan is nuaí a fháil.',
  exportProvenanceTitle: 'Cuimsíonn gach comhad',
  exportProvenanceLine: 'Déanta le Poneva · {title} · v{number} · {data} · déanta {madeAt}',
  exportProvenanceAsOf: 'sonraí amhail {time}',
  exportPlacementNotesAbout: 'Ar an mbileog Nótaí, agus comhad faoi in aice leis an CSV',
  exportProvenanceRange: 'sonraí ó {earliest} go {latest}',
  exportProvenanceUnknown: 'aois na sonraí anaithnid',
  exportAboutFile: 'Faoin gcomhad seo ({format})',
  exportPlacementFooter: 'I mbunnóta gach leathanaigh',
  exportPlacementSlide: 'I mbunnóta gach sleamhnáin',
  exportPlacementNotes: 'Ar an mbileog Nótaí',
  exportPlacementAbout: 'I gcomhad faoi in aice leis an CSV',
  exportSourcesPage: 'Cuir leathanach foinsí leis',
  exportSourcesPageHint:
    'Leathanach deiridh a liostaíonn gach uimhir leis an bhfoinse agus an t-am.',
  exportSourcesPageDeck: 'Téann sleamhnán Foinsí na láithreoireachta leis.',
  exportSourcesPageSaved: 'Cuimhnítear air don déantán seo, seoltaí sceidealaithe san áireamh.',
  libraryKindSheetTitle: 'Cineál',
  shareAccessSheetTitle: 'Rochtain',

  requestShowAll: 'Taispeáin gach rud',
  requestShowLess: 'Taispeáin níos lú',
  approveGettingReady: 'Á ullmhú…',
  approveNotReady:
    'Ní féidir é seo a cheadú faoi láthair. Athlódáil an leathanach, ansin bain triail eile as.',
  tryWithModel: 'Bain triail as le {model}',
  tryWithRequesting: 'Á thosú…',
  tryWithUnavailable: 'Ní féidir é seo a thosú faoi láthair.',
};

const hr: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Uređujete nacrt verzije {number}',
  noChangesToApply: 'Nema promjena za primjenu.',
  appliedDone: 'Primijenjeno.',
  mentionInChat: 'Spomeni u razgovoru',

  kindMarkdown: 'Bilješka',
  kindKpi: 'Metrika',
  libraryExportsList: 'Izvoz u {formats}',
  libraryOpensToRead: 'Otvara se za čitanje',
  libraryReadOnly: 'Samo za čitanje',
  libraryLiveView: 'Prikaz uživo',
  libraryOneNumber: 'Jedan broj · na dan {time}',
  retry: 'Pokušajte ponovno',
  close: 'Zatvori',
  panelLoading: 'Učitavanje…',
  panelEmpty: 'Ovdje još nema ničega.',
  panelDenied: 'Nemate pristup ovome.',
  panelBlocked: 'Ovo čeka nešto prije nego što može nastaviti.',
  panelBlockedViewerContext:
    'Ovo izvođenje nema osobu u čije ime čita pa ne može nastaviti dok netko ne odabere u čije se ime izvodi.',
  panelError: 'Ovo se nije moglo učitati.',
  panelStale: 'Ovo je možda zastarjelo.',
  sourcesTitle: 'Izvori',
  sourcesIntro: 'Odakle dolazi svaki broj i koliko je svjež.',
  sourcesLoading: 'Učitavanje izvora…',
  sourcesEmptyTitle: 'Nema izvora',
  sourcesEmptyBody: 'Ovaj artefakt ne koristi podatke tvrtke.',
  sourcesErrorBody: 'Izvori se nisu mogli učitati.',
  sourcesDeniedTitle: 'Neki izvori su skriveni',
  sourcesDeniedBody:
    'Nemate pristup za broj izvora korištenih ovdje: {count}. Zamolite vlasnika da ih podijeli.',
  sourceDeniedName: 'Izvor koji ne možete vidjeti',
  sourceAsOf: 'Na dan {time}',
  sourceAgeUnknown: 'Starost podataka nije poznata',
  sourceStatusCurrent: 'Aktualno',
  sourceStatusStale: 'Zastarjelo',
  sourceStatusGap: 'Nedostaje u zadnjem osvježavanju',
  sourceStatusDenied: 'Nema pristupa',
  sourceLastGood: 'Zadnje uspješno: {time}',
  sourceUsedBy: 'Koristi: {labels}',
  openDocument: 'Otvori dokument',
  versionsShow: 'Prikaži verzije',
  versionsHide: 'Sakrij verzije',
  versionsTitle: 'Verzije',
  versionsLoading: 'Učitavanje verzija…',
  versionsEmptyTitle: 'Još nema verzija',
  versionsEmptyBody: 'Prva se verzija pojavljuje kad se ovaj artefakt izradi.',
  versionsErrorBody: 'Verzije se nisu mogle učitati.',
  versionLabel: 'Verzija {number}',
  versionOpen: 'Otvori verziju {number}',
  versionCurrent: 'Trenutna',
  versionPublished: 'Objavljena',
  versionBy: 'Autor: {actor}',
  versionOriginNative: 'U aplikaciji Poneva',
  versionOriginMcp: 'putem vašeg vlastitog agenta',
  versionOriginTerminal: 'putem terminala',
  versionOriginJob: 'Zakazano izvođenje',
  versionCausePerson: 'Uređena',
  versionCauseAgent: 'Ažuriranje agenta',
  versionCauseRefresh: 'Osvježena',
  versionCauseRestore: 'Vraćena',
  versionCauseImport: 'Uvezena',
  versionCheckedYes: 'Provjerena',
  versionCheckedNo: 'Još nije provjerena',
  versionCheckedInvalidated: 'Uređena nakon zadnje provjere',
  compareStart: 'Usporedi',
  compareWith: 'Usporedi s verzijom {number}',
  compareTitle: 'Što se promijenilo od v{from} do v{to}',
  compareFromMark: 'Od',
  compareToMark: 'Do',
  comparePick: 'Odaberite drugu verziju za usporedbu.',
  compareNone: 'Nema razlika.',
  compareLoading: 'Uspoređivanje…',
  compareError: 'Usporedba se nije mogla učitati.',
  compareBefore: 'Prije',
  compareAfter: 'Poslije',
  compareAdded: 'Dodano',
  compareRemoved: 'Uklonjeno',
  compareChanged: 'Promijenjeno',
  restore: 'Vrati ovu verziju',
  restoreConfirmTitle: 'Vratiti verziju {number}?',
  restoreConfirmBody: 'Time se stvara nova verzija iz verzije {number}. Ništa se ne briše.',
  restoreConfirm: 'Vrati',
  restoreCancel: 'Odustani',
  restoring: 'Vraćanje…',
  restoreFailed: 'Verzija se nije mogla vratiti.',
  restoreStale:
    'Ovaj se artefakt promijenio dok ste ga gledali. Ponovno učitajte verzije i pokušajte opet.',
  restoreDenied: 'Nemate dopuštenje za vraćanje verzija.',
  restored: 'Verzija {number} vraćena je kao nova verzija.',
  publishTitle: 'Objavi',
  publishIntro: 'Verzija {number} bit će vidljiva: {audience}.',
  publishOwnerLine:
    'Gledatelji vide ono što je {owner} mogao vidjeti kad je ova verzija objavljena.',
  publishChecksTitle: 'Provjere',
  checkPassed: 'Provjereno',
  checkRunning: 'Provjera…',
  checkFailed: 'Provjera nije uspjela',
  checkStale: 'Uređeno nakon zadnje provjere',
  publishFixInChat: 'Popravi u razgovoru',
  publishAction: 'Objavi verziju {number}',
  publishing: 'Objavljivanje…',
  publishedDone: 'Verzija {number} je aktivna: {audience}.',
  publishConfirmGrant:
    'Ova verzija treba novi pristup sustavu tvrtke. Pregledajte pristup prije objave.',
  publishConfirmWrite: 'Ova verzija zapisuje u sustav tvrtke. Pregledajte pristup prije objave.',
  publishConfirmDestructive: 'Ova verzija uklanja podatke. Prvo to potvrdite u aplikaciji Poneva.',
  publishConflict: 'Prije je objavljena druga verzija. Ponovno učitajte da je vidite.',
  publishBlockedNotAuthorized: 'Nemate dopuštenje za objavu.',
  publishBlockedCheckFailed: 'Provjera nije uspjela pa se ova verzija ne može objaviti.',
  publishBlockedCheckStale: 'Artefakt se promijenio nakon zadnje provjere. Provjerite ga ponovno.',
  publishBlockedObjects: 'Neke se datoteke nisu mogle potvrditi. Pokušajte ponovno za trenutak.',
  publishBlockedPolicy: 'Postavke tvrtke promijenile su se. Pregledajte i pokušajte ponovno.',
  publishBlockedApproval: 'Potvrda više nije valjana. Potvrdite ponovno.',
  publishBlockedDependency: 'Potrebna usluga nije dostupna. Pokušajte ponovno kasnije.',
  publishAsk: 'Zamolite {publishers} da objave',
  publishAskFooter: 'Osobe koje mogu objaviti: {publishers}.',
  publishAskNone: 'Zamolite nekoga tko može objaviti.',
  publishWarnings: 'Vrijedi pogledati prije objave',
  checkWarnStale: '{subject} je zastario ({time}).',
  checkWarnGap: '{subject} je nedostajao u zadnjem osvježavanju.',
  checkWarnUnknownSource: 'Izvor koji ne možete vidjeti treba pregledati.',
  checkRefreshFirst: 'Prvo osvježi',
  checkFailedRow: '{subject} nije prošao provjeru.',
  checkFailedGeneric: 'Provjera nije uspjela.',
  publishReviewAccess: 'Pregledaj pristup',
  publishRemovalBody: 'v{number} uklanja {what} ({count}). Prvo se sprema točka vraćanja.',
  publishReviewRemoval: 'Pregledaj što se uklanja',
  publishAndRemove: 'Objavi i ukloni',
  publishReviewFirst: 'Pregledajte što se uklanja prije objave.',
  publishDoneTitle: 'Verzija {number} je aktivna: {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Pronaći će je u svojoj knjižnici i prikvačenu za tim.',
  publishUndo: 'Poništi',
  publishCopyLink: 'Kopiraj poveznicu',
  publishDone: 'Gotovo',
  shareRoleViewMeaning: 'Može otvoriti i čitati',
  shareRoleEditMeaning: 'Može mijenjati u razgovoru i pomoću inspektora',
  shareRolePublishMeaning: 'Može i objaviti te povući objavu',
  shareRoleMenu: 'Uloga za {name}',
  shareUnpublishConfirm: 'Ljudi gube pristup. Artefakt i njegove verzije ostaju sačuvani.',
  shareUnpublishCancel: 'Neka ostane aktivno',
  sendDestinationLine: '{provider} · {count} osoba',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Svi u odredištu {destination} vide sažetak. Njih {outside} od {total} nije u publici ovog artefakta pa im poveznica prikazuje „Zatraži pristup”.',
  sendAudienceGeneric: 'Osobe izvan publike vide „Zatraži pristup”.',
  sendPreview: 'Što će vidjeti',
  shareTitle: 'Dijeli',
  shareLoading: 'Učitavanje dijeljenja…',
  shareErrorBody: 'Dijeljenje se nije moglo učitati.',
  sharePrivate: 'Samo vi vidite ovaj artefakt.',
  shareShared: 'Vidljivo: {audience}.',
  shareRevoked: 'Dijeljenje je isključeno. Nitko drugi ne može otvoriti ovaj artefakt.',
  shareOwnerSnapshot:
    'Gledatelji vide brojeve kako su objavljeni. Ne mogu ih osvježavati ni uređivati.',
  shareRoleView: 'Može pregledavati',
  shareRoleEdit: 'Može uređivati',
  shareRolePublish: 'Može objavljivati',
  shareLivePublished: 'Aktivno: verzija {number}',
  shareUnpublish: 'Povuci objavu',
  shareManageDenied: 'Samo urednik može mijenjati tko ovo vidi.',
  sendTitle: 'Pošalji na',
  sendIntro: 'Pošaljite poveznicu i sažetak putem povezane aplikacije. Vi odobravate svako slanje.',
  sendLoading: 'Učitavanje odredišta…',
  sendErrorBody: 'Odredišta se nisu mogla učitati.',
  sendNone:
    'Nijedna povezana aplikacija još ne može ovo primiti. Prvo povežite Slack, Teams ili e-poštu.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-pošta',
  sendAudience: 'Ide: {audience}',
  sendSummary: 'Poruka',
  sendLeaves: 'Šalje se poveznica.',
  sendLeavesAttachment: 'Šalju se poveznica i pregledana datoteka.',
  sendNoAttachments: 'Ovo odredište ne prima datoteke pa se šalje samo poveznica.',
  sendScheduled: 'Zakazana slanja svaki put trebaju vaše odobrenje.',
  sendAction: 'Pregledaj i pošalji',
  sendFileUnsupported:
    'Ovo odredište ne može primiti datoteku. Pošaljite samo poveznicu ili odaberite drugo odredište.',
  sendLinkOnly: 'Pošalji samo poveznicu',
  sendWaiting: 'Čeka vaše odobrenje. Ništa nije poslano.',
  sendDelivered: 'Poslano na odredište {destination}.',
  sendFailed: 'Nije poslano. Ništa nije napustilo aplikaciju Poneva.',
  sendUnknown:
    'Nismo mogli potvrditi isporuku na odredište {destination}. Provjerite tamo prije ponovnog slanja.',
  sendBlockedNotAuthorized: 'Nemate dopuštenje za slanje ovoga.',
  sendBlockedUnsupported: 'Ovo odredište nije podržano.',
  sendBlockedApproval: 'Odobrenje više nije valjano. Pregledajte i pošaljite ponovno.',
  sendBlockedChanged: 'Poruka ili primatelj promijenili su se. Pregledajte ponovno.',
  sendBlockedDependency: 'Povezana aplikacija nije dostupna. Pokušajte ponovno kasnije.',
  libraryTitle: 'Knjižnica',
  librarySegmentAll: 'Sve',
  librarySegmentMine: 'Moje',
  librarySegmentShared: 'Podijeljeno sa mnom',
  librarySearch: 'Pretraži artefakte',
  libraryKindAll: 'Sve vrste',
  libraryKindReports: 'Izvješća i stranice',
  libraryKindDecks: 'Prezentacije',
  libraryKindDocuments: 'Dokumenti',
  libraryKindSpreadsheets: 'Proračunske tablice',
  libraryKindApps: 'Aplikacije',
  libraryAppsMatchNoun:
    'one:aplikacija odgovara u odjeljku Aplikacije →|few:aplikacije odgovaraju u odjeljku Aplikacije →|other:aplikacija odgovara u odjeljku Aplikacije →',
  libraryMake: 'Započnite u chatu',
  libraryEmptySharedHint: 'Kad netko podijeli artefakt s vama, pojavit će se ovdje.',
  libraryRefreshFailedAt: 'Osvježavanje nije uspjelo {time}.',
  libraryRefreshTryAgain: 'Pokušajte ponovno',
  libraryVia: 'putem: {owner}',
  savedPrivate: 'Privatno',
  savedVersion: 'Verzija {n}',
  libraryLoading: 'Učitavanje vaše knjižnice…',
  libraryErrorBody: 'Vaša se knjižnica trenutačno ne može učitati.',
  startersLoadFailed: 'Predlošci se trenutačno nisu mogli učitati.',
  libraryCannotOpen: '„{title}” se ovdje još ne može otvoriti.',
  libraryEmptyTitle: 'Ovdje još nema ničega',
  libraryEmptyAllTitle: 'Napravite nešto vrijedno dijeljenja.',
  libraryEmptyAllBody:
    'Ovdje se prikazuju dokumenti, prezentacije i aplikacije koje izradite u chatu ili koje drugi podijele s vama.',
  libraryEmptyMineBody: 'Artefakti koje izradite pojavljuju se ovdje.',
  libraryEmptySharedBody: 'S vama još ništa nije podijeljeno.',
  libraryNoMatchTitle: 'Nema rezultata',
  libraryNoMatchBody: 'Pokušajte s drugom riječi ili drugom vrstom.',
  libraryMore: 'Prikaži više',
  libraryOwner: 'Autor: {owner}',
  libraryUpdated: 'Uređeno {time}',
  libraryPublishedAs: 'Objavljeno · v{number}',
  libraryDraft: 'Nacrt',
  libraryUnpublished: 'Neobjavljeno',
  libraryRefreshFailed: 'Osvježavanje nije uspjelo',
  libraryRefreshRunning: 'Osvježava se',
  libraryAudience: 'Za: {audience}',
  kindReport: 'Izvješće',
  kindPage: 'Stranica',
  kindSite: 'Web-mjesto',
  kindDeck: 'Prezentacija',
  kindDocument: 'Dokument',
  kindApp: 'Aplikacija',
  kindSpreadsheet: 'Proračunska tablica',
  kindDashboard: 'Nadzorna ploča',
  kindTable: 'Tablica',
  kindChart: 'Grafikon',
  kindExportsReport: 'Izvozi se u PDF',
  kindExportsPage: 'Stranica samo za čitanje',
  kindExportsSite: 'Web-mjesto samo za čitanje',
  kindExportsDeck: 'Prezentirajte ili izvezite u PowerPoint i PDF',
  kindExportsDocument: 'Izvozi se u Word i PDF',
  kindExportsSpreadsheet: 'Izvozi se u Excel i CSV',
  kindExportsDashboard: 'Prikaz uživo',
  kindExportsTable: 'Izvozi se u Excel i CSV',
  kindExportsChart: 'Prikaz grafikona',
  agentPublished: '{agent} je objavio: {title} v{number}.',
  agentCheckedOn: 'Provjereno na: {views}, {themes}.',
  agentUndo: 'Poništi',
  agentRestorePrevious: 'Vrati v{number}',
  agentHeld: '{title} v{number} čeka kao nacrt.',
  agentHeldMismatch: 'Broj brojeva koji se nisu podudarali s izvorima: {count}.',
  agentHeldCheck: 'Jedna provjera nije prošla.',
  agentFixInChat: 'Popravi u razgovoru',
  agentConfirmDrop:
    '{agent} želi ukloniti {what} iz dokumenta {title}. Prvo se sprema točka vraćanja. Ništa se ne mijenja dok ne odlučite.',
  agentConfirmDropCount:
    '{agent} želi ukloniti {what} (vrijednosti: {count}) iz dokumenta {title}. Prvo se sprema točka vraćanja. Ništa se ne mijenja dok ne odlučite.',
  agentViewDesktop: 'računalo',
  agentViewTablet: 'tablet',
  agentViewPhone: 'telefon',
  agentThemeLight: 'svijetla',
  agentThemeDark: 'tamna',
  agentLookAt: 'Pogledaj v{number}',
  agentConfirmWrite: '{agent} želi zapisati {what}. Ništa se ne zapisuje dok ne odlučite.',
  agentConfirmGrant: '{agent} želi pristup: {what}. Ništa se ne dijeli dok ne odlučite.',
  agentReview: 'Pregledaj',
  agentSkipped: '{agent} je preskočio: {part}.',
  agentSkippedWhy:
    'Čita osobni sustav {system} svake osobe, a ovo izvođenje nema osobu u čije ime čita.',
  agentChooseWho: 'Odaberite u čije se ime izvodi',
  agentChangeInChat: 'Promijeni u razgovoru',
  agentFailed: '{agent} nije mogao osvježiti: {title}.',
  agentFailedBody: 'Zadnja objavljena verzija i dalje je aktivna.',
  agentRetry: 'Pokušajte ponovno',
  agentRunning: '{agent} ažurira: {title}.',
  agentMakesTitle: 'Izrađuje i ažurira',
  agentMakesLoading: 'Učitavanje…',
  agentMakesErrorBody: 'Ovo se nije moglo učitati.',
  agentMakesNone: 'Ovaj agent još ne izrađuje artefakte.',
  agentMakesNew: 'Novi artefakt pri svakom izvođenju',
  agentMakesUpdate: 'Ažurira jedan artefakt pri svakom izvođenju',
  agentNextRun: 'Sljedeće izvođenje: {when}',
  agentNoSchedule: 'Nije zakazano',
  agentCreditsPerRun: 'Krediti po izvođenju: oko {credits}',
  agentCreditsUnknown: 'Krediti po izvođenju još nisu poznati',
  agentLastResult: 'Zadnji rezultat',
  interviewTitle: 'Postavite',
  interviewIntro: 'Nekoliko pitanja oblikuje ga.',
  interviewOptional: '(neobavezno)',
  interviewAnswerNeeded: 'Odgovorite da biste nastavili.',
  interviewReadOnly: 'Samo za čitanje',
  interviewChange: 'Promijeni',
  interviewConnectionYours: 'vaša veza: {system}',
  interviewConnectionOther: 'veza korisnika {name}: {system}',
  interviewDateAny: 'Bilo koje razdoblje',
  interviewDatePrevious: 'Prethodni mjesec',
  interviewDateNext: 'Sljedeći mjesec',
  interviewDatePresets: 'Brzi rasponi',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Očisti',
  interviewDateDone: 'Gotovo',
  interviewChooseSource: 'Odaberite izvor',
  interviewSourceChosen: 'Izvor odabran',
  interviewSourceUnavailable: 'Odabir izvora ovdje još nije dostupan, pa se ne može nastaviti.',
  interviewSourceUnavailableOptional:
    'Odabir izvora ovdje još nije dostupan. Možete nastaviti i bez njega.',
  interviewStarterWithdrawn:
    'Ovaj predložak više nije dostupan. Nacrti koje ste već započeli iz njega i dalje se otvaraju.',
  interviewDateFrom: 'Od',
  interviewDateTo: 'Do',
  interviewContinue: 'Nastavi',
  interviewGrantTitle: 'Dopustiti čitanje: {source}?',
  interviewGrantBody:
    'Koristi {connection}, samo za čitanje, za {scope}. Ništa se ne čita dok ne dopustite i ne prikazuju se uzorci redaka.',
  interviewAllow: 'Dopusti',
  interviewNotNow: 'Ne sada',
  interviewConfiguring: 'Postavljanje prema vašim odgovorima…',
  interviewChecking: 'Provjera prije otvaranja…',
  interviewComplete: '{title} je spreman.',
  interviewOpen: 'Otvori: {title}',
  interviewBlocked: 'Ovo nije moglo nastaviti.',
  interviewFailed: 'Postavljanje nije uspjelo. Vaši su odgovori sačuvani.',
  interviewCancelled: 'Ovo je otkazano.',
  interviewSaving: 'Spremanje…',
  interviewSaved: 'Spremljeno',
  interviewConflict:
    'Ovaj je nacrt promijenjen drugdje. Vaši odgovori ostaju ovdje dok ne učitate trenutačni nacrt, koji ih zamjenjuje.',
  interviewReloadCurrent: 'Učitaj trenutačni nacrt',
  interviewUnsettled: 'Nismo mogli potvrditi vaše zadnje spremanje. Vaši odgovori ostaju ovdje.',
  interviewCheckAgain: 'Provjeri ponovno',
  finishAction: 'Spremi privatno',
  finishSaving: 'Spremanje…',
  finishSaved: '„{title}” je spremljen i vidite ga samo vi.',
  finishNotSaved: 'Nije spremljeno. Vaš nacrt je i dalje ovdje.',
  finishUnconfirmed: 'Nismo mogli potvrditi je li spremljeno.',
  candidateInvalid: 'Od nacrta nije bilo moguće napraviti dokument.',
  finishOpenInLibrary: 'Otvori u Knjižnici',
  savedOpenFailed: 'Trenutačno se nije moglo otvoriti.',
  savedInLibrary: 'Nalazi se u vašoj Knjižnici.',
  openSavedTitle: 'Otvori: {title} →',
  interviewReasonNotAuthorized: 'Nemate dopuštenje za promjenu ovog nacrta.',
  interviewReasonDraftNotOpen: 'Ovaj nacrt više nije otvoren.',
  interviewReasonInvalid: 'Jedan od odgovora nije valjan.',
  interviewReasonObjects: 'Neke se datoteke nisu mogle potvrditi. Vaši su odgovori sačuvani.',
  interviewReasonDependency: 'Potrebna usluga nije dostupna. Vaši su odgovori sačuvani.',
  interviewReasonReconcile: 'Zadnje se spremanje još potvrđuje.',
  exportTitle: 'Izvoz',
  exportIntro: 'Preuzmite ovaj artefakt. Datoteka navodi odakle dolaze njegovi podaci.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Slajdovi koji se mogu uređivati, s bilješkama govornika',
  exportHintPdf: 'Točno kako je prikazano',
  exportHintDocx: 'Može se uređivati, čuva naslove i tablice',
  exportHintXlsx: 'Svi listovi, čuva formule',
  exportHintCsv: 'Samo vrijednosti, UTF-8',
  exportUnavailable: 'Trenutačno nije dostupno',
  exportRunning: 'Priprema: {format}…',
  exportValidating: 'Provjera datoteke {format}…',
  exportReady: 'Datoteka {format} je spremna',
  exportDownload: 'Preuzmi {name}',
  exportVersion: 'Izvezena verzija {number}',
  exportFailed: 'Datoteka {format} nije se mogla izraditi.',
  exportFailedChecks: 'Datoteka {format} nije prošla provjere pa nije ponuđena.',
  exportDenied: 'Nemate pristup za izvoz ovog artefakta.',
  exportRevoked: 'Dijeljenje je isključeno pa se ovaj artefakt više ne može izvoziti.',
  exportStale:
    'Ovaj se artefakt promijenio tijekom izvoza. Izvezite ponovno da dobijete najnoviju verziju.',
  exportProvenanceTitle: 'Svaka datoteka sadrži',
  exportProvenanceLine:
    'Izrađeno u aplikaciji Poneva · {title} · v{number} · {data} · izrađeno {madeAt}',
  exportProvenanceAsOf: 'podaci na dan {time}',
  exportPlacementNotesAbout: 'Na listu Bilješke i u informativnoj datoteci uz CSV',
  exportProvenanceRange: 'podaci od {earliest} do {latest}',
  exportProvenanceUnknown: 'starost podataka nije poznata',
  exportAboutFile: 'O ovoj datoteci ({format})',
  exportPlacementFooter: 'U podnožju svake stranice',
  exportPlacementSlide: 'U podnožju svakog slajda',
  exportPlacementNotes: 'Na listu Bilješke',
  exportPlacementAbout: 'U informativnoj datoteci uz CSV',
  exportSourcesPage: 'Dodaj stranicu s izvorima',
  exportSourcesPageHint: 'Zadnja stranica koja navodi svaki broj, njegov izvor i vrijeme.',
  exportSourcesPageDeck: 'Prezentacijski slajd s izvorima ide uz to.',
  exportSourcesPageSaved: 'Zapamćeno za ovaj artefakt, uključujući zakazana slanja.',
  libraryKindSheetTitle: 'Vrsta',
  shareAccessSheetTitle: 'Pristup',

  requestShowAll: 'Prikaži sve',
  requestShowLess: 'Prikaži manje',
  approveGettingReady: 'Pripremamo…',
  approveNotReady:
    'Ovo trenutačno nije moguće odobriti. Ponovno učitajte stranicu i pokušajte ponovno.',
  tryWithModel: 'Pokušaj s modelom {model}',
  tryWithRequesting: 'Pokretanje…',
  tryWithUnavailable: 'Ovo se trenutačno ne može pokrenuti.',
};

const hu: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'A(z) {number}. verzió piszkozatát szerkeszti',
  noChangesToApply: 'Nincs alkalmazandó módosítás.',
  appliedDone: 'Alkalmazva.',
  mentionInChat: 'Említés a csevegésben',

  kindMarkdown: 'Jegyzet',
  kindKpi: 'Mutató',
  libraryExportsList: 'Exportálás: {formats}',
  libraryOpensToRead: 'Olvasásra nyílik meg',
  libraryReadOnly: 'Csak olvasható',
  libraryLiveView: 'Élő nézet',
  libraryOneNumber: 'Egy szám · {time} állapot szerint',
  retry: 'Próbálja újra',
  close: 'Bezárás',
  panelLoading: 'Betöltés…',
  panelEmpty: 'Itt még nincs semmi.',
  panelDenied: 'Nincs hozzáférése ehhez.',
  panelBlocked: 'Ez valamire vár, mielőtt folytatódhatna.',
  panelBlockedViewerContext:
    'Ennek a futtatásnak nincs olyan személye, akinek a jogaival olvasna, ezért nem folytatódhat, amíg valaki ki nem választja, kinek a nevében fusson.',
  panelError: 'Ezt nem sikerült betölteni.',
  panelStale: 'Ez elavult lehet.',
  sourcesTitle: 'Források',
  sourcesIntro: 'Honnan származik az egyes számok értéke, és mennyire friss.',
  sourcesLoading: 'Források betöltése…',
  sourcesEmptyTitle: 'Nincsenek források',
  sourcesEmptyBody: 'Ez az artefaktum nem használ céges adatokat.',
  sourcesErrorBody: 'A forrásokat nem sikerült betölteni.',
  sourcesDeniedTitle: 'Egyes források rejtettek',
  sourcesDeniedBody:
    'Nincs hozzáférése az itt használt {count} forráshoz. Kérje meg a tulajdonost, hogy ossza meg őket.',
  sourceDeniedName: 'Forrás, amelyet nem láthat',
  sourceAsOf: 'Állapot: {time}',
  sourceAgeUnknown: 'Az adatok kora ismeretlen',
  sourceStatusCurrent: 'Naprakész',
  sourceStatusStale: 'Elavult',
  sourceStatusGap: 'Hiányzott a legutóbbi frissítésből',
  sourceStatusDenied: 'Nincs hozzáférés',
  sourceLastGood: 'Utolsó működő: {time}',
  sourceUsedBy: 'Használja: {labels}',
  openDocument: 'Dokumentum megnyitása',
  versionsShow: 'Verziók megjelenítése',
  versionsHide: 'Verziók elrejtése',
  versionsTitle: 'Verziók',
  versionsLoading: 'Verziók betöltése…',
  versionsEmptyTitle: 'Még nincsenek verziók',
  versionsEmptyBody: 'Az első verzió akkor jelenik meg, amikor ez az artefaktum elkészül.',
  versionsErrorBody: 'A verziókat nem sikerült betölteni.',
  versionLabel: '{number}. verzió',
  versionOpen: '{number}. verzió megnyitása',
  versionCurrent: 'Jelenlegi',
  versionPublished: 'Közzétéve',
  versionBy: 'Készítette: {actor}',
  versionOriginNative: 'A Ponevában',
  versionOriginMcp: 'saját ügynökén keresztül',
  versionOriginTerminal: 'a terminálon keresztül',
  versionOriginJob: 'Ütemezett futtatás',
  versionCausePerson: 'Szerkesztve',
  versionCauseAgent: 'Ügynök általi frissítés',
  versionCauseRefresh: 'Frissítve',
  versionCauseRestore: 'Visszaállítva',
  versionCauseImport: 'Importálva',
  versionCheckedYes: 'Ellenőrizve',
  versionCheckedNo: 'Még nincs ellenőrizve',
  versionCheckedInvalidated: 'Az utolsó ellenőrzés óta szerkesztve',
  compareStart: 'Összehasonlítás',
  compareWith: 'Összehasonlítás a(z) {number}. verzióval',
  compareTitle: 'Mi változott a v{from} és a v{to} között',
  compareFromMark: 'Honnan',
  compareToMark: 'Hová',
  comparePick: 'Válasszon egy második verziót az összehasonlításhoz.',
  compareNone: 'Nincs eltérés.',
  compareLoading: 'Összehasonlítás…',
  compareError: 'Az összehasonlítást nem sikerült betölteni.',
  compareBefore: 'Előtte',
  compareAfter: 'Utána',
  compareAdded: 'Hozzáadva',
  compareRemoved: 'Eltávolítva',
  compareChanged: 'Módosítva',
  restore: 'Verzió visszaállítása',
  restoreConfirmTitle: 'Visszaállítja a(z) {number}. verziót?',
  restoreConfirmBody: 'Ez új verziót hoz létre a(z) {number}. verzióból. Semmi sem törlődik.',
  restoreConfirm: 'Visszaállítás',
  restoreCancel: 'Mégse',
  restoring: 'Visszaállítás…',
  restoreFailed: 'A verziót nem sikerült visszaállítani.',
  restoreStale:
    'Ez az artefaktum megváltozott, miközben nézte. Töltse be újra a verziókat, és próbálja újra.',
  restoreDenied: 'Nincs jogosultsága verziók visszaállítására.',
  restored: 'A(z) {number}. verzió új verzióként visszaállítva.',
  publishTitle: 'Közzététel',
  publishIntro: 'A(z) {number}. verziót látni fogja: {audience}.',
  publishOwnerLine: 'A megtekintők azt látják, amit {owner} láthatott a verzió közzétételekor.',
  publishChecksTitle: 'Ellenőrzések',
  checkPassed: 'Ellenőrizve',
  checkRunning: 'Ellenőrzés…',
  checkFailed: 'Egy ellenőrzés sikertelen volt',
  checkStale: 'Az utolsó ellenőrzés óta szerkesztve',
  publishFixInChat: 'Javítás a csevegésben',
  publishAction: 'A(z) {number}. verzió közzététele',
  publishing: 'Közzététel…',
  publishedDone: 'A(z) {number}. verzió élő: {audience}.',
  publishConfirmGrant:
    'Ehhez a verzióhoz új hozzáférés kell egy céges rendszerhez. Közzététel előtt nézze át a hozzáférést.',
  publishConfirmWrite:
    'Ez a verzió egy céges rendszerbe ír. Közzététel előtt nézze át a hozzáférést.',
  publishConfirmDestructive: 'Ez a verzió adatokat töröl. Előbb erősítse meg a Ponevában.',
  publishConflict: 'Előbb egy másik verziót tettek közzé. Töltse be újra, hogy lássa.',
  publishBlockedNotAuthorized: 'Nincs jogosultsága a közzétételhez.',
  publishBlockedCheckFailed: 'Egy ellenőrzés sikertelen volt, ezért ez a verzió nem tehető közzé.',
  publishBlockedCheckStale:
    'Az artefaktum az utolsó ellenőrzés után megváltozott. Ellenőrizze újra.',
  publishBlockedObjects: 'Néhány fájlt nem sikerült ellenőrizni. Próbálja újra egy perc múlva.',
  publishBlockedPolicy: 'A céges beállítások megváltoztak. Nézze át, és próbálja újra.',
  publishBlockedApproval: 'A megerősítés már nem érvényes. Erősítse meg újra.',
  publishBlockedDependency: 'Egy szükséges szolgáltatás nem érhető el. Próbálja újra később.',
  publishAsk: 'Kérje a közzétételt: {publishers}',
  publishAskFooter: 'Közzétehet: {publishers}.',
  publishAskNone: 'Kérjen meg valakit, aki közzétehet.',
  publishWarnings: 'Érdemes átnézni közzététel előtt',
  checkWarnStale: '{subject} elavult ({time}).',
  checkWarnGap: '{subject} hiányzott a legutóbbi frissítésből.',
  checkWarnUnknownSource: 'Egy forrás, amelyet nem láthat, figyelmet igényel.',
  checkRefreshFirst: 'Előbb frissítsen',
  checkFailedRow: '{subject} nem felelt meg az ellenőrzésen.',
  checkFailedGeneric: 'Egy ellenőrzés sikertelen volt.',
  publishReviewAccess: 'Hozzáférés áttekintése',
  publishRemovalBody:
    'A v{number} eltávolítja: {what} ({count}). Előtte visszaállítási pont készül.',
  publishReviewRemoval: 'Az eltávolítandók áttekintése',
  publishAndRemove: 'Közzététel és eltávolítás',
  publishReviewFirst: 'Közzététel előtt nézze át, mi kerül eltávolításra.',
  publishDoneTitle: 'A(z) {number}. verzió élő: {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'A Könyvtárukban találják meg, és a csapat számára ki lesz tűzve.',
  publishUndo: 'Visszavonás',
  publishCopyLink: 'Hivatkozás másolása',
  publishDone: 'Kész',
  shareRoleViewMeaning: 'Megnyithatja és elolvashatja',
  shareRoleEditMeaning: 'Módosíthatja a csevegésben és az ellenőrzővel',
  shareRolePublishMeaning: 'Közzéteheti és visszavonhatja a közzétételt is',
  shareRoleMenu: 'Szerepkör: {name}',
  shareUnpublishConfirm:
    'Az emberek elveszítik a hozzáférést. Az artefaktum és a verziói megmaradnak.',
  shareUnpublishCancel: 'Maradjon élő',
  sendDestinationLine: '{provider} · {count} személy',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Mindenki látja az összefoglalót itt: {destination}. A {total} személyből {outside} nem tartozik az artefaktum közönségéhez, ezért a hivatkozás nekik a „Hozzáférés kérése” szöveget mutatja.',
  sendAudienceGeneric: 'A közönségen kívüliek a „Hozzáférés kérése” szöveget látják.',
  sendPreview: 'Amit látni fognak',
  shareTitle: 'Megosztás',
  shareLoading: 'Megosztás betöltése…',
  shareErrorBody: 'A megosztást nem sikerült betölteni.',
  sharePrivate: 'Ezt az artefaktumot csak Ön látja.',
  shareShared: 'Látható: {audience}.',
  shareRevoked: 'A megosztás ki van kapcsolva. Senki más nem nyithatja meg ezt az artefaktumot.',
  shareOwnerSnapshot:
    'A megtekintők a számokat úgy látják, ahogy közzétették. Nem frissíthetnek és nem szerkeszthetnek.',
  shareRoleView: 'Megtekinthet',
  shareRoleEdit: 'Szerkeszthet',
  shareRolePublish: 'Közzétehet',
  shareLivePublished: 'Élő: {number}. verzió',
  shareUnpublish: 'Közzététel visszavonása',
  shareManageDenied: 'Csak szerkesztő módosíthatja, ki láthatja ezt.',
  sendTitle: 'Küldés ide',
  sendIntro:
    'Küldjön hivatkozást és összefoglalót egy csatlakoztatott alkalmazáson át. Minden küldést Ön hagy jóvá.',
  sendLoading: 'Célhelyek betöltése…',
  sendErrorBody: 'A célhelyeket nem sikerült betölteni.',
  sendNone:
    'Még egyetlen csatlakoztatott alkalmazás sem fogadhatja ezt. Előbb csatlakoztassa a Slacket, a Teamset vagy az e-mailt.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-mail',
  sendAudience: 'Címzett: {audience}',
  sendSummary: 'Üzenet',
  sendLeaves: 'Egy hivatkozás kerül elküldésre.',
  sendLeavesAttachment: 'Egy hivatkozás és az átnézett fájl kerül elküldésre.',
  sendNoAttachments: 'Ez a célhely nem fogad fájlokat, ezért csak hivatkozás kerül elküldésre.',
  sendScheduled: 'Az ütemezett küldésekhez minden alkalommal jóváhagyás kell.',
  sendAction: 'Átnézés és küldés',
  sendFileUnsupported:
    'Ez a célhely nem fogadja a fájlt. Küldjön csak hivatkozást, vagy válasszon másik célhelyet.',
  sendLinkOnly: 'Csak hivatkozás küldése',
  sendWaiting: 'Az Ön jóváhagyására vár. Még semmi sem lett elküldve.',
  sendDelivered: 'Elküldve ide: {destination}.',
  sendFailed: 'Nem lett elküldve. Semmi sem hagyta el a Ponevát.',
  sendUnknown:
    'A kézbesítést ide nem sikerült megerősíteni: {destination}. Újabb küldés előtt ellenőrizze ott.',
  sendBlockedNotAuthorized: 'Nincs jogosultsága ennek elküldéséhez.',
  sendBlockedUnsupported: 'Ez a célhely nem támogatott.',
  sendBlockedApproval: 'A jóváhagyás már nem érvényes. Nézze át, és küldje újra.',
  sendBlockedChanged: 'Az üzenet vagy a címzett megváltozott. Nézze át újra.',
  sendBlockedDependency: 'A csatlakoztatott alkalmazás nem érhető el. Próbálja újra később.',
  libraryTitle: 'Könyvtár',
  librarySegmentAll: 'Mind',
  librarySegmentMine: 'Saját',
  librarySegmentShared: 'Velem megosztva',
  librarySearch: 'Artefaktumok keresése',
  libraryKindAll: 'Minden fajta',
  libraryKindReports: 'Jelentések és oldalak',
  libraryKindDecks: 'Prezentációk',
  libraryKindDocuments: 'Dokumentumok',
  libraryKindSpreadsheets: 'Táblázatok',
  libraryKindApps: 'Alkalmazások',
  libraryAppsMatchNoun:
    'one:alkalmazás egyezik itt: Alkalmazások →|other:alkalmazás egyezik itt: Alkalmazások →',
  libraryMake: 'Kezdés a csevegésben',
  libraryEmptySharedHint: 'Ha valaki megoszt Önnel egy artefaktumot, itt jelenik meg.',
  libraryRefreshFailedAt: 'Nem sikerült frissíteni ekkor: {time}.',
  libraryRefreshTryAgain: 'Próbálja újra',
  libraryVia: '{owner} révén',
  savedPrivate: 'Privát',
  savedVersion: '{n}. verzió',
  libraryLoading: 'A Könyvtár betöltése…',
  libraryErrorBody: 'A Könyvtárat jelenleg nem sikerült betölteni.',
  startersLoadFailed: 'A sablonokat most nem sikerült betölteni.',
  libraryCannotOpen: '„{title}” itt még nem nyitható meg.',
  libraryEmptyTitle: 'Itt még nincs semmi',
  libraryEmptyAllTitle: 'Készítsen valamit, amit érdemes megosztani.',
  libraryEmptyAllBody:
    'A csevegésben készített vagy Önnel megosztott dokumentumok, bemutatók és alkalmazások itt jelennek meg.',
  libraryEmptyMineBody: 'Az Ön által készített artefaktumok itt jelennek meg.',
  libraryEmptySharedBody: 'Még semmit sem osztottak meg Önnel.',
  libraryNoMatchTitle: 'Nincs találat',
  libraryNoMatchBody: 'Próbáljon másik szót vagy más fajtát.',
  libraryMore: 'Továbbiak megjelenítése',
  libraryOwner: 'Készítette: {owner}',
  libraryUpdated: 'Szerkesztve: {time}',
  libraryPublishedAs: 'Közzétéve · v{number}',
  libraryDraft: 'Piszkozat',
  libraryUnpublished: 'Nincs közzétéve',
  libraryRefreshFailed: 'A frissítés sikertelen',
  libraryRefreshRunning: 'Frissítés folyamatban',
  libraryAudience: 'Közönség: {audience}',
  kindReport: 'Jelentés',
  kindPage: 'Oldal',
  kindSite: 'Webhely',
  kindDeck: 'Prezentáció',
  kindDocument: 'Dokumentum',
  kindApp: 'Alkalmazás',
  kindSpreadsheet: 'Táblázat',
  kindDashboard: 'Irányítópult',
  kindTable: 'Tábla',
  kindChart: 'Diagram',
  kindExportsReport: 'Exportálható PDF-be',
  kindExportsPage: 'Csak olvasható oldal',
  kindExportsSite: 'Csak olvasható webhely',
  kindExportsDeck: 'Vetíthető, vagy exportálható PowerPointba és PDF-be',
  kindExportsDocument: 'Exportálható Wordbe és PDF-be',
  kindExportsSpreadsheet: 'Exportálható Excelbe és CSV-be',
  kindExportsDashboard: 'Élő nézet',
  kindExportsTable: 'Exportálható Excelbe és CSV-be',
  kindExportsChart: 'Diagramnézet',
  agentPublished: '{agent} közzétette: {title} v{number}.',
  agentCheckedOn: 'Ellenőrizve ezeken: {views}, {themes}.',
  agentUndo: 'Visszavonás',
  agentRestorePrevious: 'A v{number} visszaállítása',
  agentHeld: '{title} v{number} piszkozatként vár.',
  agentHeldMismatch: '{count} szám nem egyezett a forrásával.',
  agentHeldCheck: 'Egy ellenőrzés nem sikerült.',
  agentFixInChat: 'Javítás a csevegésben',
  agentConfirmDrop:
    '{agent} el szeretné távolítani ezt: {what} ebből: {title}. Előtte visszaállítási pont készül. Semmi sem változik, amíg Ön nem dönt.',
  agentConfirmDropCount:
    '{agent} el szeretné távolítani ezt: {what} ({count} érték) ebből: {title}. Előtte visszaállítási pont készül. Semmi sem változik, amíg Ön nem dönt.',
  agentViewDesktop: 'asztali',
  agentViewTablet: 'táblagép',
  agentViewPhone: 'telefon',
  agentThemeLight: 'világos',
  agentThemeDark: 'sötét',
  agentLookAt: 'A v{number} megtekintése',
  agentConfirmWrite: '{agent} írni szeretne ide: {what}. Semmi sem íródik, amíg Ön nem dönt.',
  agentConfirmGrant:
    '{agent} hozzáférést kér ehhez: {what}. Semmi sem lesz megosztva, amíg Ön nem dönt.',
  agentReview: 'Áttekintés',
  agentSkipped: '{agent} kihagyta ezt: {part}.',
  agentSkippedWhy:
    'Minden személy saját {system} rendszeréből olvas, és ennek a futtatásnak nincs olyan személye, akinek a jogaival olvasna.',
  agentChooseWho: 'Válassza ki, kinek a nevében fusson',
  agentChangeInChat: 'Módosítás a csevegésben',
  agentFailed: '{agent} nem tudta frissíteni ezt: {title}.',
  agentFailedBody: 'Az utoljára közzétett verzió továbbra is él.',
  agentRetry: 'Próbálja újra',
  agentRunning: '{agent} frissíti ezt: {title}.',
  agentMakesTitle: 'Készít és frissít',
  agentMakesLoading: 'Betöltés…',
  agentMakesErrorBody: 'Ezt nem sikerült betölteni.',
  agentMakesNone: 'Ez az ügynök még nem készít artefaktumokat.',
  agentMakesNew: 'Minden futtatáskor új artefaktum',
  agentMakesUpdate: 'Minden futtatáskor egy artefaktumot frissít',
  agentNextRun: 'Következő futtatás: {when}',
  agentNoSchedule: 'Nincs ütemezve',
  agentCreditsPerRun: 'Futtatásonként kb. {credits} kredit',
  agentCreditsUnknown: 'A futtatásonkénti kreditek még nem ismertek',
  agentLastResult: 'Utolsó eredmény',
  interviewTitle: 'Beállítás',
  interviewIntro: 'Néhány kérdés alakítja ki.',
  interviewOptional: '(nem kötelező)',
  interviewAnswerNeeded: 'A folytatáshoz válaszoljon erre.',
  interviewReadOnly: 'Csak olvasható',
  interviewChange: 'Módosítás',
  interviewConnectionYours: 'az Ön {system}-kapcsolata',
  interviewConnectionOther: '{name} {system}-kapcsolata',
  interviewDateAny: 'Bármely időszak',
  interviewDatePrevious: 'Előző hónap',
  interviewDateNext: 'Következő hónap',
  interviewDatePresets: 'Gyors időszakok',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Törlés',
  interviewDateDone: 'Kész',
  interviewChooseSource: 'Válasszon forrást',
  interviewSourceChosen: 'Forrás kiválasztva',
  interviewSourceUnavailable:
    'A forrás kiválasztása itt még nem érhető el, ezért nem lehet folytatni.',
  interviewSourceUnavailableOptional:
    'A forrás kiválasztása itt még nem érhető el. Forrás nélkül is folytathatja.',
  interviewStarterWithdrawn:
    'Ez a sablon már nem érhető el. Az ebből már elkezdett piszkozatok továbbra is megnyithatók.',
  interviewDateFrom: 'Kezdete',
  interviewDateTo: 'Vége',
  interviewContinue: 'Tovább',
  interviewGrantTitle: 'Engedélyezi az olvasást: {source}?',
  interviewGrantBody:
    'A következő kapcsolatot használja: {connection}, csak olvasásra, erre: {scope}. Semmi sem lesz beolvasva, amíg Ön nem engedélyezi, és mintasorok nem jelennek meg.',
  interviewAllow: 'Engedélyezés',
  interviewNotNow: 'Most nem',
  interviewConfiguring: 'Beállítás a válaszai alapján…',
  interviewChecking: 'Ellenőrzés megnyitás előtt…',
  interviewComplete: '{title} elkészült.',
  interviewOpen: '{title} megnyitása',
  interviewBlocked: 'Ez nem tudott folytatódni.',
  interviewFailed: 'A beállítás nem sikerült. A válaszai megmaradnak.',
  interviewCancelled: 'Ez megszakadt.',
  interviewSaving: 'Mentés…',
  interviewSaved: 'Mentve',
  interviewConflict:
    'Ez a piszkozat máshol megváltozott. A válaszai itt megmaradnak, amíg be nem tölti a jelenlegi piszkozatot, amely felülírja őket.',
  interviewReloadCurrent: 'A jelenlegi piszkozat betöltése',
  interviewUnsettled: 'Az utolsó mentést nem sikerült megerősíteni. A válaszai itt megmaradnak.',
  interviewCheckAgain: 'Ellenőrzés újra',
  finishAction: 'Mentés privátként',
  finishSaving: 'Mentés…',
  finishSaved: '„{title}” mentve, csak Ön látja.',
  finishNotSaved: 'Nem lett mentve. A piszkozata továbbra is itt van.',
  finishUnconfirmed: 'Nem tudtuk megerősíteni, hogy mentésre került-e.',
  candidateInvalid: 'A piszkozatból nem sikerült dokumentumot készíteni.',
  finishOpenInLibrary: 'Megnyitás a Könyvtárban',
  savedOpenFailed: 'Most nem sikerült megnyitni.',
  savedInLibrary: 'Megtalálja a Könyvtárában.',
  openSavedTitle: '{title} megnyitása →',
  interviewReasonNotAuthorized: 'Nincs jogosultsága a piszkozat módosítására.',
  interviewReasonDraftNotOpen: 'Ez a piszkozat már nincs megnyitva.',
  interviewReasonInvalid: 'Az egyik válasz érvénytelen.',
  interviewReasonObjects: 'Néhány fájlt nem sikerült ellenőrizni. A válaszai megmaradnak.',
  interviewReasonDependency: 'Egy szükséges szolgáltatás nem érhető el. A válaszai megmaradnak.',
  interviewReasonReconcile: 'Az utolsó mentés megerősítése még folyamatban van.',
  exportTitle: 'Exportálás',
  exportIntro: 'Töltse le ezt az artefaktumot. A fájl megmondja, honnan származnak az adatai.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Szerkeszthető diák előadói jegyzetekkel',
  exportHintPdf: 'Pontosan úgy, ahogy látszik',
  exportHintDocx: 'Szerkeszthető, megtartja a címsorokat és táblázatokat',
  exportHintXlsx: 'Minden munkalap, megtartja a képleteket',
  exportHintCsv: 'Csak értékek, UTF-8',
  exportUnavailable: 'Jelenleg nem érhető el',
  exportRunning: '{format} előkészítése…',
  exportValidating: 'A(z) {format} fájl ellenőrzése…',
  exportReady: 'A(z) {format} fájl elkészült',
  exportDownload: '{name} letöltése',
  exportVersion: 'Exportált verzió: {number}',
  exportFailed: 'A(z) {format} fájlt nem sikerült elkészíteni.',
  exportFailedChecks:
    'A(z) {format} fájl nem felelt meg az ellenőrzéseken, ezért nem lett felajánlva.',
  exportDenied: 'Nincs hozzáférése az artefaktum exportálásához.',
  exportRevoked: 'A megosztás ki lett kapcsolva, ezért ez az artefaktum már nem exportálható.',
  exportStale:
    'Ez az artefaktum megváltozott az exportálás közben. Exportálja újra a legfrissebb verzióhoz.',
  exportProvenanceTitle: 'Minden fájl tartalmazza',
  exportProvenanceLine: 'Készült a Ponevával · {title} · v{number} · {data} · készült: {madeAt}',
  exportProvenanceAsOf: 'adatok állapota: {time}',
  exportPlacementNotesAbout: 'A Jegyzetek munkalapon és a CSV mellett lévő leírófájlban',
  exportProvenanceRange: 'adatok ebből az időszakból: {earliest} – {latest}',
  exportProvenanceUnknown: 'az adatok kora ismeretlen',
  exportAboutFile: 'A fájl adatai ({format})',
  exportPlacementFooter: 'Minden oldal láblécében',
  exportPlacementSlide: 'Minden dia láblécében',
  exportPlacementNotes: 'A Jegyzetek munkalapon',
  exportPlacementAbout: 'A CSV mellett lévő leírófájlban',
  exportSourcesPage: 'Forrásoldal hozzáadása',
  exportSourcesPageHint:
    'Egy utolsó oldal, amely felsorolja az egyes számokat, forrásukat és időpontjukat.',
  exportSourcesPageDeck: 'A prezentáció saját Források diája is bekerül.',
  exportSourcesPageSaved:
    'Megjegyezve ennél az artefaktumnál, az ütemezett küldéseket is beleértve.',
  libraryKindSheetTitle: 'Fajta',
  shareAccessSheetTitle: 'Hozzáférés',

  requestShowAll: 'Összes megjelenítése',
  requestShowLess: 'Kevesebb megjelenítése',
  approveGettingReady: 'Előkészítés…',
  approveNotReady: 'Ezt most nem lehet jóváhagyni. Töltse be újra az oldalt, majd próbálja újra.',
  tryWithModel: 'Kipróbálás ezzel: {model}',
  tryWithRequesting: 'Indítás…',
  tryWithUnavailable: 'Ezt most nem lehet elindítani.',
};

const it: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Stai modificando una bozza della versione {number}',
  noChangesToApply: 'Nessuna modifica da applicare.',
  appliedDone: 'Applicato.',
  mentionInChat: 'Cita nella chat',

  kindMarkdown: 'Nota',
  kindKpi: 'Metrica',
  libraryExportsList: 'Si esporta in {formats}',
  libraryOpensToRead: 'Si apre in lettura',
  libraryReadOnly: 'Sola lettura',
  libraryLiveView: 'Vista live',
  libraryOneNumber: 'Un numero · aggiornato al {time}',
  retry: 'Riprova',
  close: 'Chiudi',
  panelLoading: 'Caricamento…',
  panelEmpty: 'Non c’è ancora nulla.',
  panelDenied: 'Non hai accesso a questo contenuto.',
  panelBlocked: 'Questo è in attesa di qualcosa prima di poter continuare.',
  panelBlockedViewerContext:
    'Questa esecuzione non ha una persona di cui usare i permessi, quindi non può continuare finché qualcuno non sceglie per conto di chi viene eseguita.',
  panelError: 'Impossibile caricare questo contenuto.',
  panelStale: 'Potrebbe non essere aggiornato.',
  sourcesTitle: 'Fonti',
  sourcesIntro: 'Da dove viene ogni numero e quanto è recente.',
  sourcesLoading: 'Caricamento delle fonti…',
  sourcesEmptyTitle: 'Nessuna fonte',
  sourcesEmptyBody: 'Questo Artefatto non usa dati aziendali.',
  sourcesErrorBody: 'Impossibile caricare le fonti.',
  sourcesDeniedTitle: 'Alcune fonti sono nascoste',
  sourcesDeniedBody:
    'Non hai accesso a {count} fonti usate qui. Chiedi a un proprietario di condividerle.',
  sourceDeniedName: 'Fonte che non puoi vedere',
  sourceAsOf: 'Aggiornato a {time}',
  sourceAgeUnknown: 'Età dei dati sconosciuta',
  sourceStatusCurrent: 'Aggiornata',
  sourceStatusStale: 'Non aggiornata',
  sourceStatusGap: 'Mancante nell’ultimo aggiornamento',
  sourceStatusDenied: 'Nessun accesso',
  sourceLastGood: 'Ultima valida: {time}',
  sourceUsedBy: 'Usata da {labels}',
  openDocument: 'Apri documento',
  versionsShow: 'Mostra versioni',
  versionsHide: 'Nascondi versioni',
  versionsTitle: 'Versioni',
  versionsLoading: 'Caricamento delle versioni…',
  versionsEmptyTitle: 'Ancora nessuna versione',
  versionsEmptyBody: 'La prima versione appare quando questo Artefatto viene creato.',
  versionsErrorBody: 'Impossibile caricare le versioni.',
  versionLabel: 'Versione {number}',
  versionOpen: 'Apri la versione {number}',
  versionCurrent: 'Attuale',
  versionPublished: 'Pubblicata',
  versionBy: 'Di {actor}',
  versionOriginNative: 'In Poneva',
  versionOriginMcp: 'tramite il tuo agente',
  versionOriginTerminal: 'tramite il terminale',
  versionOriginJob: 'Esecuzione pianificata',
  versionCausePerson: 'Modificata',
  versionCauseAgent: 'Aggiornamento dell’agente',
  versionCauseRefresh: 'Aggiornata',
  versionCauseRestore: 'Ripristinata',
  versionCauseImport: 'Importata',
  versionCheckedYes: 'Controllata',
  versionCheckedNo: 'Non ancora controllata',
  versionCheckedInvalidated: 'Modificata dopo l’ultimo controllo',
  compareStart: 'Confronta',
  compareWith: 'Confronta con la versione {number}',
  compareTitle: 'Cosa è cambiato da v{from} a v{to}',
  compareFromMark: 'Da',
  compareToMark: 'A',
  comparePick: 'Scegli una seconda versione da confrontare.',
  compareNone: 'Nessuna differenza.',
  compareLoading: 'Confronto in corso…',
  compareError: 'Impossibile caricare il confronto.',
  compareBefore: 'Prima',
  compareAfter: 'Dopo',
  compareAdded: 'Aggiunto',
  compareRemoved: 'Rimosso',
  compareChanged: 'Modificato',
  restore: 'Ripristina questa versione',
  restoreConfirmTitle: 'Ripristinare la versione {number}?',
  restoreConfirmBody:
    'Verrà creata una nuova versione a partire dalla versione {number}. Non viene eliminato nulla.',
  restoreConfirm: 'Ripristina',
  restoreCancel: 'Annulla',
  restoring: 'Ripristino in corso…',
  restoreFailed: 'Impossibile ripristinare la versione.',
  restoreStale: 'Questo Artefatto è cambiato mentre lo guardavi. Ricarica le versioni e riprova.',
  restoreDenied: 'Non hai il permesso di ripristinare le versioni.',
  restored: 'Versione {number} ripristinata come nuova versione.',
  publishTitle: 'Pubblica',
  publishIntro: 'La versione {number} sarà visibile a {audience}.',
  publishOwnerLine:
    'Chi la visualizza vede ciò che {owner} poteva vedere al momento della pubblicazione di questa versione.',
  publishChecksTitle: 'Controlli',
  checkPassed: 'Controllata',
  checkRunning: 'Controllo in corso…',
  checkFailed: 'Un controllo non è riuscito',
  checkStale: 'Modificata dopo l’ultimo controllo',
  publishFixInChat: 'Correggi in chat',
  publishAction: 'Pubblica la versione {number}',
  publishing: 'Pubblicazione in corso…',
  publishedDone: 'La versione {number} è online per {audience}.',
  publishConfirmGrant:
    'Questa versione richiede un nuovo accesso a un sistema aziendale. Controlla l’accesso prima di pubblicare.',
  publishConfirmWrite:
    'Questa versione scrive in un sistema aziendale. Controlla l’accesso prima di pubblicare.',
  publishConfirmDestructive: 'Questa versione elimina dei dati. Conferma prima in Poneva.',
  publishConflict: 'Un’altra versione è stata pubblicata prima. Ricarica per vederla.',
  publishBlockedNotAuthorized: 'Non hai il permesso di pubblicare.',
  publishBlockedCheckFailed:
    'Un controllo non è riuscito, quindi questa versione non può essere pubblicata.',
  publishBlockedCheckStale: 'L’Artefatto è cambiato dopo l’ultimo controllo. Controllalo di nuovo.',
  publishBlockedObjects: 'Alcuni file non hanno potuto essere verificati. Riprova tra un momento.',
  publishBlockedPolicy: 'Le impostazioni aziendali sono cambiate. Controlla e riprova.',
  publishBlockedApproval: 'La conferma non è più valida. Conferma di nuovo.',
  publishBlockedDependency: 'Un servizio necessario non è disponibile. Riprova più tardi.',
  publishAsk: 'Chiedi a {publishers} di pubblicare',
  publishAskFooter: 'Persone che possono pubblicare: {publishers}.',
  publishAskNone: 'Chiedi a qualcuno che può pubblicare.',
  publishWarnings: 'Da controllare prima di pubblicare',
  checkWarnStale: '{subject} non è aggiornato ({time}).',
  checkWarnGap: '{subject} mancava nell’ultimo aggiornamento.',
  checkWarnUnknownSource: 'Una fonte che non puoi vedere richiede attenzione.',
  checkRefreshFirst: 'Aggiorna prima',
  checkFailedRow: '{subject} non ha superato il controllo.',
  checkFailedGeneric: 'Un controllo non è riuscito.',
  publishReviewAccess: 'Controlla l’accesso',
  publishRemovalBody:
    'v{number} elimina {what} ({count}). Prima viene salvato un punto di ripristino.',
  publishReviewRemoval: 'Controlla cosa viene eliminato',
  publishAndRemove: 'Pubblica ed elimina',
  publishReviewFirst: 'Controlla cosa viene eliminato prima di pubblicare.',
  publishDoneTitle: 'La versione {number} è online per {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'La troveranno nella loro Libreria e fissata in alto per il team.',
  publishUndo: 'Annulla',
  publishCopyLink: 'Copia link',
  publishDone: 'Fatto',
  shareRoleViewMeaning: 'Può aprirlo e leggerlo',
  shareRoleEditMeaning: 'Può modificarlo in chat e con l’ispettore',
  shareRolePublishMeaning: 'Può anche pubblicare e annullare la pubblicazione',
  shareRoleMenu: 'Ruolo per {name}',
  shareUnpublishConfirm:
    'Le persone perdono l’accesso. L’Artefatto e le sue versioni vengono conservati.',
  shareUnpublishCancel: 'Lascia online',
  sendDestinationLine: '{provider} · {count} persone',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Tutti in {destination} vedono il riepilogo. {outside} su {total} non fanno parte del pubblico di questo Artefatto, quindi il link mostra loro “Richiedi accesso”.',
  sendAudienceGeneric: 'Le persone fuori dal pubblico vedono “Richiedi accesso”.',
  sendPreview: 'Cosa vedranno',
  shareTitle: 'Condividi',
  shareLoading: 'Caricamento della condivisione…',
  shareErrorBody: 'Impossibile caricare la condivisione.',
  sharePrivate: 'Solo tu puoi vedere questo Artefatto.',
  shareShared: 'Visibile a {audience}.',
  shareRevoked: 'La condivisione è disattivata. Nessun altro può aprire questo Artefatto.',
  shareOwnerSnapshot:
    'Chi lo visualizza vede i numeri come sono stati pubblicati. Non può aggiornarli né modificarli.',
  shareRoleView: 'Può visualizzare',
  shareRoleEdit: 'Può modificare',
  shareRolePublish: 'Può pubblicare',
  shareLivePublished: 'Online: versione {number}',
  shareUnpublish: 'Annulla pubblicazione',
  shareManageDenied: 'Solo chi può modificare può cambiare chi lo vede.',
  sendTitle: 'Invia a',
  sendIntro: 'Invia un link e un riepilogo tramite un’app collegata. Approvi ogni invio.',
  sendLoading: 'Caricamento delle destinazioni…',
  sendErrorBody: 'Impossibile caricare le destinazioni.',
  sendNone: 'Nessuna app collegata può ancora riceverlo. Collega prima Slack, Teams o l’email.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'Email',
  sendAudience: 'Va a {audience}',
  sendSummary: 'Messaggio',
  sendLeaves: 'Viene inviato un link.',
  sendLeavesAttachment: 'Vengono inviati un link e il file controllato.',
  sendNoAttachments: 'Questa destinazione non accetta file, quindi viene inviato solo un link.',
  sendScheduled: 'Gli invii pianificati richiedono ogni volta la tua approvazione.',
  sendAction: 'Controlla e invia',
  sendFileUnsupported:
    'Questa destinazione non accetta il file. Invia solo un link o scegli un’altra destinazione.',
  sendLinkOnly: 'Invia solo un link',
  sendWaiting: 'In attesa della tua approvazione. Non è stato inviato nulla.',
  sendDelivered: 'Inviato a {destination}.',
  sendFailed: 'Non è stato inviato. Nulla è uscito da Poneva.',
  sendUnknown:
    'Non siamo riusciti a confermare la consegna a {destination}. Controlla lì prima di inviare di nuovo.',
  sendBlockedNotAuthorized: 'Non hai il permesso di inviarlo.',
  sendBlockedUnsupported: 'Questa destinazione non è supportata.',
  sendBlockedApproval: 'L’approvazione non è più valida. Controlla e invia di nuovo.',
  sendBlockedChanged: 'Il messaggio o il destinatario sono cambiati. Controllalo di nuovo.',
  sendBlockedDependency: 'L’app collegata non è disponibile. Riprova più tardi.',
  libraryTitle: 'Libreria',
  librarySegmentAll: 'Tutti',
  librarySegmentMine: 'Miei',
  librarySegmentShared: 'Condivisi con me',
  librarySearch: 'Cerca Artefatti',
  libraryKindAll: 'Tutti i tipi',
  libraryKindReports: 'Report e pagine',
  libraryKindDecks: 'Presentazioni',
  libraryKindDocuments: 'Documenti',
  libraryKindSpreadsheets: 'Fogli di calcolo',
  libraryKindApps: 'App',
  libraryAppsMatchNoun: 'one:App corrisponde in App →|other:App corrispondono in App →',
  libraryMake: 'Inizia in chat',
  libraryEmptySharedHint: 'Quando qualcuno condivide un Artefatto con te, appare qui.',
  libraryRefreshFailedAt: 'Impossibile aggiornare il {time}.',
  libraryRefreshTryAgain: 'Riprova',
  libraryVia: 'tramite {owner}',
  savedPrivate: 'Privato',
  savedVersion: 'Versione {n}',
  libraryLoading: 'Caricamento della tua Libreria…',
  libraryErrorBody: 'Impossibile caricare la tua Libreria in questo momento.',
  startersLoadFailed: 'Al momento non è stato possibile caricare i modelli.',
  libraryCannotOpen: 'Non è ancora possibile aprire «{title}» qui.',
  libraryEmptyTitle: 'Non c’è ancora nulla',
  libraryEmptyAllTitle: 'Crea qualcosa che valga la pena condividere.',
  libraryEmptyAllBody:
    'Documenti, presentazioni e app che crei in chat, o che altri condividono con te, compaiono qui.',
  libraryEmptyMineBody: 'Gli Artefatti che crei appaiono qui.',
  libraryEmptySharedBody: 'Non è stato ancora condiviso nulla con te.',
  libraryNoMatchTitle: 'Nessun risultato',
  libraryNoMatchBody: 'Prova un’altra parola o un tipo diverso.',
  libraryMore: 'Mostra altro',
  libraryOwner: 'Di {owner}',
  libraryUpdated: 'Modificato {time}',
  libraryPublishedAs: 'Pubblicato · v{number}',
  libraryDraft: 'Bozza',
  libraryUnpublished: 'Non pubblicato',
  libraryRefreshFailed: 'Aggiornamento non riuscito',
  libraryRefreshRunning: 'Aggiornamento in corso',
  libraryAudience: 'Per {audience}',
  kindReport: 'Report',
  kindPage: 'Pagina',
  kindSite: 'Sito',
  kindDeck: 'Presentazione',
  kindDocument: 'Documento',
  kindApp: 'App',
  kindSpreadsheet: 'Foglio di calcolo',
  kindDashboard: 'Dashboard',
  kindTable: 'Tabella',
  kindChart: 'Grafico',
  kindExportsReport: 'Esporta in PDF',
  kindExportsPage: 'Pagina di sola lettura',
  kindExportsSite: 'Sito di sola lettura',
  kindExportsDeck: 'Presenta oppure esporta in PowerPoint e PDF',
  kindExportsDocument: 'Esporta in Word e PDF',
  kindExportsSpreadsheet: 'Esporta in Excel e CSV',
  kindExportsDashboard: 'Vista in tempo reale',
  kindExportsTable: 'Esporta in Excel e CSV',
  kindExportsChart: 'Vista grafico',
  agentPublished: '{agent} ha pubblicato {title} v{number}.',
  agentCheckedOn: 'Controllato su {views}, {themes}.',
  agentUndo: 'Annulla',
  agentRestorePrevious: 'Ripristina v{number}',
  agentHeld: '{title} v{number} è in attesa come bozza.',
  agentHeldMismatch: '{count} numeri non corrispondevano alle loro fonti.',
  agentHeldCheck: 'Un controllo non è stato superato.',
  agentFixInChat: 'Correggi in chat',
  agentConfirmDrop:
    '{agent} vuole eliminare {what} da {title}. Prima viene salvato un punto di ripristino. Non cambia nulla finché non decidi.',
  agentConfirmDropCount:
    '{agent} vuole eliminare {what} ({count} valori) da {title}. Prima viene salvato un punto di ripristino. Non cambia nulla finché non decidi.',
  agentViewDesktop: 'desktop',
  agentViewTablet: 'tablet',
  agentViewPhone: 'telefono',
  agentThemeLight: 'chiaro',
  agentThemeDark: 'scuro',
  agentLookAt: 'Guarda v{number}',
  agentConfirmWrite: '{agent} vuole scrivere {what}. Non viene scritto nulla finché non decidi.',
  agentConfirmGrant:
    '{agent} vuole accedere a {what}. Non viene condiviso nulla finché non decidi.',
  agentReview: 'Controlla',
  agentSkipped: '{agent} ha saltato {part}.',
  agentSkippedWhy:
    'Legge i dati {system} di ogni persona, e questa esecuzione non ha una persona di cui usare i permessi.',
  agentChooseWho: 'Scegli per conto di chi viene eseguita',
  agentChangeInChat: 'Modifica in chat',
  agentFailed: '{agent} non è riuscito ad aggiornare {title}.',
  agentFailedBody: 'L’ultima versione pubblicata è ancora online.',
  agentRetry: 'Riprova',
  agentRunning: '{agent} sta aggiornando {title}.',
  agentMakesTitle: 'Crea e aggiorna',
  agentMakesLoading: 'Caricamento…',
  agentMakesErrorBody: 'Impossibile caricare questo contenuto.',
  agentMakesNone: 'Questo Agente non crea ancora Artefatti.',
  agentMakesNew: 'Un nuovo Artefatto a ogni esecuzione',
  agentMakesUpdate: 'Aggiorna un Artefatto a ogni esecuzione',
  agentNextRun: 'Prossima esecuzione {when}',
  agentNoSchedule: 'Non pianificato',
  agentCreditsPerRun: 'Circa {credits} crediti a esecuzione',
  agentCreditsUnknown: 'I crediti per esecuzione non sono ancora noti',
  agentLastResult: 'Ultimo risultato',
  interviewTitle: 'Configuralo',
  interviewIntro: 'Poche domande per darne forma.',
  interviewOptional: '(facoltativo)',
  interviewAnswerNeeded: 'Rispondi per continuare.',
  interviewReadOnly: 'Sola lettura',
  interviewChange: 'Cambia',
  interviewConnectionYours: 'la tua connessione {system}',
  interviewConnectionOther: 'la connessione {system} di {name}',
  interviewDateAny: 'Qualsiasi periodo',
  interviewDatePrevious: 'Mese precedente',
  interviewDateNext: 'Mese successivo',
  interviewDatePresets: 'Intervalli rapidi',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Cancella',
  interviewDateDone: 'Fatto',
  interviewChooseSource: 'Scegli una fonte',
  interviewSourceChosen: 'Fonte scelta',
  interviewSourceUnavailable:
    'La scelta di una fonte non è ancora disponibile qui, quindi non si può continuare.',
  interviewSourceUnavailableOptional:
    'La scelta di una fonte non è ancora disponibile qui. Puoi continuare senza.',
  interviewStarterWithdrawn:
    'Questo modello non è più disponibile. Le bozze che hai già iniziato da questo si aprono ancora.',
  interviewDateFrom: 'Da',
  interviewDateTo: 'A',
  interviewContinue: 'Continua',
  interviewGrantTitle: 'Consentire la lettura di {source}?',
  interviewGrantBody:
    'Usa {connection}, in sola lettura, per {scope}. Non viene letto nulla finché non lo consenti e non vengono mostrate righe di esempio.',
  interviewAllow: 'Consenti',
  interviewNotNow: 'Non ora',
  interviewConfiguring: 'Configurazione in base alle tue risposte…',
  interviewChecking: 'Controllo prima dell’apertura…',
  interviewComplete: '{title} è pronto.',
  interviewOpen: 'Apri {title}',
  interviewBlocked: 'Non è stato possibile continuare.',
  interviewFailed: 'Configurazione non riuscita. Le tue risposte sono state conservate.',
  interviewCancelled: 'È stato annullato.',
  interviewSaving: 'Salvataggio…',
  interviewSaved: 'Salvato',
  interviewConflict:
    'Questa bozza è cambiata altrove. Le tue risposte restano qui finché non carichi la bozza attuale, che le sostituisce.',
  interviewReloadCurrent: 'Carica la bozza attuale',
  interviewUnsettled:
    'Non siamo riusciti a confermare l’ultimo salvataggio. Le tue risposte restano qui.',
  interviewCheckAgain: 'Controlla di nuovo',
  finishAction: 'Salva in privato',
  finishSaving: 'Salvataggio…',
  finishSaved: '«{title}» è salvato, visibile solo a te.',
  finishNotSaved: 'Non è stato salvato. La tua bozza è ancora qui.',
  finishUnconfirmed: 'Non siamo riusciti a confermare se è stato salvato.',
  candidateInvalid: 'Non è stato possibile trasformare la bozza in un documento.',
  finishOpenInLibrary: 'Apri nella Libreria',
  savedOpenFailed: 'Al momento non è stato possibile aprirlo.',
  savedInLibrary: 'Si trova nella tua Libreria.',
  openSavedTitle: 'Apri {title} →',
  interviewReasonNotAuthorized: 'Non hai il permesso di modificare questa bozza.',
  interviewReasonDraftNotOpen: 'Questa bozza non è più aperta.',
  interviewReasonInvalid: 'Una delle risposte non è valida.',
  interviewReasonObjects:
    'Alcuni file non hanno potuto essere verificati. Le tue risposte sono state conservate.',
  interviewReasonDependency:
    'Un servizio necessario non è disponibile. Le tue risposte sono state conservate.',
  interviewReasonReconcile: 'L’ultimo salvataggio è ancora in fase di conferma.',
  exportTitle: 'Esporta',
  exportIntro: 'Scarica questo Artefatto. Il file indica da dove provengono i dati.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Diapositive modificabili con note del relatore',
  exportHintPdf: 'Esattamente come appare',
  exportHintDocx: 'Modificabile, mantiene titoli e tabelle',
  exportHintXlsx: 'Tutti i fogli, mantiene le formule',
  exportHintCsv: 'Solo valori, UTF-8',
  exportUnavailable: 'Non disponibile al momento',
  exportRunning: 'Preparazione di {format}…',
  exportValidating: 'Controllo del file {format}…',
  exportReady: 'Il file {format} è pronto',
  exportDownload: 'Scarica {name}',
  exportVersion: 'Versione {number} esportata',
  exportFailed: 'Impossibile creare il file {format}.',
  exportFailedChecks: 'Il file {format} non ha superato i controlli, quindi non è stato offerto.',
  exportDenied: 'Non hai accesso per esportare questo Artefatto.',
  exportRevoked:
    'La condivisione è stata disattivata, quindi questo Artefatto non può più essere esportato.',
  exportStale:
    'Questo Artefatto è cambiato durante l’esportazione. Esporta di nuovo per ottenere la versione più recente.',
  exportProvenanceTitle: 'Ogni file include',
  exportProvenanceLine: 'Creato con Poneva · {title} · v{number} · {data} · creato {madeAt}',
  exportProvenanceAsOf: 'dati aggiornati a {time}',
  exportPlacementNotesAbout: 'Nel foglio Note e in un file informativo accanto al CSV',
  exportProvenanceRange: 'dati da {earliest} a {latest}',
  exportProvenanceUnknown: 'età dei dati sconosciuta',
  exportAboutFile: 'Informazioni su questo file ({format})',
  exportPlacementFooter: 'Nel piè di pagina di ogni pagina',
  exportPlacementSlide: 'Nel piè di pagina di ogni diapositiva',
  exportPlacementNotes: 'Nel foglio Note',
  exportPlacementAbout: 'In un file informativo accanto al CSV',
  exportSourcesPage: 'Aggiungi una pagina delle fonti',
  exportSourcesPageHint: 'Una pagina finale che elenca ogni numero, la sua fonte e la sua data.',
  exportSourcesPageDeck: 'La diapositiva Fonti della presentazione viene inclusa.',
  exportSourcesPageSaved: 'Ricordato per questo Artefatto, compresi gli invii pianificati.',
  libraryKindSheetTitle: 'Tipo',
  shareAccessSheetTitle: 'Accesso',

  requestShowAll: 'Mostra tutto',
  requestShowLess: 'Mostra meno',
  approveGettingReady: 'Preparazione in corso…',
  approveNotReady: 'Al momento non è possibile approvare. Ricarica la pagina e riprova.',
  tryWithModel: 'Prova con {model}',
  tryWithRequesting: 'Avvio in corso…',
  tryWithUnavailable: 'Non è possibile avviarlo in questo momento.',
};

const lt: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Redaguojate {number} versijos juodraštį',
  noChangesToApply: 'Nėra pakeitimų, kuriuos būtų galima pritaikyti.',
  appliedDone: 'Pritaikyta.',
  mentionInChat: 'Paminėti pokalbyje',

  kindMarkdown: 'Pastaba',
  kindKpi: 'Rodiklis',
  libraryExportsList: 'Eksportuojama į {formats}',
  libraryOpensToRead: 'Atidaroma skaityti',
  libraryReadOnly: 'Tik skaitymui',
  libraryLiveView: 'Gyvas rodinys',
  libraryOneNumber: 'Vienas skaičius · {time}',
  retry: 'Bandyti dar kartą',
  close: 'Uždaryti',
  panelLoading: 'Įkeliama…',
  panelEmpty: 'Čia kol kas nieko nėra.',
  panelDenied: 'Neturite prieigos prie šio elemento.',
  panelBlocked: 'Prieš tęsiant reikia šiek tiek palaukti.',
  panelBlockedViewerContext:
    'Šiam vykdymui nenurodytas asmuo, kurio vardu skaityti, todėl jis negali tęstis, kol kas nors nepasirinks, kieno vardu jis veikia.',
  panelError: 'Šio elemento nepavyko įkelti.',
  panelStale: 'Tai gali būti pasenę.',
  sourcesTitle: 'Šaltiniai',
  sourcesIntro: 'Iš kur kiekvienas skaičius ir kaip jis šviežias.',
  sourcesLoading: 'Įkeliami šaltiniai…',
  sourcesEmptyTitle: 'Šaltinių nėra',
  sourcesEmptyBody: 'Šis artefaktas nenaudoja įmonės duomenų.',
  sourcesErrorBody: 'Šaltinių nepavyko įkelti.',
  sourcesDeniedTitle: 'Kai kurie šaltiniai paslėpti',
  sourcesDeniedBody:
    'Neturite prieigos prie šių čia naudojamų šaltinių: {count}. Paprašykite savininko jais pasidalyti.',
  sourceDeniedName: 'Šaltinis, kurio nematote',
  sourceAsOf: 'Duomenys: {time}',
  sourceAgeUnknown: 'Duomenų amžius nežinomas',
  sourceStatusCurrent: 'Aktualus',
  sourceStatusStale: 'Pasenęs',
  sourceStatusGap: 'Trūko paskutiniame atnaujinime',
  sourceStatusDenied: 'Nėra prieigos',
  sourceLastGood: 'Paskutinis tinkamas: {time}',
  sourceUsedBy: 'Naudoja: {labels}',
  openDocument: 'Atidaryti dokumentą',
  versionsShow: 'Rodyti versijas',
  versionsHide: 'Slėpti versijas',
  versionsTitle: 'Versijos',
  versionsLoading: 'Įkeliamos versijos…',
  versionsEmptyTitle: 'Versijų dar nėra',
  versionsEmptyBody: 'Pirmoji versija atsiras, kai šis artefaktas bus sukurtas.',
  versionsErrorBody: 'Versijų nepavyko įkelti.',
  versionLabel: 'Versija {number}',
  versionOpen: 'Atidaryti versiją {number}',
  versionCurrent: 'Dabartinė',
  versionPublished: 'Paskelbta',
  versionBy: 'Autorius: {actor}',
  versionOriginNative: 'Programoje Poneva',
  versionOriginMcp: 'per jūsų pačių agentą',
  versionOriginTerminal: 'per terminalą',
  versionOriginJob: 'Suplanuotas vykdymas',
  versionCausePerson: 'Redaguota',
  versionCauseAgent: 'Agento atnaujinimas',
  versionCauseRefresh: 'Atnaujinta duomenimis',
  versionCauseRestore: 'Atkurta',
  versionCauseImport: 'Importuota',
  versionCheckedYes: 'Patikrinta',
  versionCheckedNo: 'Dar nepatikrinta',
  versionCheckedInvalidated: 'Redaguota po paskutinio patikrinimo',
  compareStart: 'Palyginti',
  compareWith: 'Palyginti su versija {number}',
  compareTitle: 'Kas pasikeitė nuo v{from} iki v{to}',
  compareFromMark: 'Iš',
  compareToMark: 'Į',
  comparePick: 'Pasirinkite antrą versiją palyginimui.',
  compareNone: 'Skirtumų nėra.',
  compareLoading: 'Lyginama…',
  compareError: 'Palyginimo nepavyko įkelti.',
  compareBefore: 'Prieš',
  compareAfter: 'Po',
  compareAdded: 'Pridėta',
  compareRemoved: 'Pašalinta',
  compareChanged: 'Pakeista',
  restore: 'Atkurti šią versiją',
  restoreConfirmTitle: 'Atkurti versiją {number}?',
  restoreConfirmBody: 'Iš versijos {number} bus sukurta nauja versija. Nieko neištrinama.',
  restoreConfirm: 'Atkurti',
  restoreCancel: 'Atšaukti',
  restoring: 'Atkuriama…',
  restoreFailed: 'Versijos nepavyko atkurti.',
  restoreStale:
    'Šis artefaktas pasikeitė, kol jį žiūrėjote. Iš naujo įkelkite versijas ir bandykite dar kartą.',
  restoreDenied: 'Neturite leidimo atkurti versijų.',
  restored: 'Versija {number} atkurta kaip nauja versija.',
  publishTitle: 'Skelbti',
  publishIntro: 'Versiją {number} matys: {audience}.',
  publishOwnerLine:
    'Žiūrintieji mato tai, ką {owner} galėjo matyti, kai ši versija buvo paskelbta.',
  publishChecksTitle: 'Patikrinimai',
  checkPassed: 'Patikrinta',
  checkRunning: 'Tikrinama…',
  checkFailed: 'Patikrinimas nepavyko',
  checkStale: 'Redaguota po paskutinio patikrinimo',
  publishFixInChat: 'Taisyti pokalbyje',
  publishAction: 'Skelbti versiją {number}',
  publishing: 'Skelbiama…',
  publishedDone: 'Versija {number} paskelbta: {audience}.',
  publishConfirmGrant:
    'Šiai versijai reikia naujos prieigos prie įmonės sistemos. Prieš skelbdami peržiūrėkite prieigą.',
  publishConfirmWrite: 'Ši versija rašo į įmonės sistemą. Prieš skelbdami peržiūrėkite prieigą.',
  publishConfirmDestructive:
    'Ši versija pašalina duomenis. Pirmiausia patvirtinkite tai programoje Poneva.',
  publishConflict: 'Pirmiau buvo paskelbta kita versija. Įkelkite iš naujo, kad ją pamatytumėte.',
  publishBlockedNotAuthorized: 'Neturite leidimo skelbti.',
  publishBlockedCheckFailed: 'Patikrinimas nepavyko, todėl šios versijos paskelbti negalima.',
  publishBlockedCheckStale:
    'Artefaktas pasikeitė po paskutinio patikrinimo. Patikrinkite jį dar kartą.',
  publishBlockedObjects: 'Kai kurių failų nepavyko patikrinti. Po akimirkos bandykite dar kartą.',
  publishBlockedPolicy: 'Įmonės nustatymai pasikeitė. Peržiūrėkite ir bandykite dar kartą.',
  publishBlockedApproval: 'Patvirtinimas nebegalioja. Patvirtinkite dar kartą.',
  publishBlockedDependency: 'Reikalinga paslauga nepasiekiama. Bandykite vėliau.',
  publishAsk: 'Paprašyti, kad paskelbtų: {publishers}',
  publishAskFooter: 'Asmenys, galintys skelbti: {publishers}.',
  publishAskNone: 'Paprašykite asmens, galinčio skelbti.',
  publishWarnings: 'Verta peržiūrėti prieš skelbiant',
  checkWarnStale: '{subject} pasenęs ({time}).',
  checkWarnGap: '{subject} trūko paskutiniame atnaujinime.',
  checkWarnUnknownSource: 'Šaltinį, kurio nematote, reikia peržiūrėti.',
  checkRefreshFirst: 'Pirmiausia atnaujinti',
  checkFailedRow: '{subject} neišlaikė patikrinimo.',
  checkFailedGeneric: 'Patikrinimas nepavyko.',
  publishReviewAccess: 'Peržiūrėti prieigą',
  publishRemovalBody:
    'v{number} pašalina: {what} ({count}). Pirmiausia išsaugomas atkūrimo taškas.',
  publishReviewRemoval: 'Peržiūrėti, kas šalinama',
  publishAndRemove: 'Skelbti ir pašalinti',
  publishReviewFirst: 'Prieš skelbdami peržiūrėkite, kas šalinama.',
  publishDoneTitle: 'Versija {number} paskelbta: {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Jie ras ją savo bibliotekoje ir komandai prisegtą.',
  publishUndo: 'Anuliuoti',
  publishCopyLink: 'Kopijuoti nuorodą',
  publishDone: 'Atlikta',
  shareRoleViewMeaning: 'Gali atidaryti ir skaityti',
  shareRoleEditMeaning: 'Gali keisti pokalbyje ir naudodamas inspektorių',
  shareRolePublishMeaning: 'Taip pat gali skelbti ir atšaukti skelbimą',
  shareRoleMenu: 'Vaidmuo: {name}',
  shareUnpublishConfirm: 'Žmonės neteks prieigos. Artefaktas ir jo versijos išlaikomi.',
  shareUnpublishCancel: 'Palikti paskelbtą',
  sendDestinationLine: '{provider} · asmenų: {count}',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Santrauką mato visi: {destination}. {outside} iš {total} nėra šio artefakto auditorijoje, todėl nuorodoje jiems rodoma „Prašyti prieigos“.',
  sendAudienceGeneric: 'Asmenims už auditorijos ribų rodoma „Prašyti prieigos“.',
  sendPreview: 'Ką jie matys',
  shareTitle: 'Bendrinti',
  shareLoading: 'Įkeliamas bendrinimas…',
  shareErrorBody: 'Bendrinimo nepavyko įkelti.',
  sharePrivate: 'Šį artefaktą matote tik jūs.',
  shareShared: 'Matoma: {audience}.',
  shareRevoked: 'Bendrinimas išjungtas. Niekas kitas šio artefakto atidaryti negali.',
  shareOwnerSnapshot:
    'Žiūrintieji mato skaičius tokius, kokie buvo paskelbti. Jie negali atnaujinti ar redaguoti.',
  shareRoleView: 'Gali peržiūrėti',
  shareRoleEdit: 'Gali redaguoti',
  shareRolePublish: 'Gali skelbti',
  shareLivePublished: 'Paskelbta: versija {number}',
  shareUnpublish: 'Atšaukti skelbimą',
  shareManageDenied: 'Keisti, kas tai mato, gali tik redaktorius.',
  sendTitle: 'Siųsti į',
  sendIntro:
    'Išsiųskite nuorodą ir santrauką per prijungtą programą. Kiekvieną siuntimą patvirtinate jūs.',
  sendLoading: 'Įkeliami adresatai…',
  sendErrorBody: 'Adresatų nepavyko įkelti.',
  sendNone:
    'Jokia prijungta programa kol kas negali to gauti. Pirmiausia prijunkite Slack, Teams arba el. paštą.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'El. paštas',
  sendAudience: 'Gaus: {audience}',
  sendSummary: 'Žinutė',
  sendLeaves: 'Išsiunčiama nuoroda.',
  sendLeavesAttachment: 'Išsiunčiama nuoroda ir peržiūrėtas failas.',
  sendNoAttachments: 'Šis adresatas negali priimti failų, todėl siunčiama tik nuoroda.',
  sendScheduled: 'Suplanuotus siuntimus kaskart reikia patvirtinti.',
  sendAction: 'Peržiūrėti ir siųsti',
  sendFileUnsupported:
    'Šis adresatas negali priimti failo. Siųskite tik nuorodą arba pasirinkite kitą adresatą.',
  sendLinkOnly: 'Siųsti tik nuorodą',
  sendWaiting: 'Laukiama jūsų patvirtinimo. Nieko neišsiųsta.',
  sendDelivered: 'Išsiųsta: {destination}.',
  sendFailed: 'Neišsiųsta. Nieko neišėjo iš Poneva.',
  sendUnknown:
    'Nepavyko patvirtinti pristatymo: {destination}. Prieš siųsdami dar kartą, patikrinkite ten.',
  sendBlockedNotAuthorized: 'Neturite leidimo to siųsti.',
  sendBlockedUnsupported: 'Šis adresatas nepalaikomas.',
  sendBlockedApproval: 'Patvirtinimas nebegalioja. Peržiūrėkite ir siųskite dar kartą.',
  sendBlockedChanged: 'Žinutė arba gavėjas pasikeitė. Peržiūrėkite dar kartą.',
  sendBlockedDependency: 'Prijungta programa nepasiekiama. Bandykite vėliau.',
  libraryTitle: 'Biblioteka',
  librarySegmentAll: 'Visi',
  librarySegmentMine: 'Mano',
  librarySegmentShared: 'Su manimi pasidalinta',
  librarySearch: 'Ieškoti artefaktų',
  libraryKindAll: 'Visos rūšys',
  libraryKindReports: 'Ataskaitos ir puslapiai',
  libraryKindDecks: 'Pristatymai',
  libraryKindDocuments: 'Dokumentai',
  libraryKindSpreadsheets: 'Skaičiuoklės',
  libraryKindApps: 'Programėlės',
  libraryAppsMatchNoun:
    'one:programėlė atitinka skiltyje Programėlės →|few:programėlės atitinka skiltyje Programėlės →|many:programėlės atitinka skiltyje Programėlės →|other:programėlių atitinka skiltyje Programėlės →',
  libraryMake: 'Pradėti pokalbyje',
  libraryEmptySharedHint: 'Kai kas nors pasidalins su jumis artefaktu, jis atsiras čia.',
  libraryRefreshFailedAt: 'Nepavyko atnaujinti {time}.',
  libraryRefreshTryAgain: 'Bandyti dar kartą',
  libraryVia: 'per {owner}',
  savedPrivate: 'Privatus',
  savedVersion: '{n} versija',
  libraryLoading: 'Įkeliama jūsų biblioteka…',
  libraryErrorBody: 'Jūsų bibliotekos šiuo metu nepavyko įkelti.',
  startersLoadFailed: 'Šiuo metu nepavyko įkelti šablonų.',
  libraryCannotOpen: '„{title}“ čia dar negalima atidaryti.',
  libraryEmptyTitle: 'Čia kol kas nieko nėra',
  libraryEmptyAllTitle: 'Sukurkite tai, kuo verta pasidalyti.',
  libraryEmptyAllBody:
    'Čia rodomi dokumentai, pristatymai ir programos, kuriuos sukuriate pokalbyje arba kuriais su jumis pasidalija kiti.',
  libraryEmptyMineBody: 'Čia atsiranda jūsų sukurti artefaktai.',
  libraryEmptySharedBody: 'Su jumis kol kas niekuo nepasidalinta.',
  libraryNoMatchTitle: 'Nieko nerasta',
  libraryNoMatchBody: 'Išbandykite kitą žodį arba kitą rūšį.',
  libraryMore: 'Rodyti daugiau',
  libraryOwner: 'Autorius: {owner}',
  libraryUpdated: 'Redaguota {time}',
  libraryPublishedAs: 'Paskelbta · v{number}',
  libraryDraft: 'Juodraštis',
  libraryUnpublished: 'Nepaskelbta',
  libraryRefreshFailed: 'Atnaujinti nepavyko',
  libraryRefreshRunning: 'Atnaujinama',
  libraryAudience: 'Skirta: {audience}',
  kindReport: 'Ataskaita',
  kindPage: 'Puslapis',
  kindSite: 'Svetainė',
  kindDeck: 'Pristatymas',
  kindDocument: 'Dokumentas',
  kindApp: 'Programėlė',
  kindSpreadsheet: 'Skaičiuoklė',
  kindDashboard: 'Skydelis',
  kindTable: 'Lentelė',
  kindChart: 'Diagrama',
  kindExportsReport: 'Eksportuojama į PDF',
  kindExportsPage: 'Puslapis tik skaitymui',
  kindExportsSite: 'Svetainė tik skaitymui',
  kindExportsDeck: 'Pristatykite arba eksportuokite į PowerPoint ir PDF',
  kindExportsDocument: 'Eksportuojama į Word ir PDF',
  kindExportsSpreadsheet: 'Eksportuojama į Excel ir CSV',
  kindExportsDashboard: 'Gyvas rodinys',
  kindExportsTable: 'Eksportuojama į Excel ir CSV',
  kindExportsChart: 'Diagramos rodinys',
  agentPublished: '{agent} paskelbė {title} v{number}.',
  agentCheckedOn: 'Patikrinta: {views}, {themes}.',
  agentUndo: 'Anuliuoti',
  agentRestorePrevious: 'Atkurti v{number}',
  agentHeld: '{title} v{number} laukia kaip juodraštis.',
  agentHeldMismatch: 'Skaičių, nesutapusių su šaltiniais: {count}.',
  agentHeldCheck: 'Patikrinimas neišlaikytas.',
  agentFixInChat: 'Taisyti pokalbyje',
  agentConfirmDrop:
    '{agent} nori pašalinti {what} iš {title}. Pirmiausia išsaugomas atkūrimo taškas. Nieko nekeičiama, kol nenuspręsite.',
  agentConfirmDropCount:
    '{agent} nori pašalinti {what} (reikšmių: {count}) iš {title}. Pirmiausia išsaugomas atkūrimo taškas. Nieko nekeičiama, kol nenuspręsite.',
  agentViewDesktop: 'kompiuteris',
  agentViewTablet: 'planšetė',
  agentViewPhone: 'telefonas',
  agentThemeLight: 'šviesi',
  agentThemeDark: 'tamsi',
  agentLookAt: 'Peržiūrėti v{number}',
  agentConfirmWrite: '{agent} nori rašyti: {what}. Nieko nerašoma, kol nenuspręsite.',
  agentConfirmGrant: '{agent} nori prieigos prie: {what}. Niekuo nesidalijama, kol nenuspręsite.',
  agentReview: 'Peržiūrėti',
  agentSkipped: '{agent} praleido: {part}.',
  agentSkippedWhy:
    'Jis skaito kiekvieno asmens savo sistemą {system}, o šiam vykdymui nenurodytas asmuo, kurio vardu skaityti.',
  agentChooseWho: 'Pasirinkti, kieno vardu veikia',
  agentChangeInChat: 'Keisti pokalbyje',
  agentFailed: '{agent} nepavyko atnaujinti: {title}.',
  agentFailedBody: 'Paskutinė paskelbta versija tebėra paskelbta.',
  agentRetry: 'Bandyti dar kartą',
  agentRunning: '{agent} atnaujina: {title}.',
  agentMakesTitle: 'Kuria ir atnaujina',
  agentMakesLoading: 'Įkeliama…',
  agentMakesErrorBody: 'Šio elemento nepavyko įkelti.',
  agentMakesNone: 'Šis agentas kol kas nekuria artefaktų.',
  agentMakesNew: 'Naujas artefaktas kiekvieną kartą',
  agentMakesUpdate: 'Kiekvieną kartą atnaujina vieną artefaktą',
  agentNextRun: 'Kitas vykdymas {when}',
  agentNoSchedule: 'Nesuplanuota',
  agentCreditsPerRun: 'Kreditai už vykdymą: apie {credits}',
  agentCreditsUnknown: 'Kreditai už vykdymą dar nežinomi',
  agentLastResult: 'Paskutinis rezultatas',
  interviewTitle: 'Nustatyti',
  interviewIntro: 'Keli klausimai padės tai sukurti.',
  interviewOptional: '(neprivaloma)',
  interviewAnswerNeeded: 'Atsakykite, kad tęstumėte.',
  interviewReadOnly: 'Tik skaitymui',
  interviewChange: 'Keisti',
  interviewConnectionYours: 'jūsų {system} ryšys',
  interviewConnectionOther: '{name} {system} ryšys',
  interviewDateAny: 'Bet kuris laikotarpis',
  interviewDatePrevious: 'Ankstesnis mėnuo',
  interviewDateNext: 'Kitas mėnuo',
  interviewDatePresets: 'Greiti laikotarpiai',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Išvalyti',
  interviewDateDone: 'Atlikta',
  interviewChooseSource: 'Pasirinkite šaltinį',
  interviewSourceChosen: 'Šaltinis pasirinktas',
  interviewSourceUnavailable: 'Šaltinio pasirinkimas čia dar nepasiekiamas, todėl tęsti negalima.',
  interviewSourceUnavailableOptional:
    'Šaltinio pasirinkimas čia dar negalimas. Galite tęsti ir be jo.',
  interviewStarterWithdrawn:
    'Šis šablonas nebepasiekiamas. Juodraščiai, kuriuos jau pradėjote pagal jį, vis dar atsidaro.',
  interviewDateFrom: 'Nuo',
  interviewDateTo: 'Iki',
  interviewContinue: 'Tęsti',
  interviewGrantTitle: 'Leisti skaityti: {source}?',
  interviewGrantBody:
    'Naudojamas {connection}, tik skaitymui, šiam tikslui: {scope}. Nieko neskaitoma, kol neleisite, ir pavyzdinės eilutės nerodomos.',
  interviewAllow: 'Leisti',
  interviewNotNow: 'Ne dabar',
  interviewConfiguring: 'Nustatoma pagal jūsų atsakymus…',
  interviewChecking: 'Tikrinama prieš atidarant…',
  interviewComplete: '{title} paruošta.',
  interviewOpen: 'Atidaryti: {title}',
  interviewBlocked: 'Tęsti nepavyko.',
  interviewFailed: 'Nustatyti nepavyko. Jūsų atsakymai išsaugoti.',
  interviewCancelled: 'Tai buvo atšaukta.',
  interviewSaving: 'Saugoma…',
  interviewSaved: 'Išsaugota',
  interviewConflict:
    'Šis juodraštis pasikeitė kitur. Jūsų atsakymai čia išlaikomi, kol įkelsite dabartinį juodraštį, kuris juos pakeis.',
  interviewReloadCurrent: 'Įkelti dabartinį juodraštį',
  interviewUnsettled:
    'Nepavyko patvirtinti jūsų paskutinio išsaugojimo. Jūsų atsakymai čia išlaikomi.',
  interviewCheckAgain: 'Patikrinti dar kartą',
  finishAction: 'Išsaugoti privačiai',
  finishSaving: 'Išsaugoma…',
  finishSaved: '„{title}“ išsaugotas, jį matote tik jūs.',
  finishNotSaved: 'Neišsaugota. Jūsų juodraštis vis dar čia.',
  finishUnconfirmed: 'Nepavyko patvirtinti, ar išsaugota.',
  candidateInvalid: 'Iš juodraščio nepavyko sukurti dokumento.',
  finishOpenInLibrary: 'Atidaryti Bibliotekoje',
  savedOpenFailed: 'Šiuo metu nepavyko atidaryti.',
  savedInLibrary: 'Tai yra jūsų Bibliotekoje.',
  openSavedTitle: 'Atidaryti: {title} →',
  interviewReasonNotAuthorized: 'Neturite leidimo keisti šio juodraščio.',
  interviewReasonDraftNotOpen: 'Šis juodraštis nebeatidarytas.',
  interviewReasonInvalid: 'Vienas iš atsakymų netinkamas.',
  interviewReasonObjects: 'Kai kurių failų nepavyko patikrinti. Jūsų atsakymai išsaugoti.',
  interviewReasonDependency: 'Reikalinga paslauga nepasiekiama. Jūsų atsakymai išsaugoti.',
  interviewReasonReconcile: 'Paskutinis išsaugojimas vis dar patvirtinamas.',
  exportTitle: 'Eksportuoti',
  exportIntro: 'Atsisiųskite šį artefaktą. Faile nurodyta, iš kur gauti jo duomenys.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Redaguojamos skaidrės su pranešėjo pastabomis',
  exportHintPdf: 'Tiksliai taip, kaip rodoma',
  exportHintDocx: 'Redaguojamas, išlaiko antraštes ir lenteles',
  exportHintXlsx: 'Visi lapai, išlaiko formules',
  exportHintCsv: 'Tik reikšmės, UTF-8',
  exportUnavailable: 'Šiuo metu nepasiekiama',
  exportRunning: 'Ruošiama: {format}…',
  exportValidating: 'Tikrinamas failas: {format}…',
  exportReady: 'Failas {format} paruoštas',
  exportDownload: 'Atsisiųsti: {name}',
  exportVersion: 'Eksportuota versija {number}',
  exportFailed: 'Failo {format} nepavyko sukurti.',
  exportFailedChecks: 'Failas {format} neišlaikė patikrinimų, todėl jis nepasiūlytas.',
  exportDenied: 'Neturite prieigos eksportuoti šio artefakto.',
  exportRevoked: 'Bendrinimas buvo išjungtas, todėl šio artefakto nebegalima eksportuoti.',
  exportStale:
    'Šis artefaktas eksportuojant pasikeitė. Eksportuokite dar kartą, kad gautumėte naujausią versiją.',
  exportProvenanceTitle: 'Kiekviename faile yra',
  exportProvenanceLine: 'Sukurta su Poneva · {title} · v{number} · {data} · sukurta {madeAt}',
  exportProvenanceAsOf: 'duomenys: {time}',
  exportPlacementNotesAbout: 'Pastabų lape ir informaciniame faile šalia CSV',
  exportProvenanceRange: 'duomenys nuo {earliest} iki {latest}',
  exportProvenanceUnknown: 'duomenų amžius nežinomas',
  exportAboutFile: 'Apie šį failą ({format})',
  exportPlacementFooter: 'Kiekvieno puslapio poraštėje',
  exportPlacementSlide: 'Kiekvienos skaidrės poraštėje',
  exportPlacementNotes: 'Pastabų lape',
  exportPlacementAbout: 'Informaciniame faile šalia CSV',
  exportSourcesPage: 'Pridėti šaltinių puslapį',
  exportSourcesPageHint:
    'Paskutinis puslapis, kuriame nurodytas kiekvienas skaičius, jo šaltinis ir laikas.',
  exportSourcesPageDeck: 'Kartu pridedama paties pristatymo šaltinių skaidrė.',
  exportSourcesPageSaved: 'Įsimenama šiam artefaktui, įskaitant suplanuotus siuntimus.',
  libraryKindSheetTitle: 'Rūšis',
  shareAccessSheetTitle: 'Prieiga',

  requestShowAll: 'Rodyti viską',
  requestShowLess: 'Rodyti mažiau',
  approveGettingReady: 'Ruošiama…',
  approveNotReady:
    'Šiuo metu to patvirtinti negalima. Įkelkite puslapį iš naujo ir bandykite dar kartą.',
  tryWithModel: 'Bandyti su modeliu {model}',
  tryWithRequesting: 'Paleidžiama…',
  tryWithUnavailable: 'Šiuo metu to paleisti negalima.',
};

const lv: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Jūs rediģējat versijas {number} melnrakstu',
  noChangesToApply: 'Nav izmaiņu, ko lietot.',
  appliedDone: 'Lietots.',
  mentionInChat: 'Pieminēt tērzēšanā',

  kindMarkdown: 'Piezīme',
  kindKpi: 'Rādītājs',
  libraryExportsList: 'Eksportē uz {formats}',
  libraryOpensToRead: 'Atveras lasīšanai',
  libraryReadOnly: 'Tikai lasīšana',
  libraryLiveView: 'Tiešraides skats',
  libraryOneNumber: 'Viens skaitlis · uz {time}',
  retry: 'Mēģināt vēlreiz',
  close: 'Aizvērt',
  panelLoading: 'Notiek ielāde…',
  panelEmpty: 'Šeit vēl nekā nav.',
  panelDenied: 'Jums nav piekļuves tam.',
  panelBlocked: 'Pirms turpināt, ir jāgaida kāds nosacījums.',
  panelBlockedViewerContext:
    'Šim izpildījumam nav personas, kuras vārdā lasīt, tāpēc tas nevar turpināties, kamēr kāds neizvēlas, kā vārdā tas darbojas.',
  panelError: 'Šo neizdevās ielādēt.',
  panelStale: 'Šis var būt novecojis.',
  sourcesTitle: 'Avoti',
  sourcesIntro: 'No kurienes nāk katrs skaitlis un cik tas ir svaigs.',
  sourcesLoading: 'Notiek avotu ielāde…',
  sourcesEmptyTitle: 'Nav avotu',
  sourcesEmptyBody: 'Šis artefakts neizmanto uzņēmuma datus.',
  sourcesErrorBody: 'Avotus neizdevās ielādēt.',
  sourcesDeniedTitle: 'Daži avoti ir paslēpti',
  sourcesDeniedBody:
    'Jums nav piekļuves {count} šeit izmantotajiem avotiem. Palūdziet īpašniekam tos kopīgot.',
  sourceDeniedName: 'Avots, ko jūs neredzat',
  sourceAsOf: 'Uz {time}',
  sourceAgeUnknown: 'Datu vecums nav zināms',
  sourceStatusCurrent: 'Aktuāls',
  sourceStatusStale: 'Novecojis',
  sourceStatusGap: 'Trūka pēdējā atjauninājumā',
  sourceStatusDenied: 'Nav piekļuves',
  sourceLastGood: 'Pēdējais derīgais: {time}',
  sourceUsedBy: 'Izmanto: {labels}',
  openDocument: 'Atvērt dokumentu',
  versionsShow: 'Rādīt versijas',
  versionsHide: 'Paslēpt versijas',
  versionsTitle: 'Versijas',
  versionsLoading: 'Notiek versiju ielāde…',
  versionsEmptyTitle: 'Versiju vēl nav',
  versionsEmptyBody: 'Pirmā versija parādīsies, kad šis artefakts tiks izveidots.',
  versionsErrorBody: 'Versijas neizdevās ielādēt.',
  versionLabel: 'Versija {number}',
  versionOpen: 'Atvērt versiju {number}',
  versionCurrent: 'Pašreizējā',
  versionPublished: 'Publicēta',
  versionBy: 'Autors: {actor}',
  versionOriginNative: 'Programmā Poneva',
  versionOriginMcp: 'ar jūsu pašu aģentu',
  versionOriginTerminal: 'ar termināli',
  versionOriginJob: 'Ieplānots izpildījums',
  versionCausePerson: 'Rediģēta',
  versionCauseAgent: 'Aģenta atjauninājums',
  versionCauseRefresh: 'Atsvaidzināta',
  versionCauseRestore: 'Atjaunota',
  versionCauseImport: 'Importēta',
  versionCheckedYes: 'Pārbaudīta',
  versionCheckedNo: 'Vēl nav pārbaudīta',
  versionCheckedInvalidated: 'Rediģēta pēc pēdējās pārbaudes',
  compareStart: 'Salīdzināt',
  compareWith: 'Salīdzināt ar versiju {number}',
  compareTitle: 'Kas mainījās no v{from} uz v{to}',
  compareFromMark: 'No',
  compareToMark: 'Uz',
  comparePick: 'Izvēlieties otru versiju, ko salīdzināt.',
  compareNone: 'Atšķirību nav.',
  compareLoading: 'Notiek salīdzināšana…',
  compareError: 'Salīdzinājumu neizdevās ielādēt.',
  compareBefore: 'Pirms',
  compareAfter: 'Pēc',
  compareAdded: 'Pievienots',
  compareRemoved: 'Noņemts',
  compareChanged: 'Mainīts',
  restore: 'Atjaunot šo versiju',
  restoreConfirmTitle: 'Atjaunot versiju {number}?',
  restoreConfirmBody: 'Tiks izveidota jauna versija no versijas {number}. Nekas netiek dzēsts.',
  restoreConfirm: 'Atjaunot',
  restoreCancel: 'Atcelt',
  restoring: 'Notiek atjaunošana…',
  restoreFailed: 'Versiju neizdevās atjaunot.',
  restoreStale:
    'Šis artefakts mainījās, kamēr to skatījāties. Pārlādējiet versijas un mēģiniet vēlreiz.',
  restoreDenied: 'Jums nav atļaujas atjaunot versijas.',
  restored: 'Versija {number} atjaunota kā jauna versija.',
  publishTitle: 'Publicēt',
  publishIntro: 'Versija {number} būs redzama: {audience}.',
  publishOwnerLine: 'Skatītāji redz to, ko {owner} varēja redzēt, kad šī versija tika publicēta.',
  publishChecksTitle: 'Pārbaudes',
  checkPassed: 'Pārbaudīta',
  checkRunning: 'Notiek pārbaude…',
  checkFailed: 'Pārbaude neizdevās',
  checkStale: 'Rediģēta pēc pēdējās pārbaudes',
  publishFixInChat: 'Labot tērzēšanā',
  publishAction: 'Publicēt versiju {number}',
  publishing: 'Notiek publicēšana…',
  publishedDone: 'Versija {number} ir publiska: {audience}.',
  publishConfirmGrant:
    'Šai versijai vajadzīga jauna piekļuve uzņēmuma sistēmai. Pirms publicēšanas pārskatiet piekļuvi.',
  publishConfirmWrite:
    'Šī versija raksta uzņēmuma sistēmā. Pirms publicēšanas pārskatiet piekļuvi.',
  publishConfirmDestructive: 'Šī versija noņem datus. Vispirms apstipriniet to Poneva.',
  publishConflict: 'Vispirms tika publicēta cita versija. Pārlādējiet, lai to redzētu.',
  publishBlockedNotAuthorized: 'Jums nav atļaujas publicēt.',
  publishBlockedCheckFailed: 'Pārbaude neizdevās, tāpēc šo versiju nevar publicēt.',
  publishBlockedCheckStale: 'Artefakts mainījās pēc pēdējās pārbaudes. Pārbaudiet to vēlreiz.',
  publishBlockedObjects: 'Dažus failus neizdevās pārbaudīt. Pēc brīža mēģiniet vēlreiz.',
  publishBlockedPolicy: 'Uzņēmuma iestatījumi mainījās. Pārskatiet tos un mēģiniet vēlreiz.',
  publishBlockedApproval: 'Apstiprinājums vairs nav derīgs. Apstipriniet vēlreiz.',
  publishBlockedDependency: 'Nepieciešamais pakalpojums nav pieejams. Mēģiniet vēlāk.',
  publishAsk: 'Palūgt {publishers} publicēt',
  publishAskFooter: 'Personas, kas var publicēt: {publishers}.',
  publishAskNone: 'Palūdziet kādam, kas var publicēt.',
  publishWarnings: 'Vērts pārbaudīt pirms publicēšanas',
  checkWarnStale: '{subject} ir novecojis ({time}).',
  checkWarnGap: '{subject} trūka pēdējā atjauninājumā.',
  checkWarnUnknownSource: 'Avotam, ko jūs neredzat, nepieciešama uzmanība.',
  checkRefreshFirst: 'Vispirms atsvaidzināt',
  checkFailedRow: '{subject} neizturēja pārbaudi.',
  checkFailedGeneric: 'Pārbaude neizdevās.',
  publishReviewAccess: 'Pārskatīt piekļuvi',
  publishRemovalBody:
    'v{number} noņem: {what} ({count}). Iepriekš tiek saglabāts atjaunošanas punkts.',
  publishReviewRemoval: 'Pārskatīt noņemamo',
  publishAndRemove: 'Publicēt un noņemt',
  publishReviewFirst: 'Pirms publicēšanas pārskatiet, kas tiek noņemts.',
  publishDoneTitle: 'Versija {number} ir publiska: {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Viņi to atradīs savā bibliotēkā un piespraustu komandai.',
  publishUndo: 'Atsaukt',
  publishCopyLink: 'Kopēt saiti',
  publishDone: 'Gatavs',
  shareRoleViewMeaning: 'Var atvērt un lasīt',
  shareRoleEditMeaning: 'Var mainīt tērzēšanā un ar inspektoru',
  shareRolePublishMeaning: 'Var arī publicēt un atcelt publicēšanu',
  shareRoleMenu: 'Loma: {name}',
  shareUnpublishConfirm: 'Cilvēki zaudēs piekļuvi. Artefakts un tā versijas tiek saglabātas.',
  shareUnpublishCancel: 'Atstāt publisku',
  sendDestinationLine: '{provider} · {count} personas',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Visi grupā {destination} redz kopsavilkumu. {outside} no {total} nav šī artefakta auditorijā, tāpēc saitē viņi redzēs “Pieprasīt piekļuvi”.',
  sendAudienceGeneric: 'Personas ārpus auditorijas redz “Pieprasīt piekļuvi”.',
  sendPreview: 'Ko viņi redzēs',
  shareTitle: 'Kopīgot',
  shareLoading: 'Notiek kopīgošanas ielāde…',
  shareErrorBody: 'Kopīgošanu neizdevās ielādēt.',
  sharePrivate: 'Šo artefaktu redzat tikai jūs.',
  shareShared: 'Redzams: {audience}.',
  shareRevoked: 'Kopīgošana ir izslēgta. Neviens cits nevar atvērt šo artefaktu.',
  shareOwnerSnapshot:
    'Skatītāji redz skaitļus tādus, kādi tie tika publicēti. Viņi nevar atsvaidzināt vai rediģēt.',
  shareRoleView: 'Var skatīt',
  shareRoleEdit: 'Var rediģēt',
  shareRolePublish: 'Var publicēt',
  shareLivePublished: 'Publisks: versija {number}',
  shareUnpublish: 'Atcelt publicēšanu',
  shareManageDenied: 'Mainīt, kas to redz, var tikai redaktors.',
  sendTitle: 'Sūtīt uz',
  sendIntro:
    'Nosūtiet saiti un kopsavilkumu ar pievienotu lietotni. Jūs apstiprināt katru sūtījumu.',
  sendLoading: 'Notiek galamērķu ielāde…',
  sendErrorBody: 'Galamērķus neizdevās ielādēt.',
  sendNone:
    'Neviena pievienota lietotne to vēl nevar saņemt. Vispirms pievienojiet Slack, Teams vai e-pastu.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-pasts',
  sendAudience: 'Tiek nosūtīts: {audience}',
  sendSummary: 'Ziņojums',
  sendLeaves: 'Tiek nosūtīta saite.',
  sendLeavesAttachment: 'Tiek nosūtīta saite un pārskatītais fails.',
  sendNoAttachments: 'Šis galamērķis nevar saņemt failus, tāpēc tiek nosūtīta tikai saite.',
  sendScheduled: 'Ieplānotiem sūtījumiem katru reizi nepieciešams jūsu apstiprinājums.',
  sendAction: 'Pārskatīt un sūtīt',
  sendFileUnsupported:
    'Šis galamērķis nevar saņemt failu. Sūtiet tikai saiti vai izvēlieties citu galamērķi.',
  sendLinkOnly: 'Sūtīt tikai saiti',
  sendWaiting: 'Gaida jūsu apstiprinājumu. Nekas nav nosūtīts.',
  sendDelivered: 'Nosūtīts uz: {destination}.',
  sendFailed: 'Nenosūtīts. Nekas nav atstājis Poneva.',
  sendUnknown:
    'Mēs nevarējām apstiprināt piegādi uz: {destination}. Pirms sūtāt vēlreiz, pārbaudiet tur.',
  sendBlockedNotAuthorized: 'Jums nav atļaujas to sūtīt.',
  sendBlockedUnsupported: 'Šis galamērķis netiek atbalstīts.',
  sendBlockedApproval: 'Apstiprinājums vairs nav derīgs. Pārskatiet un sūtiet vēlreiz.',
  sendBlockedChanged: 'Ziņojums vai saņēmējs mainījās. Pārskatiet to vēlreiz.',
  sendBlockedDependency: 'Pievienotā lietotne nav pieejama. Mēģiniet vēlāk.',
  libraryTitle: 'Bibliotēka',
  librarySegmentAll: 'Visi',
  librarySegmentMine: 'Mani',
  librarySegmentShared: 'Man kopīgotie',
  librarySearch: 'Meklēt artefaktus',
  libraryKindAll: 'Visi veidi',
  libraryKindReports: 'Pārskati un lapas',
  libraryKindDecks: 'Prezentācijas',
  libraryKindDocuments: 'Dokumenti',
  libraryKindSpreadsheets: 'Izklājlapas',
  libraryKindApps: 'Lietotnes',
  libraryAppsMatchNoun:
    'zero:lietotņu atbilst sadaļā Lietotnes →|one:lietotne atbilst sadaļā Lietotnes →|other:lietotnes atbilst sadaļā Lietotnes →',
  libraryMake: 'Sākt tērzēšanā',
  libraryEmptySharedHint: 'Kad kāds ar jums kopīgos artefaktu, tas parādīsies šeit.',
  libraryRefreshFailedAt: 'Neizdevās atsvaidzināt {time}.',
  libraryRefreshTryAgain: 'Mēģināt vēlreiz',
  libraryVia: 'caur {owner}',
  savedPrivate: 'Privāts',
  savedVersion: '{n}. versija',
  libraryLoading: 'Notiek jūsu bibliotēkas ielāde…',
  libraryErrorBody: 'Jūsu bibliotēku šobrīd neizdevās ielādēt.',
  startersLoadFailed: 'Veidnes pašlaik neizdevās ielādēt.',
  libraryCannotOpen: '„{title}” šeit vēl nevar atvērt.',
  libraryEmptyTitle: 'Šeit vēl nekā nav',
  libraryEmptyAllTitle: 'Izveidojiet kaut ko, ar ko ir vērts dalīties.',
  libraryEmptyAllBody:
    'Šeit tiek rādīti dokumenti, prezentācijas un lietotnes, ko izveidojat tērzēšanā vai ko citi kopīgo ar jums.',
  libraryEmptyMineBody: 'Artefakti, ko izveidojat, parādās šeit.',
  libraryEmptySharedBody: 'Ar jums vēl nekas nav kopīgots.',
  libraryNoMatchTitle: 'Nekas nav atrasts',
  libraryNoMatchBody: 'Izmēģiniet citu vārdu vai citu veidu.',
  libraryMore: 'Rādīt vairāk',
  libraryOwner: 'Autors: {owner}',
  libraryUpdated: 'Rediģēts {time}',
  libraryPublishedAs: 'Publicēts · v{number}',
  libraryDraft: 'Melnraksts',
  libraryUnpublished: 'Nepublicēts',
  libraryRefreshFailed: 'Atsvaidzināšana neizdevās',
  libraryRefreshRunning: 'Notiek atsvaidzināšana',
  libraryAudience: 'Auditorijai: {audience}',
  kindReport: 'Pārskats',
  kindPage: 'Lapa',
  kindSite: 'Vietne',
  kindDeck: 'Prezentācija',
  kindDocument: 'Dokuments',
  kindApp: 'Lietotne',
  kindSpreadsheet: 'Izklājlapa',
  kindDashboard: 'Informācijas panelis',
  kindTable: 'Tabula',
  kindChart: 'Diagramma',
  kindExportsReport: 'Eksportējams uz PDF',
  kindExportsPage: 'Lapa tikai lasīšanai',
  kindExportsSite: 'Vietne tikai lasīšanai',
  kindExportsDeck: 'Prezentējams vai eksportējams uz PowerPoint un PDF',
  kindExportsDocument: 'Eksportējams uz Word un PDF',
  kindExportsSpreadsheet: 'Eksportējams uz Excel un CSV',
  kindExportsDashboard: 'Tiešraides skats',
  kindExportsTable: 'Eksportējams uz Excel un CSV',
  kindExportsChart: 'Diagrammas skats',
  agentPublished: '{agent} publicēja {title} v{number}.',
  agentCheckedOn: 'Pārbaudīts: {views}, {themes}.',
  agentUndo: 'Atsaukt',
  agentRestorePrevious: 'Atjaunot v{number}',
  agentHeld: '{title} v{number} gaida kā melnraksts.',
  agentHeldMismatch: '{count} skaitļi neatbilda saviem avotiem.',
  agentHeldCheck: 'Pārbaude netika izturēta.',
  agentFixInChat: 'Labot tērzēšanā',
  agentConfirmDrop:
    '{agent} vēlas noņemt {what} no {title}. Iepriekš tiek saglabāts atjaunošanas punkts. Nekas nemainās, kamēr jūs nenolemjat.',
  agentConfirmDropCount:
    '{agent} vēlas noņemt {what} ({count} vērtības) no {title}. Iepriekš tiek saglabāts atjaunošanas punkts. Nekas nemainās, kamēr jūs nenolemjat.',
  agentViewDesktop: 'dators',
  agentViewTablet: 'planšete',
  agentViewPhone: 'tālrunis',
  agentThemeLight: 'gaišs',
  agentThemeDark: 'tumšs',
  agentLookAt: 'Skatīt v{number}',
  agentConfirmWrite: '{agent} vēlas rakstīt: {what}. Nekas netiek rakstīts, kamēr jūs nenolemjat.',
  agentConfirmGrant: '{agent} vēlas piekļuvi: {what}. Nekas netiek kopīgots, kamēr jūs nenolemjat.',
  agentReview: 'Pārskatīt',
  agentSkipped: '{agent} izlaida: {part}.',
  agentSkippedWhy:
    'Tas lasa katras personas savu sistēmu {system}, bet šim izpildījumam nav personas, kuras vārdā lasīt.',
  agentChooseWho: 'Izvēlēties, kā vārdā tas darbojas',
  agentChangeInChat: 'Mainīt tērzēšanā',
  agentFailed: '{agent} nevarēja atsvaidzināt {title}.',
  agentFailedBody: 'Pēdējā publicētā versija joprojām ir publiska.',
  agentRetry: 'Mēģināt vēlreiz',
  agentRunning: '{agent} atjaunina {title}.',
  agentMakesTitle: 'Izveido un atjaunina',
  agentMakesLoading: 'Notiek ielāde…',
  agentMakesErrorBody: 'Šo neizdevās ielādēt.',
  agentMakesNone: 'Šis aģents vēl neveido artefaktus.',
  agentMakesNew: 'Jauns artefakts katrā izpildījumā',
  agentMakesUpdate: 'Katrā izpildījumā atjaunina vienu artefaktu',
  agentNextRun: 'Nākamais izpildījums {when}',
  agentNoSchedule: 'Nav ieplānots',
  agentCreditsPerRun: 'Kredīti par izpildījumu: apmēram {credits}',
  agentCreditsUnknown: 'Kredīti par izpildījumu vēl nav zināmi',
  agentLastResult: 'Pēdējais rezultāts',
  interviewTitle: 'Iestatīt',
  interviewIntro: 'Daži jautājumi to izveido.',
  interviewOptional: '(neobligāti)',
  interviewAnswerNeeded: 'Atbildiet, lai turpinātu.',
  interviewReadOnly: 'Tikai lasīšana',
  interviewChange: 'Mainīt',
  interviewConnectionYours: 'jūsu {system} savienojums',
  interviewConnectionOther: '{name} {system} savienojums',
  interviewDateAny: 'Jebkurš periods',
  interviewDatePrevious: 'Iepriekšējais mēnesis',
  interviewDateNext: 'Nākamais mēnesis',
  interviewDatePresets: 'Ātrie periodi',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Notīrīt',
  interviewDateDone: 'Gatavs',
  interviewChooseSource: 'Izvēlieties avotu',
  interviewSourceChosen: 'Avots izvēlēts',
  interviewSourceUnavailable: 'Avota izvēle šeit vēl nav pieejama, tāpēc turpināt nevar.',
  interviewSourceUnavailableOptional:
    'Avota izvēle šeit vēl nav pieejama. Varat turpināt arī bez tā.',
  interviewStarterWithdrawn:
    'Šī veidne vairs nav pieejama. Melnraksti, ko jau sākāt no tās, joprojām atveras.',
  interviewDateFrom: 'No',
  interviewDateTo: 'Līdz',
  interviewContinue: 'Turpināt',
  interviewGrantTitle: 'Atļaut lasīt: {source}?',
  interviewGrantBody:
    'Tiek izmantots {connection}, tikai lasīšanai, šim mērķim: {scope}. Nekas netiek lasīts, kamēr neatļaujat, un parauga rindas netiek rādītas.',
  interviewAllow: 'Atļaut',
  interviewNotNow: 'Ne tagad',
  interviewConfiguring: 'Notiek iestatīšana pēc jūsu atbildēm…',
  interviewChecking: 'Notiek pārbaude pirms atvēršanas…',
  interviewComplete: '{title} ir gatavs.',
  interviewOpen: 'Atvērt: {title}',
  interviewBlocked: 'Turpināt neizdevās.',
  interviewFailed: 'Iestatīšana neizdevās. Jūsu atbildes ir saglabātas.',
  interviewCancelled: 'Tas tika atcelts.',
  interviewSaving: 'Notiek saglabāšana…',
  interviewSaved: 'Saglabāts',
  interviewConflict:
    'Šis melnraksts mainījās citur. Jūsu atbildes šeit tiek saglabātas, kamēr ielādējat pašreizējo melnrakstu, kas tās aizstāj.',
  interviewReloadCurrent: 'Ielādēt pašreizējo melnrakstu',
  interviewUnsettled:
    'Mēs nevarējām apstiprināt jūsu pēdējo saglabāšanu. Jūsu atbildes šeit ir saglabātas.',
  interviewCheckAgain: 'Pārbaudīt vēlreiz',
  finishAction: 'Saglabāt privāti',
  finishSaving: 'Saglabā…',
  finishSaved: '“{title}” ir saglabāts, un to redzat tikai jūs.',
  finishNotSaved: 'Netika saglabāts. Jūsu melnraksts joprojām ir šeit.',
  finishUnconfirmed: 'Mēs nevarējām apstiprināt, vai tas tika saglabāts.',
  candidateInvalid: 'No melnraksta neizdevās izveidot dokumentu.',
  finishOpenInLibrary: 'Atvērt Bibliotēkā',
  savedOpenFailed: 'Pašlaik to neizdevās atvērt.',
  savedInLibrary: 'Tas ir jūsu Bibliotēkā.',
  openSavedTitle: 'Atvērt: {title} →',
  interviewReasonNotAuthorized: 'Jums nav atļaujas mainīt šo melnrakstu.',
  interviewReasonDraftNotOpen: 'Šis melnraksts vairs nav atvērts.',
  interviewReasonInvalid: 'Viena no atbildēm nav derīga.',
  interviewReasonObjects: 'Dažus failus neizdevās pārbaudīt. Jūsu atbildes ir saglabātas.',
  interviewReasonDependency:
    'Nepieciešamais pakalpojums nav pieejams. Jūsu atbildes ir saglabātas.',
  interviewReasonReconcile: 'Pēdējā saglabāšana vēl tiek apstiprināta.',
  exportTitle: 'Eksportēt',
  exportIntro: 'Lejupielādējiet šo artefaktu. Failā norādīts, no kurienes nāk tā dati.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Rediģējami slaidi ar runātāja piezīmēm',
  exportHintPdf: 'Tieši tāds, kāds redzams',
  exportHintDocx: 'Rediģējams, saglabā virsrakstus un tabulas',
  exportHintXlsx: 'Visas lapas, saglabā formulas',
  exportHintCsv: 'Tikai vērtības, UTF-8',
  exportUnavailable: 'Pašlaik nav pieejams',
  exportRunning: 'Notiek sagatavošana: {format}…',
  exportValidating: 'Notiek faila pārbaude: {format}…',
  exportReady: 'Fails {format} ir gatavs',
  exportDownload: 'Lejupielādēt: {name}',
  exportVersion: 'Eksportēta versija {number}',
  exportFailed: 'Failu {format} neizdevās izveidot.',
  exportFailedChecks: 'Fails {format} neizturēja pārbaudes, tāpēc tas netika piedāvāts.',
  exportDenied: 'Jums nav piekļuves, lai eksportētu šo artefaktu.',
  exportRevoked: 'Kopīgošana tika izslēgta, tāpēc šo artefaktu vairs nevar eksportēt.',
  exportStale:
    'Šis artefakts eksportēšanas laikā mainījās. Eksportējiet vēlreiz, lai iegūtu jaunāko versiju.',
  exportProvenanceTitle: 'Katrs fails satur',
  exportProvenanceLine: 'Izveidots ar Poneva · {title} · v{number} · {data} · izveidots {madeAt}',
  exportProvenanceAsOf: 'dati uz {time}',
  exportPlacementNotesAbout: 'Piezīmju lapā un informācijas failā blakus CSV',
  exportProvenanceRange: 'dati no {earliest} līdz {latest}',
  exportProvenanceUnknown: 'datu vecums nav zināms',
  exportAboutFile: 'Par šo failu ({format})',
  exportPlacementFooter: 'Katras lapas kājenē',
  exportPlacementSlide: 'Katra slaida kājenē',
  exportPlacementNotes: 'Piezīmju lapā',
  exportPlacementAbout: 'Informācijas failā blakus CSV',
  exportSourcesPage: 'Pievienot avotu lapu',
  exportSourcesPageHint: 'Pēdējā lapa, kurā norādīts katrs skaitlis, tā avots un laiks.',
  exportSourcesPageDeck: 'Prezentācijas pašas avotu slaids tiek pievienots.',
  exportSourcesPageSaved: 'Tiek atcerēts šim artefaktam, ieskaitot ieplānotos sūtījumus.',
  libraryKindSheetTitle: 'Veids',
  shareAccessSheetTitle: 'Piekļuve',

  requestShowAll: 'Rādīt visu',
  requestShowLess: 'Rādīt mazāk',
  approveGettingReady: 'Sagatavo…',
  approveNotReady: 'Pašlaik to nevar apstiprināt. Pārlādējiet lapu un mēģiniet vēlreiz.',
  tryWithModel: 'Mēģināt ar modeli {model}',
  tryWithRequesting: 'Notiek palaišana…',
  tryWithUnavailable: 'Pašlaik to nevar palaist.',
};

const mt: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Qed teditja abbozz tal-verżjoni {number}',
  noChangesToApply: 'M’hemm l-ebda bidla x’tapplika.',
  appliedDone: 'Applikat.',
  mentionInChat: 'Semmi fiċ-chat',

  kindMarkdown: 'Nota',
  kindKpi: 'Metrika',
  libraryExportsList: 'Jiġi esportat bħala {formats}',
  libraryOpensToRead: 'Jinfetaħ għall-qari',
  libraryReadOnly: 'Għall-qari biss',
  libraryLiveView: 'Veduta diretta',
  libraryOneNumber: 'Numru wieħed · mill-{time}',
  retry: 'Erġa’ pprova',
  close: 'Agħlaq',
  panelLoading: 'Qed jitgħabba…',
  panelEmpty: 'Għad m’hawn xejn.',
  panelDenied: 'M’għandekx aċċess għal dan.',
  panelBlocked: 'Dan qed jistenna xi ħaġa qabel ma jkun jista’ jkompli.',
  panelBlockedViewerContext:
    'Dan it-tħaddim m’għandu ebda persuna biex jaqra f’isimha, għalhekk ma jistax ikompli sakemm xi ħadd jagħżel f’isem min jaħdem.',
  panelError: 'Dan ma setax jitgħabba.',
  panelStale: 'Dan jista’ jkun skadut.',
  sourcesTitle: 'Sorsi',
  sourcesIntro: 'Minn fejn jiġi kull numru, u kemm hu aġġornat.',
  sourcesLoading: 'Is-sorsi qed jitgħabbew…',
  sourcesEmptyTitle: 'L-ebda sors',
  sourcesEmptyBody: 'Dan l-artifatt ma juża ebda dejta tal-kumpanija.',
  sourcesErrorBody: 'Is-sorsi ma setgħux jitgħabbew.',
  sourcesDeniedTitle: 'Xi sorsi huma moħbija',
  sourcesDeniedBody:
    'M’għandekx aċċess għal xi sorsi użati hawn ({count}). Itlob lil sid biex jaqsamhom.',
  sourceDeniedName: 'Sors li ma tistax tara',
  sourceAsOf: 'Aġġornat sa {time}',
  sourceAgeUnknown: 'L-età tad-dejta mhix magħrufa',
  sourceStatusCurrent: 'Attwali',
  sourceStatusStale: 'Skadut',
  sourceStatusGap: 'Nieqes mill-aħħar aġġornament',
  sourceStatusDenied: 'Ebda aċċess',
  sourceLastGood: 'L-aħħar tajjeb: {time}',
  sourceUsedBy: 'Użat minn {labels}',
  openDocument: 'Iftaħ id-dokument',
  versionsShow: 'Uri l-verżjonijiet',
  versionsHide: 'Aħbi l-verżjonijiet',
  versionsTitle: 'Verżjonijiet',
  versionsLoading: 'Il-verżjonijiet qed jitgħabbew…',
  versionsEmptyTitle: 'Għad m’hemm l-ebda verżjoni',
  versionsEmptyBody: 'L-ewwel verżjoni tidher meta jinbena dan l-artifatt.',
  versionsErrorBody: 'Il-verżjonijiet ma setgħux jitgħabbew.',
  versionLabel: 'Verżjoni {number}',
  versionOpen: 'Iftaħ il-verżjoni {number}',
  versionCurrent: 'Attwali',
  versionPublished: 'Ippubblikata',
  versionBy: 'Minn {actor}',
  versionOriginNative: 'F’Poneva',
  versionOriginMcp: 'permezz tal-aġent tiegħek stess',
  versionOriginTerminal: 'permezz tat-terminal',
  versionOriginJob: 'Tħaddim skedat',
  versionCausePerson: 'Editjat',
  versionCauseAgent: 'Aġġornament tal-aġent',
  versionCauseRefresh: 'Aġġornat',
  versionCauseRestore: 'Irrestawrat',
  versionCauseImport: 'Importat',
  versionCheckedYes: 'Ikkontrollat',
  versionCheckedNo: 'Għadu mhux ikkontrollat',
  versionCheckedInvalidated: 'Editjat mindu sar l-aħħar kontroll',
  compareStart: 'Qabbel',
  compareWith: 'Qabbel mal-verżjoni {number}',
  compareTitle: 'X’inbidel minn v{from} sa v{to}',
  compareFromMark: 'Minn',
  compareToMark: 'Sa',
  comparePick: 'Agħżel it-tieni verżjoni biex tqabbel.',
  compareNone: 'L-ebda differenza.',
  compareLoading: 'Qed iqabbel…',
  compareError: 'Il-paragun ma setax jitgħabba.',
  compareBefore: 'Qabel',
  compareAfter: 'Wara',
  compareAdded: 'Miżjud',
  compareRemoved: 'Imneħħi',
  compareChanged: 'Mibdul',
  restore: 'Irrestawra din il-verżjoni',
  restoreConfirmTitle: 'Tirrestawra l-verżjoni {number}?',
  restoreConfirmBody: 'Dan joħloq verżjoni ġdida mill-verżjoni {number}. Xejn ma jitħassar.',
  restoreConfirm: 'Irrestawra',
  restoreCancel: 'Ikkanċella',
  restoring: 'Qed jirrestawra…',
  restoreFailed: 'Il-verżjoni ma setgħetx tiġi rrestawrata.',
  restoreStale:
    'Dan l-artifatt inbidel waqt li kont qed tħares lejh. Erġa’ ttella’ l-verżjonijiet u erġa’ pprova.',
  restoreDenied: 'M’għandekx permess tirrestawra verżjonijiet.',
  restored: 'Il-verżjoni {number} ġiet irrestawrata bħala verżjoni ġdida.',
  publishTitle: 'Ippubblika',
  publishIntro: 'Il-verżjoni {number} se tkun tidher għal {audience}.',
  publishOwnerLine:
    'L-udjenza tara dak li seta’ jara {owner} meta ġiet ippubblikata din il-verżjoni.',
  publishChecksTitle: 'Kontrolli',
  checkPassed: 'Ikkontrollat',
  checkRunning: 'Qed jikkontrolla…',
  checkFailed: 'Kontroll falla',
  checkStale: 'Editjat mindu sar l-aħħar kontroll',
  publishFixInChat: 'Irranġa fiċ-chat',
  publishAction: 'Ippubblika l-verżjoni {number}',
  publishing: 'Qed jippubblika…',
  publishedDone: 'Il-verżjoni {number} hija attiva għal {audience}.',
  publishConfirmGrant:
    'Din il-verżjoni teħtieġ aċċess ġdid għal sistema tal-kumpanija. Irrevedi l-aċċess qabel tippubblika.',
  publishConfirmWrite:
    'Din il-verżjoni tikteb fuq sistema tal-kumpanija. Irrevedi l-aċċess qabel tippubblika.',
  publishConfirmDestructive: 'Din il-verżjoni tneħħi dejta. Ikkonfermaha f’Poneva l-ewwel.',
  publishConflict: 'Ġiet ippubblikata verżjoni differenti l-ewwel. Erġa’ ttella’ biex taraha.',
  publishBlockedNotAuthorized: 'M’għandekx permess tippubblika.',
  publishBlockedCheckFailed: 'Kontroll falla, għalhekk din il-verżjoni ma tistax tiġi ppubblikata.',
  publishBlockedCheckStale:
    'L-artifatt inbidel wara l-aħħar kontroll tiegħu. Ikkontrollah mill-ġdid.',
  publishBlockedObjects: 'Xi fajls ma setgħux jiġu vverifikati. Erġa’ pprova wara ftit.',
  publishBlockedPolicy: 'Is-settings tal-kumpanija nbidlu. Irrevedi u erġa’ pprova.',
  publishBlockedApproval: 'Il-konferma m’għadhiex valida. Ikkonferma mill-ġdid.',
  publishBlockedDependency: 'Servizz meħtieġ mhuwiex disponibbli. Erġa’ pprova aktar tard.',
  publishAsk: 'Itlob pubblikazzjoni minn {publishers}',
  publishAskFooter: 'Persuni li jistgħu jippubblikaw: {publishers}.',
  publishAskNone: 'Itlob lil xi ħadd li jista’ jippubblika.',
  publishWarnings: 'Tajjeb li tagħti ħarsa qabel tippubblika',
  checkWarnStale: 'Skadut: {subject} ({time}).',
  checkWarnGap: 'Nieqes mill-aħħar aġġornament: {subject}.',
  checkWarnUnknownSource: 'Sors li ma tistax tara jeħtieġ ħarsa.',
  checkRefreshFirst: 'Aġġorna l-ewwel',
  checkFailedRow: 'Kontroll falla għal {subject}.',
  checkFailedGeneric: 'Kontroll falla.',
  publishReviewAccess: 'Irrevedi l-aċċess',
  publishRemovalBody: 'v{number} tneħħi {what} ({count}). L-ewwel jiġi salvat punt ta’ restawr.',
  publishReviewRemoval: 'Irrevedi dak li jitneħħa',
  publishAndRemove: 'Ippubblika u neħħi',
  publishReviewFirst: 'Irrevedi dak li jitneħħa qabel tippubblika.',
  publishDoneTitle: 'Il-verżjoni {number} hija attiva għal {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Se jsibuha fil-Librerija tagħhom u mwaħħla għat-tim.',
  publishUndo: 'Annulla',
  publishCopyLink: 'Ikkopja l-link',
  publishDone: 'Lest',
  shareRoleViewMeaning: 'Jista’ jiftaħha u jaqrah',
  shareRoleEditMeaning: 'Jista’ jbiddlu fiċ-chat u bl-ispettur',
  shareRolePublishMeaning: 'Jista’ wkoll jippubblikah u jneħħih mill-pubblikazzjoni',
  shareRoleMenu: 'Rwol għal {name}',
  shareUnpublishConfirm: 'In-nies jitilfu l-aċċess. L-artifatt u l-verżjonijiet tiegħu jinżammu.',
  shareUnpublishCancel: 'Żommu attiv',
  sendDestinationLine: '{provider} · persuni: {count}',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Kulħadd f’{destination} jara s-sommarju. {outside} minn {total} mhumiex fl-udjenza ta’ dan l-artifatt, għalhekk il-link turihom “Itlob aċċess”.',
  sendAudienceGeneric: 'In-nies barra mill-udjenza jaraw “Itlob aċċess”.',
  sendPreview: 'X’se jaraw',
  shareTitle: 'Aqsam',
  shareLoading: 'Il-qsim qed jitgħabba…',
  shareErrorBody: 'Il-qsim ma setax jitgħabba.',
  sharePrivate: 'Int biss tista’ tara dan l-artifatt.',
  shareShared: 'Jidher għal {audience}.',
  shareRevoked: 'Il-qsim intefa. Ħadd ieħor ma jista’ jiftaħ dan l-artifatt.',
  shareOwnerSnapshot:
    'L-udjenza tara n-numri kif ġew ippubblikati. Ma tistax taġġorna jew teditja.',
  shareRoleView: 'Jista’ jara',
  shareRoleEdit: 'Jista’ jeditja',
  shareRolePublish: 'Jista’ jippubblika',
  shareLivePublished: 'Attiv: verżjoni {number}',
  shareUnpublish: 'Neħħi mill-pubblikazzjoni',
  shareManageDenied: 'Editur biss jista’ jbiddel min jara dan.',
  sendTitle: 'Ibgħat lil',
  sendIntro:
    'Ibgħat link u sommarju permezz ta’ app konnessa. Int tapprova kull ħaġa li tinbagħat.',
  sendLoading: 'Id-destinazzjonijiet qed jitgħabbew…',
  sendErrorBody: 'Id-destinazzjonijiet ma setgħux jitgħabbew.',
  sendNone:
    'L-ebda app konnessa ma tista’ tirċievi dan għadu. Qabbad Slack, Teams jew email l-ewwel.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'Email',
  sendAudience: 'Jintbagħat lil {audience}',
  sendSummary: 'Messaġġ',
  sendLeaves: 'Jinbagħat link.',
  sendLeavesAttachment: 'Jinbagħtu link u l-fajl irrevedut.',
  sendNoAttachments: 'Din id-destinazzjoni ma tistax tirċievi fajls, għalhekk jinbagħat link biss.',
  sendScheduled: 'L-bgħatiet skedati jeħtieġu l-approvazzjoni tiegħek kull darba.',
  sendAction: 'Irrevedi u ibgħat',
  sendFileUnsupported:
    'Din id-destinazzjoni ma tistax tirċievi l-fajl. Ibgħat link biss, jew agħżel destinazzjoni oħra.',
  sendLinkOnly: 'Ibgħat link biss',
  sendWaiting: 'Qed jistenna l-approvazzjoni tiegħek. Ma ntbagħat xejn.',
  sendDelivered: 'Intbagħat lil {destination}.',
  sendFailed: 'Ma ntbagħatx. Xejn ma ħareġ minn Poneva.',
  sendUnknown:
    'Ma stajniex nikkonfermaw il-kunsinna lil {destination}. Iċċekkja hemm qabel terġa’ tibgħat.',
  sendBlockedNotAuthorized: 'M’għandekx permess tibgħat dan.',
  sendBlockedUnsupported: 'Din id-destinazzjoni mhix appoġġjata.',
  sendBlockedApproval: 'L-approvazzjoni m’għadhiex valida. Irrevedi u erġa’ ibgħat.',
  sendBlockedChanged: 'Il-messaġġ jew id-destinatarju nbidel. Irrevedih mill-ġdid.',
  sendBlockedDependency: 'L-app konnessa mhix disponibbli. Erġa’ pprova aktar tard.',
  libraryTitle: 'Librerija',
  librarySegmentAll: 'Kollha',
  librarySegmentMine: 'Tiegħi',
  librarySegmentShared: 'Maqsuma miegħi',
  librarySearch: 'Fittex artifatti',
  libraryKindAll: 'It-tipi kollha',
  libraryKindReports: 'Rapporti u paġni',
  libraryKindDecks: 'Preżentazzjonijiet',
  libraryKindDocuments: 'Dokumenti',
  libraryKindSpreadsheets: 'Fogli tal-kalkolu',
  libraryKindApps: 'Apps',
  libraryAppsMatchNoun:
    'one:app taqbel f’Apps →|two:apps jaqblu f’Apps →|few:apps jaqblu f’Apps →|many:app jaqblu f’Apps →|other:app jaqblu f’Apps →',
  libraryMake: 'Ibda fiċ-chat',
  libraryEmptySharedHint: 'Meta xi ħadd jaqsam artifatt miegħek, jidher hawn.',
  libraryRefreshFailedAt: 'Ma setax jiġi aġġornat fi {time}.',
  libraryRefreshTryAgain: 'Erġa’ pprova',
  libraryVia: 'permezz ta’ {owner}',
  savedPrivate: 'Privat',
  savedVersion: 'Verżjoni {n}',
  libraryLoading: 'Il-Librerija tiegħek qed titgħabba…',
  libraryErrorBody: 'Il-Librerija tiegħek ma setgħetx titgħabba issa.',
  startersLoadFailed: 'Il-mudelli ma setgħux jitgħabbew bħalissa.',
  libraryCannotOpen: 'Għadu ma jistax jinfetaħ hawn: “{title}”.',
  libraryEmptyTitle: 'Għad m’hawn xejn',
  libraryEmptyAllTitle: 'Agħmel xi ħaġa li tiswa li taqsamha.',
  libraryEmptyAllBody:
    'Id-dokumenti, il-preżentazzjonijiet u l-apps li tagħmel fiċ-chat, jew li oħrajn jaqsmu miegħek, jidhru hawn.',
  libraryEmptyMineBody: 'L-artifatti li tagħmel jidhru hawn.',
  libraryEmptySharedBody: 'Għad ma tqasam xejn miegħek.',
  libraryNoMatchTitle: 'L-ebda riżultat',
  libraryNoMatchBody: 'Ipprova kelma oħra jew tip differenti.',
  libraryMore: 'Uri aktar',
  libraryOwner: 'Minn {owner}',
  libraryUpdated: 'Editjat {time}',
  libraryPublishedAs: 'Ippubblikat · v{number}',
  libraryDraft: 'Abbozz',
  libraryUnpublished: 'Mhux ippubblikat',
  libraryRefreshFailed: 'L-aġġornament falla',
  libraryRefreshRunning: 'Qed jiġi aġġornat',
  libraryAudience: 'Għal {audience}',
  kindReport: 'Rapport',
  kindPage: 'Paġna',
  kindSite: 'Sit',
  kindDeck: 'Preżentazzjoni',
  kindDocument: 'Dokument',
  kindApp: 'App',
  kindSpreadsheet: 'Foll tal-kalkolu',
  kindDashboard: 'Pannell tal-kontroll',
  kindTable: 'Tabella',
  kindChart: 'Grafiku',
  kindExportsReport: 'Jiġi esportat għal PDF',
  kindExportsPage: 'Paġna għall-qari biss',
  kindExportsSite: 'Sit għall-qari biss',
  kindExportsDeck: 'Ippreżenta, jew esporta għal PowerPoint u PDF',
  kindExportsDocument: 'Jiġi esportat għal Word u PDF',
  kindExportsSpreadsheet: 'Jiġi esportat għal Excel u CSV',
  kindExportsDashboard: 'Veduta ħajja',
  kindExportsTable: 'Jiġi esportat għal Excel u CSV',
  kindExportsChart: 'Veduta tal-grafiku',
  agentPublished: '{agent} ippubblika {title} v{number}.',
  agentCheckedOn: 'Ikkontrollat fuq {views}, {themes}.',
  agentUndo: 'Annulla',
  agentRestorePrevious: 'Irrestawra v{number}',
  agentHeld: '{title} v{number} qed jistenna bħala abbozz.',
  agentHeldMismatch: 'In-numri li ma qablux mas-sorsi tagħhom: {count}.',
  agentHeldCheck: 'Kontroll ma għaddiex.',
  agentFixInChat: 'Irranġa fiċ-chat',
  agentConfirmDrop:
    '{agent} jixtieq ineħħi {what} minn {title}. L-ewwel jiġi salvat punt ta’ restawr. Xejn ma jinbidel sakemm tiddeċiedi int.',
  agentConfirmDropCount:
    '{agent} jixtieq ineħħi {what} (valuri: {count}) minn {title}. L-ewwel jiġi salvat punt ta’ restawr. Xejn ma jinbidel sakemm tiddeċiedi int.',
  agentViewDesktop: 'kompjuter tal-mejda',
  agentViewTablet: 'tablet',
  agentViewPhone: 'telefon',
  agentThemeLight: 'ċar',
  agentThemeDark: 'skur',
  agentLookAt: 'Ħares lejn v{number}',
  agentConfirmWrite: '{agent} jixtieq jikteb {what}. Xejn ma jinkiteb sakemm tiddeċiedi int.',
  agentConfirmGrant:
    '{agent} jixtieq aċċess għal {what}. Xejn ma jiġi maqsum sakemm tiddeċiedi int.',
  agentReview: 'Irrevedi',
  agentSkipped: '{agent} qabeż {part}.',
  agentSkippedWhy:
    'Jaqra {system} ta’ kull persuna b’mod individwali, u dan it-tħaddim m’għandu ebda persuna biex jaqra f’isimha.',
  agentChooseWho: 'Agħżel f’isem min jaħdem',
  agentChangeInChat: 'Biddel fiċ-chat',
  agentFailed: '{agent} ma setax jaġġorna {title}.',
  agentFailedBody: 'L-aħħar verżjoni ppubblikata għadha attiva.',
  agentRetry: 'Erġa’ pprova',
  agentRunning: '{agent} qed jaġġorna {title}.',
  agentMakesTitle: 'Jagħmel u jaġġorna',
  agentMakesLoading: 'Qed jitgħabba…',
  agentMakesErrorBody: 'Dan ma setax jitgħabba.',
  agentMakesNone: 'Dan l-aġent għad ma jagħmel l-ebda artifatt.',
  agentMakesNew: 'Artifatt ġdid f’kull tħaddim',
  agentMakesUpdate: 'Jaġġorna artifatt wieħed f’kull tħaddim',
  agentNextRun: 'It-tħaddim li jmiss {when}',
  agentNoSchedule: 'Mhux skedat',
  agentCreditsPerRun: 'Madwar {credits} krediti għal kull tħaddim',
  agentCreditsUnknown: 'Il-krediti għal kull tħaddim għadhom mhumiex magħrufa',
  agentLastResult: 'L-aħħar riżultat',
  interviewTitle: 'Issettjah',
  interviewIntro: 'Ftit mistoqsijiet jiffurmawh.',
  interviewOptional: '(mhux obbligatorju)',
  interviewAnswerNeeded: 'Wieġeb dan biex tkompli.',
  interviewReadOnly: 'Għall-qari biss',
  interviewChange: 'Biddel',
  interviewConnectionYours: 'il-konnessjoni {system} tiegħek',
  interviewConnectionOther: 'il-konnessjoni {system} ta’ {name}',
  interviewDateAny: 'Kull perjodu',
  interviewDatePrevious: 'Ix-xahar preċedenti',
  interviewDateNext: 'Ix-xahar li jmiss',
  interviewDatePresets: 'Firxiet rapidi',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Neħħi',
  interviewDateDone: 'Lest',
  interviewChooseSource: 'Agħżel sors',
  interviewSourceChosen: 'Sors magħżula',
  interviewSourceUnavailable:
    'L-għażla ta’ sors għadha mhix disponibbli hawn, għalhekk ma tistax tkompli.',
  interviewSourceUnavailableOptional:
    'L-għażla ta’ sors għadha mhux disponibbli hawn. Tista’ tkompli mingħajru.',
  interviewStarterWithdrawn:
    'Dan il-mudell m’għadux disponibbli. L-abbozzi li diġà bdejt minnu għadhom jinfetħu.',
  interviewDateFrom: 'Minn',
  interviewDateTo: 'Sa',
  interviewContinue: 'Kompli',
  interviewGrantTitle: 'Tippermetti l-qari ta’ {source}?',
  interviewGrantBody:
    'Juża {connection}, għall-qari biss, għal {scope}. Xejn ma jinqara sakemm tippermetti, u ma jintwerew l-ebda ringieli ta’ kampjun.',
  interviewAllow: 'Ippermetti',
  interviewNotNow: 'Mhux issa',
  interviewConfiguring: 'Qed jiġi ssettjat mit-tweġibiet tiegħek…',
  interviewChecking: 'Qed jiġi kkontrollat qabel jinfetaħ…',
  interviewComplete: '{title} lest.',
  interviewOpen: 'Iftaħ {title}',
  interviewBlocked: 'Dan ma setax ikompli.',
  interviewFailed: 'L-issettjar falla. It-tweġibiet tiegħek inżammu.',
  interviewCancelled: 'Dan ġie kkanċellat.',
  interviewSaving: 'Qed jiġi salvat…',
  interviewSaved: 'Salvat',
  interviewConflict:
    'Dan l-abbozz inbidel x’imkien ieħor. It-tweġibiet tiegħek jinżammu hawn sakemm tittella’ l-verżjoni attwali tal-abbozz, li tieħu posthom.',
  interviewReloadCurrent: 'Tella’ l-abbozz attwali',
  interviewUnsettled:
    'Ma stajniex nikkonfermaw l-aħħar salvataġġ tiegħek. It-tweġibiet tiegħek jinżammu hawn.',
  interviewCheckAgain: 'Ikkontrolla mill-ġdid',
  finishAction: 'Issejvja privatament',
  finishSaving: 'Qed jissejvja…',
  finishSaved: '“{title}” ġie ssejvjat u tarah int biss.',
  finishNotSaved: 'Ma ġiex issejvjat. L-abbozz tiegħek għadu hawn.',
  finishUnconfirmed: 'Ma stajniex nikkonfermaw jekk ġiex issejvjat.',
  candidateInvalid: 'L-abbozz ma setax isir dokument.',
  finishOpenInLibrary: 'Iftaħ fil-Librerija',
  savedOpenFailed: 'Ma setax jinfetaħ bħalissa.',
  savedInLibrary: 'Qiegħed fil-Librerija tiegħek.',
  openSavedTitle: 'Iftaħ {title} →',
  interviewReasonNotAuthorized: 'M’għandekx permess tibdel dan l-abbozz.',
  interviewReasonDraftNotOpen: 'Dan l-abbozz m’għadux miftuħ.',
  interviewReasonInvalid: 'Waħda mit-tweġibiet mhix valida.',
  interviewReasonObjects: 'Xi fajls ma setgħux jiġu vverifikati. It-tweġibiet tiegħek inżammu.',
  interviewReasonDependency: 'Servizz meħtieġ mhuwiex disponibbli. It-tweġibiet tiegħek inżammu.',
  interviewReasonReconcile: 'L-aħħar salvataġġ għadu qed jiġi kkonfermat.',
  exportTitle: 'Esporta',
  exportIntro: 'Niżżel dan l-artifatt. Il-fajl jgħid minn fejn tiġi d-dejta tiegħu.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Slajds editjabbli b’noti tal-kelliem',
  exportHintPdf: 'Eżattament kif jidher',
  exportHintDocx: 'Editjabbli, iżomm l-intestaturi u t-tabelli',
  exportHintXlsx: 'Il-folji kollha, iżomm il-formuli',
  exportHintCsv: 'Valuri biss, UTF-8',
  exportUnavailable: 'Mhux disponibbli bħalissa',
  exportRunning: '{format} qed jitħejja…',
  exportValidating: 'Qed jiġi kkontrollat il-fajl {format}…',
  exportReady: 'Il-fajl {format} huwa lest',
  exportDownload: 'Niżżel {name}',
  exportVersion: 'Verżjoni {number} esportata',
  exportFailed: 'Il-fajl {format} ma setax jinħoloq.',
  exportFailedChecks:
    'Il-fajl {format} ma għaddiex mill-kontrolli tiegħu, għalhekk ma ġiex offrut.',
  exportDenied: 'M’għandekx aċċess biex tesporta dan l-artifatt.',
  exportRevoked: 'Il-qsim intefa, għalhekk dan l-artifatt ma jistax jiġi esportat aktar.',
  exportStale:
    'Dan l-artifatt inbidel waqt l-esportazzjoni. Esporta mill-ġdid biex tikseb l-aħħar verżjoni.',
  exportProvenanceTitle: 'Kull fajl jinkludi',
  exportProvenanceLine: 'Magħmul b’Poneva · {title} · v{number} · {data} · magħmul {madeAt}',
  exportProvenanceAsOf: 'dejta sa {time}',
  exportPlacementNotesAbout: 'Fil-folja Noti, u fi fajl ta’ tagħrif ħdejn is-CSV',
  exportProvenanceRange: 'dejta minn {earliest} sa {latest}',
  exportProvenanceUnknown: 'l-età tad-dejta mhix magħrufa',
  exportAboutFile: 'Dwar dan il-fajl ({format})',
  exportPlacementFooter: 'Fil-qiegħ ta’ kull paġna',
  exportPlacementSlide: 'Fil-qiegħ ta’ kull slajd',
  exportPlacementNotes: 'Fil-folja Noti',
  exportPlacementAbout: 'Fi fajl ta’ tagħrif ħdejn is-CSV',
  exportSourcesPage: 'Żid paġna tas-sorsi',
  exportSourcesPageHint: 'Paġna tal-aħħar li telenka kull numru, is-sors tiegħu u ż-żmien tiegħu.',
  exportSourcesPageDeck: 'Is-slajd tas-Sorsi tal-preżentazzjoni jmur miegħu.',
  exportSourcesPageSaved: 'Tiftakar għal dan l-artifatt, inklużi l-bgħatiet skedati.',
  libraryKindSheetTitle: 'Tip',
  shareAccessSheetTitle: 'Aċċess',

  requestShowAll: 'Uri kollox',
  requestShowLess: 'Uri inqas',
  approveGettingReady: 'Qed jitħejja…',
  approveNotReady:
    'Dan ma jistax jiġi approvat bħalissa. Erġa’ tella’ l-paġna, imbagħad erġa’ pprova.',
  tryWithModel: 'Ipprova b’{model}',
  tryWithRequesting: 'Qed jibda…',
  tryWithUnavailable: 'Dan ma jistax jibda bħalissa.',
};

const nl: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Je bewerkt een concept van versie {number}',
  noChangesToApply: 'Geen wijzigingen om toe te passen.',
  appliedDone: 'Toegepast.',
  mentionInChat: 'Vermelden in chat',

  kindMarkdown: 'Notitie',
  kindKpi: 'Meetwaarde',
  libraryExportsList: 'Exporteert naar {formats}',
  libraryOpensToRead: 'Opent om te lezen',
  libraryReadOnly: 'Alleen lezen',
  libraryLiveView: 'Live weergave',
  libraryOneNumber: 'Eén getal · per {time}',
  retry: 'Opnieuw proberen',
  close: 'Sluiten',
  panelLoading: 'Laden…',
  panelEmpty: 'Nog niets hier.',
  panelDenied: 'Je hebt hier geen toegang toe.',
  panelBlocked: 'Dit wacht op iets voordat het kan doorgaan.',
  panelBlockedViewerContext:
    'Deze run heeft geen persoon om als te lezen, dus hij kan niet doorgaan totdat iemand kiest als wie hij draait.',
  panelError: 'Dit kon niet worden geladen.',
  panelStale: 'Dit is mogelijk verouderd.',
  sourcesTitle: 'Bronnen',
  sourcesIntro: 'Waar elk getal vandaan komt en hoe actueel het is.',
  sourcesLoading: 'Bronnen laden…',
  sourcesEmptyTitle: 'Geen bronnen',
  sourcesEmptyBody: 'Dit artefact gebruikt geen bedrijfsgegevens.',
  sourcesErrorBody: 'Bronnen konden niet worden geladen.',
  sourcesDeniedTitle: 'Sommige bronnen zijn verborgen',
  sourcesDeniedBody:
    'Je hebt geen toegang tot {count} bronnen die hier worden gebruikt. Vraag een eigenaar om ze te delen.',
  sourceDeniedName: 'Bron die je niet kunt zien',
  sourceAsOf: 'Per {time}',
  sourceAgeUnknown: 'Leeftijd van gegevens onbekend',
  sourceStatusCurrent: 'Actueel',
  sourceStatusStale: 'Verouderd',
  sourceStatusGap: 'Ontbrak bij de laatste vernieuwing',
  sourceStatusDenied: 'Geen toegang',
  sourceLastGood: 'Laatst goed: {time}',
  sourceUsedBy: 'Gebruikt door {labels}',
  openDocument: 'Document openen',
  versionsShow: 'Versies tonen',
  versionsHide: 'Versies verbergen',
  versionsTitle: 'Versies',
  versionsLoading: 'Versies laden…',
  versionsEmptyTitle: 'Nog geen versies',
  versionsEmptyBody: 'De eerste versie verschijnt zodra dit artefact is gebouwd.',
  versionsErrorBody: 'Versies konden niet worden geladen.',
  versionLabel: 'Versie {number}',
  versionOpen: 'Versie {number} openen',
  versionCurrent: 'Huidig',
  versionPublished: 'Gepubliceerd',
  versionBy: 'Door {actor}',
  versionOriginNative: 'In Poneva',
  versionOriginMcp: 'via je eigen agent',
  versionOriginTerminal: 'via de terminal',
  versionOriginJob: 'Geplande run',
  versionCausePerson: 'Bewerkt',
  versionCauseAgent: 'Agentupdate',
  versionCauseRefresh: 'Vernieuwd',
  versionCauseRestore: 'Hersteld',
  versionCauseImport: 'Geïmporteerd',
  versionCheckedYes: 'Gecontroleerd',
  versionCheckedNo: 'Nog niet gecontroleerd',
  versionCheckedInvalidated: 'Bewerkt sinds de laatste controle',
  compareStart: 'Vergelijken',
  compareWith: 'Vergelijken met versie {number}',
  compareTitle: 'Wat er veranderde van v{from} naar v{to}',
  compareFromMark: 'Van',
  compareToMark: 'Naar',
  comparePick: 'Kies een tweede versie om mee te vergelijken.',
  compareNone: 'Geen verschillen.',
  compareLoading: 'Vergelijken…',
  compareError: 'De vergelijking kon niet worden geladen.',
  compareBefore: 'Voor',
  compareAfter: 'Na',
  compareAdded: 'Toegevoegd',
  compareRemoved: 'Verwijderd',
  compareChanged: 'Gewijzigd',
  restore: 'Deze versie herstellen',
  restoreConfirmTitle: 'Versie {number} herstellen?',
  restoreConfirmBody:
    'Hiermee wordt een nieuwe versie gemaakt op basis van versie {number}. Er wordt niets verwijderd.',
  restoreConfirm: 'Herstellen',
  restoreCancel: 'Annuleren',
  restoring: 'Herstellen…',
  restoreFailed: 'De versie kon niet worden hersteld.',
  restoreStale:
    'Dit artefact is gewijzigd terwijl je keek. Laad de versies opnieuw en probeer het nog eens.',
  restoreDenied: 'Je hebt geen toestemming om versies te herstellen.',
  restored: 'Versie {number} hersteld als nieuwe versie.',
  publishTitle: 'Publiceren',
  publishIntro: 'Versie {number} wordt zichtbaar voor {audience}.',
  publishOwnerLine: 'Kijkers zien wat {owner} kon zien toen deze versie werd gepubliceerd.',
  publishChecksTitle: 'Controles',
  checkPassed: 'Gecontroleerd',
  checkRunning: 'Controleren…',
  checkFailed: 'Een controle is mislukt',
  checkStale: 'Bewerkt sinds de laatste controle',
  publishFixInChat: 'Oplossen in de chat',
  publishAction: 'Versie {number} publiceren',
  publishing: 'Publiceren…',
  publishedDone: 'Versie {number} staat live voor {audience}.',
  publishConfirmGrant:
    'Deze versie heeft nieuwe toegang tot een bedrijfssysteem nodig. Bekijk de toegang voordat je publiceert.',
  publishConfirmWrite:
    'Deze versie schrijft naar een bedrijfssysteem. Bekijk de toegang voordat je publiceert.',
  publishConfirmDestructive: 'Deze versie verwijdert gegevens. Bevestig dat eerst in Poneva.',
  publishConflict: 'Er is eerst een andere versie gepubliceerd. Laad opnieuw om die te zien.',
  publishBlockedNotAuthorized: 'Je hebt geen toestemming om te publiceren.',
  publishBlockedCheckFailed:
    'Een controle is mislukt, dus deze versie kan niet worden gepubliceerd.',
  publishBlockedCheckStale:
    'Het artefact is gewijzigd na de laatste controle. Controleer het opnieuw.',
  publishBlockedObjects:
    'Sommige bestanden konden niet worden geverifieerd. Probeer het zo meteen opnieuw.',
  publishBlockedPolicy: 'De bedrijfsinstellingen zijn gewijzigd. Bekijk ze en probeer het opnieuw.',
  publishBlockedApproval: 'De bevestiging is niet meer geldig. Bevestig opnieuw.',
  publishBlockedDependency: 'Een benodigde service is niet beschikbaar. Probeer het later opnieuw.',
  publishAsk: 'Vraag {publishers} om te publiceren',
  publishAskFooter: 'Mensen die kunnen publiceren: {publishers}.',
  publishAskNone: 'Vraag iemand die kan publiceren.',
  publishWarnings: 'Goed om te bekijken voordat je publiceert',
  checkWarnStale: '{subject} is verouderd ({time}).',
  checkWarnGap: '{subject} ontbrak bij de laatste vernieuwing.',
  checkWarnUnknownSource: 'Een bron die je niet kunt zien, verdient aandacht.',
  checkRefreshFirst: 'Eerst vernieuwen',
  checkFailedRow: '{subject} heeft de controle niet doorstaan.',
  checkFailedGeneric: 'Een controle is mislukt.',
  publishReviewAccess: 'Toegang bekijken',
  publishRemovalBody:
    'v{number} verwijdert {what} ({count}). Eerst wordt een herstelpunt opgeslagen.',
  publishReviewRemoval: 'Bekijk wat er wordt verwijderd',
  publishAndRemove: 'Publiceren en verwijderen',
  publishReviewFirst: 'Bekijk wat er wordt verwijderd voordat je publiceert.',
  publishDoneTitle: 'Versie {number} staat live voor {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Ze vinden het in hun bibliotheek en vastgepind voor het team.',
  publishUndo: 'Ongedaan maken',
  publishCopyLink: 'Link kopiëren',
  publishDone: 'Klaar',
  shareRoleViewMeaning: 'Kan het openen en lezen',
  shareRoleEditMeaning: 'Kan het wijzigen in de chat en met de inspector',
  shareRolePublishMeaning: 'Kan ook publiceren en depubliceren',
  shareRoleMenu: 'Rol voor {name}',
  shareUnpublishConfirm: 'Mensen verliezen toegang. Het artefact en de versies blijven bewaard.',
  shareUnpublishCancel: 'Live houden',
  sendDestinationLine: '{provider} · {count} mensen',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Iedereen in {destination} ziet de samenvatting. {outside} van de {total} horen niet bij het publiek van dit artefact, dus de link toont hen “Toegang vragen”.',
  sendAudienceGeneric: 'Mensen buiten het publiek zien “Toegang vragen”.',
  sendPreview: 'Wat zij te zien krijgen',
  shareTitle: 'Delen',
  shareLoading: 'Delen laden…',
  shareErrorBody: 'Delen kon niet worden geladen.',
  sharePrivate: 'Alleen jij kunt dit artefact zien.',
  shareShared: 'Zichtbaar voor {audience}.',
  shareRevoked: 'Delen is uitgezet. Niemand anders kan dit artefact openen.',
  shareOwnerSnapshot:
    'Kijkers zien de cijfers zoals gepubliceerd. Ze kunnen niet vernieuwen of bewerken.',
  shareRoleView: 'Kan bekijken',
  shareRoleEdit: 'Kan bewerken',
  shareRolePublish: 'Kan publiceren',
  shareLivePublished: 'Live: versie {number}',
  shareUnpublish: 'Depubliceren',
  shareManageDenied: 'Alleen een bewerker kan wijzigen wie dit ziet.',
  sendTitle: 'Versturen naar',
  sendIntro:
    'Verstuur een link en samenvatting via een gekoppelde app. Jij keurt elke verzending goed.',
  sendLoading: 'Bestemmingen laden…',
  sendErrorBody: 'Bestemmingen konden niet worden geladen.',
  sendNone: 'Nog geen gekoppelde app kan dit ontvangen. Koppel eerst Slack, Teams of e-mail.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-mail',
  sendAudience: 'Gaat naar {audience}',
  sendSummary: 'Bericht',
  sendLeaves: 'Er wordt een link verstuurd.',
  sendLeavesAttachment: 'Er worden een link en het gecontroleerde bestand verstuurd.',
  sendNoAttachments:
    'Deze bestemming kan geen bestanden ontvangen, dus alleen een link wordt verstuurd.',
  sendScheduled: 'Geplande verzendingen hebben elke keer jouw goedkeuring nodig.',
  sendAction: 'Bekijken en versturen',
  sendFileUnsupported:
    'Deze bestemming kan het bestand niet ontvangen. Verstuur alleen een link of kies een andere bestemming.',
  sendLinkOnly: 'Alleen een link versturen',
  sendWaiting: 'Wacht op jouw goedkeuring. Er is niets verstuurd.',
  sendDelivered: 'Verstuurd naar {destination}.',
  sendFailed: 'Het is niet verstuurd. Er heeft niets Poneva verlaten.',
  sendUnknown:
    'We konden de aflevering bij {destination} niet bevestigen. Controleer dat daar voordat je opnieuw verstuurt.',
  sendBlockedNotAuthorized: 'Je hebt geen toestemming om dit te versturen.',
  sendBlockedUnsupported: 'Deze bestemming wordt niet ondersteund.',
  sendBlockedApproval: 'De goedkeuring is niet meer geldig. Bekijk en verstuur opnieuw.',
  sendBlockedChanged: 'Het bericht of de ontvanger is gewijzigd. Bekijk het opnieuw.',
  sendBlockedDependency: 'De gekoppelde app is niet beschikbaar. Probeer het later opnieuw.',
  libraryTitle: 'Bibliotheek',
  librarySegmentAll: 'Alles',
  librarySegmentMine: 'Van mij',
  librarySegmentShared: 'Met mij gedeeld',
  librarySearch: 'Artefacten zoeken',
  libraryKindAll: 'Alle soorten',
  libraryKindReports: 'Rapporten en pagina’s',
  libraryKindDecks: 'Presentaties',
  libraryKindDocuments: 'Documenten',
  libraryKindSpreadsheets: 'Spreadsheets',
  libraryKindApps: 'Apps',
  libraryAppsMatchNoun: 'one:app komt overeen in Apps →|other:apps komen overeen in Apps →',
  libraryMake: 'Begin in de chat',
  libraryEmptySharedHint: 'Als iemand een artefact met je deelt, verschijnt het hier.',
  libraryRefreshFailedAt: 'Vernieuwen mislukt op {time}.',
  libraryRefreshTryAgain: 'Opnieuw proberen',
  libraryVia: 'via {owner}',
  savedPrivate: 'Privé',
  savedVersion: 'Versie {n}',
  libraryLoading: 'Je bibliotheek laden…',
  libraryErrorBody: 'Je bibliotheek kon op dit moment niet worden geladen.',
  startersLoadFailed: 'Sjablonen konden nu niet worden geladen.',
  libraryCannotOpen: '“{title}” kan hier nog niet worden geopend.',
  libraryEmptyTitle: 'Nog niets hier',
  libraryEmptyAllTitle: 'Maak iets wat het delen waard is.',
  libraryEmptyAllBody:
    'Documenten, presentaties en apps die je in de chat maakt, of die anderen met je delen, verschijnen hier.',
  libraryEmptyMineBody: 'Artefacten die je maakt, verschijnen hier.',
  libraryEmptySharedBody: 'Er is nog niets met je gedeeld.',
  libraryNoMatchTitle: 'Geen resultaten',
  libraryNoMatchBody: 'Probeer een ander woord of een andere soort.',
  libraryMore: 'Meer tonen',
  libraryOwner: 'Door {owner}',
  libraryUpdated: 'Bewerkt {time}',
  libraryPublishedAs: 'Gepubliceerd · v{number}',
  libraryDraft: 'Concept',
  libraryUnpublished: 'Niet gepubliceerd',
  libraryRefreshFailed: 'Vernieuwen mislukt',
  libraryRefreshRunning: 'Vernieuwen',
  libraryAudience: 'Voor {audience}',
  kindReport: 'Rapport',
  kindPage: 'Pagina',
  kindSite: 'Site',
  kindDeck: 'Presentatie',
  kindDocument: 'Document',
  kindApp: 'App',
  kindSpreadsheet: 'Spreadsheet',
  kindDashboard: 'Dashboard',
  kindTable: 'Tabel',
  kindChart: 'Grafiek',
  kindExportsReport: 'Exporteert naar PDF',
  kindExportsPage: 'Alleen-lezen pagina',
  kindExportsSite: 'Alleen-lezen site',
  kindExportsDeck: 'Presenteren, of exporteren naar PowerPoint en PDF',
  kindExportsDocument: 'Exporteert naar Word en PDF',
  kindExportsSpreadsheet: 'Exporteert naar Excel en CSV',
  kindExportsDashboard: 'Liveweergave',
  kindExportsTable: 'Exporteert naar Excel en CSV',
  kindExportsChart: 'Grafiekweergave',
  agentPublished: '{agent} publiceerde {title} v{number}.',
  agentCheckedOn: 'Gecontroleerd op {views}, {themes}.',
  agentUndo: 'Ongedaan maken',
  agentRestorePrevious: 'v{number} herstellen',
  agentHeld: '{title} v{number} wacht als concept.',
  agentHeldMismatch: '{count} getallen kwamen niet overeen met hun bronnen.',
  agentHeldCheck: 'Een controle is niet geslaagd.',
  agentFixInChat: 'Oplossen in de chat',
  agentConfirmDrop:
    '{agent} wil {what} verwijderen uit {title}. Eerst wordt een herstelpunt opgeslagen. Er verandert niets totdat jij beslist.',
  agentConfirmDropCount:
    '{agent} wil {what} ({count} waarden) verwijderen uit {title}. Eerst wordt een herstelpunt opgeslagen. Er verandert niets totdat jij beslist.',
  agentViewDesktop: 'computer',
  agentViewTablet: 'tablet',
  agentViewPhone: 'telefoon',
  agentThemeLight: 'licht thema',
  agentThemeDark: 'donker thema',
  agentLookAt: 'Bekijk v{number}',
  agentConfirmWrite: '{agent} wil {what} schrijven. Er wordt niets geschreven totdat jij beslist.',
  agentConfirmGrant: '{agent} wil toegang tot {what}. Er wordt niets gedeeld totdat jij beslist.',
  agentReview: 'Bekijken',
  agentSkipped: '{agent} heeft {part} overgeslagen.',
  agentSkippedWhy:
    'Hij leest het eigen {system} van elke persoon, en deze run heeft geen persoon om als te lezen.',
  agentChooseWho: 'Kies als wie hij draait',
  agentChangeInChat: 'Wijzigen in de chat',
  agentFailed: '{agent} kon {title} niet vernieuwen.',
  agentFailedBody: 'De laatst gepubliceerde versie staat nog live.',
  agentRetry: 'Opnieuw proberen',
  agentRunning: '{agent} werkt {title} bij.',
  agentMakesTitle: 'Maakt en werkt bij',
  agentMakesLoading: 'Laden…',
  agentMakesErrorBody: 'Dit kon niet worden geladen.',
  agentMakesNone: 'Deze agent maakt nog geen artefacten.',
  agentMakesNew: 'Elke run een nieuw artefact',
  agentMakesUpdate: 'Werkt elke run één artefact bij',
  agentNextRun: 'Volgende run {when}',
  agentNoSchedule: 'Niet gepland',
  agentCreditsPerRun: 'Ongeveer {credits} credits per run',
  agentCreditsUnknown: 'De credits per run zijn nog niet bekend',
  agentLastResult: 'Laatste resultaat',
  interviewTitle: 'Instellen',
  interviewIntro: 'Een paar vragen bepalen de vorm.',
  interviewOptional: '(optioneel)',
  interviewAnswerNeeded: 'Beantwoord dit om door te gaan.',
  interviewReadOnly: 'Alleen lezen',
  interviewChange: 'Wijzigen',
  interviewConnectionYours: 'je {system}-verbinding',
  interviewConnectionOther: 'de {system}-verbinding van {name}',
  interviewDateAny: 'Elke periode',
  interviewDatePrevious: 'Vorige maand',
  interviewDateNext: 'Volgende maand',
  interviewDatePresets: 'Snelle bereiken',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Wissen',
  interviewDateDone: 'Klaar',
  interviewChooseSource: 'Kies een bron',
  interviewSourceChosen: 'Bron gekozen',
  interviewSourceUnavailable:
    'Een bron kiezen is hier nog niet beschikbaar, dus het kan niet verdergaan.',
  interviewSourceUnavailableOptional:
    'Een bron kiezen is hier nog niet beschikbaar. Je kunt ook zonder doorgaan.',
  interviewStarterWithdrawn:
    'Dit sjabloon is niet meer beschikbaar. Concepten die je er al mee bent begonnen, kun je nog steeds openen.',
  interviewDateFrom: 'Van',
  interviewDateTo: 'Tot',
  interviewContinue: 'Doorgaan',
  interviewGrantTitle: 'Lezen van {source} toestaan?',
  interviewGrantBody:
    'Hij gebruikt {connection}, alleen lezen, voor {scope}. Er wordt niets gelezen totdat je het toestaat, en er worden geen voorbeeldrijen getoond.',
  interviewAllow: 'Toestaan',
  interviewNotNow: 'Niet nu',
  interviewConfiguring: 'Instellen op basis van je antwoorden…',
  interviewChecking: 'Controleren voordat het opent…',
  interviewComplete: '{title} is klaar.',
  interviewOpen: '{title} openen',
  interviewBlocked: 'Dit kon niet doorgaan.',
  interviewFailed: 'Instellen is mislukt. Je antwoorden blijven bewaard.',
  interviewCancelled: 'Dit is geannuleerd.',
  interviewSaving: 'Opslaan…',
  interviewSaved: 'Opgeslagen',
  interviewConflict:
    'Dit concept is ergens anders gewijzigd. Je antwoorden blijven hier bewaard totdat je het huidige concept laadt, dat ze vervangt.',
  interviewReloadCurrent: 'Huidig concept laden',
  interviewUnsettled:
    'We konden je laatste opslag niet bevestigen. Je antwoorden blijven hier bewaard.',
  interviewCheckAgain: 'Opnieuw controleren',
  finishAction: 'Privé opslaan',
  finishSaving: 'Opslaan…',
  finishSaved: '{title} is opgeslagen en alleen voor jou zichtbaar.',
  finishNotSaved: 'Het is niet opgeslagen. Je concept staat er nog.',
  finishUnconfirmed: 'We konden niet bevestigen of het is opgeslagen.',
  candidateInvalid: 'Het concept kon niet tot een document worden gemaakt.',
  finishOpenInLibrary: 'Openen in Bibliotheek',
  savedOpenFailed: 'Het kon nu niet worden geopend.',
  savedInLibrary: 'Het staat in je Bibliotheek.',
  openSavedTitle: '{title} openen →',
  interviewReasonNotAuthorized: 'Je hebt geen toestemming om dit concept te wijzigen.',
  interviewReasonDraftNotOpen: 'Dit concept is niet meer open.',
  interviewReasonInvalid: 'Een van de antwoorden is niet geldig.',
  interviewReasonObjects:
    'Sommige bestanden konden niet worden geverifieerd. Je antwoorden blijven bewaard.',
  interviewReasonDependency:
    'Een benodigde service is niet beschikbaar. Je antwoorden blijven bewaard.',
  interviewReasonReconcile: 'De laatste opslag wordt nog bevestigd.',
  exportTitle: 'Exporteren',
  exportIntro: 'Download dit artefact. Het bestand vermeldt waar de gegevens vandaan komen.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Bewerkbare dia’s met spreekaantekeningen',
  exportHintPdf: 'Precies zoals getoond',
  exportHintDocx: 'Bewerkbaar, behoudt koppen en tabellen',
  exportHintXlsx: 'Alle bladen, behoudt formules',
  exportHintCsv: 'Alleen waarden, UTF-8',
  exportUnavailable: 'Nu niet beschikbaar',
  exportRunning: '{format} voorbereiden…',
  exportValidating: 'Het {format}-bestand controleren…',
  exportReady: 'Het {format}-bestand is klaar',
  exportDownload: '{name} downloaden',
  exportVersion: 'Geëxporteerde versie {number}',
  exportFailed: 'Het {format}-bestand kon niet worden gemaakt.',
  exportFailedChecks:
    'Het {format}-bestand heeft de controles niet doorstaan, dus het is niet aangeboden.',
  exportDenied: 'Je hebt geen toegang om dit artefact te exporteren.',
  exportRevoked: 'Delen is uitgezet, dus dit artefact kan niet meer worden geëxporteerd.',
  exportStale:
    'Dit artefact is gewijzigd tijdens het exporteren. Exporteer opnieuw voor de nieuwste versie.',
  exportProvenanceTitle: 'Elk bestand bevat',
  exportProvenanceLine: 'Gemaakt met Poneva · {title} · v{number} · {data} · gemaakt {madeAt}',
  exportProvenanceAsOf: 'gegevens per {time}',
  exportPlacementNotesAbout: 'Op het blad Notities en in een over-bestand naast het CSV-bestand',
  exportProvenanceRange: 'gegevens van {earliest} tot {latest}',
  exportProvenanceUnknown: 'leeftijd van gegevens onbekend',
  exportAboutFile: 'Over dit bestand ({format})',
  exportPlacementFooter: 'In de voettekst van elke pagina',
  exportPlacementSlide: 'In de voettekst van elke dia',
  exportPlacementNotes: 'Op het blad Notities',
  exportPlacementAbout: 'In een over-bestand naast het CSV-bestand',
  exportSourcesPage: 'Een bronnenpagina toevoegen',
  exportSourcesPageHint: 'Een laatste pagina met elk getal, de bron en het tijdstip.',
  exportSourcesPageDeck: 'De eigen bronnendia van de presentatie gaat mee.',
  exportSourcesPageSaved: 'Onthouden voor dit artefact, ook bij geplande verzendingen.',
  libraryKindSheetTitle: 'Soort',
  shareAccessSheetTitle: 'Toegang',

  requestShowAll: 'Alles tonen',
  requestShowLess: 'Minder tonen',
  approveGettingReady: 'Wordt voorbereid…',
  approveNotReady:
    'Dit kan nu niet worden goedgekeurd. Laad de pagina opnieuw en probeer het dan nog eens.',
  tryWithModel: 'Proberen met {model}',
  tryWithRequesting: 'Starten…',
  tryWithUnavailable: 'Dit kan nu niet worden gestart.',
};

const pl: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Edytujesz szkic wersji {number}',
  noChangesToApply: 'Brak zmian do zastosowania.',
  appliedDone: 'Zastosowano.',
  mentionInChat: 'Wspomnij na czacie',

  kindMarkdown: 'Notatka',
  kindKpi: 'Wskaźnik',
  libraryExportsList: 'Eksport do {formats}',
  libraryOpensToRead: 'Otwiera się do czytania',
  libraryReadOnly: 'Tylko do odczytu',
  libraryLiveView: 'Widok na żywo',
  libraryOneNumber: 'Jedna liczba · stan na {time}',
  retry: 'Spróbuj ponownie',
  close: 'Zamknij',
  panelLoading: 'Wczytywanie…',
  panelEmpty: 'Na razie nic tu nie ma.',
  panelDenied: 'Nie masz do tego dostępu.',
  panelBlocked: 'Coś musi się najpierw wydarzyć, zanim to będzie mogło iść dalej.',
  panelBlockedViewerContext:
    'To uruchomienie nie ma osoby, w imieniu której czyta dane, więc nie może iść dalej, dopóki ktoś nie wybierze, w czyim imieniu działa.',
  panelError: 'Nie udało się tego wczytać.',
  panelStale: 'To może być nieaktualne.',
  sourcesTitle: 'Źródła',
  sourcesIntro: 'Skąd pochodzi każda liczba i jak jest świeża.',
  sourcesLoading: 'Wczytywanie źródeł…',
  sourcesEmptyTitle: 'Brak źródeł',
  sourcesEmptyBody: 'Ten artefakt nie korzysta z danych firmy.',
  sourcesErrorBody: 'Nie udało się wczytać źródeł.',
  sourcesDeniedTitle: 'Część źródeł jest ukryta',
  sourcesDeniedBody:
    'Brak dostępu do źródeł użytych tutaj: {count}. Poproś właściciela, aby je udostępnił.',
  sourceDeniedName: 'Źródło, którego nie widzisz',
  sourceAsOf: 'Stan na {time}',
  sourceAgeUnknown: 'Wiek danych nieznany',
  sourceStatusCurrent: 'Aktualne',
  sourceStatusStale: 'Nieaktualne',
  sourceStatusGap: 'Brak w ostatnim odświeżeniu',
  sourceStatusDenied: 'Brak dostępu',
  sourceLastGood: 'Ostatnie poprawne: {time}',
  sourceUsedBy: 'Używa: {labels}',
  openDocument: 'Otwórz dokument',
  versionsShow: 'Pokaż wersje',
  versionsHide: 'Ukryj wersje',
  versionsTitle: 'Wersje',
  versionsLoading: 'Wczytywanie wersji…',
  versionsEmptyTitle: 'Brak wersji',
  versionsEmptyBody: 'Pierwsza wersja pojawi się, gdy ten artefakt zostanie zbudowany.',
  versionsErrorBody: 'Nie udało się wczytać wersji.',
  versionLabel: 'Wersja {number}',
  versionOpen: 'Otwórz wersję {number}',
  versionCurrent: 'Bieżąca',
  versionPublished: 'Opublikowana',
  versionBy: 'Autor: {actor}',
  versionOriginNative: 'W Poneva',
  versionOriginMcp: 'przez twojego agenta',
  versionOriginTerminal: 'przez terminal',
  versionOriginJob: 'Zaplanowane uruchomienie',
  versionCausePerson: 'Edytowano',
  versionCauseAgent: 'Aktualizacja agenta',
  versionCauseRefresh: 'Odświeżono',
  versionCauseRestore: 'Przywrócono',
  versionCauseImport: 'Zaimportowano',
  versionCheckedYes: 'Sprawdzona',
  versionCheckedNo: 'Jeszcze niesprawdzona',
  versionCheckedInvalidated: 'Edytowana po ostatnim sprawdzeniu',
  compareStart: 'Porównaj',
  compareWith: 'Porównaj z wersją {number}',
  compareTitle: 'Co się zmieniło od v{from} do v{to}',
  compareFromMark: 'Z',
  compareToMark: 'Do',
  comparePick: 'Wybierz drugą wersję do porównania.',
  compareNone: 'Brak różnic.',
  compareLoading: 'Porównywanie…',
  compareError: 'Nie udało się wczytać porównania.',
  compareBefore: 'Przed',
  compareAfter: 'Po',
  compareAdded: 'Dodano',
  compareRemoved: 'Usunięto',
  compareChanged: 'Zmieniono',
  restore: 'Przywróć tę wersję',
  restoreConfirmTitle: 'Przywrócić wersję {number}?',
  restoreConfirmBody:
    'Powstanie nowa wersja na podstawie wersji {number}. Nic nie zostanie usunięte.',
  restoreConfirm: 'Przywróć',
  restoreCancel: 'Anuluj',
  restoring: 'Przywracanie…',
  restoreFailed: 'Nie udało się przywrócić wersji.',
  restoreStale:
    'Ten artefakt zmienił się, gdy go oglądasz. Wczytaj wersje ponownie i spróbuj jeszcze raz.',
  restoreDenied: 'Nie masz uprawnień do przywracania wersji.',
  restored: 'Wersja {number} została przywrócona jako nowa wersja.',
  publishTitle: 'Opublikuj',
  publishIntro: 'Wersja {number} będzie widoczna dla: {audience}.',
  publishOwnerLine:
    'Odbiorcy zobaczą to, co mógł zobaczyć {owner}, gdy ta wersja była publikowana.',
  publishChecksTitle: 'Sprawdzenia',
  checkPassed: 'Sprawdzona',
  checkRunning: 'Sprawdzanie…',
  checkFailed: 'Sprawdzenie nie powiodło się',
  checkStale: 'Edytowana po ostatnim sprawdzeniu',
  publishFixInChat: 'Popraw na czacie',
  publishAction: 'Opublikuj wersję {number}',
  publishing: 'Publikowanie…',
  publishedDone: 'Wersja {number} jest aktywna dla: {audience}.',
  publishConfirmGrant:
    'Ta wersja wymaga nowego dostępu do systemu firmy. Przejrzyj dostęp przed publikacją.',
  publishConfirmWrite:
    'Ta wersja zapisuje dane w systemie firmy. Przejrzyj dostęp przed publikacją.',
  publishConfirmDestructive: 'Ta wersja usuwa dane. Najpierw potwierdź to w Poneva.',
  publishConflict: 'Najpierw opublikowano inną wersję. Wczytaj ponownie, aby ją zobaczyć.',
  publishBlockedNotAuthorized: 'Nie masz uprawnień do publikowania.',
  publishBlockedCheckFailed: 'Sprawdzenie nie powiodło się, więc tej wersji nie można opublikować.',
  publishBlockedCheckStale: 'Artefakt zmienił się po ostatnim sprawdzeniu. Sprawdź go ponownie.',
  publishBlockedObjects: 'Nie udało się zweryfikować części plików. Spróbuj za chwilę.',
  publishBlockedPolicy: 'Ustawienia firmy się zmieniły. Przejrzyj je i spróbuj ponownie.',
  publishBlockedApproval: 'Potwierdzenie nie jest już ważne. Potwierdź ponownie.',
  publishBlockedDependency: 'Wymagana usługa jest niedostępna. Spróbuj później.',
  publishAsk: 'Poproś o publikację: {publishers}',
  publishAskFooter: 'Osoby, które mogą publikować: {publishers}.',
  publishAskNone: 'Poproś kogoś, kto może publikować.',
  publishWarnings: 'Warto sprawdzić przed publikacją',
  checkWarnStale: '{subject} jest nieaktualne ({time}).',
  checkWarnGap: '{subject} – brak w ostatnim odświeżeniu.',
  checkWarnUnknownSource: 'Źródło, którego nie widzisz, wymaga uwagi.',
  checkRefreshFirst: 'Najpierw odśwież',
  checkFailedRow: '{subject} – sprawdzenie nie powiodło się.',
  checkFailedGeneric: 'Sprawdzenie nie powiodło się.',
  publishReviewAccess: 'Przejrzyj dostęp',
  publishRemovalBody:
    'v{number} usuwa: {what} ({count}). Najpierw zapisywany jest punkt przywracania.',
  publishReviewRemoval: 'Przejrzyj, co zostanie usunięte',
  publishAndRemove: 'Opublikuj i usuń',
  publishReviewFirst: 'Przed publikacją przejrzyj, co zostanie usunięte.',
  publishDoneTitle: 'Wersja {number} jest aktywna dla: {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Znajdą ją w swojej Bibliotece i przypiętą dla zespołu.',
  publishUndo: 'Cofnij',
  publishCopyLink: 'Kopiuj link',
  publishDone: 'Gotowe',
  shareRoleViewMeaning: 'Może otworzyć i czytać',
  shareRoleEditMeaning: 'Może zmieniać na czacie i w inspektorze',
  shareRolePublishMeaning: 'Może też publikować i cofać publikację',
  shareRoleMenu: 'Rola dla: {name}',
  shareUnpublishConfirm: 'Ludzie stracą dostęp. Artefakt i jego wersje zostaną zachowane.',
  shareUnpublishCancel: 'Zostaw aktywne',
  sendDestinationLine: '{provider} · osób: {count}',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Wszyscy w {destination} widzą podsumowanie. Osób spoza grona odbiorców tego artefaktu: {outside} z {total}. Link pokaże im „Poproś o dostęp”.',
  sendAudienceGeneric: 'Osoby spoza grona odbiorców zobaczą „Poproś o dostęp”.',
  sendPreview: 'Co zobaczą',
  shareTitle: 'Udostępnij',
  shareLoading: 'Wczytywanie udostępniania…',
  shareErrorBody: 'Nie udało się wczytać udostępniania.',
  sharePrivate: 'Tylko ty widzisz ten artefakt.',
  shareShared: 'Widoczne dla: {audience}.',
  shareRevoked: 'Udostępnianie jest wyłączone. Nikt inny nie może otworzyć tego artefaktu.',
  shareOwnerSnapshot:
    'Odbiorcy widzą liczby w stanie z publikacji. Nie mogą odświeżać ani edytować.',
  shareRoleView: 'Może wyświetlać',
  shareRoleEdit: 'Może edytować',
  shareRolePublish: 'Może publikować',
  shareLivePublished: 'Aktywna: wersja {number}',
  shareUnpublish: 'Cofnij publikację',
  shareManageDenied: 'Tylko edytor może zmienić, kto to widzi.',
  sendTitle: 'Wyślij do',
  sendIntro:
    'Wyślij link i podsumowanie przez połączoną aplikację. Każde wysłanie zatwierdzasz ty.',
  sendLoading: 'Wczytywanie miejsc docelowych…',
  sendErrorBody: 'Nie udało się wczytać miejsc docelowych.',
  sendNone:
    'Żadna połączona aplikacja nie może jeszcze tego odebrać. Najpierw połącz Slack, Teams lub e-mail.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-mail',
  sendAudience: 'Trafi do: {audience}',
  sendSummary: 'Wiadomość',
  sendLeaves: 'Zostanie wysłany link.',
  sendLeavesAttachment: 'Zostaną wysłane link i sprawdzony plik.',
  sendNoAttachments: 'To miejsce docelowe nie przyjmuje plików, więc zostanie wysłany tylko link.',
  sendScheduled: 'Zaplanowane wysyłki wymagają twojego zatwierdzenia za każdym razem.',
  sendAction: 'Przejrzyj i wyślij',
  sendFileUnsupported:
    'To miejsce docelowe nie przyjmie pliku. Wyślij tylko link albo wybierz inne miejsce.',
  sendLinkOnly: 'Wyślij tylko link',
  sendWaiting: 'Czeka na twoje zatwierdzenie. Nic nie zostało wysłane.',
  sendDelivered: 'Wysłano do: {destination}.',
  sendFailed: 'Nie wysłano. Nic nie opuściło Poneva.',
  sendUnknown:
    'Nie udało się potwierdzić dostarczenia do: {destination}. Sprawdź tam, zanim wyślesz ponownie.',
  sendBlockedNotAuthorized: 'Nie masz uprawnień do wysłania tego.',
  sendBlockedUnsupported: 'To miejsce docelowe nie jest obsługiwane.',
  sendBlockedApproval: 'Zatwierdzenie nie jest już ważne. Przejrzyj i wyślij ponownie.',
  sendBlockedChanged: 'Wiadomość lub odbiorca się zmienili. Przejrzyj to ponownie.',
  sendBlockedDependency: 'Połączona aplikacja jest niedostępna. Spróbuj później.',
  libraryTitle: 'Biblioteka',
  librarySegmentAll: 'Wszystkie',
  librarySegmentMine: 'Moje',
  librarySegmentShared: 'Udostępnione mi',
  librarySearch: 'Szukaj artefaktów',
  libraryKindAll: 'Wszystkie rodzaje',
  libraryKindReports: 'Raporty i strony',
  libraryKindDecks: 'Prezentacje',
  libraryKindDocuments: 'Dokumenty',
  libraryKindSpreadsheets: 'Arkusze',
  libraryKindApps: 'Aplikacje',
  libraryAppsMatchNoun:
    'one:aplikacja pasuje w sekcji Aplikacje →|few:aplikacje pasują w sekcji Aplikacje →|many:aplikacji pasuje w sekcji Aplikacje →|other:aplikacji pasuje w sekcji Aplikacje →',
  libraryMake: 'Zacznij na czacie',
  libraryEmptySharedHint: 'Gdy ktoś udostępni ci artefakt, pojawi się tutaj.',
  libraryRefreshFailedAt: 'Nie udało się odświeżyć {time}.',
  libraryRefreshTryAgain: 'Spróbuj ponownie',
  libraryVia: 'przez: {owner}',
  savedPrivate: 'Prywatny',
  savedVersion: 'Wersja {n}',
  libraryLoading: 'Wczytywanie Biblioteki…',
  libraryErrorBody: 'Nie udało się teraz wczytać Biblioteki.',
  startersLoadFailed: 'Nie udało się teraz wczytać szablonów.',
  libraryCannotOpen: 'Elementu „{title}” nie można jeszcze otworzyć tutaj.',
  libraryEmptyTitle: 'Na razie nic tu nie ma',
  libraryEmptyAllTitle: 'Stwórz coś, czym warto się podzielić.',
  libraryEmptyAllBody:
    'Tutaj pojawiają się dokumenty, prezentacje i aplikacje utworzone przez Ciebie na czacie lub udostępnione Ci przez innych.',
  libraryEmptyMineBody: 'Tu pojawią się artefakty, które tworzysz.',
  libraryEmptySharedBody: 'Nic ci jeszcze nie udostępniono.',
  libraryNoMatchTitle: 'Brak wyników',
  libraryNoMatchBody: 'Spróbuj innego słowa lub innego rodzaju.',
  libraryMore: 'Pokaż więcej',
  libraryOwner: 'Autor: {owner}',
  libraryUpdated: 'Edytowano {time}',
  libraryPublishedAs: 'Opublikowano · v{number}',
  libraryDraft: 'Wersja robocza',
  libraryUnpublished: 'Nieopublikowane',
  libraryRefreshFailed: 'Odświeżenie nie powiodło się',
  libraryRefreshRunning: 'Odświeżanie',
  libraryAudience: 'Dla: {audience}',
  kindReport: 'Raport',
  kindPage: 'Strona',
  kindSite: 'Witryna',
  kindDeck: 'Prezentacja',
  kindDocument: 'Dokument',
  kindApp: 'Aplikacja',
  kindSpreadsheet: 'Arkusz',
  kindDashboard: 'Pulpit',
  kindTable: 'Tabela',
  kindChart: 'Wykres',
  kindExportsReport: 'Eksport do PDF',
  kindExportsPage: 'Strona tylko do odczytu',
  kindExportsSite: 'Witryna tylko do odczytu',
  kindExportsDeck: 'Pokaz lub eksport do PowerPoint i PDF',
  kindExportsDocument: 'Eksport do Word i PDF',
  kindExportsSpreadsheet: 'Eksport do Excel i CSV',
  kindExportsDashboard: 'Widok na żywo',
  kindExportsTable: 'Eksport do Excel i CSV',
  kindExportsChart: 'Widok wykresu',
  agentPublished: '{agent}: opublikowano {title} v{number}.',
  agentCheckedOn: 'Sprawdzono na: {views}, {themes}.',
  agentUndo: 'Cofnij',
  agentRestorePrevious: 'Przywróć v{number}',
  agentHeld: '{title} v{number} czeka jako wersja robocza.',
  agentHeldMismatch: 'Liczby niezgodne ze źródłami: {count}.',
  agentHeldCheck: 'Sprawdzenie nie przeszło.',
  agentFixInChat: 'Popraw na czacie',
  agentConfirmDrop:
    '{agent} chce usunąć {what} z: {title}. Najpierw zapisywany jest punkt przywracania. Nic się nie zmieni, dopóki nie zdecydujesz.',
  agentConfirmDropCount:
    '{agent} chce usunąć {what} (wartości: {count}) z: {title}. Najpierw zapisywany jest punkt przywracania. Nic się nie zmieni, dopóki nie zdecydujesz.',
  agentViewDesktop: 'komputer',
  agentViewTablet: 'tablet',
  agentViewPhone: 'telefon',
  agentThemeLight: 'jasny',
  agentThemeDark: 'ciemny',
  agentLookAt: 'Zobacz v{number}',
  agentConfirmWrite:
    '{agent} chce zapisać: {what}. Nic nie zostanie zapisane, dopóki nie zdecydujesz.',
  agentConfirmGrant:
    '{agent} chce dostępu do: {what}. Nic nie zostanie udostępnione, dopóki nie zdecydujesz.',
  agentReview: 'Przejrzyj',
  agentSkipped: '{agent}: pominięto {part}.',
  agentSkippedWhy:
    'Czyta dane {system} każdej osoby osobno, a to uruchomienie nie ma osoby, w imieniu której czyta.',
  agentChooseWho: 'Wybierz, w czyim imieniu działa',
  agentChangeInChat: 'Zmień na czacie',
  agentFailed: '{agent}: nie udało się odświeżyć {title}.',
  agentFailedBody: 'Ostatnia opublikowana wersja nadal jest aktywna.',
  agentRetry: 'Spróbuj ponownie',
  agentRunning: '{agent} aktualizuje {title}.',
  agentMakesTitle: 'Tworzy i aktualizuje',
  agentMakesLoading: 'Wczytywanie…',
  agentMakesErrorBody: 'Nie udało się tego wczytać.',
  agentMakesNone: 'Ten agent nie tworzy jeszcze artefaktów.',
  agentMakesNew: 'Nowy artefakt przy każdym uruchomieniu',
  agentMakesUpdate: 'Aktualizuje jeden artefakt przy każdym uruchomieniu',
  agentNextRun: 'Następne uruchomienie: {when}',
  agentNoSchedule: 'Brak harmonogramu',
  agentCreditsPerRun: 'Kredyty za uruchomienie: około {credits}',
  agentCreditsUnknown: 'Kredyty za uruchomienie nie są jeszcze znane',
  agentLastResult: 'Ostatni wynik',
  interviewTitle: 'Skonfiguruj',
  interviewIntro: 'Kilka pytań nada mu kształt.',
  interviewOptional: '(opcjonalnie)',
  interviewAnswerNeeded: 'Odpowiedz, aby kontynuować.',
  interviewReadOnly: 'Tylko do odczytu',
  interviewChange: 'Zmień',
  interviewConnectionYours: 'twoje połączenie z {system}',
  interviewConnectionOther: 'połączenie z {system} użytkownika {name}',
  interviewDateAny: 'Dowolny okres',
  interviewDatePrevious: 'Poprzedni miesiąc',
  interviewDateNext: 'Następny miesiąc',
  interviewDatePresets: 'Szybkie zakresy',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Wyczyść',
  interviewDateDone: 'Gotowe',
  interviewChooseSource: 'Wybierz źródło',
  interviewSourceChosen: 'Źródło wybrane',
  interviewSourceUnavailable:
    'Wybór źródła nie jest tu jeszcze dostępny, więc nie można kontynuować.',
  interviewSourceUnavailableOptional:
    'Wybór źródła nie jest tu jeszcze dostępny. Możesz kontynuować bez niego.',
  interviewStarterWithdrawn:
    'Ten szablon nie jest już dostępny. Wersje robocze, które już z niego rozpoczęto, nadal można otworzyć.',
  interviewDateFrom: 'Od',
  interviewDateTo: 'Do',
  interviewContinue: 'Dalej',
  interviewGrantTitle: 'Zezwolić na odczyt: {source}?',
  interviewGrantBody:
    'Używa: {connection}, tylko do odczytu, w zakresie: {scope}. Nic nie zostanie odczytane, dopóki nie zezwolisz, a przykładowe wiersze nie są pokazywane.',
  interviewAllow: 'Zezwól',
  interviewNotNow: 'Nie teraz',
  interviewConfiguring: 'Konfigurowanie na podstawie twoich odpowiedzi…',
  interviewChecking: 'Sprawdzanie przed otwarciem…',
  interviewComplete: '{title} jest gotowe.',
  interviewOpen: 'Otwórz: {title}',
  interviewBlocked: 'Nie można było kontynuować.',
  interviewFailed: 'Konfiguracja się nie powiodła. Twoje odpowiedzi zostały zachowane.',
  interviewCancelled: 'Anulowano.',
  interviewSaving: 'Zapisywanie…',
  interviewSaved: 'Zapisano',
  interviewConflict:
    'Ta wersja robocza zmieniła się gdzie indziej. Twoje odpowiedzi zostają tutaj, dopóki nie wczytasz bieżącej wersji roboczej, która je zastąpi.',
  interviewReloadCurrent: 'Wczytaj bieżącą wersję roboczą',
  interviewUnsettled:
    'Nie udało się potwierdzić ostatniego zapisu. Twoje odpowiedzi zostały tutaj zachowane.',
  interviewCheckAgain: 'Sprawdź ponownie',
  finishAction: 'Zapisz prywatnie',
  finishSaving: 'Zapisywanie…',
  finishSaved: '„{title}” zapisano – widzisz go tylko ty.',
  finishNotSaved: 'Nie zapisano. Twoja wersja robocza nadal tu jest.',
  finishUnconfirmed: 'Nie udało się potwierdzić, czy zapisano.',
  candidateInvalid: 'Nie udało się utworzyć dokumentu z wersji roboczej.',
  finishOpenInLibrary: 'Otwórz w Bibliotece',
  savedOpenFailed: 'Nie udało się teraz otworzyć.',
  savedInLibrary: 'Znajdziesz to w swojej Bibliotece.',
  openSavedTitle: 'Otwórz: {title} →',
  interviewReasonNotAuthorized: 'Nie masz uprawnień do zmiany tej wersji roboczej.',
  interviewReasonDraftNotOpen: 'Ta wersja robocza nie jest już otwarta.',
  interviewReasonInvalid: 'Jedna z odpowiedzi jest nieprawidłowa.',
  interviewReasonObjects:
    'Nie udało się zweryfikować części plików. Twoje odpowiedzi zostały zachowane.',
  interviewReasonDependency:
    'Wymagana usługa jest niedostępna. Twoje odpowiedzi zostały zachowane.',
  interviewReasonReconcile: 'Ostatni zapis nadal jest potwierdzany.',
  exportTitle: 'Eksportuj',
  exportIntro: 'Pobierz ten artefakt. Plik podaje, skąd pochodzą jego dane.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Edytowalne slajdy z notatkami prelegenta',
  exportHintPdf: 'Dokładnie tak, jak widać',
  exportHintDocx: 'Edytowalny, zachowuje nagłówki i tabele',
  exportHintXlsx: 'Wszystkie arkusze, zachowuje formuły',
  exportHintCsv: 'Tylko wartości, UTF-8',
  exportUnavailable: 'Chwilowo niedostępne',
  exportRunning: 'Przygotowywanie: {format}…',
  exportValidating: 'Sprawdzanie pliku: {format}…',
  exportReady: 'Plik {format} jest gotowy',
  exportDownload: 'Pobierz {name}',
  exportVersion: 'Wyeksportowana wersja {number}',
  exportFailed: 'Nie udało się utworzyć pliku: {format}.',
  exportFailedChecks: 'Plik {format} nie przeszedł sprawdzeń, więc nie został zaoferowany.',
  exportDenied: 'Nie masz dostępu do eksportu tego artefaktu.',
  exportRevoked: 'Udostępnianie zostało wyłączone, więc tego artefaktu nie można już eksportować.',
  exportStale:
    'Ten artefakt zmienił się podczas eksportu. Wyeksportuj ponownie, aby dostać najnowszą wersję.',
  exportProvenanceTitle: 'Każdy plik zawiera',
  exportProvenanceLine: 'Utworzono w Poneva · {title} · v{number} · {data} · utworzono {madeAt}',
  exportProvenanceAsOf: 'dane na {time}',
  exportPlacementNotesAbout: 'W arkuszu Notatki oraz w pliku o eksporcie obok pliku CSV',
  exportProvenanceRange: 'dane od {earliest} do {latest}',
  exportProvenanceUnknown: 'wiek danych nieznany',
  exportAboutFile: 'O tym pliku ({format})',
  exportPlacementFooter: 'W stopce każdej strony',
  exportPlacementSlide: 'W stopce każdego slajdu',
  exportPlacementNotes: 'W arkuszu Notatki',
  exportPlacementAbout: 'W pliku o eksporcie obok pliku CSV',
  exportSourcesPage: 'Dodaj stronę ze źródłami',
  exportSourcesPageHint: 'Ostatnia strona z listą każdej liczby, jej źródła i czasu.',
  exportSourcesPageDeck: 'Własny slajd Źródła z prezentacji jest dołączany.',
  exportSourcesPageSaved: 'Zapamiętane dla tego artefaktu, także dla zaplanowanych wysyłek.',
  libraryKindSheetTitle: 'Rodzaj',
  shareAccessSheetTitle: 'Dostęp',

  requestShowAll: 'Pokaż wszystko',
  requestShowLess: 'Pokaż mniej',
  approveGettingReady: 'Przygotowujemy…',
  approveNotReady: 'Nie można tego teraz zatwierdzić. Odśwież stronę i spróbuj ponownie.',
  tryWithModel: 'Spróbuj z modelem {model}',
  tryWithRequesting: 'Uruchamianie…',
  tryWithUnavailable: 'Nie można tego teraz uruchomić.',
};

const pt: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Está a editar um rascunho da versão {number}',
  noChangesToApply: 'Não há alterações a aplicar.',
  appliedDone: 'Aplicado.',
  mentionInChat: 'Mencionar no chat',

  kindMarkdown: 'Nota',
  kindKpi: 'Métrica',
  libraryExportsList: 'Exporta para {formats}',
  libraryOpensToRead: 'Abre para leitura',
  libraryReadOnly: 'Só de leitura',
  libraryLiveView: 'Vista em direto',
  libraryOneNumber: 'Um número · a {time}',
  retry: 'Tentar novamente',
  close: 'Fechar',
  panelLoading: 'A carregar…',
  panelEmpty: 'Ainda não há nada aqui.',
  panelDenied: 'Não tem acesso a este conteúdo.',
  panelBlocked: 'Está à espera de algo antes de poder continuar.',
  panelBlockedViewerContext:
    'Esta execução não tem uma pessoa em nome de quem ler, por isso não pode continuar até alguém escolher em nome de quem é executada.',
  panelError: 'Não foi possível carregar este conteúdo.',
  panelStale: 'Pode estar desatualizado.',
  sourcesTitle: 'Fontes',
  sourcesIntro: 'De onde vem cada número e quão recente é.',
  sourcesLoading: 'A carregar as fontes…',
  sourcesEmptyTitle: 'Sem fontes',
  sourcesEmptyBody: 'Este Artefacto não usa dados da empresa.',
  sourcesErrorBody: 'Não foi possível carregar as fontes.',
  sourcesDeniedTitle: 'Algumas fontes estão ocultas',
  sourcesDeniedBody:
    'Não tem acesso a {count} fontes usadas aqui. Peça a um proprietário que as partilhe.',
  sourceDeniedName: 'Fonte que não pode ver',
  sourceAsOf: 'Em {time}',
  sourceAgeUnknown: 'Idade dos dados desconhecida',
  sourceStatusCurrent: 'Atual',
  sourceStatusStale: 'Desatualizada',
  sourceStatusGap: 'Em falta na última atualização',
  sourceStatusDenied: 'Sem acesso',
  sourceLastGood: 'Última válida: {time}',
  sourceUsedBy: 'Usada por {labels}',
  openDocument: 'Abrir documento',
  versionsShow: 'Mostrar versões',
  versionsHide: 'Ocultar versões',
  versionsTitle: 'Versões',
  versionsLoading: 'A carregar as versões…',
  versionsEmptyTitle: 'Ainda sem versões',
  versionsEmptyBody: 'A primeira versão aparece quando este Artefacto for criado.',
  versionsErrorBody: 'Não foi possível carregar as versões.',
  versionLabel: 'Versão {number}',
  versionOpen: 'Abrir a versão {number}',
  versionCurrent: 'Atual',
  versionPublished: 'Publicada',
  versionBy: 'Por {actor}',
  versionOriginNative: 'Na Poneva',
  versionOriginMcp: 'através do seu próprio agente',
  versionOriginTerminal: 'através do terminal',
  versionOriginJob: 'Execução agendada',
  versionCausePerson: 'Editada',
  versionCauseAgent: 'Atualização do agente',
  versionCauseRefresh: 'Atualizada',
  versionCauseRestore: 'Restaurada',
  versionCauseImport: 'Importada',
  versionCheckedYes: 'Verificada',
  versionCheckedNo: 'Ainda não verificada',
  versionCheckedInvalidated: 'Editada desde a última verificação',
  compareStart: 'Comparar',
  compareWith: 'Comparar com a versão {number}',
  compareTitle: 'O que mudou de v{from} para v{to}',
  compareFromMark: 'De',
  compareToMark: 'Para',
  comparePick: 'Escolha uma segunda versão para comparar.',
  compareNone: 'Sem diferenças.',
  compareLoading: 'A comparar…',
  compareError: 'Não foi possível carregar a comparação.',
  compareBefore: 'Antes',
  compareAfter: 'Depois',
  compareAdded: 'Adicionado',
  compareRemoved: 'Removido',
  compareChanged: 'Alterado',
  restore: 'Restaurar esta versão',
  restoreConfirmTitle: 'Restaurar a versão {number}?',
  restoreConfirmBody: 'Isto cria uma nova versão a partir da versão {number}. Nada é eliminado.',
  restoreConfirm: 'Restaurar',
  restoreCancel: 'Cancelar',
  restoring: 'A restaurar…',
  restoreFailed: 'Não foi possível restaurar a versão.',
  restoreStale: 'Este Artefacto mudou enquanto o via. Recarregue as versões e tente novamente.',
  restoreDenied: 'Não tem permissão para restaurar versões.',
  restored: 'Versão {number} restaurada como nova versão.',
  publishTitle: 'Publicar',
  publishIntro: 'A versão {number} ficará visível para {audience}.',
  publishOwnerLine: 'Quem a vê, vê o que {owner} podia ver quando esta versão foi publicada.',
  publishChecksTitle: 'Verificações',
  checkPassed: 'Verificada',
  checkRunning: 'A verificar…',
  checkFailed: 'Uma verificação falhou',
  checkStale: 'Editada desde a última verificação',
  publishFixInChat: 'Corrigir no chat',
  publishAction: 'Publicar a versão {number}',
  publishing: 'A publicar…',
  publishedDone: 'A versão {number} está ativa para {audience}.',
  publishConfirmGrant:
    'Esta versão precisa de novo acesso a um sistema da empresa. Reveja o acesso antes de publicar.',
  publishConfirmWrite:
    'Esta versão escreve num sistema da empresa. Reveja o acesso antes de publicar.',
  publishConfirmDestructive: 'Esta versão remove dados. Confirme primeiro na Poneva.',
  publishConflict: 'Foi publicada outra versão primeiro. Recarregue para a ver.',
  publishBlockedNotAuthorized: 'Não tem permissão para publicar.',
  publishBlockedCheckFailed: 'Uma verificação falhou, por isso esta versão não pode ser publicada.',
  publishBlockedCheckStale:
    'O Artefacto mudou depois da última verificação. Verifique-o novamente.',
  publishBlockedObjects:
    'Não foi possível verificar alguns ficheiros. Tente novamente dentro de instantes.',
  publishBlockedPolicy: 'As definições da empresa mudaram. Reveja e tente novamente.',
  publishBlockedApproval: 'A confirmação já não é válida. Confirme novamente.',
  publishBlockedDependency:
    'Um serviço necessário não está disponível. Tente novamente mais tarde.',
  publishAsk: 'Pedir a {publishers} que publique',
  publishAskFooter: 'Pessoas que podem publicar: {publishers}.',
  publishAskNone: 'Peça a alguém que possa publicar.',
  publishWarnings: 'Vale a pena ver antes de publicar',
  checkWarnStale: '{subject} está desatualizado ({time}).',
  checkWarnGap: '{subject} estava em falta na última atualização.',
  checkWarnUnknownSource: 'Uma fonte que não pode ver precisa de atenção.',
  checkRefreshFirst: 'Atualizar primeiro',
  checkFailedRow: '{subject} falhou na verificação.',
  checkFailedGeneric: 'Uma verificação falhou.',
  publishReviewAccess: 'Rever acesso',
  publishRemovalBody:
    'A v{number} remove {what} ({count}). Antes disso, é guardado um ponto de restauro.',
  publishReviewRemoval: 'Rever o que é removido',
  publishAndRemove: 'Publicar e remover',
  publishReviewFirst: 'Reveja o que é removido antes de publicar.',
  publishDoneTitle: 'A versão {number} está ativa para {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Vão encontrá-la na Biblioteca e afixada para a equipa.',
  publishUndo: 'Anular',
  publishCopyLink: 'Copiar ligação',
  publishDone: 'Concluído',
  shareRoleViewMeaning: 'Pode abri-lo e lê-lo',
  shareRoleEditMeaning: 'Pode alterá-lo no chat e com o inspetor',
  shareRolePublishMeaning: 'Pode também publicar e anular a publicação',
  shareRoleMenu: 'Função de {name}',
  shareUnpublishConfirm: 'As pessoas perdem o acesso. O Artefacto e as suas versões são mantidos.',
  shareUnpublishCancel: 'Manter ativo',
  sendDestinationLine: '{provider} · {count} pessoas',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Todos em {destination} veem o resumo. {outside} das {total} pessoas não fazem parte do público deste Artefacto, por isso a ligação mostra-lhes «Pedir acesso».',
  sendAudienceGeneric: 'As pessoas fora do público veem «Pedir acesso».',
  sendPreview: 'O que vão ver',
  shareTitle: 'Partilhar',
  shareLoading: 'A carregar a partilha…',
  shareErrorBody: 'Não foi possível carregar a partilha.',
  sharePrivate: 'Só você pode ver este Artefacto.',
  shareShared: 'Visível para {audience}.',
  shareRevoked: 'A partilha está desativada. Ninguém mais pode abrir este Artefacto.',
  shareOwnerSnapshot:
    'Quem o vê, vê os números como foram publicados. Não pode atualizar nem editar.',
  shareRoleView: 'Pode ver',
  shareRoleEdit: 'Pode editar',
  shareRolePublish: 'Pode publicar',
  shareLivePublished: 'Ativa: versão {number}',
  shareUnpublish: 'Anular publicação',
  shareManageDenied: 'Só um editor pode alterar quem vê isto.',
  sendTitle: 'Enviar para',
  sendIntro: 'Envie uma ligação e um resumo através de uma aplicação ligada. Aprova cada envio.',
  sendLoading: 'A carregar os destinos…',
  sendErrorBody: 'Não foi possível carregar os destinos.',
  sendNone:
    'Nenhuma aplicação ligada pode receber isto ainda. Ligue primeiro o Slack, o Teams ou o email.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'Email',
  sendAudience: 'Vai para {audience}',
  sendSummary: 'Mensagem',
  sendLeaves: 'É enviada uma ligação.',
  sendLeavesAttachment: 'São enviados uma ligação e o ficheiro revisto.',
  sendNoAttachments: 'Este destino não aceita ficheiros, por isso só é enviada uma ligação.',
  sendScheduled: 'Os envios agendados precisam da sua aprovação de cada vez.',
  sendAction: 'Rever e enviar',
  sendFileUnsupported:
    'Este destino não aceita o ficheiro. Envie só uma ligação ou escolha outro destino.',
  sendLinkOnly: 'Enviar só uma ligação',
  sendWaiting: 'À espera da sua aprovação. Nada foi enviado.',
  sendDelivered: 'Enviado para {destination}.',
  sendFailed: 'Não foi enviado. Nada saiu da Poneva.',
  sendUnknown:
    'Não foi possível confirmar a entrega em {destination}. Verifique aí antes de enviar novamente.',
  sendBlockedNotAuthorized: 'Não tem permissão para enviar isto.',
  sendBlockedUnsupported: 'Este destino não é suportado.',
  sendBlockedApproval: 'A aprovação já não é válida. Reveja e envie novamente.',
  sendBlockedChanged: 'A mensagem ou o destinatário mudaram. Reveja novamente.',
  sendBlockedDependency: 'A aplicação ligada não está disponível. Tente novamente mais tarde.',
  libraryTitle: 'Biblioteca',
  librarySegmentAll: 'Todos',
  librarySegmentMine: 'Meus',
  librarySegmentShared: 'Partilhados comigo',
  librarySearch: 'Pesquisar Artefactos',
  libraryKindAll: 'Todos os tipos',
  libraryKindReports: 'Relatórios e páginas',
  libraryKindDecks: 'Apresentações',
  libraryKindDocuments: 'Documentos',
  libraryKindSpreadsheets: 'Folhas de cálculo',
  libraryKindApps: 'Apps',
  libraryAppsMatchNoun:
    'one:App corresponde em Apps →|many:de Apps correspondem em Apps →|other:Apps correspondem em Apps →',
  libraryMake: 'Começar no chat',
  libraryEmptySharedHint: 'Quando alguém partilhar um Artefacto consigo, aparece aqui.',
  libraryRefreshFailedAt: 'Não foi possível atualizar em {time}.',
  libraryRefreshTryAgain: 'Tentar novamente',
  libraryVia: 'através de {owner}',
  savedPrivate: 'Privado',
  savedVersion: 'Versão {n}',
  libraryLoading: 'A carregar a sua Biblioteca…',
  libraryErrorBody: 'Não foi possível carregar a sua Biblioteca neste momento.',
  startersLoadFailed: 'Não foi possível carregar os modelos neste momento.',
  libraryCannotOpen: 'Ainda não é possível abrir “{title}” aqui.',
  libraryEmptyTitle: 'Ainda não há nada aqui',
  libraryEmptyAllTitle: 'Crie algo que valha a pena partilhar.',
  libraryEmptyAllBody:
    'Os documentos, apresentações e apps que criar no chat, ou que outros partilhem consigo, aparecem aqui.',
  libraryEmptyMineBody: 'Os Artefactos que criar aparecem aqui.',
  libraryEmptySharedBody: 'Ainda não foi partilhado nada consigo.',
  libraryNoMatchTitle: 'Sem resultados',
  libraryNoMatchBody: 'Tente outra palavra ou outro tipo.',
  libraryMore: 'Mostrar mais',
  libraryOwner: 'Por {owner}',
  libraryUpdated: 'Editado {time}',
  libraryPublishedAs: 'Publicado · v{number}',
  libraryDraft: 'Rascunho',
  libraryUnpublished: 'Não publicado',
  libraryRefreshFailed: 'Falha na atualização',
  libraryRefreshRunning: 'A atualizar',
  libraryAudience: 'Para {audience}',
  kindReport: 'Relatório',
  kindPage: 'Página',
  kindSite: 'Site',
  kindDeck: 'Apresentação',
  kindDocument: 'Documento',
  kindApp: 'App',
  kindSpreadsheet: 'Folha de cálculo',
  kindDashboard: 'Painel',
  kindTable: 'Tabela',
  kindChart: 'Gráfico',
  kindExportsReport: 'Exporta para PDF',
  kindExportsPage: 'Página só de leitura',
  kindExportsSite: 'Site só de leitura',
  kindExportsDeck: 'Apresente, ou exporte para PowerPoint e PDF',
  kindExportsDocument: 'Exporta para Word e PDF',
  kindExportsSpreadsheet: 'Exporta para Excel e CSV',
  kindExportsDashboard: 'Vista em direto',
  kindExportsTable: 'Exporta para Excel e CSV',
  kindExportsChart: 'Vista de gráfico',
  agentPublished: '{agent} publicou {title} v{number}.',
  agentCheckedOn: 'Verificado em {views}, {themes}.',
  agentUndo: 'Anular',
  agentRestorePrevious: 'Restaurar v{number}',
  agentHeld: '{title} v{number} está à espera como rascunho.',
  agentHeldMismatch: '{count} números não coincidiam com as suas fontes.',
  agentHeldCheck: 'Uma verificação não passou.',
  agentFixInChat: 'Corrigir no chat',
  agentConfirmDrop:
    '{agent} quer remover {what} de {title}. Antes disso, é guardado um ponto de restauro. Nada muda até decidir.',
  agentConfirmDropCount:
    '{agent} quer remover {what} ({count} valores) de {title}. Antes disso, é guardado um ponto de restauro. Nada muda até decidir.',
  agentViewDesktop: 'computador',
  agentViewTablet: 'tablet',
  agentViewPhone: 'telemóvel',
  agentThemeLight: 'claro',
  agentThemeDark: 'escuro',
  agentLookAt: 'Ver v{number}',
  agentConfirmWrite: '{agent} quer escrever {what}. Nada é escrito até decidir.',
  agentConfirmGrant: '{agent} quer aceder a {what}. Nada é partilhado até decidir.',
  agentReview: 'Rever',
  agentSkipped: '{agent} ignorou {part}.',
  agentSkippedWhy:
    'Lê os dados de {system} de cada pessoa, e esta execução não tem uma pessoa em nome de quem ler.',
  agentChooseWho: 'Escolher em nome de quem é executada',
  agentChangeInChat: 'Alterar no chat',
  agentFailed: '{agent} não conseguiu atualizar {title}.',
  agentFailedBody: 'A última versão publicada continua ativa.',
  agentRetry: 'Tentar novamente',
  agentRunning: '{agent} está a atualizar {title}.',
  agentMakesTitle: 'Cria e atualiza',
  agentMakesLoading: 'A carregar…',
  agentMakesErrorBody: 'Não foi possível carregar este conteúdo.',
  agentMakesNone: 'Este Agente ainda não cria Artefactos.',
  agentMakesNew: 'Um novo Artefacto em cada execução',
  agentMakesUpdate: 'Atualiza um Artefacto em cada execução',
  agentNextRun: 'Próxima execução {when}',
  agentNoSchedule: 'Não agendado',
  agentCreditsPerRun: 'Cerca de {credits} créditos por execução',
  agentCreditsUnknown: 'Os créditos por execução ainda não são conhecidos',
  agentLastResult: 'Último resultado',
  interviewTitle: 'Configurar',
  interviewIntro: 'Algumas perguntas dão-lhe forma.',
  interviewOptional: '(opcional)',
  interviewAnswerNeeded: 'Responda para continuar.',
  interviewReadOnly: 'Só de leitura',
  interviewChange: 'Alterar',
  interviewConnectionYours: 'a sua ligação {system}',
  interviewConnectionOther: 'a ligação {system} de {name}',
  interviewDateAny: 'Qualquer período',
  interviewDatePrevious: 'Mês anterior',
  interviewDateNext: 'Mês seguinte',
  interviewDatePresets: 'Intervalos rápidos',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Limpar',
  interviewDateDone: 'Concluído',
  interviewChooseSource: 'Escolha uma fonte',
  interviewSourceChosen: 'Fonte escolhida',
  interviewSourceUnavailable:
    'A escolha de uma fonte ainda não está disponível aqui, por isso não é possível continuar.',
  interviewSourceUnavailableOptional:
    'A escolha de uma fonte ainda não está disponível aqui. Pode continuar sem ela.',
  interviewStarterWithdrawn:
    'Este modelo já não está disponível. Os rascunhos que já começou a partir dele continuam a abrir.',
  interviewDateFrom: 'De',
  interviewDateTo: 'Até',
  interviewContinue: 'Continuar',
  interviewGrantTitle: 'Permitir a leitura de {source}?',
  interviewGrantBody:
    'Usa {connection}, só de leitura, para {scope}. Nada é lido até permitir e não são mostradas linhas de exemplo.',
  interviewAllow: 'Permitir',
  interviewNotNow: 'Agora não',
  interviewConfiguring: 'A configurar com base nas suas respostas…',
  interviewChecking: 'A verificar antes de abrir…',
  interviewComplete: '{title} está pronto.',
  interviewOpen: 'Abrir {title}',
  interviewBlocked: 'Não foi possível continuar.',
  interviewFailed: 'A configuração falhou. As suas respostas foram guardadas.',
  interviewCancelled: 'Foi cancelado.',
  interviewSaving: 'A guardar…',
  interviewSaved: 'Guardado',
  interviewConflict:
    'Este rascunho mudou noutro local. As suas respostas ficam aqui até carregar o rascunho atual, que as substitui.',
  interviewReloadCurrent: 'Carregar o rascunho atual',
  interviewUnsettled: 'Não foi possível confirmar a última gravação. As suas respostas ficam aqui.',
  interviewCheckAgain: 'Verificar novamente',
  finishAction: 'Guardar em privado',
  finishSaving: 'A guardar…',
  finishSaved: '«{title}» foi guardado e só você o pode ver.',
  finishNotSaved: 'Não foi guardado. O seu rascunho continua aqui.',
  finishUnconfirmed: 'Não foi possível confirmar se foi guardado.',
  candidateInvalid: 'Não foi possível transformar o rascunho num documento.',
  finishOpenInLibrary: 'Abrir na Biblioteca',
  savedOpenFailed: 'Não foi possível abrir neste momento.',
  savedInLibrary: 'Está na sua Biblioteca.',
  openSavedTitle: 'Abrir {title} →',
  interviewReasonNotAuthorized: 'Não tem permissão para alterar este rascunho.',
  interviewReasonDraftNotOpen: 'Este rascunho já não está aberto.',
  interviewReasonInvalid: 'Uma das respostas não é válida.',
  interviewReasonObjects:
    'Não foi possível verificar alguns ficheiros. As suas respostas foram guardadas.',
  interviewReasonDependency:
    'Um serviço necessário não está disponível. As suas respostas foram guardadas.',
  interviewReasonReconcile: 'A última gravação ainda está a ser confirmada.',
  exportTitle: 'Exportar',
  exportIntro: 'Transfira este Artefacto. O ficheiro indica de onde vêm os dados.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Diapositivos editáveis com notas do orador',
  exportHintPdf: 'Exatamente como é mostrado',
  exportHintDocx: 'Editável, mantém títulos e tabelas',
  exportHintXlsx: 'Todas as folhas, mantém as fórmulas',
  exportHintCsv: 'Só valores, UTF-8',
  exportUnavailable: 'Não disponível de momento',
  exportRunning: 'A preparar {format}…',
  exportValidating: 'A verificar o ficheiro {format}…',
  exportReady: 'O ficheiro {format} está pronto',
  exportDownload: 'Transferir {name}',
  exportVersion: 'Versão {number} exportada',
  exportFailed: 'Não foi possível criar o ficheiro {format}.',
  exportFailedChecks:
    'O ficheiro {format} não passou nas verificações, por isso não foi disponibilizado.',
  exportDenied: 'Não tem acesso para exportar este Artefacto.',
  exportRevoked: 'A partilha foi desativada, por isso este Artefacto já não pode ser exportado.',
  exportStale:
    'Este Artefacto mudou durante a exportação. Exporte novamente para obter a versão mais recente.',
  exportProvenanceTitle: 'Cada ficheiro inclui',
  exportProvenanceLine: 'Criado com a Poneva · {title} · v{number} · {data} · criado {madeAt}',
  exportProvenanceAsOf: 'dados de {time}',
  exportPlacementNotesAbout: 'Na folha Notas e num ficheiro de informação junto ao CSV',
  exportProvenanceRange: 'dados de {earliest} a {latest}',
  exportProvenanceUnknown: 'idade dos dados desconhecida',
  exportAboutFile: 'Sobre este ficheiro ({format})',
  exportPlacementFooter: 'No rodapé de cada página',
  exportPlacementSlide: 'No rodapé de cada diapositivo',
  exportPlacementNotes: 'Na folha Notas',
  exportPlacementAbout: 'Num ficheiro de informação junto ao CSV',
  exportSourcesPage: 'Adicionar uma página de fontes',
  exportSourcesPageHint: 'Uma última página que lista cada número, a sua fonte e a sua data.',
  exportSourcesPageDeck: 'O diapositivo Fontes da apresentação acompanha-o.',
  exportSourcesPageSaved: 'Memorizado para este Artefacto, incluindo envios agendados.',
  libraryKindSheetTitle: 'Tipo',
  shareAccessSheetTitle: 'Acesso',

  requestShowAll: 'Mostrar tudo',
  requestShowLess: 'Mostrar menos',
  approveGettingReady: 'A preparar…',
  approveNotReady: 'Não é possível aprovar neste momento. Recarregue a página e tente novamente.',
  tryWithModel: 'Experimentar com {model}',
  tryWithRequesting: 'A iniciar…',
  tryWithUnavailable: 'Não é possível iniciar neste momento.',
};

const ro: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Editați o ciornă a versiunii {number}',
  noChangesToApply: 'Nu există modificări de aplicat.',
  appliedDone: 'Aplicat.',
  mentionInChat: 'Menționează în chat',

  kindMarkdown: 'Notă',
  kindKpi: 'Indicator',
  libraryExportsList: 'Se exportă în {formats}',
  libraryOpensToRead: 'Se deschide pentru citire',
  libraryReadOnly: 'Doar citire',
  libraryLiveView: 'Vizualizare live',
  libraryOneNumber: 'Un număr · la {time}',
  retry: 'Încercați din nou',
  close: 'Închide',
  panelLoading: 'Se încarcă…',
  panelEmpty: 'Nu este nimic aici încă.',
  panelDenied: 'Nu aveți acces la acest conținut.',
  panelBlocked: 'Așteaptă ceva înainte de a putea continua.',
  panelBlockedViewerContext:
    'Această rulare nu are o persoană în numele căreia să citească, așa că nu poate continua până când cineva alege în numele cui rulează.',
  panelError: 'Conținutul nu a putut fi încărcat.',
  panelStale: 'Ar putea fi depășit.',
  sourcesTitle: 'Surse',
  sourcesIntro: 'De unde vine fiecare număr și cât de recent este.',
  sourcesLoading: 'Se încarcă sursele…',
  sourcesEmptyTitle: 'Nicio sursă',
  sourcesEmptyBody: 'Acest artefact nu folosește date ale companiei.',
  sourcesErrorBody: 'Sursele nu au putut fi încărcate.',
  sourcesDeniedTitle: 'Unele surse sunt ascunse',
  sourcesDeniedBody:
    'Nu aveți acces la {count} surse folosite aici. Rugați un proprietar să le partajeze.',
  sourceDeniedName: 'Sursă pe care nu o puteți vedea',
  sourceAsOf: 'La data de {time}',
  sourceAgeUnknown: 'Vechimea datelor este necunoscută',
  sourceStatusCurrent: 'Actuală',
  sourceStatusStale: 'Depășită',
  sourceStatusGap: 'Lipsă la ultima actualizare',
  sourceStatusDenied: 'Fără acces',
  sourceLastGood: 'Ultima validă: {time}',
  sourceUsedBy: 'Folosită de {labels}',
  openDocument: 'Deschide documentul',
  versionsShow: 'Afișează versiunile',
  versionsHide: 'Ascunde versiunile',
  versionsTitle: 'Versiuni',
  versionsLoading: 'Se încarcă versiunile…',
  versionsEmptyTitle: 'Nicio versiune încă',
  versionsEmptyBody: 'Prima versiune apare când acest artefact este creat.',
  versionsErrorBody: 'Versiunile nu au putut fi încărcate.',
  versionLabel: 'Versiunea {number}',
  versionOpen: 'Deschide versiunea {number}',
  versionCurrent: 'Curentă',
  versionPublished: 'Publicată',
  versionBy: 'De {actor}',
  versionOriginNative: 'În Poneva',
  versionOriginMcp: 'prin propriul agent',
  versionOriginTerminal: 'prin terminal',
  versionOriginJob: 'Rulare programată',
  versionCausePerson: 'Editată',
  versionCauseAgent: 'Actualizare de la agent',
  versionCauseRefresh: 'Actualizată',
  versionCauseRestore: 'Restaurată',
  versionCauseImport: 'Importată',
  versionCheckedYes: 'Verificată',
  versionCheckedNo: 'Încă neverificată',
  versionCheckedInvalidated: 'Editată după ultima verificare',
  compareStart: 'Compară',
  compareWith: 'Compară cu versiunea {number}',
  compareTitle: 'Ce s-a schimbat de la v{from} la v{to}',
  compareFromMark: 'De la',
  compareToMark: 'La',
  comparePick: 'Alegeți o a doua versiune pentru comparare.',
  compareNone: 'Nicio diferență.',
  compareLoading: 'Se compară…',
  compareError: 'Comparația nu a putut fi încărcată.',
  compareBefore: 'Înainte',
  compareAfter: 'După',
  compareAdded: 'Adăugat',
  compareRemoved: 'Eliminat',
  compareChanged: 'Modificat',
  restore: 'Restaurează această versiune',
  restoreConfirmTitle: 'Restaurați versiunea {number}?',
  restoreConfirmBody:
    'Aceasta creează o versiune nouă pornind de la versiunea {number}. Nu se șterge nimic.',
  restoreConfirm: 'Restaurează',
  restoreCancel: 'Anulează',
  restoring: 'Se restaurează…',
  restoreFailed: 'Versiunea nu a putut fi restaurată.',
  restoreStale:
    'Acest artefact s-a schimbat cât timp l-ați vizualizat. Reîncărcați versiunile și încercați din nou.',
  restoreDenied: 'Nu aveți permisiunea de a restaura versiuni.',
  restored: 'Versiunea {number} a fost restaurată ca versiune nouă.',
  publishTitle: 'Publică',
  publishIntro: 'Versiunea {number} va fi vizibilă pentru {audience}.',
  publishOwnerLine:
    'Cei care o vizualizează văd ce putea vedea {owner} când a fost publicată această versiune.',
  publishChecksTitle: 'Verificări',
  checkPassed: 'Verificată',
  checkRunning: 'Se verifică…',
  checkFailed: 'O verificare a eșuat',
  checkStale: 'Editată după ultima verificare',
  publishFixInChat: 'Corectați în chat',
  publishAction: 'Publică versiunea {number}',
  publishing: 'Se publică…',
  publishedDone: 'Versiunea {number} este activă pentru {audience}.',
  publishConfirmGrant:
    'Această versiune necesită acces nou la un sistem al companiei. Verificați accesul înainte de publicare.',
  publishConfirmWrite:
    'Această versiune scrie într-un sistem al companiei. Verificați accesul înainte de publicare.',
  publishConfirmDestructive: 'Această versiune elimină date. Confirmați mai întâi în Poneva.',
  publishConflict: 'O altă versiune a fost publicată prima. Reîncărcați pentru a o vedea.',
  publishBlockedNotAuthorized: 'Nu aveți permisiunea de a publica.',
  publishBlockedCheckFailed: 'O verificare a eșuat, deci această versiune nu poate fi publicată.',
  publishBlockedCheckStale: 'Artefactul s-a schimbat după ultima verificare. Verificați-l din nou.',
  publishBlockedObjects:
    'Unele fișiere nu au putut fi verificate. Încercați din nou peste puțin timp.',
  publishBlockedPolicy: 'Setările companiei s-au schimbat. Verificați și încercați din nou.',
  publishBlockedApproval: 'Confirmarea nu mai este valabilă. Confirmați din nou.',
  publishBlockedDependency: 'Un serviciu necesar nu este disponibil. Încercați din nou mai târziu.',
  publishAsk: 'Rugați-l pe {publishers} să publice',
  publishAskFooter: 'Persoane care pot publica: {publishers}.',
  publishAskNone: 'Rugați o persoană care poate publica.',
  publishWarnings: 'Merită verificat înainte de publicare',
  checkWarnStale: '{subject} este depășit ({time}).',
  checkWarnGap: '{subject} lipsea la ultima actualizare.',
  checkWarnUnknownSource: 'O sursă pe care nu o puteți vedea necesită atenție.',
  checkRefreshFirst: 'Actualizați mai întâi',
  checkFailedRow: '{subject} a eșuat la verificare.',
  checkFailedGeneric: 'O verificare a eșuat.',
  publishReviewAccess: 'Verificați accesul',
  publishRemovalBody:
    'v{number} elimină {what} ({count}). Mai întâi se salvează un punct de restaurare.',
  publishReviewRemoval: 'Verificați ce se elimină',
  publishAndRemove: 'Publică și elimină',
  publishReviewFirst: 'Verificați ce se elimină înainte de a publica.',
  publishDoneTitle: 'Versiunea {number} este activă pentru {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'O vor găsi în Biblioteca lor și fixată pentru echipă.',
  publishUndo: 'Anulează',
  publishCopyLink: 'Copiază linkul',
  publishDone: 'Gata',
  shareRoleViewMeaning: 'Îl poate deschide și citi',
  shareRoleEditMeaning: 'Îl poate modifica în chat și cu inspectorul',
  shareRolePublishMeaning: 'Poate, de asemenea, să publice și să anuleze publicarea',
  shareRoleMenu: 'Rol pentru {name}',
  shareUnpublishConfirm: 'Persoanele pierd accesul. Artefactul și versiunile lui sunt păstrate.',
  shareUnpublishCancel: 'Păstrează activ',
  sendDestinationLine: '{provider} · {count} persoane',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Toți din {destination} văd rezumatul. {outside} din cei {total} nu fac parte din publicul acestui artefact, așa că linkul le afișează „Solicită acces”.',
  sendAudienceGeneric: 'Persoanele din afara publicului văd „Solicită acces”.',
  sendPreview: 'Ce vor vedea',
  shareTitle: 'Partajează',
  shareLoading: 'Se încarcă partajarea…',
  shareErrorBody: 'Partajarea nu a putut fi încărcată.',
  sharePrivate: 'Doar dumneavoastră puteți vedea acest artefact.',
  shareShared: 'Vizibil pentru {audience}.',
  shareRevoked: 'Partajarea este dezactivată. Nimeni altcineva nu poate deschide acest artefact.',
  shareOwnerSnapshot:
    'Cei care îl vizualizează văd cifrele așa cum au fost publicate. Nu pot actualiza sau edita.',
  shareRoleView: 'Poate vizualiza',
  shareRoleEdit: 'Poate edita',
  shareRolePublish: 'Poate publica',
  shareLivePublished: 'Activă: versiunea {number}',
  shareUnpublish: 'Anulează publicarea',
  shareManageDenied: 'Doar un editor poate schimba cine vede acest lucru.',
  sendTitle: 'Trimite către',
  sendIntro:
    'Trimiteți un link și un rezumat printr-o aplicație conectată. Aprobați fiecare trimitere.',
  sendLoading: 'Se încarcă destinațiile…',
  sendErrorBody: 'Destinațiile nu au putut fi încărcate.',
  sendNone:
    'Nicio aplicație conectată nu poate primi încă acest lucru. Conectați mai întâi Slack, Teams sau e-mailul.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-mail',
  sendAudience: 'Ajunge la {audience}',
  sendSummary: 'Mesaj',
  sendLeaves: 'Se trimite un link.',
  sendLeavesAttachment: 'Se trimit un link și fișierul verificat.',
  sendNoAttachments: 'Această destinație nu poate primi fișiere, așa că se trimite doar un link.',
  sendScheduled: 'Trimiterile programate necesită aprobarea dumneavoastră de fiecare dată.',
  sendAction: 'Verifică și trimite',
  sendFileUnsupported:
    'Această destinație nu poate primi fișierul. Trimiteți doar un link sau alegeți altă destinație.',
  sendLinkOnly: 'Trimite doar un link',
  sendWaiting: 'Se așteaptă aprobarea dumneavoastră. Nu s-a trimis nimic.',
  sendDelivered: 'Trimis către {destination}.',
  sendFailed: 'Nu s-a trimis. Nimic nu a părăsit Poneva.',
  sendUnknown:
    'Nu am putut confirma livrarea către {destination}. Verificați acolo înainte de a trimite din nou.',
  sendBlockedNotAuthorized: 'Nu aveți permisiunea de a trimite acest lucru.',
  sendBlockedUnsupported: 'Această destinație nu este acceptată.',
  sendBlockedApproval: 'Aprobarea nu mai este valabilă. Verificați și trimiteți din nou.',
  sendBlockedChanged: 'Mesajul sau destinatarul s-a schimbat. Verificați din nou.',
  sendBlockedDependency: 'Aplicația conectată nu este disponibilă. Încercați din nou mai târziu.',
  libraryTitle: 'Bibliotecă',
  librarySegmentAll: 'Toate',
  librarySegmentMine: 'Ale mele',
  librarySegmentShared: 'Partajate cu mine',
  librarySearch: 'Caută artefacte',
  libraryKindAll: 'Toate tipurile',
  libraryKindReports: 'Rapoarte și pagini',
  libraryKindDecks: 'Prezentări',
  libraryKindDocuments: 'Documente',
  libraryKindSpreadsheets: 'Foi de calcul',
  libraryKindApps: 'Aplicații',
  libraryAppsMatchNoun:
    'one:aplicație corespunde în Aplicații →|few:aplicații corespund în Aplicații →|other:de aplicații corespund în Aplicații →',
  libraryMake: 'Începe în chat',
  libraryEmptySharedHint: 'Când cineva partajează un artefact cu dumneavoastră, apare aici.',
  libraryRefreshFailedAt: 'Nu s-a putut actualiza pe {time}.',
  libraryRefreshTryAgain: 'Încercați din nou',
  libraryVia: 'prin {owner}',
  savedPrivate: 'Privat',
  savedVersion: 'Versiunea {n}',
  libraryLoading: 'Se încarcă Biblioteca dumneavoastră…',
  libraryErrorBody: 'Biblioteca dumneavoastră nu a putut fi încărcată acum.',
  startersLoadFailed: 'Șabloanele nu au putut fi încărcate acum.',
  libraryCannotOpen: 'Deocamdată nu se poate deschide aici „{title}”.',
  libraryEmptyTitle: 'Nu este nimic aici încă',
  libraryEmptyAllTitle: 'Creează ceva care merită împărtășit.',
  libraryEmptyAllBody:
    'Documentele, prezentările și aplicațiile pe care le creezi în chat sau pe care alții le partajează cu tine apar aici.',
  libraryEmptyMineBody: 'Artefactele pe care le creați apar aici.',
  libraryEmptySharedBody: 'Nu s-a partajat încă nimic cu dumneavoastră.',
  libraryNoMatchTitle: 'Niciun rezultat',
  libraryNoMatchBody: 'Încercați alt cuvânt sau alt tip.',
  libraryMore: 'Arată mai multe',
  libraryOwner: 'De {owner}',
  libraryUpdated: 'Editat {time}',
  libraryPublishedAs: 'Publicat · v{number}',
  libraryDraft: 'Ciornă',
  libraryUnpublished: 'Nepublicat',
  libraryRefreshFailed: 'Actualizarea a eșuat',
  libraryRefreshRunning: 'Se actualizează',
  libraryAudience: 'Pentru {audience}',
  kindReport: 'Raport',
  kindPage: 'Pagină',
  kindSite: 'Site',
  kindDeck: 'Prezentare',
  kindDocument: 'Document',
  kindApp: 'Aplicație',
  kindSpreadsheet: 'Foaie de calcul',
  kindDashboard: 'Panou',
  kindTable: 'Tabel',
  kindChart: 'Grafic',
  kindExportsReport: 'Se exportă în PDF',
  kindExportsPage: 'Pagină doar pentru citire',
  kindExportsSite: 'Site doar pentru citire',
  kindExportsDeck: 'Prezentați sau exportați în PowerPoint și PDF',
  kindExportsDocument: 'Se exportă în Word și PDF',
  kindExportsSpreadsheet: 'Se exportă în Excel și CSV',
  kindExportsDashboard: 'Vizualizare în timp real',
  kindExportsTable: 'Se exportă în Excel și CSV',
  kindExportsChart: 'Vizualizare grafic',
  agentPublished: '{agent} a publicat {title} v{number}.',
  agentCheckedOn: 'Verificat pe {views}, {themes}.',
  agentUndo: 'Anulează',
  agentRestorePrevious: 'Restaurează v{number}',
  agentHeld: '{title} v{number} așteaptă ca ciornă.',
  agentHeldMismatch: '{count} numere nu se potriveau cu sursele lor.',
  agentHeldCheck: 'O verificare nu a trecut.',
  agentFixInChat: 'Corectați în chat',
  agentConfirmDrop:
    '{agent} vrea să elimine {what} din {title}. Mai întâi se salvează un punct de restaurare. Nu se schimbă nimic până nu decideți.',
  agentConfirmDropCount:
    '{agent} vrea să elimine {what} ({count} valori) din {title}. Mai întâi se salvează un punct de restaurare. Nu se schimbă nimic până nu decideți.',
  agentViewDesktop: 'desktop',
  agentViewTablet: 'tabletă',
  agentViewPhone: 'telefon',
  agentThemeLight: 'luminos',
  agentThemeDark: 'întunecat',
  agentLookAt: 'Vedeți v{number}',
  agentConfirmWrite: '{agent} vrea să scrie {what}. Nu se scrie nimic până nu decideți.',
  agentConfirmGrant: '{agent} vrea acces la {what}. Nu se partajează nimic până nu decideți.',
  agentReview: 'Verificați',
  agentSkipped: '{agent} a omis {part}.',
  agentSkippedWhy:
    'Citește datele {system} ale fiecărei persoane, iar această rulare nu are o persoană în numele căreia să citească.',
  agentChooseWho: 'Alegeți în numele cui rulează',
  agentChangeInChat: 'Modificați în chat',
  agentFailed: '{agent} nu a putut actualiza {title}.',
  agentFailedBody: 'Ultima versiune publicată este încă activă.',
  agentRetry: 'Încercați din nou',
  agentRunning: '{agent} actualizează {title}.',
  agentMakesTitle: 'Creează și actualizează',
  agentMakesLoading: 'Se încarcă…',
  agentMakesErrorBody: 'Conținutul nu a putut fi încărcat.',
  agentMakesNone: 'Acest agent nu creează încă artefacte.',
  agentMakesNew: 'Un artefact nou la fiecare rulare',
  agentMakesUpdate: 'Actualizează un artefact la fiecare rulare',
  agentNextRun: 'Următoarea rulare {when}',
  agentNoSchedule: 'Neprogramat',
  agentCreditsPerRun: 'Credite per rulare: aproximativ {credits}',
  agentCreditsUnknown: 'Creditele per rulare nu sunt încă cunoscute',
  agentLastResult: 'Ultimul rezultat',
  interviewTitle: 'Configurați',
  interviewIntro: 'Câteva întrebări îi dau forma.',
  interviewOptional: '(opțional)',
  interviewAnswerNeeded: 'Răspundeți pentru a continua.',
  interviewReadOnly: 'Doar citire',
  interviewChange: 'Modifică',
  interviewConnectionYours: 'conexiunea dumneavoastră {system}',
  interviewConnectionOther: 'conexiunea {system} a lui {name}',
  interviewDateAny: 'Orice perioadă',
  interviewDatePrevious: 'Luna anterioară',
  interviewDateNext: 'Luna următoare',
  interviewDatePresets: 'Intervale rapide',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Șterge',
  interviewDateDone: 'Gata',
  interviewChooseSource: 'Alegeți o sursă',
  interviewSourceChosen: 'Sursă aleasă',
  interviewSourceUnavailable:
    'Alegerea unei surse nu este încă disponibilă aici, deci nu se poate continua.',
  interviewSourceUnavailableOptional:
    'Alegerea unei surse nu este încă disponibilă aici. Puteți continua și fără una.',
  interviewStarterWithdrawn:
    'Acest șablon nu mai este disponibil. Ciornele pe care le-ați început deja din el se deschid în continuare.',
  interviewDateFrom: 'De la',
  interviewDateTo: 'Până la',
  interviewContinue: 'Continuă',
  interviewGrantTitle: 'Permiteți citirea {source}?',
  interviewGrantBody:
    'Folosește {connection}, doar pentru citire, pentru {scope}. Nu se citește nimic până nu permiteți și nu se afișează rânduri de exemplu.',
  interviewAllow: 'Permite',
  interviewNotNow: 'Nu acum',
  interviewConfiguring: 'Se configurează pe baza răspunsurilor dumneavoastră…',
  interviewChecking: 'Se verifică înainte de a se deschide…',
  interviewComplete: '{title} este gata.',
  interviewOpen: 'Deschide {title}',
  interviewBlocked: 'Nu s-a putut continua.',
  interviewFailed: 'Configurarea a eșuat. Răspunsurile dumneavoastră au fost păstrate.',
  interviewCancelled: 'A fost anulat.',
  interviewSaving: 'Se salvează…',
  interviewSaved: 'Salvat',
  interviewConflict:
    'Această ciornă s-a schimbat în altă parte. Răspunsurile dumneavoastră rămân aici până încărcați ciorna curentă, care le înlocuiește.',
  interviewReloadCurrent: 'Încarcă ciorna curentă',
  interviewUnsettled: 'Nu am putut confirma ultima salvare. Răspunsurile dumneavoastră rămân aici.',
  interviewCheckAgain: 'Verificați din nou',
  finishAction: 'Salvare privată',
  finishSaving: 'Se salvează…',
  finishSaved: '„{title}” a fost salvat și este vizibil doar pentru dumneavoastră.',
  finishNotSaved: 'Nu a fost salvat. Ciorna dumneavoastră este încă aici.',
  finishUnconfirmed: 'Nu am putut confirma dacă a fost salvat.',
  candidateInvalid: 'Ciorna nu a putut fi transformată într-un document.',
  finishOpenInLibrary: 'Deschideți în Bibliotecă',
  savedOpenFailed: 'Nu s-a putut deschide acum.',
  savedInLibrary: 'Se află în Biblioteca dumneavoastră.',
  openSavedTitle: 'Deschide {title} →',
  interviewReasonNotAuthorized: 'Nu aveți permisiunea de a modifica această ciornă.',
  interviewReasonDraftNotOpen: 'Această ciornă nu mai este deschisă.',
  interviewReasonInvalid: 'Unul dintre răspunsuri nu este valid.',
  interviewReasonObjects:
    'Unele fișiere nu au putut fi verificate. Răspunsurile dumneavoastră au fost păstrate.',
  interviewReasonDependency:
    'Un serviciu necesar nu este disponibil. Răspunsurile dumneavoastră au fost păstrate.',
  interviewReasonReconcile: 'Ultima salvare este încă în curs de confirmare.',
  exportTitle: 'Exportă',
  exportIntro: 'Descărcați acest artefact. Fișierul indică de unde provin datele.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Diapozitive editabile cu notițe pentru prezentator',
  exportHintPdf: 'Exact cum apare',
  exportHintDocx: 'Editabil, păstrează titlurile și tabelele',
  exportHintXlsx: 'Toate foile, păstrează formulele',
  exportHintCsv: 'Doar valori, UTF-8',
  exportUnavailable: 'Indisponibil momentan',
  exportRunning: 'Se pregătește {format}…',
  exportValidating: 'Se verifică fișierul {format}…',
  exportReady: 'Fișierul {format} este gata',
  exportDownload: 'Descarcă {name}',
  exportVersion: 'Versiunea {number} exportată',
  exportFailed: 'Fișierul {format} nu a putut fi creat.',
  exportFailedChecks: 'Fișierul {format} nu a trecut verificările, așa că nu a fost oferit.',
  exportDenied: 'Nu aveți acces pentru a exporta acest artefact.',
  exportRevoked: 'Partajarea a fost dezactivată, așa că acest artefact nu mai poate fi exportat.',
  exportStale:
    'Acest artefact s-a schimbat în timpul exportului. Exportați din nou pentru a obține cea mai recentă versiune.',
  exportProvenanceTitle: 'Fiecare fișier include',
  exportProvenanceLine: 'Creat cu Poneva · {title} · v{number} · {data} · creat {madeAt}',
  exportProvenanceAsOf: 'date la {time}',
  exportPlacementNotesAbout: 'În foaia Note și într-un fișier de informații lângă CSV',
  exportProvenanceRange: 'date de la {earliest} până la {latest}',
  exportProvenanceUnknown: 'vechimea datelor este necunoscută',
  exportAboutFile: 'Despre acest fișier ({format})',
  exportPlacementFooter: 'În subsolul fiecărei pagini',
  exportPlacementSlide: 'În subsolul fiecărui diapozitiv',
  exportPlacementNotes: 'În foaia Note',
  exportPlacementAbout: 'Într-un fișier de informații lângă CSV',
  exportSourcesPage: 'Adăugați o pagină cu surse',
  exportSourcesPageHint: 'O ultimă pagină care listează fiecare număr, sursa și data lui.',
  exportSourcesPageDeck: 'Diapozitivul Surse al prezentării este inclus.',
  exportSourcesPageSaved: 'Reținut pentru acest artefact, inclusiv pentru trimiterile programate.',
  libraryKindSheetTitle: 'Tip',
  shareAccessSheetTitle: 'Acces',

  requestShowAll: 'Afișează tot',
  requestShowLess: 'Afișează mai puțin',
  approveGettingReady: 'Se pregătește…',
  approveNotReady: 'Nu se poate aproba acum. Reîncărcați pagina, apoi încercați din nou.',
  tryWithModel: 'Încearcă cu {model}',
  tryWithRequesting: 'Se pornește…',
  tryWithUnavailable: 'Nu se poate porni acum.',
};

const sk: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Upravujete koncept verzie {number}',
  noChangesToApply: 'Žiadne zmeny na použitie.',
  appliedDone: 'Použité.',
  mentionInChat: 'Spomenúť v chate',

  kindMarkdown: 'Poznámka',
  kindKpi: 'Metrika',
  libraryExportsList: 'Export do {formats}',
  libraryOpensToRead: 'Otvorí sa na čítanie',
  libraryReadOnly: 'Iba na čítanie',
  libraryLiveView: 'Živý prehľad',
  libraryOneNumber: 'Jedno číslo · k {time}',
  retry: 'Skúsiť znova',
  close: 'Zavrieť',
  panelLoading: 'Načítava sa…',
  panelEmpty: 'Zatiaľ tu nič nie je.',
  panelDenied: 'K tomuto nemáte prístup.',
  panelBlocked: 'Kým sa bude dať pokračovať, čaká sa na niečo iné.',
  panelBlockedViewerContext:
    'Toto spustenie nemá osobu, v mene ktorej by čítalo údaje, takže nemôže pokračovať, kým niekto nevyberie, v mene koho pobeží.',
  panelError: 'Toto sa nepodarilo načítať.',
  panelStale: 'Môže byť neaktuálne.',
  sourcesTitle: 'Zdroje',
  sourcesIntro: 'Odkiaľ každé číslo pochádza a aké je aktuálne.',
  sourcesLoading: 'Načítavajú sa zdroje…',
  sourcesEmptyTitle: 'Žiadne zdroje',
  sourcesEmptyBody: 'Tento artefakt nepoužíva firemné údaje.',
  sourcesErrorBody: 'Zdroje sa nepodarilo načítať.',
  sourcesDeniedTitle: 'Niektoré zdroje sú skryté',
  sourcesDeniedBody:
    'Počet tu použitých zdrojov, ku ktorým nemáte prístup: {count}. Požiadajte vlastníka, aby ich zdieľal.',
  sourceDeniedName: 'Zdroj, ktorý nevidíte',
  sourceAsOf: 'K {time}',
  sourceAgeUnknown: 'Vek údajov je neznámy',
  sourceStatusCurrent: 'Aktuálne',
  sourceStatusStale: 'Neaktuálne',
  sourceStatusGap: 'Chýba v poslednej aktualizácii',
  sourceStatusDenied: 'Bez prístupu',
  sourceLastGood: 'Naposledy v poriadku: {time}',
  sourceUsedBy: 'Používa: {labels}',
  openDocument: 'Otvoriť dokument',
  versionsShow: 'Zobraziť verzie',
  versionsHide: 'Skryť verzie',
  versionsTitle: 'Verzie',
  versionsLoading: 'Načítavajú sa verzie…',
  versionsEmptyTitle: 'Zatiaľ žiadne verzie',
  versionsEmptyBody: 'Prvá verzia sa zobrazí, keď sa tento artefakt vytvorí.',
  versionsErrorBody: 'Verzie sa nepodarilo načítať.',
  versionLabel: 'Verzia {number}',
  versionOpen: 'Otvoriť verziu {number}',
  versionCurrent: 'Aktuálna',
  versionPublished: 'Zverejnená',
  versionBy: 'Autor: {actor}',
  versionOriginNative: 'V Poneva',
  versionOriginMcp: 'cez vášho vlastného agenta',
  versionOriginTerminal: 'cez terminál',
  versionOriginJob: 'Naplánované spustenie',
  versionCausePerson: 'Upravené',
  versionCauseAgent: 'Aktualizácia agentom',
  versionCauseRefresh: 'Aktualizované',
  versionCauseRestore: 'Obnovené',
  versionCauseImport: 'Importované',
  versionCheckedYes: 'Skontrolované',
  versionCheckedNo: 'Zatiaľ neskontrolované',
  versionCheckedInvalidated: 'Upravené od poslednej kontroly',
  compareStart: 'Porovnať',
  compareWith: 'Porovnať s verziou {number}',
  compareTitle: 'Čo sa zmenilo od v{from} do v{to}',
  compareFromMark: 'Z',
  compareToMark: 'Na',
  comparePick: 'Vyberte druhú verziu na porovnanie.',
  compareNone: 'Žiadne rozdiely.',
  compareLoading: 'Porovnáva sa…',
  compareError: 'Porovnanie sa nepodarilo načítať.',
  compareBefore: 'Pred',
  compareAfter: 'Po',
  compareAdded: 'Pridané',
  compareRemoved: 'Odstránené',
  compareChanged: 'Zmenené',
  restore: 'Obnoviť túto verziu',
  restoreConfirmTitle: 'Obnoviť verziu {number}?',
  restoreConfirmBody: 'Z verzie {number} vznikne nová verzia. Nič sa nevymaže.',
  restoreConfirm: 'Obnoviť',
  restoreCancel: 'Zrušiť',
  restoring: 'Obnovuje sa…',
  restoreFailed: 'Verziu sa nepodarilo obnoviť.',
  restoreStale:
    'Tento artefakt sa zmenil, kým ste ho prezerali. Načítajte verzie znova a skúste to znova.',
  restoreDenied: 'Na obnovovanie verzií nemáte oprávnenie.',
  restored: 'Verzia {number} bola obnovená ako nová verzia.',
  publishTitle: 'Zverejniť',
  publishIntro: 'Verziu {number} uvidí: {audience}.',
  publishOwnerLine: 'Čitatelia uvidia to, čo mohol vidieť {owner}, keď sa táto verzia zverejnila.',
  publishChecksTitle: 'Kontroly',
  checkPassed: 'Skontrolované',
  checkRunning: 'Kontroluje sa…',
  checkFailed: 'Kontrola zlyhala',
  checkStale: 'Upravené od poslednej kontroly',
  publishFixInChat: 'Opraviť v chate',
  publishAction: 'Zverejniť verziu {number}',
  publishing: 'Zverejňuje sa…',
  publishedDone: 'Verzia {number} je naživo pre: {audience}.',
  publishConfirmGrant:
    'Táto verzia potrebuje nový prístup k firemnému systému. Pred zverejnením prístup skontrolujte.',
  publishConfirmWrite:
    'Táto verzia zapisuje do firemného systému. Pred zverejnením prístup skontrolujte.',
  publishConfirmDestructive: 'Táto verzia odstraňuje údaje. Najprv to potvrďte v Poneva.',
  publishConflict: 'Najprv sa zverejnila iná verzia. Načítajte znova, aby ste ju videli.',
  publishBlockedNotAuthorized: 'Na zverejňovanie nemáte oprávnenie.',
  publishBlockedCheckFailed: 'Kontrola zlyhala, preto túto verziu nemožno zverejniť.',
  publishBlockedCheckStale: 'Artefakt sa po poslednej kontrole zmenil. Skontrolujte ho znova.',
  publishBlockedObjects: 'Niektoré súbory sa nepodarilo overiť. Skúste to o chvíľu.',
  publishBlockedPolicy: 'Firemné nastavenia sa zmenili. Skontrolujte ich a skúste to znova.',
  publishBlockedApproval: 'Potvrdenie už neplatí. Potvrďte znova.',
  publishBlockedDependency: 'Potrebná služba nie je dostupná. Skúste to neskôr.',
  publishAsk: 'Požiadať o zverejnenie: {publishers}',
  publishAskFooter: 'Ľudia, ktorí môžu zverejňovať: {publishers}.',
  publishAskNone: 'Požiadajte niekoho, kto môže zverejňovať.',
  publishWarnings: 'Oplatí sa skontrolovať pred zverejnením',
  checkWarnStale: '{subject} je neaktuálne ({time}).',
  checkWarnGap: '{subject} – chýba v poslednej aktualizácii.',
  checkWarnUnknownSource: 'Zdroj, ktorý nevidíte, vyžaduje pozornosť.',
  checkRefreshFirst: 'Najprv aktualizovať',
  checkFailedRow: '{subject} – kontrola zlyhala.',
  checkFailedGeneric: 'Kontrola zlyhala.',
  publishReviewAccess: 'Skontrolovať prístup',
  publishRemovalBody: 'v{number} odstraňuje: {what} ({count}). Najprv sa uloží bod obnovenia.',
  publishReviewRemoval: 'Skontrolovať, čo sa odstráni',
  publishAndRemove: 'Zverejniť a odstrániť',
  publishReviewFirst: 'Pred zverejnením skontrolujte, čo sa odstráni.',
  publishDoneTitle: 'Verzia {number} je naživo pre: {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Nájdu ju vo svojej Knižnici a pripnutú pre tím.',
  publishUndo: 'Vrátiť späť',
  publishCopyLink: 'Kopírovať odkaz',
  publishDone: 'Hotovo',
  shareRoleViewMeaning: 'Môže ju otvoriť a čítať',
  shareRoleEditMeaning: 'Môže ju meniť v chate a v inšpektore',
  shareRolePublishMeaning: 'Môže tiež zverejňovať a rušiť zverejnenie',
  shareRoleMenu: 'Rola pre: {name}',
  shareUnpublishConfirm: 'Ľudia stratia prístup. Artefakt a jeho verzie sa zachovajú.',
  shareUnpublishCancel: 'Ponechať naživo',
  sendDestinationLine: '{provider} · ľudí: {count}',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Zhrnutie uvidia všetci v {destination}. Ľudí mimo okruhu tohto artefaktu je {outside} z {total}, takže im odkaz ukáže „Požiadať o prístup“.',
  sendAudienceGeneric: 'Ľudia mimo okruhu uvidia „Požiadať o prístup“.',
  sendPreview: 'Čo uvidia',
  shareTitle: 'Zdieľať',
  shareLoading: 'Načítava sa zdieľanie…',
  shareErrorBody: 'Zdieľanie sa nepodarilo načítať.',
  sharePrivate: 'Tento artefakt vidíte len vy.',
  shareShared: 'Viditeľné pre: {audience}.',
  shareRevoked: 'Zdieľanie je vypnuté. Nikto iný tento artefakt neotvorí.',
  shareOwnerSnapshot:
    'Čitatelia vidia čísla tak, ako boli zverejnené. Nemôžu ich aktualizovať ani upravovať.',
  shareRoleView: 'Môže zobraziť',
  shareRoleEdit: 'Môže upravovať',
  shareRolePublish: 'Môže zverejňovať',
  shareLivePublished: 'Naživo: verzia {number}',
  shareUnpublish: 'Zrušiť zverejnenie',
  shareManageDenied: 'Zmeniť, kto to vidí, môže iba editor.',
  sendTitle: 'Odoslať do',
  sendIntro: 'Odošlite odkaz a zhrnutie cez pripojenú aplikáciu. Každé odoslanie schvaľujete vy.',
  sendLoading: 'Načítavajú sa ciele…',
  sendErrorBody: 'Ciele sa nepodarilo načítať.',
  sendNone:
    'Zatiaľ to nemôže prijať žiadna pripojená aplikácia. Najprv pripojte Slack, Teams alebo e-mail.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-mail',
  sendAudience: 'Pôjde pre: {audience}',
  sendSummary: 'Správa',
  sendLeaves: 'Odošle sa odkaz.',
  sendLeavesAttachment: 'Odošle sa odkaz a skontrolovaný súbor.',
  sendNoAttachments: 'Tento cieľ neprijíma súbory, takže sa odošle iba odkaz.',
  sendScheduled: 'Naplánované odoslania vyžadujú vaše schválenie zakaždým.',
  sendAction: 'Skontrolovať a odoslať',
  sendFileUnsupported: 'Tento cieľ súbor neprijme. Odošlite iba odkaz alebo vyberte iný cieľ.',
  sendLinkOnly: 'Odoslať iba odkaz',
  sendWaiting: 'Čaká na vaše schválenie. Nič sa neodoslalo.',
  sendDelivered: 'Odoslané do: {destination}.',
  sendFailed: 'Neodoslané. Nič Poneva neopustilo.',
  sendUnknown:
    'Doručenie do {destination} sa nepodarilo potvrdiť. Skôr než odošlete znova, skontrolujte to tam.',
  sendBlockedNotAuthorized: 'Na odoslanie nemáte oprávnenie.',
  sendBlockedUnsupported: 'Tento cieľ nie je podporovaný.',
  sendBlockedApproval: 'Schválenie už neplatí. Skontrolujte a odošlite znova.',
  sendBlockedChanged: 'Správa alebo príjemca sa zmenili. Skontrolujte to znova.',
  sendBlockedDependency: 'Pripojená aplikácia nie je dostupná. Skúste to neskôr.',
  libraryTitle: 'Knižnica',
  librarySegmentAll: 'Všetko',
  librarySegmentMine: 'Moje',
  librarySegmentShared: 'Zdieľané so mnou',
  librarySearch: 'Hľadať artefakty',
  libraryKindAll: 'Všetky druhy',
  libraryKindReports: 'Reporty a stránky',
  libraryKindDecks: 'Prezentácie',
  libraryKindDocuments: 'Dokumenty',
  libraryKindSpreadsheets: 'Tabuľky',
  libraryKindApps: 'Aplikácie',
  libraryAppsMatchNoun:
    'one:aplikácia zodpovedá v sekcii Aplikácie →|few:aplikácie zodpovedajú v sekcii Aplikácie →|many:aplikácie zodpovedá v sekcii Aplikácie →|other:aplikácií zodpovedá v sekcii Aplikácie →',
  libraryMake: 'Začať v chate',
  libraryEmptySharedHint: 'Keď s vami niekto zdieľa artefakt, zobrazí sa tu.',
  libraryRefreshFailedAt: 'Aktualizácia sa nepodarila {time}.',
  libraryRefreshTryAgain: 'Skúsiť znova',
  libraryVia: 'cez: {owner}',
  savedPrivate: 'Súkromné',
  savedVersion: 'Verzia {n}',
  libraryLoading: 'Načítava sa Knižnica…',
  libraryErrorBody: 'Knižnicu sa teraz nepodarilo načítať.',
  startersLoadFailed: 'Šablóny sa teraz nepodarilo načítať.',
  libraryCannotOpen: '„{title}“ sa tu zatiaľ nedá otvoriť.',
  libraryEmptyTitle: 'Zatiaľ tu nič nie je',
  libraryEmptyAllTitle: 'Vytvorte niečo, čo stojí za zdieľanie.',
  libraryEmptyAllBody:
    'Dokumenty, prezentácie a aplikácie, ktoré vytvoríte v chate alebo ktoré s vami zdieľajú ostatní, sa zobrazia tu.',
  libraryEmptyMineBody: 'Artefakty, ktoré vytvoríte, sa zobrazia tu.',
  libraryEmptySharedBody: 'Zatiaľ s vami nikto nič nezdieľal.',
  libraryNoMatchTitle: 'Žiadne zhody',
  libraryNoMatchBody: 'Skúste iné slovo alebo iný druh.',
  libraryMore: 'Zobraziť viac',
  libraryOwner: 'Autor: {owner}',
  libraryUpdated: 'Upravené {time}',
  libraryPublishedAs: 'Zverejnené · v{number}',
  libraryDraft: 'Koncept',
  libraryUnpublished: 'Nezverejnené',
  libraryRefreshFailed: 'Aktualizácia zlyhala',
  libraryRefreshRunning: 'Prebieha aktualizácia',
  libraryAudience: 'Pre: {audience}',
  kindReport: 'Report',
  kindPage: 'Stránka',
  kindSite: 'Web',
  kindDeck: 'Prezentácia',
  kindDocument: 'Dokument',
  kindApp: 'Aplikácia',
  kindSpreadsheet: 'Tabuľka',
  kindDashboard: 'Prehľad',
  kindTable: 'Tabuľka',
  kindChart: 'Graf',
  kindExportsReport: 'Export do PDF',
  kindExportsPage: 'Stránka iba na čítanie',
  kindExportsSite: 'Web iba na čítanie',
  kindExportsDeck: 'Prezentovať alebo exportovať do PowerPointu a PDF',
  kindExportsDocument: 'Export do Wordu a PDF',
  kindExportsSpreadsheet: 'Export do Excelu a CSV',
  kindExportsDashboard: 'Živý náhľad',
  kindExportsTable: 'Export do Excelu a CSV',
  kindExportsChart: 'Zobrazenie grafu',
  agentPublished: '{agent}: zverejnené {title} v{number}.',
  agentCheckedOn: 'Skontrolované na: {views}, {themes}.',
  agentUndo: 'Vrátiť späť',
  agentRestorePrevious: 'Obnoviť v{number}',
  agentHeld: '{title} v{number} čaká ako koncept.',
  agentHeldMismatch: 'Čísla, ktoré sa nezhodujú so zdrojmi: {count}.',
  agentHeldCheck: 'Kontrola neprešla.',
  agentFixInChat: 'Opraviť v chate',
  agentConfirmDrop:
    '{agent} chce odstrániť {what} z: {title}. Najprv sa uloží bod obnovenia. Nič sa nezmení, kým sa nerozhodnete.',
  agentConfirmDropCount:
    '{agent} chce odstrániť {what} (hodnôt: {count}) z: {title}. Najprv sa uloží bod obnovenia. Nič sa nezmení, kým sa nerozhodnete.',
  agentViewDesktop: 'počítač',
  agentViewTablet: 'tablet',
  agentViewPhone: 'telefón',
  agentThemeLight: 'svetlý',
  agentThemeDark: 'tmavý',
  agentLookAt: 'Zobraziť v{number}',
  agentConfirmWrite: '{agent} chce zapísať: {what}. Nič sa nezapíše, kým sa nerozhodnete.',
  agentConfirmGrant: '{agent} chce prístup k: {what}. Nič sa nezdieľa, kým sa nerozhodnete.',
  agentReview: 'Skontrolovať',
  agentSkipped: '{agent}: vynechané {part}.',
  agentSkippedWhy:
    'Číta vlastné {system} každého človeka a toto spustenie nemá osobu, v mene ktorej by čítalo.',
  agentChooseWho: 'Vyberte, v mene koho pobeží',
  agentChangeInChat: 'Zmeniť v chate',
  agentFailed: '{agent}: nepodarilo sa aktualizovať {title}.',
  agentFailedBody: 'Posledná zverejnená verzia je stále naživo.',
  agentRetry: 'Skúsiť znova',
  agentRunning: '{agent} aktualizuje {title}.',
  agentMakesTitle: 'Vytvára a aktualizuje',
  agentMakesLoading: 'Načítava sa…',
  agentMakesErrorBody: 'Toto sa nepodarilo načítať.',
  agentMakesNone: 'Tento agent zatiaľ nevytvára žiadne artefakty.',
  agentMakesNew: 'Nový artefakt pri každom spustení',
  agentMakesUpdate: 'Pri každom spustení aktualizuje jeden artefakt',
  agentNextRun: 'Ďalšie spustenie: {when}',
  agentNoSchedule: 'Nenaplánované',
  agentCreditsPerRun: 'Kredity za spustenie: približne {credits}',
  agentCreditsUnknown: 'Kredity za spustenie zatiaľ nie sú známe',
  agentLastResult: 'Posledný výsledok',
  interviewTitle: 'Nastavenie',
  interviewIntro: 'Niekoľko otázok ho vytvaruje.',
  interviewOptional: '(voliteľné)',
  interviewAnswerNeeded: 'Odpovedzte, aby ste mohli pokračovať.',
  interviewReadOnly: 'Iba na čítanie',
  interviewChange: 'Zmeniť',
  interviewConnectionYours: 'vaše pripojenie k {system}',
  interviewConnectionOther: 'pripojenie k {system} používateľa {name}',
  interviewDateAny: 'Akékoľvek obdobie',
  interviewDatePrevious: 'Predchádzajúci mesiac',
  interviewDateNext: 'Ďalší mesiac',
  interviewDatePresets: 'Rýchle rozsahy',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Vymazať',
  interviewDateDone: 'Hotovo',
  interviewChooseSource: 'Vyberte zdroj',
  interviewSourceChosen: 'Zdroj je vybraný',
  interviewSourceUnavailable:
    'Výber zdroja tu zatiaľ nie je k dispozícii, preto nemožno pokračovať.',
  interviewSourceUnavailableOptional:
    'Výber zdroja tu zatiaľ nie je k dispozícii. Môžete pokračovať aj bez neho.',
  interviewStarterWithdrawn:
    'Táto šablóna už nie je k dispozícii. Koncepty, ktoré ste z nej už začali, sa dajú stále otvoriť.',
  interviewDateFrom: 'Od',
  interviewDateTo: 'Do',
  interviewContinue: 'Pokračovať',
  interviewGrantTitle: 'Povoliť čítanie: {source}?',
  interviewGrantBody:
    'Používa {connection}, iba na čítanie, pre: {scope}. Nič sa neprečíta, kým to nepovolíte, a nezobrazujú sa žiadne ukážkové riadky.',
  interviewAllow: 'Povoliť',
  interviewNotNow: 'Teraz nie',
  interviewConfiguring: 'Nastavuje sa podľa vašich odpovedí…',
  interviewChecking: 'Kontrola pred otvorením…',
  interviewComplete: '{title} je pripravené.',
  interviewOpen: 'Otvoriť: {title}',
  interviewBlocked: 'Nedalo sa pokračovať.',
  interviewFailed: 'Nastavenie zlyhalo. Vaše odpovede sú zachované.',
  interviewCancelled: 'Bolo zrušené.',
  interviewSaving: 'Ukladá sa…',
  interviewSaved: 'Uložené',
  interviewConflict:
    'Tento koncept sa zmenil inde. Vaše odpovede tu zostanú, kým nenačítate aktuálny koncept, ktorý ich nahradí.',
  interviewReloadCurrent: 'Načítať aktuálny koncept',
  interviewUnsettled: 'Posledné uloženie sa nepodarilo potvrdiť. Vaše odpovede tu zostávajú.',
  interviewCheckAgain: 'Skontrolovať znova',
  finishAction: 'Uložiť súkromne',
  finishSaving: 'Ukladá sa…',
  finishSaved: '„{title}“ je uložený a vidíte ho len vy.',
  finishNotSaved: 'Neuložilo sa. Váš koncept tu stále je.',
  finishUnconfirmed: 'Nepodarilo sa overiť, či sa uložilo.',
  candidateInvalid: 'Z konceptu sa nepodarilo vytvoriť dokument.',
  finishOpenInLibrary: 'Otvoriť v Knižnici',
  savedOpenFailed: 'Teraz sa to nepodarilo otvoriť.',
  savedInLibrary: 'Nájdete to vo svojej Knižnici.',
  openSavedTitle: 'Otvoriť: {title} →',
  interviewReasonNotAuthorized: 'Na zmenu tohto konceptu nemáte oprávnenie.',
  interviewReasonDraftNotOpen: 'Tento koncept už nie je otvorený.',
  interviewReasonInvalid: 'Jedna z odpovedí nie je platná.',
  interviewReasonObjects: 'Niektoré súbory sa nepodarilo overiť. Vaše odpovede sú zachované.',
  interviewReasonDependency: 'Potrebná služba nie je dostupná. Vaše odpovede sú zachované.',
  interviewReasonReconcile: 'Posledné uloženie sa stále potvrdzuje.',
  exportTitle: 'Exportovať',
  exportIntro: 'Stiahnite si tento artefakt. Súbor uvádza, odkiaľ jeho údaje pochádzajú.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Upraviteľné snímky s poznámkami rečníka',
  exportHintPdf: 'Presne tak, ako sa zobrazuje',
  exportHintDocx: 'Upraviteľný, zachová nadpisy a tabuľky',
  exportHintXlsx: 'Všetky hárky, zachová vzorce',
  exportHintCsv: 'Iba hodnoty, UTF-8',
  exportUnavailable: 'Momentálne nie je k dispozícii',
  exportRunning: 'Pripravuje sa: {format}…',
  exportValidating: 'Kontroluje sa súbor: {format}…',
  exportReady: 'Súbor {format} je pripravený',
  exportDownload: 'Stiahnuť {name}',
  exportVersion: 'Exportovaná verzia {number}',
  exportFailed: 'Súbor {format} sa nepodarilo vytvoriť.',
  exportFailedChecks: 'Súbor {format} neprešiel kontrolami, preto nebol ponúknutý.',
  exportDenied: 'Na export tohto artefaktu nemáte prístup.',
  exportRevoked: 'Zdieľanie sa vypnulo, preto sa tento artefakt už nedá exportovať.',
  exportStale:
    'Tento artefakt sa počas exportu zmenil. Exportujte znova, aby ste získali najnovšiu verziu.',
  exportProvenanceTitle: 'Každý súbor obsahuje',
  exportProvenanceLine: 'Vytvorené v Poneva · {title} · v{number} · {data} · vytvorené {madeAt}',
  exportProvenanceAsOf: 'údaje k {time}',
  exportPlacementNotesAbout: 'Na hárku Poznámky a v informačnom súbore vedľa CSV',
  exportProvenanceRange: 'údaje od {earliest} do {latest}',
  exportProvenanceUnknown: 'vek údajov neznámy',
  exportAboutFile: 'O tomto súbore ({format})',
  exportPlacementFooter: 'V päte každej stránky',
  exportPlacementSlide: 'V päte každého snímku',
  exportPlacementNotes: 'Na hárku Poznámky',
  exportPlacementAbout: 'V informačnom súbore vedľa CSV',
  exportSourcesPage: 'Pridať stránku so zdrojmi',
  exportSourcesPageHint: 'Posledná stránka so zoznamom každého čísla, jeho zdroja a času.',
  exportSourcesPageDeck: 'Súčasťou je aj snímka Zdroje z prezentácie.',
  exportSourcesPageSaved: 'Zapamätané pre tento artefakt, vrátane naplánovaných odoslaní.',
  libraryKindSheetTitle: 'Druh',
  shareAccessSheetTitle: 'Prístup',

  requestShowAll: 'Zobraziť všetko',
  requestShowLess: 'Zobraziť menej',
  approveGettingReady: 'Pripravujeme…',
  approveNotReady: 'Teraz to nemožno schváliť. Znova načítajte stránku a skúste to znova.',
  tryWithModel: 'Skúsiť s modelom {model}',
  tryWithRequesting: 'Spúšťa sa…',
  tryWithUnavailable: 'Teraz to nemožno spustiť.',
};

const sl: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Urejate osnutek različice {number}',
  noChangesToApply: 'Ni sprememb za uveljavitev.',
  appliedDone: 'Uveljavljeno.',
  mentionInChat: 'Omeni v klepetu',

  kindMarkdown: 'Opomba',
  kindKpi: 'Kazalnik',
  libraryExportsList: 'Izvoz v {formats}',
  libraryOpensToRead: 'Odpre se za branje',
  libraryReadOnly: 'Samo za branje',
  libraryLiveView: 'Pogled v živo',
  libraryOneNumber: 'Ena številka · na dan {time}',
  retry: 'Poskusi znova',
  close: 'Zapri',
  panelLoading: 'Nalaganje …',
  panelEmpty: 'Tukaj še ni ničesar.',
  panelDenied: 'Nimate dostopa do tega.',
  panelBlocked: 'To čaka na nekaj, preden se lahko nadaljuje.',
  panelBlockedViewerContext:
    'Ta zagon nima osebe, v imenu katere bi bral, zato se ne more nadaljevati, dokler nekdo ne izbere, v čigavem imenu teče.',
  panelError: 'Tega ni bilo mogoče naložiti.',
  panelStale: 'To je morda zastarelo.',
  sourcesTitle: 'Viri',
  sourcesIntro: 'Od kod prihaja vsaka številka in kako sveža je.',
  sourcesLoading: 'Nalaganje virov …',
  sourcesEmptyTitle: 'Ni virov',
  sourcesEmptyBody: 'Ta artefakt ne uporablja podatkov podjetja.',
  sourcesErrorBody: 'Virov ni bilo mogoče naložiti.',
  sourcesDeniedTitle: 'Nekateri viri so skriti',
  sourcesDeniedBody:
    'Nimate dostopa do virov, uporabljenih tukaj: {count}. Prosite lastnika, naj jih deli z vami.',
  sourceDeniedName: 'Vir, ki ga ne vidite',
  sourceAsOf: 'Stanje: {time}',
  sourceAgeUnknown: 'Starost podatkov ni znana',
  sourceStatusCurrent: 'Aktualen',
  sourceStatusStale: 'Zastarel',
  sourceStatusGap: 'Manjka v zadnji osvežitvi',
  sourceStatusDenied: 'Brez dostopa',
  sourceLastGood: 'Zadnji veljavni: {time}',
  sourceUsedBy: 'Uporablja: {labels}',
  openDocument: 'Odpri dokument',
  versionsShow: 'Pokaži različice',
  versionsHide: 'Skrij različice',
  versionsTitle: 'Različice',
  versionsLoading: 'Nalaganje različic …',
  versionsEmptyTitle: 'Še ni različic',
  versionsEmptyBody: 'Prva različica se pojavi, ko je ta artefakt izdelan.',
  versionsErrorBody: 'Različic ni bilo mogoče naložiti.',
  versionLabel: 'Različica {number}',
  versionOpen: 'Odpri različico {number}',
  versionCurrent: 'Trenutna',
  versionPublished: 'Objavljena',
  versionBy: 'Avtor: {actor}',
  versionOriginNative: 'V aplikaciji Poneva',
  versionOriginMcp: 'prek vašega lastnega agenta',
  versionOriginTerminal: 'prek terminala',
  versionOriginJob: 'Načrtovani zagon',
  versionCausePerson: 'Urejena',
  versionCauseAgent: 'Posodobitev agenta',
  versionCauseRefresh: 'Osvežena',
  versionCauseRestore: 'Obnovljena',
  versionCauseImport: 'Uvožena',
  versionCheckedYes: 'Preverjena',
  versionCheckedNo: 'Še ni preverjena',
  versionCheckedInvalidated: 'Urejena po zadnjem preverjanju',
  compareStart: 'Primerjaj',
  compareWith: 'Primerjaj z različico {number}',
  compareTitle: 'Kaj se je spremenilo od v{from} do v{to}',
  compareFromMark: 'Od',
  compareToMark: 'Do',
  comparePick: 'Izberite drugo različico za primerjavo.',
  compareNone: 'Ni razlik.',
  compareLoading: 'Primerjanje …',
  compareError: 'Primerjave ni bilo mogoče naložiti.',
  compareBefore: 'Prej',
  compareAfter: 'Potem',
  compareAdded: 'Dodano',
  compareRemoved: 'Odstranjeno',
  compareChanged: 'Spremenjeno',
  restore: 'Obnovi to različico',
  restoreConfirmTitle: 'Obnovim različico {number}?',
  restoreConfirmBody: 'Iz različice {number} nastane nova različica. Nič se ne izbriše.',
  restoreConfirm: 'Obnovi',
  restoreCancel: 'Prekliči',
  restoring: 'Obnavljanje …',
  restoreFailed: 'Različice ni bilo mogoče obnoviti.',
  restoreStale:
    'Ta artefakt se je spremenil, medtem ko ste ga gledali. Znova naložite različice in poskusite znova.',
  restoreDenied: 'Nimate dovoljenja za obnavljanje različic.',
  restored: 'Različica {number} je obnovljena kot nova različica.',
  publishTitle: 'Objavi',
  publishIntro: 'Različica {number} bo vidna: {audience}.',
  publishOwnerLine: 'Gledalci vidijo tisto, kar je {owner} lahko videl ob objavi te različice.',
  publishChecksTitle: 'Preverjanja',
  checkPassed: 'Preverjeno',
  checkRunning: 'Preverjanje …',
  checkFailed: 'Preverjanje ni uspelo',
  checkStale: 'Urejeno po zadnjem preverjanju',
  publishFixInChat: 'Popravi v klepetu',
  publishAction: 'Objavi različico {number}',
  publishing: 'Objavljanje …',
  publishedDone: 'Različica {number} je objavljena: {audience}.',
  publishConfirmGrant:
    'Ta različica potrebuje nov dostop do sistema podjetja. Pred objavo preglejte dostop.',
  publishConfirmWrite: 'Ta različica piše v sistem podjetja. Pred objavo preglejte dostop.',
  publishConfirmDestructive:
    'Ta različica odstrani podatke. Najprej to potrdite v aplikaciji Poneva.',
  publishConflict: 'Najprej je bila objavljena druga različica. Znova naložite, da jo vidite.',
  publishBlockedNotAuthorized: 'Nimate dovoljenja za objavo.',
  publishBlockedCheckFailed: 'Preverjanje ni uspelo, zato te različice ni mogoče objaviti.',
  publishBlockedCheckStale: 'Artefakt se je spremenil po zadnjem preverjanju. Preverite ga znova.',
  publishBlockedObjects:
    'Nekaterih datotek ni bilo mogoče preveriti. Čez trenutek poskusite znova.',
  publishBlockedPolicy: 'Nastavitve podjetja so se spremenile. Preglejte jih in poskusite znova.',
  publishBlockedApproval: 'Potrditev ni več veljavna. Potrdite znova.',
  publishBlockedDependency: 'Potrebna storitev ni na voljo. Poskusite pozneje.',
  publishAsk: 'Prosi za objavo: {publishers}',
  publishAskFooter: 'Osebe, ki lahko objavljajo: {publishers}.',
  publishAskNone: 'Prosite nekoga, ki lahko objavlja.',
  publishWarnings: 'Vredno pogleda pred objavo',
  checkWarnStale: '{subject} je zastarel ({time}).',
  checkWarnGap: '{subject} je manjkal v zadnji osvežitvi.',
  checkWarnUnknownSource: 'Vir, ki ga ne vidite, je treba pregledati.',
  checkRefreshFirst: 'Najprej osveži',
  checkFailedRow: '{subject} ni prestal preverjanja.',
  checkFailedGeneric: 'Preverjanje ni uspelo.',
  publishReviewAccess: 'Preglej dostop',
  publishRemovalBody: 'v{number} odstrani: {what} ({count}). Prej se shrani obnovitvena točka.',
  publishReviewRemoval: 'Preglej, kaj se odstrani',
  publishAndRemove: 'Objavi in odstrani',
  publishReviewFirst: 'Pred objavo preglejte, kaj se odstrani.',
  publishDoneTitle: 'Različica {number} je objavljena: {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'Našli jo bodo v svoji knjižnici in pripeto za ekipo.',
  publishUndo: 'Razveljavi',
  publishCopyLink: 'Kopiraj povezavo',
  publishDone: 'Končano',
  shareRoleViewMeaning: 'Lahko odpre in prebere',
  shareRoleEditMeaning: 'Lahko spreminja v klepetu in z inšpektorjem',
  shareRolePublishMeaning: 'Lahko tudi objavi in prekliče objavo',
  shareRoleMenu: 'Vloga: {name}',
  shareUnpublishConfirm: 'Ljudje izgubijo dostop. Artefakt in njegove različice se ohranijo.',
  shareUnpublishCancel: 'Pusti objavljeno',
  sendDestinationLine: '{provider} · oseb: {count}',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Povzetek vidijo vsi v: {destination}. {outside} od {total} ni v občinstvu tega artefakta, zato jim povezava prikaže „Zaprosi za dostop“.',
  sendAudienceGeneric: 'Osebe zunaj občinstva vidijo „Zaprosi za dostop“.',
  sendPreview: 'Kaj bodo videli',
  shareTitle: 'Deli',
  shareLoading: 'Nalaganje deljenja …',
  shareErrorBody: 'Deljenja ni bilo mogoče naložiti.',
  sharePrivate: 'Ta artefakt vidite samo vi.',
  shareShared: 'Vidno: {audience}.',
  shareRevoked: 'Deljenje je izklopljeno. Nihče drug ne more odpreti tega artefakta.',
  shareOwnerSnapshot:
    'Gledalci vidijo številke, kot so bile objavljene. Ne morejo osvežiti ali urejati.',
  shareRoleView: 'Lahko ogleda',
  shareRoleEdit: 'Lahko ureja',
  shareRolePublish: 'Lahko objavlja',
  shareLivePublished: 'Objavljeno: različica {number}',
  shareUnpublish: 'Prekliči objavo',
  shareManageDenied: 'Kdo to vidi, lahko spremeni samo urejevalec.',
  sendTitle: 'Pošlji v',
  sendIntro:
    'Pošljite povezavo in povzetek prek povezane aplikacije. Vsako pošiljanje odobrite vi.',
  sendLoading: 'Nalaganje ciljev …',
  sendErrorBody: 'Ciljev ni bilo mogoče naložiti.',
  sendNone:
    'Nobena povezana aplikacija tega še ne more prejeti. Najprej povežite Slack, Teams ali e-pošto.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-pošta',
  sendAudience: 'Prejme: {audience}',
  sendSummary: 'Sporočilo',
  sendLeaves: 'Pošlje se povezava.',
  sendLeavesAttachment: 'Pošljeta se povezava in pregledana datoteka.',
  sendNoAttachments: 'Ta cilj ne more sprejeti datotek, zato se pošlje samo povezava.',
  sendScheduled: 'Načrtovana pošiljanja potrebujejo vašo odobritev vsakič.',
  sendAction: 'Preglej in pošlji',
  sendFileUnsupported:
    'Ta cilj ne more sprejeti datoteke. Pošljite samo povezavo ali izberite drug cilj.',
  sendLinkOnly: 'Pošlji samo povezavo',
  sendWaiting: 'Čaka na vašo odobritev. Nič ni bilo poslano.',
  sendDelivered: 'Poslano: {destination}.',
  sendFailed: 'Ni bilo poslano. Nič ni zapustilo aplikacije Poneva.',
  sendUnknown:
    'Dostave ni bilo mogoče potrditi: {destination}. Preden pošljete znova, preverite tam.',
  sendBlockedNotAuthorized: 'Nimate dovoljenja za pošiljanje tega.',
  sendBlockedUnsupported: 'Ta cilj ni podprt.',
  sendBlockedApproval: 'Odobritev ni več veljavna. Preglejte in pošljite znova.',
  sendBlockedChanged: 'Sporočilo ali prejemnik se je spremenil. Preglejte znova.',
  sendBlockedDependency: 'Povezana aplikacija ni na voljo. Poskusite pozneje.',
  libraryTitle: 'Knjižnica',
  librarySegmentAll: 'Vse',
  librarySegmentMine: 'Moje',
  librarySegmentShared: 'Deljeno z mano',
  librarySearch: 'Išči artefakte',
  libraryKindAll: 'Vse vrste',
  libraryKindReports: 'Poročila in strani',
  libraryKindDecks: 'Predstavitve',
  libraryKindDocuments: 'Dokumenti',
  libraryKindSpreadsheets: 'Preglednice',
  libraryKindApps: 'Aplikacije',
  libraryAppsMatchNoun:
    'one:aplikacija se ujema v razdelku Aplikacije →|two:aplikaciji se ujemata v razdelku Aplikacije →|few:aplikacije se ujemajo v razdelku Aplikacije →|other:aplikacij se ujema v razdelku Aplikacije →',
  libraryMake: 'Začnite v klepetu',
  libraryEmptySharedHint: 'Ko bo nekdo z vami delil artefakt, se bo pojavil tukaj.',
  libraryRefreshFailedAt: 'Osvežitev ni uspela {time}.',
  libraryRefreshTryAgain: 'Poskusi znova',
  libraryVia: 'prek: {owner}',
  savedPrivate: 'Zasebno',
  savedVersion: 'Različica {n}',
  libraryLoading: 'Nalaganje vaše knjižnice …',
  libraryErrorBody: 'Vaše knjižnice trenutno ni bilo mogoče naložiti.',
  startersLoadFailed: 'Predlog trenutno ni bilo mogoče naložiti.',
  libraryCannotOpen: '»{title}« tukaj še ni mogoče odpreti.',
  libraryEmptyTitle: 'Tukaj še ni ničesar',
  libraryEmptyAllTitle: 'Ustvarite nekaj, kar je vredno deliti.',
  libraryEmptyAllBody:
    'Tukaj so prikazani dokumenti, predstavitve in aplikacije, ki jih ustvarite v klepetu ali jih drugi delijo z vami.',
  libraryEmptyMineBody: 'Tukaj se pojavijo artefakti, ki jih ustvarite.',
  libraryEmptySharedBody: 'Z vami še ni bilo deljeno nič.',
  libraryNoMatchTitle: 'Ni zadetkov',
  libraryNoMatchBody: 'Poskusite z drugo besedo ali drugo vrsto.',
  libraryMore: 'Pokaži več',
  libraryOwner: 'Avtor: {owner}',
  libraryUpdated: 'Urejeno {time}',
  libraryPublishedAs: 'Objavljeno · v{number}',
  libraryDraft: 'Osnutek',
  libraryUnpublished: 'Neobjavljeno',
  libraryRefreshFailed: 'Osvežitev ni uspela',
  libraryRefreshRunning: 'Osveževanje',
  libraryAudience: 'Za: {audience}',
  kindReport: 'Poročilo',
  kindPage: 'Stran',
  kindSite: 'Spletno mesto',
  kindDeck: 'Predstavitev',
  kindDocument: 'Dokument',
  kindApp: 'Aplikacija',
  kindSpreadsheet: 'Preglednica',
  kindDashboard: 'Nadzorna plošča',
  kindTable: 'Tabela',
  kindChart: 'Grafikon',
  kindExportsReport: 'Izvoz v PDF',
  kindExportsPage: 'Stran samo za branje',
  kindExportsSite: 'Spletno mesto samo za branje',
  kindExportsDeck: 'Predstavite ali izvozite v PowerPoint in PDF',
  kindExportsDocument: 'Izvoz v Word in PDF',
  kindExportsSpreadsheet: 'Izvoz v Excel in CSV',
  kindExportsDashboard: 'Pogled v živo',
  kindExportsTable: 'Izvoz v Excel in CSV',
  kindExportsChart: 'Pogled grafikona',
  agentPublished: '{agent} je objavil {title} v{number}.',
  agentCheckedOn: 'Preverjeno: {views}, {themes}.',
  agentUndo: 'Razveljavi',
  agentRestorePrevious: 'Obnovi v{number}',
  agentHeld: '{title} v{number} čaka kot osnutek.',
  agentHeldMismatch: 'Številk, ki se niso ujemale z viri: {count}.',
  agentHeldCheck: 'Preverjanje ni uspelo.',
  agentFixInChat: 'Popravi v klepetu',
  agentConfirmDrop:
    '{agent} želi odstraniti {what} iz {title}. Prej se shrani obnovitvena točka. Nič se ne spremeni, dokler se ne odločite.',
  agentConfirmDropCount:
    '{agent} želi odstraniti {what} (vrednosti: {count}) iz {title}. Prej se shrani obnovitvena točka. Nič se ne spremeni, dokler se ne odločite.',
  agentViewDesktop: 'računalnik',
  agentViewTablet: 'tablica',
  agentViewPhone: 'telefon',
  agentThemeLight: 'svetla',
  agentThemeDark: 'temna',
  agentLookAt: 'Poglej v{number}',
  agentConfirmWrite: '{agent} želi pisati: {what}. Nič se ne zapiše, dokler se ne odločite.',
  agentConfirmGrant: '{agent} želi dostop do: {what}. Nič se ne deli, dokler se ne odločite.',
  agentReview: 'Preglej',
  agentSkipped: '{agent} je preskočil: {part}.',
  agentSkippedWhy:
    'Bere vsakemu uporabniku lasten sistem {system}, ta zagon pa nima osebe, v imenu katere bi bral.',
  agentChooseWho: 'Izberi, v čigavem imenu teče',
  agentChangeInChat: 'Spremeni v klepetu',
  agentFailed: '{agent} ni mogel osvežiti: {title}.',
  agentFailedBody: 'Zadnja objavljena različica je še vedno objavljena.',
  agentRetry: 'Poskusi znova',
  agentRunning: '{agent} posodablja: {title}.',
  agentMakesTitle: 'Ustvarja in posodablja',
  agentMakesLoading: 'Nalaganje …',
  agentMakesErrorBody: 'Tega ni bilo mogoče naložiti.',
  agentMakesNone: 'Ta agent še ne ustvarja artefaktov.',
  agentMakesNew: 'Nov artefakt ob vsakem zagonu',
  agentMakesUpdate: 'Ob vsakem zagonu posodobi en artefakt',
  agentNextRun: 'Naslednji zagon {when}',
  agentNoSchedule: 'Ni načrtovano',
  agentCreditsPerRun: 'Krediti na zagon: približno {credits}',
  agentCreditsUnknown: 'Krediti na zagon še niso znani',
  agentLastResult: 'Zadnji rezultat',
  interviewTitle: 'Nastavi',
  interviewIntro: 'Nekaj vprašanj ga oblikuje.',
  interviewOptional: '(neobvezno)',
  interviewAnswerNeeded: 'Odgovorite, da nadaljujete.',
  interviewReadOnly: 'Samo za branje',
  interviewChange: 'Spremeni',
  interviewConnectionYours: 'vaša povezava {system}',
  interviewConnectionOther: 'povezava {system} uporabnika {name}',
  interviewDateAny: 'Katero koli obdobje',
  interviewDatePrevious: 'Prejšnji mesec',
  interviewDateNext: 'Naslednji mesec',
  interviewDatePresets: 'Hitra obdobja',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Počisti',
  interviewDateDone: 'Končano',
  interviewChooseSource: 'Izberite vir',
  interviewSourceChosen: 'Vir je izbran',
  interviewSourceUnavailable: 'Izbira vira tukaj še ni na voljo, zato ni mogoče nadaljevati.',
  interviewSourceUnavailableOptional:
    'Izbira vira tukaj še ni na voljo. Nadaljujete lahko tudi brez njega.',
  interviewStarterWithdrawn:
    'Ta predloga ni več na voljo. Osnutke, ki ste jih že začeli iz nje, je še vedno mogoče odpreti.',
  interviewDateFrom: 'Od',
  interviewDateTo: 'Do',
  interviewContinue: 'Nadaljuj',
  interviewGrantTitle: 'Dovolim branje: {source}?',
  interviewGrantBody:
    'Uporabi {connection}, samo za branje, za: {scope}. Nič se ne prebere, dokler ne dovolite, vzorčne vrstice pa se ne prikažejo.',
  interviewAllow: 'Dovoli',
  interviewNotNow: 'Ne zdaj',
  interviewConfiguring: 'Nastavljanje na podlagi vaših odgovorov …',
  interviewChecking: 'Preverjanje pred odprtjem …',
  interviewComplete: '{title} je pripravljen.',
  interviewOpen: 'Odpri: {title}',
  interviewBlocked: 'Nadaljevanje ni bilo mogoče.',
  interviewFailed: 'Nastavitev ni uspela. Vaši odgovori so ohranjeni.',
  interviewCancelled: 'To je bilo preklicano.',
  interviewSaving: 'Shranjevanje …',
  interviewSaved: 'Shranjeno',
  interviewConflict:
    'Ta osnutek se je spremenil drugje. Vaši odgovori so tukaj ohranjeni, dokler ne naložite trenutnega osnutka, ki jih nadomesti.',
  interviewReloadCurrent: 'Naloži trenutni osnutek',
  interviewUnsettled:
    'Zadnjega shranjevanja ni bilo mogoče potrditi. Vaši odgovori so tukaj ohranjeni.',
  interviewCheckAgain: 'Preveri znova',
  finishAction: 'Shrani zasebno',
  finishSaving: 'Shranjevanje…',
  finishSaved: '»{title}« je shranjen in ga vidite samo vi.',
  finishNotSaved: 'Ni bilo shranjeno. Vaš osnutek je še vedno tukaj.',
  finishUnconfirmed: 'Nismo mogli potrditi, ali je bilo shranjeno.',
  candidateInvalid: 'Iz osnutka ni bilo mogoče narediti dokumenta.',
  finishOpenInLibrary: 'Odpri v Knjižnici',
  savedOpenFailed: 'Trenutno se ni dalo odpreti.',
  savedInLibrary: 'Najdete ga v svoji Knjižnici.',
  openSavedTitle: 'Odpri: {title} →',
  interviewReasonNotAuthorized: 'Nimate dovoljenja za spreminjanje tega osnutka.',
  interviewReasonDraftNotOpen: 'Ta osnutek ni več odprt.',
  interviewReasonInvalid: 'Eden od odgovorov ni veljaven.',
  interviewReasonObjects: 'Nekaterih datotek ni bilo mogoče preveriti. Vaši odgovori so ohranjeni.',
  interviewReasonDependency: 'Potrebna storitev ni na voljo. Vaši odgovori so ohranjeni.',
  interviewReasonReconcile: 'Zadnje shranjevanje se še potrjuje.',
  exportTitle: 'Izvozi',
  exportIntro: 'Prenesite ta artefakt. Datoteka navaja, od kod prihajajo njeni podatki.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Prosojnice, ki jih lahko urejate, z govornikovimi opombami',
  exportHintPdf: 'Natanko tako, kot je prikazano',
  exportHintDocx: 'Za urejanje, ohrani naslove in tabele',
  exportHintXlsx: 'Vsi listi, ohrani formule',
  exportHintCsv: 'Samo vrednosti, UTF-8',
  exportUnavailable: 'Trenutno ni na voljo',
  exportRunning: 'Pripravljanje: {format} …',
  exportValidating: 'Preverjanje datoteke: {format} …',
  exportReady: 'Datoteka {format} je pripravljena',
  exportDownload: 'Prenesi: {name}',
  exportVersion: 'Izvožena različica {number}',
  exportFailed: 'Datoteke {format} ni bilo mogoče izdelati.',
  exportFailedChecks: 'Datoteka {format} ni prestala preverjanj, zato ni bila ponujena.',
  exportDenied: 'Nimate dostopa za izvoz tega artefakta.',
  exportRevoked: 'Deljenje je bilo izklopljeno, zato tega artefakta ni več mogoče izvoziti.',
  exportStale:
    'Ta artefakt se je med izvozom spremenil. Izvozite znova, da dobite najnovejšo različico.',
  exportProvenanceTitle: 'Vsaka datoteka vsebuje',
  exportProvenanceLine: 'Izdelano s Poneva · {title} · v{number} · {data} · izdelano {madeAt}',
  exportProvenanceAsOf: 'podatki na dan {time}',
  exportPlacementNotesAbout: 'Na listu Opombe in v informacijski datoteki ob CSV',
  exportProvenanceRange: 'podatki od {earliest} do {latest}',
  exportProvenanceUnknown: 'starost podatkov ni znana',
  exportAboutFile: 'O tej datoteki ({format})',
  exportPlacementFooter: 'V nogi vsake strani',
  exportPlacementSlide: 'V nogi vsake prosojnice',
  exportPlacementNotes: 'Na listu Opombe',
  exportPlacementAbout: 'V informacijski datoteki ob CSV',
  exportSourcesPage: 'Dodaj stran z viri',
  exportSourcesPageHint: 'Zadnja stran, ki navaja vsako številko, njen vir in čas.',
  exportSourcesPageDeck: 'Priložena je tudi lastna prosojnica Viri predstavitve.',
  exportSourcesPageSaved: 'Zapomnjeno za ta artefakt, vključno z načrtovanimi pošiljanji.',
  libraryKindSheetTitle: 'Vrsta',
  shareAccessSheetTitle: 'Dostop',

  requestShowAll: 'Pokaži vse',
  requestShowLess: 'Pokaži manj',
  approveGettingReady: 'Pripravljamo…',
  approveNotReady: 'Tega trenutno ni mogoče odobriti. Znova naložite stran in poskusite znova.',
  tryWithModel: 'Poskusi z modelom {model}',
  tryWithRequesting: 'Zaganjanje…',
  tryWithUnavailable: 'Tega trenutno ni mogoče zagnati.',
};

const sv: Record<ArtifactPanelsCopyKey, string> = {
  editingDraftOf: 'Redigerar ett utkast av version {number}',
  noChangesToApply: 'Inga ändringar att tillämpa.',
  appliedDone: 'Tillämpat.',
  mentionInChat: 'Nämn i chatten',

  kindMarkdown: 'Anteckning',
  kindKpi: 'Nyckeltal',
  libraryExportsList: 'Exporteras till {formats}',
  libraryOpensToRead: 'Öppnas för läsning',
  libraryReadOnly: 'Skrivskyddad',
  libraryLiveView: 'Livevy',
  libraryOneNumber: 'Ett tal · per {time}',
  retry: 'Försök igen',
  close: 'Stäng',
  panelLoading: 'Läser in…',
  panelEmpty: 'Inget här ännu.',
  panelDenied: 'Du har inte åtkomst till detta.',
  panelBlocked: 'Detta väntar på något innan det kan fortsätta.',
  panelBlockedViewerContext:
    'Den här körningen har ingen person att läsa som, så den kan inte fortsätta förrän någon väljer vem den körs som.',
  panelError: 'Det gick inte att läsa in detta.',
  panelStale: 'Detta kan vara inaktuellt.',
  sourcesTitle: 'Källor',
  sourcesIntro: 'Varifrån varje siffra kommer, och hur aktuell den är.',
  sourcesLoading: 'Läser in källor…',
  sourcesEmptyTitle: 'Inga källor',
  sourcesEmptyBody: 'Den här artefakten använder inga företagsdata.',
  sourcesErrorBody: 'Det gick inte att läsa in källorna.',
  sourcesDeniedTitle: 'Vissa källor är dolda',
  sourcesDeniedBody:
    'Du har inte åtkomst till {count} källor som används här. Be en ägare dela dem.',
  sourceDeniedName: 'Källa du inte kan se',
  sourceAsOf: 'Per {time}',
  sourceAgeUnknown: 'Datans ålder är okänd',
  sourceStatusCurrent: 'Aktuell',
  sourceStatusStale: 'Inaktuell',
  sourceStatusGap: 'Saknades vid senaste uppdateringen',
  sourceStatusDenied: 'Ingen åtkomst',
  sourceLastGood: 'Senast fungerande: {time}',
  sourceUsedBy: 'Används av {labels}',
  openDocument: 'Öppna dokument',
  versionsShow: 'Visa versioner',
  versionsHide: 'Dölj versioner',
  versionsTitle: 'Versioner',
  versionsLoading: 'Läser in versioner…',
  versionsEmptyTitle: 'Inga versioner ännu',
  versionsEmptyBody: 'Den första versionen visas när den här artefakten har byggts.',
  versionsErrorBody: 'Det gick inte att läsa in versionerna.',
  versionLabel: 'Version {number}',
  versionOpen: 'Öppna version {number}',
  versionCurrent: 'Aktuell',
  versionPublished: 'Publicerad',
  versionBy: 'Av {actor}',
  versionOriginNative: 'I Poneva',
  versionOriginMcp: 'via din egen agent',
  versionOriginTerminal: 'via terminalen',
  versionOriginJob: 'Schemalagd körning',
  versionCausePerson: 'Redigerad',
  versionCauseAgent: 'Agentuppdatering',
  versionCauseRefresh: 'Uppdaterad',
  versionCauseRestore: 'Återställd',
  versionCauseImport: 'Importerad',
  versionCheckedYes: 'Kontrollerad',
  versionCheckedNo: 'Inte kontrollerad ännu',
  versionCheckedInvalidated: 'Redigerad efter senaste kontrollen',
  compareStart: 'Jämför',
  compareWith: 'Jämför med version {number}',
  compareTitle: 'Vad som ändrades från v{from} till v{to}',
  compareFromMark: 'Från',
  compareToMark: 'Till',
  comparePick: 'Välj en andra version att jämföra med.',
  compareNone: 'Inga skillnader.',
  compareLoading: 'Jämför…',
  compareError: 'Det gick inte att läsa in jämförelsen.',
  compareBefore: 'Före',
  compareAfter: 'Efter',
  compareAdded: 'Tillagt',
  compareRemoved: 'Borttaget',
  compareChanged: 'Ändrat',
  restore: 'Återställ den här versionen',
  restoreConfirmTitle: 'Återställa version {number}?',
  restoreConfirmBody: 'Detta skapar en ny version från version {number}. Inget raderas.',
  restoreConfirm: 'Återställ',
  restoreCancel: 'Avbryt',
  restoring: 'Återställer…',
  restoreFailed: 'Det gick inte att återställa versionen.',
  restoreStale:
    'Den här artefakten ändrades medan du tittade. Läs in versionerna igen och försök på nytt.',
  restoreDenied: 'Du har inte behörighet att återställa versioner.',
  restored: 'Version {number} återställd som en ny version.',
  publishTitle: 'Publicera',
  publishIntro: 'Version {number} blir synlig för {audience}.',
  publishOwnerLine: 'Läsare ser det som {owner} kunde se när den här versionen publicerades.',
  publishChecksTitle: 'Kontroller',
  checkPassed: 'Kontrollerad',
  checkRunning: 'Kontrollerar…',
  checkFailed: 'En kontroll misslyckades',
  checkStale: 'Redigerad efter senaste kontrollen',
  publishFixInChat: 'Åtgärda i chatten',
  publishAction: 'Publicera version {number}',
  publishing: 'Publicerar…',
  publishedDone: 'Version {number} är live för {audience}.',
  publishConfirmGrant:
    'Den här versionen behöver ny åtkomst till ett företagssystem. Granska åtkomsten innan du publicerar.',
  publishConfirmWrite:
    'Den här versionen skriver till ett företagssystem. Granska åtkomsten innan du publicerar.',
  publishConfirmDestructive: 'Den här versionen tar bort data. Bekräfta det i Poneva först.',
  publishConflict: 'En annan version publicerades först. Läs in igen för att se den.',
  publishBlockedNotAuthorized: 'Du har inte behörighet att publicera.',
  publishBlockedCheckFailed: 'En kontroll misslyckades, så den här versionen kan inte publiceras.',
  publishBlockedCheckStale: 'Artefakten ändrades efter sin senaste kontroll. Kontrollera den igen.',
  publishBlockedObjects: 'Vissa filer kunde inte verifieras. Försök igen om en stund.',
  publishBlockedPolicy: 'Företagets inställningar ändrades. Granska och försök igen.',
  publishBlockedApproval: 'Bekräftelsen är inte längre giltig. Bekräfta igen.',
  publishBlockedDependency: 'En tjänst som behövs är inte tillgänglig. Försök igen senare.',
  publishAsk: 'Be {publishers} publicera',
  publishAskFooter: 'Personer som kan publicera: {publishers}.',
  publishAskNone: 'Be någon som kan publicera.',
  publishWarnings: 'Värt att titta på innan du publicerar',
  checkWarnStale: '{subject} är inaktuell ({time}).',
  checkWarnGap: '{subject} saknades vid senaste uppdateringen.',
  checkWarnUnknownSource: 'En källa du inte kan se behöver ses över.',
  checkRefreshFirst: 'Uppdatera först',
  checkFailedRow: '{subject} klarade inte sin kontroll.',
  checkFailedGeneric: 'En kontroll misslyckades.',
  publishReviewAccess: 'Granska åtkomst',
  publishRemovalBody: 'v{number} tar bort {what} ({count}). En återställningspunkt sparas först.',
  publishReviewRemoval: 'Granska vad som tas bort',
  publishAndRemove: 'Publicera och ta bort',
  publishReviewFirst: 'Granska vad som tas bort innan du publicerar.',
  publishDoneTitle: 'Version {number} är live för {audience}.',
  publishDoneWho: '{names}',
  publishDoneWhere: 'De hittar den i sitt bibliotek och fäst för teamet.',
  publishUndo: 'Ångra',
  publishCopyLink: 'Kopiera länk',
  publishDone: 'Klar',
  shareRoleViewMeaning: 'Kan öppna och läsa den',
  shareRoleEditMeaning: 'Kan ändra den i chatten och med inspektören',
  shareRolePublishMeaning: 'Kan också publicera och avpublicera',
  shareRoleMenu: 'Roll för {name}',
  shareUnpublishConfirm: 'Personer förlorar åtkomsten. Artefakten och dess versioner behålls.',
  shareUnpublishCancel: 'Behåll live',
  sendDestinationLine: '{provider} · {count} personer',
  sendDestinationLineUnknown: '{provider}',
  sendAudienceAuthoritative:
    'Alla i {destination} ser sammanfattningen. {outside} av de {total} ingår inte i den här artefaktens målgrupp, så länken visar ”Be om åtkomst” för dem.',
  sendAudienceGeneric: 'Personer utanför målgruppen ser ”Be om åtkomst”.',
  sendPreview: 'Det här kommer de att se',
  shareTitle: 'Dela',
  shareLoading: 'Läser in delning…',
  shareErrorBody: 'Det gick inte att läsa in delningen.',
  sharePrivate: 'Bara du kan se den här artefakten.',
  shareShared: 'Synlig för {audience}.',
  shareRevoked: 'Delning är avstängd. Ingen annan kan öppna den här artefakten.',
  shareOwnerSnapshot:
    'Läsare ser siffrorna som de publicerades. De kan inte uppdatera eller redigera.',
  shareRoleView: 'Kan visa',
  shareRoleEdit: 'Kan redigera',
  shareRolePublish: 'Kan publicera',
  shareLivePublished: 'Live: version {number}',
  shareUnpublish: 'Avpublicera',
  shareManageDenied: 'Bara en redigerare kan ändra vem som ser detta.',
  sendTitle: 'Skicka till',
  sendIntro:
    'Skicka en länk och en sammanfattning via en ansluten app. Du godkänner varje utskick.',
  sendLoading: 'Läser in mottagare…',
  sendErrorBody: 'Det gick inte att läsa in mottagarna.',
  sendNone: 'Ingen ansluten app kan ta emot detta ännu. Anslut Slack, Teams eller e-post först.',
  sendProviderSlack: 'Slack',
  sendProviderTeams: 'Teams',
  sendProviderEmail: 'E-post',
  sendAudience: 'Går till {audience}',
  sendSummary: 'Meddelande',
  sendLeaves: 'En länk skickas.',
  sendLeavesAttachment: 'En länk och den granskade filen skickas.',
  sendNoAttachments: 'Den här mottagaren kan inte ta emot filer, så bara en länk skickas.',
  sendScheduled: 'Schemalagda utskick behöver ditt godkännande varje gång.',
  sendAction: 'Granska och skicka',
  sendFileUnsupported:
    'Den här mottagaren kan inte ta emot filen. Skicka bara en länk, eller välj en annan mottagare.',
  sendLinkOnly: 'Skicka bara en länk',
  sendWaiting: 'Väntar på ditt godkännande. Inget har skickats.',
  sendDelivered: 'Skickat till {destination}.',
  sendFailed: 'Det skickades inte. Inget lämnade Poneva.',
  sendUnknown:
    'Vi kunde inte bekräfta leveransen till {destination}. Kontrollera där innan du skickar igen.',
  sendBlockedNotAuthorized: 'Du har inte behörighet att skicka detta.',
  sendBlockedUnsupported: 'Den här mottagaren stöds inte.',
  sendBlockedApproval: 'Godkännandet är inte längre giltigt. Granska och skicka igen.',
  sendBlockedChanged: 'Meddelandet eller mottagaren ändrades. Granska det igen.',
  sendBlockedDependency: 'Den anslutna appen är inte tillgänglig. Försök igen senare.',
  libraryTitle: 'Bibliotek',
  librarySegmentAll: 'Alla',
  librarySegmentMine: 'Mina',
  librarySegmentShared: 'Delat med mig',
  librarySearch: 'Sök artefakter',
  libraryKindAll: 'Alla typer',
  libraryKindReports: 'Rapporter och sidor',
  libraryKindDecks: 'Presentationer',
  libraryKindDocuments: 'Dokument',
  libraryKindSpreadsheets: 'Kalkylblad',
  libraryKindApps: 'Appar',
  libraryAppsMatchNoun: 'one:app matchar i Appar →|other:appar matchar i Appar →',
  libraryMake: 'Börja i chatten',
  libraryEmptySharedHint: 'När någon delar en artefakt med dig visas den här.',
  libraryRefreshFailedAt: 'Det gick inte att uppdatera {time}.',
  libraryRefreshTryAgain: 'Försök igen',
  libraryVia: 'via {owner}',
  savedPrivate: 'Privat',
  savedVersion: 'Version {n}',
  libraryLoading: 'Läser in ditt bibliotek…',
  libraryErrorBody: 'Det gick inte att läsa in ditt bibliotek just nu.',
  startersLoadFailed: 'Mallarna kunde inte läsas in just nu.',
  libraryCannotOpen: '”{title}” kan inte öppnas här ännu.',
  libraryEmptyTitle: 'Inget här ännu',
  libraryEmptyAllTitle: 'Skapa något värt att dela.',
  libraryEmptyAllBody:
    'Dokument, presentationer och appar som du skapar i chatten, eller som andra delar med dig, visas här.',
  libraryEmptyMineBody: 'Artefakter du skapar visas här.',
  libraryEmptySharedBody: 'Inget har delats med dig ännu.',
  libraryNoMatchTitle: 'Inga träffar',
  libraryNoMatchBody: 'Prova ett annat ord eller en annan typ.',
  libraryMore: 'Visa fler',
  libraryOwner: 'Av {owner}',
  libraryUpdated: 'Ändrad {time}',
  libraryPublishedAs: 'Publicerad · v{number}',
  libraryDraft: 'Utkast',
  libraryUnpublished: 'Avpublicerad',
  libraryRefreshFailed: 'Uppdateringen misslyckades',
  libraryRefreshRunning: 'Uppdaterar',
  libraryAudience: 'För {audience}',
  kindReport: 'Rapport',
  kindPage: 'Sida',
  kindSite: 'Webbplats',
  kindDeck: 'Presentation',
  kindDocument: 'Dokument',
  kindApp: 'App',
  kindSpreadsheet: 'Kalkylblad',
  kindDashboard: 'Instrumentpanel',
  kindTable: 'Tabell',
  kindChart: 'Diagram',
  kindExportsReport: 'Exporteras till PDF',
  kindExportsPage: 'Skrivskyddad sida',
  kindExportsSite: 'Skrivskyddad webbplats',
  kindExportsDeck: 'Presentera, eller exportera till PowerPoint och PDF',
  kindExportsDocument: 'Exporteras till Word och PDF',
  kindExportsSpreadsheet: 'Exporteras till Excel och CSV',
  kindExportsDashboard: 'Liveläge',
  kindExportsTable: 'Exporteras till Excel och CSV',
  kindExportsChart: 'Diagramvy',
  agentPublished: '{agent} publicerade {title} v{number}.',
  agentCheckedOn: 'Kontrollerad för {views}, {themes}.',
  agentUndo: 'Ångra',
  agentRestorePrevious: 'Återställ v{number}',
  agentHeld: '{title} v{number} väntar som utkast.',
  agentHeldMismatch: '{count} siffror stämde inte med sina källor.',
  agentHeldCheck: 'En kontroll godkändes inte.',
  agentFixInChat: 'Åtgärda i chatten',
  agentConfirmDrop:
    '{agent} vill ta bort {what} från {title}. En återställningspunkt sparas först. Inget ändras förrän du bestämmer.',
  agentConfirmDropCount:
    '{agent} vill ta bort {what} ({count} värden) från {title}. En återställningspunkt sparas först. Inget ändras förrän du bestämmer.',
  agentViewDesktop: 'dator',
  agentViewTablet: 'surfplatta',
  agentViewPhone: 'mobil',
  agentThemeLight: 'ljust tema',
  agentThemeDark: 'mörkt tema',
  agentLookAt: 'Titta på v{number}',
  agentConfirmWrite: '{agent} vill skriva {what}. Inget skrivs förrän du bestämmer.',
  agentConfirmGrant: '{agent} vill ha åtkomst till {what}. Inget delas förrän du bestämmer.',
  agentReview: 'Granska',
  agentSkipped: '{agent} hoppade över {part}.',
  agentSkippedWhy:
    'Den läser varje persons eget {system}, och den här körningen har ingen person att läsa som.',
  agentChooseWho: 'Välj vem den körs som',
  agentChangeInChat: 'Ändra i chatten',
  agentFailed: '{agent} kunde inte uppdatera {title}.',
  agentFailedBody: 'Den senast publicerade versionen är fortfarande live.',
  agentRetry: 'Försök igen',
  agentRunning: '{agent} uppdaterar {title}.',
  agentMakesTitle: 'Skapar och uppdaterar',
  agentMakesLoading: 'Läser in…',
  agentMakesErrorBody: 'Det gick inte att läsa in detta.',
  agentMakesNone: 'Den här agenten skapar inga artefakter ännu.',
  agentMakesNew: 'En ny artefakt vid varje körning',
  agentMakesUpdate: 'Uppdaterar en artefakt vid varje körning',
  agentNextRun: 'Nästa körning {when}',
  agentNoSchedule: 'Inte schemalagd',
  agentCreditsPerRun: 'Cirka {credits} krediter per körning',
  agentCreditsUnknown: 'Krediter per körning är inte kända ännu',
  agentLastResult: 'Senaste resultat',
  interviewTitle: 'Ställ in det',
  interviewIntro: 'Några frågor formar det.',
  interviewOptional: '(valfritt)',
  interviewAnswerNeeded: 'Svara på detta för att fortsätta.',
  interviewReadOnly: 'Skrivskyddad',
  interviewChange: 'Ändra',
  interviewConnectionYours: 'din {system}-anslutning',
  interviewConnectionOther: '{name}s {system}-anslutning',
  interviewDateAny: 'Valfri period',
  interviewDatePrevious: 'Föregående månad',
  interviewDateNext: 'Nästa månad',
  interviewDatePresets: 'Snabbval',
  interviewDatePending: '{from} – …',
  interviewDateClear: 'Rensa',
  interviewDateDone: 'Klar',
  interviewChooseSource: 'Välj en källa',
  interviewSourceChosen: 'Källa vald',
  interviewSourceUnavailable:
    'Att välja en källa är inte tillgängligt här ännu, så det går inte att fortsätta.',
  interviewSourceUnavailableOptional:
    'Det går inte att välja en källa här än. Du kan fortsätta utan.',
  interviewStarterWithdrawn:
    'Den här mallen är inte längre tillgänglig. Utkast som du redan har påbörjat från den går fortfarande att öppna.',
  interviewDateFrom: 'Från',
  interviewDateTo: 'Till',
  interviewContinue: 'Fortsätt',
  interviewGrantTitle: 'Tillåta läsning av {source}?',
  interviewGrantBody:
    'Den använder {connection}, skrivskyddat, för {scope}. Inget läses förrän du tillåter det, och inga exempelrader visas.',
  interviewAllow: 'Tillåt',
  interviewNotNow: 'Inte nu',
  interviewConfiguring: 'Ställer in det utifrån dina svar…',
  interviewChecking: 'Kontrollerar det innan det öppnas…',
  interviewComplete: '{title} är klar.',
  interviewOpen: 'Öppna {title}',
  interviewBlocked: 'Det gick inte att fortsätta.',
  interviewFailed: 'Inställningen misslyckades. Dina svar finns kvar.',
  interviewCancelled: 'Detta avbröts.',
  interviewSaving: 'Sparar…',
  interviewSaved: 'Sparat',
  interviewConflict:
    'Det här utkastet ändrades någon annanstans. Dina svar finns kvar här tills du läser in det aktuella utkastet, som ersätter dem.',
  interviewReloadCurrent: 'Läs in det aktuella utkastet',
  interviewUnsettled: 'Vi kunde inte bekräfta din senaste sparning. Dina svar finns kvar här.',
  interviewCheckAgain: 'Kontrollera igen',
  finishAction: 'Spara privat',
  finishSaving: 'Sparar…',
  finishSaved: '{title} är sparad och syns bara för dig.',
  finishNotSaved: 'Det sparades inte. Ditt utkast finns kvar här.',
  finishUnconfirmed: 'Vi kunde inte bekräfta om det sparades.',
  candidateInvalid: 'Utkastet kunde inte göras till ett dokument.',
  finishOpenInLibrary: 'Öppna i Bibliotek',
  savedOpenFailed: 'Det gick inte att öppna just nu.',
  savedInLibrary: 'Den finns i ditt Bibliotek.',
  openSavedTitle: 'Öppna {title} →',
  interviewReasonNotAuthorized: 'Du har inte behörighet att ändra det här utkastet.',
  interviewReasonDraftNotOpen: 'Det här utkastet är inte längre öppet.',
  interviewReasonInvalid: 'Ett av svaren är inte giltigt.',
  interviewReasonObjects: 'Vissa filer kunde inte verifieras. Dina svar finns kvar.',
  interviewReasonDependency: 'En tjänst som behövs är inte tillgänglig. Dina svar finns kvar.',
  interviewReasonReconcile: 'Den senaste sparningen bekräftas fortfarande.',
  exportTitle: 'Exportera',
  exportIntro: 'Ladda ner den här artefakten. Filen anger varifrån dess data kommer.',
  exportFormatPptx: 'PowerPoint',
  exportFormatPdf: 'PDF',
  exportFormatDocx: 'Word',
  exportFormatXlsx: 'Excel',
  exportFormatCsv: 'CSV',
  exportHintPptx: 'Redigerbara bilder med talaranteckningar',
  exportHintPdf: 'Exakt som visas',
  exportHintDocx: 'Redigerbart, behåller rubriker och tabeller',
  exportHintXlsx: 'Alla blad, behåller formler',
  exportHintCsv: 'Bara värden, UTF-8',
  exportUnavailable: 'Inte tillgängligt just nu',
  exportRunning: 'Förbereder {format}…',
  exportValidating: 'Kontrollerar {format}-filen…',
  exportReady: '{format}-filen är klar',
  exportDownload: 'Ladda ner {name}',
  exportVersion: 'Exporterad version {number}',
  exportFailed: 'Det gick inte att skapa {format}-filen.',
  exportFailedChecks: '{format}-filen klarade inte sina kontroller, så den erbjöds inte.',
  exportDenied: 'Du har inte åtkomst att exportera den här artefakten.',
  exportRevoked: 'Delningen stängdes av, så den här artefakten kan inte längre exporteras.',
  exportStale:
    'Den här artefakten ändrades under exporten. Exportera igen för att få den senaste versionen.',
  exportProvenanceTitle: 'Varje fil innehåller',
  exportProvenanceLine: 'Skapad med Poneva · {title} · v{number} · {data} · skapad {madeAt}',
  exportProvenanceAsOf: 'data per {time}',
  exportPlacementNotesAbout: 'På bladet Anteckningar, och i en om-fil bredvid CSV-filen',
  exportProvenanceRange: 'data från {earliest} till {latest}',
  exportProvenanceUnknown: 'datans ålder okänd',
  exportAboutFile: 'Om den här filen ({format})',
  exportPlacementFooter: 'I sidfoten på varje sida',
  exportPlacementSlide: 'I sidfoten på varje bild',
  exportPlacementNotes: 'På bladet Anteckningar',
  exportPlacementAbout: 'I en om-fil bredvid CSV-filen',
  exportSourcesPage: 'Lägg till en källsida',
  exportSourcesPageHint: 'En sista sida som listar varje siffra, dess källa och dess tid.',
  exportSourcesPageDeck: 'Presentationens egen källbild följer med.',
  exportSourcesPageSaved: 'Sparas för den här artefakten, även vid schemalagda utskick.',
  libraryKindSheetTitle: 'Typ',
  shareAccessSheetTitle: 'Åtkomst',

  requestShowAll: 'Visa allt',
  requestShowLess: 'Visa mindre',
  approveGettingReady: 'Gör i ordning…',
  approveNotReady: 'Det går inte att godkänna just nu. Ladda om sidan och försök igen.',
  tryWithModel: 'Prova med {model}',
  tryWithRequesting: 'Startar…',
  tryWithUnavailable: 'Det går inte att starta just nu.',
};

const artifactPanelsCopy: NamespaceCatalog<ArtifactPanelsCopyKey> = {
  bg,
  hr,
  cs,
  da,
  nl,
  en,
  et,
  fi,
  fr,
  de,
  el,
  hu,
  ga,
  it,
  lv,
  lt,
  mt,
  pl,
  pt,
  ro,
  sk,
  sl,
  es,
  sv,
};
export default artifactPanelsCopy;
